import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';

// =============================================
// CONFIGURAZIONE SUPABASE
// =============================================
const SUPABASE_URL = 'https://riofnsttuvjknvdrtrcr.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJpb2Zuc3R0dXZqa252ZHJ0cmNyIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njk3NzY2NTgsImV4cCI6MjA4NTM1MjY1OH0.t6dt47I49kE-23E2D6yoj2oKNFmj2_sa2-91rH8C8RE';

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// Data di consegna prevista di una scheda (null se non c'è o è "da valutare")
const calcolaConsegnaPrevista = (scheda) => {
  const t = scheda && scheda.tempoStimato;
  if (t) {
    if (t.startsWith('data:') || t.startsWith('data_')) {
      const d = new Date(t.slice(5) + 'T00:00:00');
      return isNaN(d) ? null : d;
    }
    const base = new Date(scheda.dataIngresso || Date.now());
    if (isNaN(base)) return null;
    const giorni = { stesso_giorno: 0, '1_giorno': 1, '2_3_giorni': 3, '1_settimana': 7, '2_settimane': 14 }[t];
    if (giorni !== undefined) { base.setDate(base.getDate() + giorni); return base; }
  }
  if (scheda && scheda.dataConsegnaPrevista) {
    const d = new Date(scheda.dataConsegnaPrevista);
    return isNaN(d) ? null : d;
  }
  return null;
};

// =============================================
// FIRME CLIENTE - helper (v2.4.6)
// =============================================
// Una firma richiesta al cliente è identificata da tipo + data di richiesta
const firmaInAttesa = (f) => !!(f && f.richiesta && !f.firma);
const chiaveFirma = (schedaId, f) => schedaId + '|' + (f.tipo || '') + '|' + (f.data || '');
// Se il cliente ha già firmato (dato remoto), non sovrascrivere con la versione locale "in attesa"
const unisciFirme = (locali, remote) => (locali || []).map(f => {
  if (!firmaInAttesa(f)) return f;
  const r = (remote || []).find(x => x && x.firma && x.tipo === f.tipo && x.data === f.data);
  return r ? { ...f, firma: r.firma, dataFirma: r.dataFirma } : f;
});
const FIRME_NOTIFICATE_KEY = 'pccare-firme-notificate';
const leggiFirmeNotificate = () => {
  try { const v = localStorage.getItem(FIRME_NOTIFICATE_KEY); return v ? JSON.parse(v) : null; } catch (e) { return null; }
};
const scriviFirmeNotificate = (lista) => {
  try { localStorage.setItem(FIRME_NOTIFICATE_KEY, JSON.stringify(lista.slice(-500))); } catch (e) {}
};
const suonoFirma = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    [660, 880].forEach((freq, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = freq;
      o.connect(g); g.connect(ctx.destination);
      const t = ctx.currentTime + i * 0.18;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.25, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
      o.start(t); o.stop(t + 0.17);
    });
    setTimeout(() => { try { ctx.close(); } catch (e) {} }, 800);
  } catch (e) {}
};

// Utente admin di default (non eliminabile)
const DEFAULT_ADMIN = { username: 'Oreste', password: 'Pa$$w0rd', isAdmin: true, tabs: [] };

// Funzione per ottenere gli utenti (dal localStorage per il login, poi dal data)
const getStoredUsers = () => {
  try {
    const stored = localStorage.getItem('resoconto-users');
    if (stored) {
      const users = JSON.parse(stored);
      // Assicurati che l'admin esista sempre
      if (!users.find(u => u.username === 'Oreste')) {
        users.unshift(DEFAULT_ADMIN);
      }
      return users;
    }
  } catch (e) {}
  return [DEFAULT_ADMIN];
};

const DEFAULT_CATEGORIE_ENTRATE = [
  { id: 'banche', nome: 'Banche e Conti', icon: '🏦', color: '#3b82f6' },
  { id: 'cassa', nome: 'Cassa', icon: '💵', color: '#22c55e' },
  { id: 'clienti', nome: 'Clienti', icon: '👥', color: '#8b5cf6' },
  { id: 'linee', nome: 'Linee / Vendite', icon: '📦', color: '#f59e0b' },
  { id: 'altro_in', nome: 'Altro', icon: '📥', color: '#64748b' },
];

const DEFAULT_CATEGORIE_USCITE = [
  { id: 'affitti', nome: 'Affitti', icon: '🏠', color: '#ef4444' },
  { id: 'finanziamenti', nome: 'Finanziamenti', icon: '🏛️', color: '#dc2626' },
  { id: 'utenze', nome: 'Utenze', icon: '💡', color: '#f97316' },
  { id: 'fornitori', nome: 'Fornitori', icon: '🚚', color: '#8b5cf6' },
  { id: 'tasse', nome: 'Tasse e Commercialista', icon: '📋', color: '#6366f1' },
  { id: 'altro_out', nome: 'Altro', icon: '📤', color: '#64748b' },
];

const ICONE = ['🏦','💵','👥','📦','📥','🏠','🏛️','💡','🚚','📋','📤','🚗','✈️','🍔','🛒','💳','📱','🎮','🏥','🎓','💰','💸','📊','🔧','⚡'];
const COLORI = ['#3b82f6','#22c55e','#8b5cf6','#f59e0b','#64748b','#ef4444','#dc2626','#f97316','#6366f1','#ec4899','#14b8a6','#84cc16'];

const DEFAULT_DATA = {
  categorieEntrate: DEFAULT_CATEGORIE_ENTRATE,
  categorieUscite: DEFAULT_CATEGORIE_USCITE,
  entrate: [
    { id: '1', nome: 'BANCA CARIPARMA PC CARE', importo: 0, note: '', categoria: 'banche' },
    { id: '2', nome: 'CASSA NEGOZIO', importo: 0, note: '', categoria: 'cassa' },
    { id: '3', nome: 'CASSA CASA', importo: 0, note: '', categoria: 'cassa' },
    { id: '4', nome: 'BANCA CARIPARMA PERSONALE', importo: 0, note: '', categoria: 'banche' },
    { id: '5', nome: 'BANCA BNL', importo: 0, note: '', categoria: 'banche' },
    { id: '6', nome: 'BUNQ PERSONALE', importo: 0, note: '', categoria: 'banche' },
    { id: '7', nome: 'BUNQ 081', importo: 0, note: '', categoria: 'banche' },
    { id: '8', nome: 'POSTEPAY', importo: 0, note: '', categoria: 'banche' },
    { id: '9', nome: 'PAYPAL', importo: 0, note: '', categoria: 'banche' },
    { id: '10', nome: 'SALDO AMAZON', importo: 0, note: '', categoria: 'banche' },
    { id: '11', nome: 'REVOLUT', importo: 0, note: '', categoria: 'banche' },
  ],
  uscite: [
    { id: '1', nome: 'FITTO NEGOZIO', importo: 0, note: '', categoria: 'affitti' },
    { id: '2', nome: 'FITTO CASA', importo: 0, note: '', categoria: 'affitti' },
    { id: '3', nome: 'AUTO', importo: 0, note: '', categoria: 'altro_out' },
    { id: '4', nome: 'AMEX', importo: 0, note: '', categoria: 'finanziamenti' },
    { id: '5', nome: 'FINANZIAM. COMPASS', importo: 0, note: '', categoria: 'finanziamenti' },
    { id: '6', nome: 'FINANZIAMENTO BNL', importo: 0, note: '', categoria: 'finanziamenti' },
    { id: '7', nome: 'COFIDIS', importo: 0, note: '', categoria: 'finanziamenti' },
    { id: '8', nome: 'EDISON CASA', importo: 0, note: '', categoria: 'utenze' },
    { id: '9', nome: 'EDISON NEGOZIO', importo: 0, note: '', categoria: 'utenze' },
    { id: '10', nome: 'LINEE', importo: 0, note: '', categoria: 'utenze' },
    { id: '11', nome: 'TASSE/IVA', importo: 0, note: '', categoria: 'tasse' },
    { id: '12', nome: 'ACQUA', importo: 0, note: '', categoria: 'utenze' },
    { id: '13', nome: 'COMMERCIALISTA', importo: 0, note: '', categoria: 'tasse' },
    { id: '14', nome: 'CONDOMINIO', importo: 0, note: '', categoria: 'affitti' },
  ],
  contratti: [], ricorrenze: [], storico: [], note: [], weekend: [],
  magazzino: [], movimentiMagazzino: [], // Magazzino: persistito fra le impostazioni chiave-valore
  laboratorio: [], // Schede riparazione
  laboratorioContatore: 0, // Contatore annuale per ID schede
  clientiLab: [], // Anagrafica clienti laboratorio
  tabOrder: ['resoconto', 'weekend', 'contratti', 'ricorrenze', 'storico', 'note', 'config']
};

const fmtCur = (n) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n || 0);

// Saldo effettivamente da incassare alla consegna di una riparazione.
// L'eventuale acconto è già stato registrato su un conto al momento
// dell'accettazione, quindi alla consegna si incassa solo la differenza.
// L'acconto viene scalato SOLO se era stato registrato su un conto: se manca
// accontoConto quei soldi non sono mai entrati in cassa, quindi vanno ancora incassati.
const accontoRegistrato = (scheda) => (scheda?.accontoConto ? (parseFloat(scheda?.acconto) || 0) : 0);

const saldoDaIncassare = (scheda) => {
  const totale = (scheda?.importoFinale || scheda?.preventivo || 0);
  return Math.max(0, totale - accontoRegistrato(scheda));
};

// Conti disponibili per incasso: prende dinamicamente dalle entrate esistenti,
// escludendo le voci temporanee di riparazioni e ordini.
// Definita a livello di modulo perché serve in LaboratorioTab, SchedaModal e OrdiniTab.
const getContiDisponibili = (data) => (data?.entrate || [])
  .filter(e => !e.isRiparazione && !e.isOrdine && !e.id?.startsWith('lab_') && !e.id?.startsWith('ord_') && !e.id?.startsWith('da_incassare_'))
  .sort((a, b) => (b.importo || 0) - (a.importo || 0))
  .map(e => ({
    id: e.id,
    nome: e.nome,
    importo: e.importo || 0,
    icon: e.nome?.includes('BANCA') || e.nome?.includes('BNL') || e.nome?.includes('BUNQ') ? '🏦' :
          e.nome?.includes('CASSA') ? '💵' :
          e.nome?.includes('POSTEPAY') ? '💳' :
          e.nome?.includes('PAYPAL') ? '🅿️' :
          e.nome?.includes('REVOLUT') ? '💜' :
          e.nome?.includes('AMAZON') ? '📦' : '💰'
  }));
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '';

const exportExcel = (data) => {
  let csv = '\ufeff';
  csv += 'ENTRATE\nNome;Importo;Categoria\n';
  data.entrate.forEach(e => csv += `${e.nome};${e.importo};${e.categoria}\n`);
  csv += '\nUSCITE\nNome;Importo;Categoria\n';
  data.uscite.forEach(e => csv += `${e.nome};${e.importo};${e.categoria}\n`);
  csv += '\nCONTRATTI\nCliente;Descrizione;Scadenza;Costo\n';
  (data.contratti||[]).forEach(c => csv += `${c.cliente};${c.descrizione};${c.scadenza};${c.costo}\n`);
  csv += '\nRICORRENZE\nNome;Tipo;Importo;Giorno\n';
  (data.ricorrenze||[]).forEach(r => csv += `${r.nome};${r.tipo};${r.importo};${r.giorno}\n`);
  csv += '\nSTORICO\nMese;Entrate;Uscite;Saldo\n';
  (data.storico||[]).forEach(s => csv += `${s.mese};${s.entrate};${s.uscite};${s.saldo}\n`);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `resoconto_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
};

function Modal({ title, children, onClose, onSave, dark }) {
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20}} onClick={onClose}>
      <div style={{background:dark?'#1e293b':'#fff',borderRadius:20,width:'100%',maxWidth:420,maxHeight:'90vh',overflow:'auto'}} onClick={e=>e.stopPropagation()}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'16px 20px',borderBottom:'1px solid rgba(128,128,128,0.2)'}}>
          <h2 style={{margin:0,fontSize:18,fontWeight:700,color:dark?'#e2e8f0':'#1e293b'}}>{title}</h2>
          <button onClick={onClose} style={{background:'transparent',border:'none',fontSize:24,color:'#64748b',cursor:'pointer'}}>×</button>
        </div>
        <div style={{padding:20}}>{children}</div>
        <div style={{display:'flex',gap:12,padding:20,borderTop:'1px solid rgba(128,128,128,0.2)'}}>
          <button onClick={onClose} style={{flex:1,padding:12,background:dark?'rgba(255,255,255,0.1)':'#f1f5f9',border:'none',borderRadius:10,color:'#64748b',cursor:'pointer'}}>Annulla</button>
          <button onClick={onSave} style={{flex:1,padding:12,background:'linear-gradient(135deg,#3b82f6,#2563eb)',border:'none',borderRadius:10,color:'#fff',fontWeight:600,cursor:'pointer'}}>Salva</button>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [auth, setAuth] = useState(false);
  const [user, setUser] = useState(null);
  const [userId, setUserId] = useState(null); // ID utente Supabase
  const [checking, setChecking] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [salvataggioManuale, setSalvataggioManuale] = useState(null); // null, 'saving', 'success', 'error'
  const [tab, setTab] = useState('home');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarPinned, setSidebarPinned] = useState(() => localStorage.getItem('sidebar-pinned') === 'true');
  const [editModal, setEditModal] = useState(null);
  const [addModal, setAddModal] = useState(null);
  const [catModal, setCatModal] = useState(null);
  const [alertContratti, setAlertContratti] = useState(null);
  const [alertAbbonamenti, setAlertAbbonamenti] = useState(null);
  const [search, setSearch] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [showPrint, setShowPrint] = useState(false);
  const [showPromemoriaDropdown, setShowPromemoriaDropdown] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('resoconto-theme') || 'dark');
  
  // Undo/Redo - Stack semplice
  const undoStackRef = useRef([]); // Stack degli stati precedenti
  const redoStackRef = useRef([]); // Stack degli stati per redo
  const MAX_UNDO = 30;
  const isUndoRedoRef = useRef(false); // Flag per evitare di salvare durante undo/redo
  // FIX undo: serializzazione dell'ultimo stato effettivamente salvato.
  // Serve perché diverse funzioni (upd, transferParziale, payParziale, eseguiSomma...)
  // modificano gli oggetti dentro `data` in place prima di chiamare save():
  // fotografare `data` dentro save() catturava quindi lo stato GIÀ modificato.
  const lastSavedSnapshotRef = useRef(null);

  // FIX: ogni sostituzione dello stato che NON passa da save() (caricamento da
  // Supabase, fallback su localStorage, ripristino backup, salvataggio automatico
  // dello storico) deve aggiornare anche la fotografia usata dall'undo. Altrimenti
  // l'undo successivo riporterebbe indietro a uno stato precedente al caricamento
  // e lo rimanderebbe pure su Supabase, cancellando il ripristino.
  const setDataConSnapshot = (d) => {
    setData(d);
    try { lastSavedSnapshotRef.current = JSON.stringify(d); } catch (e) { lastSavedSnapshotRef.current = null; }
  };
  
  // Ref per debounce salvataggio
  const saveTimeoutRef = useRef(null);
  const pendingDataRef = useRef(null);

  // Upload file su Supabase Storage
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Backup Drive notification
  const [backupNotification, setBackupNotification] = useState(null); // null, 'loading', 'success', 'error'
  const [backupMessage, setBackupMessage] = useState('');
  const [backupStatus, setBackupStatus] = useState(() => {
    const oggi = new Date().toISOString().split('T')[0];
    const lastBackup = localStorage.getItem('pccare-last-backup-date');
    return lastBackup === oggi ? 'done' : 'pending'; // 'pending', 'done', 'error'
  });
  
  // Forza re-render per aggiornare stato pulsanti
  const [, forceUpdate] = useState(0);

  const dark = theme === 'dark';
  const bg = dark ? 'linear-gradient(145deg,#0c0f1a,#1a1f35)' : 'linear-gradient(145deg,#f8fafc,#e2e8f0)';
  const bgCard = dark ? 'rgba(30,41,59,0.5)' : 'rgba(255,255,255,0.9)';
  const bgInput = dark ? 'rgba(15,23,42,0.6)' : '#f1f5f9';
  const text = dark ? '#e2e8f0' : '#1e293b';
  const muted = '#64748b';
  const bord = dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const inputStyle = {width:'100%',padding:'12px 14px',background:bgInput,border:'1px solid ' + bord,borderRadius:10,color:text,fontSize:14,outline:'none',boxSizing:'border-box'};
  const labelStyle = {display:'block',fontSize:12,fontWeight:500,color:muted,marginBottom:6,marginTop:12};

  useEffect(() => { localStorage.setItem('resoconto-theme', theme); }, [theme]);

  // Ottieni utenti (da data o localStorage)
  const getUsers = useCallback(() => {
    if (data?.utenti && data.utenti.length > 0) {
      // Sincronizza con localStorage per il login
      localStorage.setItem('resoconto-users', JSON.stringify(data.utenti));
      return data.utenti;
    }
    return getStoredUsers();
  }, [data]);

  useEffect(() => {
    const s = localStorage.getItem('resoconto-session');
    if (s) { 
      try { 
        const p = JSON.parse(s); 
        if (p.username && p.userId) { 
          setAuth(true); 
          setUser(p.username);
          setUserId(p.userId);
        } 
      } catch(e) {} 
    }
    setChecking(false);
  }, []);

  const login = async (u, p) => {
    try {
      // Cerca utente in Supabase
      const { data: users, error } = await supabase
        .from('utenti')
        .select('*')
        .eq('username', u)
        .eq('password_hash', p)
        .single();
      
      if (error || !users) {
        console.error('Login fallito:', error);
        return false;
      }
      
      setAuth(true); 
      setUser(users.username);
      setUserId(users.id);
      localStorage.setItem('resoconto-session', JSON.stringify({ 
        username: users.username, 
        userId: users.id,
        isAdmin: users.is_admin 
      })); 
      localStorage.setItem('resoconto-remembered-user', users.username);
      return true; 
    } catch (e) {
      console.error('Errore login:', e);
      return false;
    }
  };

  const logout = () => {
    setAuth(false);
    setUser(null);
    setData(null);
    // FIX: senza questo azzeramento gli stack undo conservavano gli snapshot
    // dell'utente precedente: dopo il login di un altro utente, premere Annulla
    // avrebbe scritto i dati del primo nell'account del secondo.
    undoStackRef.current = [];
    redoStackRef.current = [];
    lastSavedSnapshotRef.current = null;
    localStorage.removeItem('resoconto-session');
    // NON rimuovere resoconto-remembered-user per mantenere l'utente memorizzato
  };

  // =============================================
  // BACKUP AUTOMATICO SU GOOGLE DRIVE
  // =============================================
  const eseguiBackupDrive = async (dataToBackup) => {
    if (!dataToBackup) return;
    
    const oggi = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const lastBackup = localStorage.getItem('pccare-last-backup-date');
    
    // Se già fatto backup oggi, esci
    if (lastBackup === oggi) {
      console.log('📁 Backup già eseguito oggi');
      return;
    }
    
    console.log('📁 Avvio backup automatico su Google Drive...');
    setBackupNotification('loading');
    setBackupMessage('Backup in corso su Google Drive...');
    
    try {
      // Prepara il file JSON
      const backupData = {
        ...dataToBackup,
        backupDate: new Date().toISOString(),
        backupVersion: '2.4.4'
      };
      const jsonContent = JSON.stringify(backupData, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json' });
      
      // Nome file con data e ora
      const now = new Date();
      const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const fileName = `PCCare_Backup_${timestamp}.json`;
      
      // 1. Cerca o crea la cartella Backup_PCCare
      let folderId = localStorage.getItem('pccare-backup-folder-id');
      
      if (!folderId) {
        // Cerca la cartella esistente
        const searchResponse = await fetch(
          `https://www.googleapis.com/drive/v3/files?q=name='Backup_PCCare' and mimeType='application/vnd.google-apps.folder' and trashed=false&fields=files(id,name)`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('google-access-token')}`
            }
          }
        );
        
        if (searchResponse.ok) {
          const searchData = await searchResponse.json();
          if (searchData.files && searchData.files.length > 0) {
            folderId = searchData.files[0].id;
            localStorage.setItem('pccare-backup-folder-id', folderId);
          }
        }
        
        // Se non esiste, creala
        if (!folderId) {
          const createFolderResponse = await fetch(
            'https://www.googleapis.com/drive/v3/files',
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('google-access-token')}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                name: 'Backup_PCCare',
                mimeType: 'application/vnd.google-apps.folder'
              })
            }
          );
          
          if (createFolderResponse.ok) {
            const folderData = await createFolderResponse.json();
            folderId = folderData.id;
            localStorage.setItem('pccare-backup-folder-id', folderId);
          }
        }
      }
      
      // 2. Carica il file nella cartella
      const metadata = {
        name: fileName,
        parents: folderId ? [folderId] : []
      };
      
      const formData = new FormData();
      formData.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
      formData.append('file', blob);
      
      const uploadResponse = await fetch(
        'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('google-access-token')}`
          },
          body: formData
        }
      );
      
      if (!uploadResponse.ok) {
        throw new Error('Errore upload file');
      }
      
      // 3. Pulisci backup vecchi (mantieni ultimi 30)
      if (folderId) {
        const listResponse = await fetch(
          `https://www.googleapis.com/drive/v3/files?q='${folderId}' in parents and trashed=false&orderBy=createdTime desc&fields=files(id,name,createdTime)&pageSize=100`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('google-access-token')}`
            }
          }
        );
        
        if (listResponse.ok) {
          const listData = await listResponse.json();
          const files = listData.files || [];
          
          // Elimina file oltre il 30°
          if (files.length > 30) {
            const filesToDelete = files.slice(30);
            for (const file of filesToDelete) {
              await fetch(
                `https://www.googleapis.com/drive/v3/files/${file.id}`,
                {
                  method: 'DELETE',
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('google-access-token')}`
                  }
                }
              );
            }
            console.log(`📁 Eliminati ${filesToDelete.length} backup vecchi`);
          }
        }
      }
      
      // Salva data ultimo backup
      localStorage.setItem('pccare-last-backup-date', oggi);
      
      setBackupStatus('done');
      setBackupNotification('success');
      setBackupMessage(`✅ Backup completato: ${fileName}`);
      
      // Nascondi notifica dopo 5 secondi
      setTimeout(() => {
        setBackupNotification(null);
      }, 5000);
      
    } catch (error) {
      console.error('❌ Errore backup Drive:', error);
      setBackupStatus('error');
      setBackupNotification('error');
      setBackupMessage('❌ Errore durante il backup su Drive');
      
      // Nascondi notifica dopo 5 secondi
      setTimeout(() => {
        setBackupNotification(null);
      }, 5000);
    }
  };

  // Auto-logout dopo 20 minuti di inattività
  useEffect(() => {
    if (!auth) return;
    
    let timeoutId;
    const TIMEOUT_DURATION = 20 * 60 * 1000; // 20 minuti in millisecondi
    
    const resetTimer = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        alert('Sessione scaduta per inattività. Effettua nuovamente il login.');
        logout();
      }, TIMEOUT_DURATION);
    };
    
    // Eventi che resettano il timer
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => window.addEventListener(event, resetTimer));
    
    // Avvia il timer iniziale
    resetTimer();
    
    // Cleanup
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      events.forEach(event => window.removeEventListener(event, resetTimer));
    };
  }, [auth]);

  // =============================================
  // GOOGLE DRIVE - Autenticazione OAuth
  // =============================================
  const GOOGLE_CLIENT_ID = '706207346262-nt1c3d1r3shosi9m3k15fheeh4ou2m04.apps.googleusercontent.com';
  const GOOGLE_SCOPES = 'https://www.googleapis.com/auth/drive.file';
  
  // Carica Google Identity Services
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);
  
  const collegaGoogleDrive = () => {
    if (!window.google?.accounts?.oauth2) {
      alert('Errore: Google Identity Services non caricato. Ricarica la pagina.');
      return;
    }
    
    const tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: GOOGLE_SCOPES,
      callback: (response) => {
        if (response.access_token) {
          localStorage.setItem('google-access-token', response.access_token);
          // Salva anche la scadenza (default 1 ora)
          const expiresAt = Date.now() + (response.expires_in * 1000);
          localStorage.setItem('google-token-expires', expiresAt.toString());
          setBackupStatus('pending');
          alert('✅ Google Drive collegato!\n\nIl backup automatico è ora attivo.');
          forceUpdate(n => n + 1);
          // Avvia subito un backup
          setTimeout(() => eseguiBackupDrive(data), 1000);
        } else if (response.error) {
          console.error('Errore OAuth:', response.error);
          alert('Errore durante il collegamento: ' + response.error);
        }
      },
      error_callback: (error) => {
        console.error('Errore OAuth:', error);
        alert('Errore durante il collegamento a Google Drive');
      }
    });
    
    tokenClient.requestAccessToken({ prompt: 'consent' });
  };
  
  const scollegaGoogleDrive = () => {
    if (window.confirm('Vuoi scollegare Google Drive?\n\nI backup automatici verranno disattivati.')) {
      const token = localStorage.getItem('google-access-token');
      // Revoca il token
      if (token && window.google?.accounts?.oauth2) {
        window.google.accounts.oauth2.revoke(token, () => {
          console.log('Token revocato');
        });
      }
      localStorage.removeItem('google-access-token');
      localStorage.removeItem('google-token-expires');
      localStorage.removeItem('pccare-backup-folder-id');
      localStorage.removeItem('pccare-last-backup-date');
      setBackupStatus('pending');
      alert('Google Drive scollegato.');
      forceUpdate(n => n + 1);
    }
  };
  
  // Verifica se il token è scaduto
  const isGoogleTokenValid = () => {
    const token = localStorage.getItem('google-access-token');
    const expires = localStorage.getItem('google-token-expires');
    if (!token) return false;
    if (expires && Date.now() > parseInt(expires)) {
      // Token scaduto, rimuovilo
      localStorage.removeItem('google-access-token');
      localStorage.removeItem('google-token-expires');
      return false;
    }
    return true;
  };
  
  // Rinnova token se necessario prima del backup
  const rinnovaTokenSeNecessario = async () => {
    const expires = localStorage.getItem('google-token-expires');
    // Se scade entro 5 minuti, rinnova
    if (expires && Date.now() > parseInt(expires) - 300000) {
      return new Promise((resolve) => {
        if (!window.google?.accounts?.oauth2) {
          resolve(false);
          return;
        }
        const tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: GOOGLE_SCOPES,
          callback: (response) => {
            if (response.access_token) {
              localStorage.setItem('google-access-token', response.access_token);
              const expiresAt = Date.now() + (response.expires_in * 1000);
              localStorage.setItem('google-token-expires', expiresAt.toString());
              resolve(true);
            } else {
              resolve(false);
            }
          }
        });
        tokenClient.requestAccessToken({ prompt: '' });
      });
    }
    return true;
  };

  // =============================================
  // SUPABASE STORAGE - Upload/Download File
  // =============================================
  
  // Upload file su Supabase Storage
  const uploadFileToStorage = async (file, cartella = 'generale') => {
    if (!userId) {
      alert('Devi essere loggato per caricare file');
      return null;
    }
    
    setUploadingFile(true);
    
    try {
      // Crea un nome file unico con timestamp
      const timestamp = Date.now();
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `${userId}/${cartella}/${timestamp}_${sanitizedName}`;
      
      // Upload su Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('file-utili')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });
      
      if (uploadError) {
        console.error('Errore upload:', uploadError);
        // Se il bucket non esiste, prova a crearlo
        if (uploadError.message?.includes('not found')) {
          alert('Bucket "file-utili" non trovato. Crealo nel pannello Supabase.');
        } else {
          alert('Errore durante il caricamento: ' + uploadError.message);
        }
        setUploadingFile(false);
        return null;
      }
      
      // Ottieni URL pubblico
      const { data: urlData } = supabase.storage
        .from('file-utili')
        .getPublicUrl(filePath);
      
      setUploadingFile(false);
      
      return {
        id: timestamp.toString(),
        nome: file.name,
        path: filePath,
        url: urlData.publicUrl,
        tipo: file.type,
        size: file.size,
        dataUpload: new Date().toISOString()
      };
    } catch (e) {
      console.error('Errore upload:', e);
      setUploadingFile(false);
      alert('Errore durante il caricamento del file');
      return null;
    }
  };
  
  // Download file da Supabase Storage
  const downloadFileFromStorage = async (filePath, fileName) => {
    try {
      const { data, error } = await supabase.storage
        .from('file-utili')
        .download(filePath);
      
      if (error) {
        console.error('Errore download:', error);
        alert('Errore durante il download: ' + error.message);
        return false;
      }
      
      // Crea un link di download
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      return true;
    } catch (e) {
      console.error('Errore download:', e);
      alert('Errore durante il download del file');
      return false;
    }
  };
  
  // Elimina file da Supabase Storage
  const deleteFileFromStorage = async (filePath) => {
    try {
      const { error } = await supabase.storage
        .from('file-utili')
        .remove([filePath]);
      
      if (error) {
        console.error('Errore eliminazione:', error);
        return false;
      }
      
      return true;
    } catch (e) {
      console.error('Errore eliminazione:', e);
      return false;
    }
  };

  useEffect(() => { 
    if (auth && userId) {
      console.log('🔄 Avvio caricamento dati per userId:', userId);
      loadData(); 
    }
  }, [auth, userId]);

  // Stato per tracciare se c'è un'interazione in corso (modal aperti)
  const [syncEnabled, setSyncEnabled] = useState(true);
  
  // Auto-sync: ricarica dati solo quando non ci sono modal aperti
  // Il sync avviene solo quando l'utente clicca esplicitamente sul tasto sync o dopo 2 minuti di inattività
  useEffect(() => {
    if (!auth || !userId) return;
    
    // Sync automatico ogni 2 minuti (solo se la tab è visibile e non ci sono interazioni)
    const syncInterval = setInterval(() => {
      if (document.visibilityState === 'visible' && syncEnabled) {
        // Controlla se l'utente ha interagito negli ultimi 30 secondi
        const lastInteraction = window.lastUserInteraction || 0;
        const now = Date.now();
        if (now - lastInteraction > 30000) {
          console.log('🔄 Sync automatico (nessuna interazione recente)...');
          loadData();
        }
      }
    }, 120000); // 2 minuti
    
    // Traccia le interazioni dell'utente
    const trackInteraction = () => {
      window.lastUserInteraction = Date.now();
    };
    
    document.addEventListener('click', trackInteraction);
    document.addEventListener('keydown', trackInteraction);
    
    return () => {
      clearInterval(syncInterval);
      document.removeEventListener('click', trackInteraction);
      document.removeEventListener('keydown', trackInteraction);
    };
  }, [auth, userId, syncEnabled]);

  // Applica ricorrenze automaticamente al caricamento dati
  useEffect(() => {
    if (!data || !data.ricorrenze || data.ricorrenze.length === 0) return;
    
    const oggi = new Date();
    const giornoOggi = oggi.getDate();
    const meseOggi = oggi.getMonth();
    const annoOggi = oggi.getFullYear();
    const chiaveOggi = `${annoOggi}-${meseOggi}-${giornoOggi}`;
    
    let d = { ...data };
    let cnt = 0;
    let ricorrenzeAggiornate = [...(data.ricorrenze || [])];
    
    data.ricorrenze.forEach((r, index) => {
      // Controlla se oggi è il giorno della ricorrenza
      if (r.giorno === giornoOggi) {
        // Controlla se è già stata applicata oggi
        const ultimaApplicazione = r.ultimaApplicazione || '';
        
        if (ultimaApplicazione !== chiaveOggi) {
          // Applica la ricorrenza
          const arr = r.tipo === 'entrata' ? d.entrate : d.uscite;
          const v = arr.find(x => x.nome === r.nome);
          if (v) {
            v.importo = (v.importo || 0) + r.importo;
            cnt++;
            
            // Aggiorna la data di ultima applicazione
            ricorrenzeAggiornate[index] = {
              ...r,
              ultimaApplicazione: chiaveOggi
            };
            
            console.log(`🔄 Ricorrenza applicata: ${r.nome} (+${r.importo}€)`);
          }
        }
      }
    });
    
    if (cnt > 0) {
      d.ricorrenze = ricorrenzeAggiornate;
      save(d);
      console.log(`✅ ${cnt} ricorrenze applicate automaticamente`);
    }
  }, [data?.ricorrenze?.length]); // Esegui quando cambiano le ricorrenze o al primo caricamento

  // Disabilita scroll su tutti gli input type="number" per evitare modifiche accidentali
  useEffect(() => {
    const handleWheel = (e) => {
      if (e.target.type === 'number') {
        e.target.blur(); // Rimuove il focus per evitare la modifica
      }
    };
    
    document.addEventListener('wheel', handleWheel, { passive: false });
    return () => document.removeEventListener('wheel', handleWheel);
  }, []);

  const loadData = async (forceLocal = false) => {
    if (!userId) {
      console.warn('loadData: userId non disponibile');
      return;
    }
    setLoading(true);
    console.log('📥 Caricamento dati...', forceLocal ? '(da localStorage)' : '(da Supabase)');
    
    // Se forzato locale, usa localStorage come fallback
    if (forceLocal) {
      const local = localStorage.getItem('resoconto-backup');
      if (local) {
        try {
          const localData = JSON.parse(local);
          setDataConSnapshot({ ...DEFAULT_DATA, ...localData });
          console.log('✅ Dati caricati da localStorage');
        } catch (e) {
          console.error('❌ Errore parsing localStorage:', e);
        }
      }
      setLoading(false);
      return;
    }
    
    try {
      // Carica tutti i dati da Supabase in parallelo
      const [
        { data: categorie },
        { data: entrate },
        { data: uscite },
        { data: clienti },
        { data: riparazioni },
        { data: ordini },
        { data: preventivi },
        { data: vendite },
        { data: movimenti },
        { data: abbonamenti },
        { data: contratti },
        { data: ricorrenze },
        { data: promemoria },
        { data: fileUtili },
        { data: impostazioni }
      ] = await Promise.all([
        supabase.from('categorie').select('*').eq('user_id', userId),
        supabase.from('entrate').select('*').eq('user_id', userId),
        supabase.from('uscite').select('*').eq('user_id', userId),
        supabase.from('clienti').select('*').eq('user_id', userId),
        supabase.from('riparazioni').select('*').eq('user_id', userId).order('data_ingresso', { ascending: false }),
        supabase.from('ordini').select('*').eq('user_id', userId).order('data_ordine', { ascending: false }),
        supabase.from('preventivi').select('*').eq('user_id', userId).order('data', { ascending: false }),
        supabase.from('vendite').select('*').eq('user_id', userId).order('data', { ascending: false }),
        supabase.from('movimenti').select('*').eq('user_id', userId).order('data', { ascending: false }),
        supabase.from('abbonamenti').select('*').eq('user_id', userId),
        supabase.from('contratti').select('*').eq('user_id', userId),
        supabase.from('ricorrenze').select('*').eq('user_id', userId),
        supabase.from('promemoria').select('*').eq('user_id', userId),
        supabase.from('file_utili').select('*').eq('user_id', userId),
        supabase.from('impostazioni').select('*').eq('user_id', userId)
      ]);

      // Converti i dati dal formato Supabase al formato app
      const categorieEntrate = (categorie || []).filter(c => c.tipo === 'entrate').map(c => ({
        id: c.id,
        nome: c.nome,
        icon: c.icon,
        ordine: c.ordine
      }));
      
      const categorieUscite = (categorie || []).filter(c => c.tipo === 'uscite').map(c => ({
        id: c.id,
        nome: c.nome,
        icon: c.icon,
        ordine: c.ordine
      }));

      // Converte entrate
      const entrateApp = (entrate || []).map(e => ({
        id: e.id,
        nome: e.nome,
        importo: parseFloat(e.importo) || 0,
        icon: e.icon,
        categoria: e.categoria_id,
        note: e.note,
        isAcconto: e.is_acconto || false,
        isRiparazione: e.is_riparazione || false,
        isOrdine: e.is_ordine || false,
        schedaId: e.scheda_id,
        ordineId: e.ordine_id,
        metodoPagamento: e.metodo_pagamento
      }));

      // Converte uscite
      const usciteApp = (uscite || []).map(u => ({
        id: u.id,
        nome: u.nome,
        importo: parseFloat(u.importo) || 0,
        icon: u.icon,
        categoria: u.categoria_id,
        note: u.note
      }));

      // Converte clienti
      const clientiApp = (clienti || []).map(c => ({
        id: c.id,
        nome: c.nome,
        telefono: c.telefono,
        email: c.email,
        indirizzo: c.indirizzo,
        azienda: c.azienda,
        codiceFiscale: c.codice_fiscale,
        note: c.note
      }));

      // Converte riparazioni (laboratorio)
      const laboratorioApp = (riparazioni || []).map(r => ({
        id: r.id,
        numero: r.numero,
        cliente: r.cliente_nome,
        telefono: r.telefono,
        email: r.email,
        dispositivo: r.dispositivo,
        marca: r.marca,
        modello: r.modello,
        seriale: r.seriale,
        accessori: r.accessori,
        problema: r.problema,
        diagnosi: r.diagnosi,
        soluzione: r.soluzione,
        stato: r.stato,
        priorita: r.priorita,
        preventivo: parseFloat(r.preventivo) || 0,
        importoFinale: parseFloat(r.importo_finale) || 0,
        costoRiparazione: parseFloat(r.costo_riparazione) || 0,
        acconto: parseFloat(r.acconto) || 0,
        accontoConto: r.acconto_conto,
        incassato: r.incassato,
        incassatoSu: r.incassato_su,
        dataIngresso: r.data_ingresso,
        dataConsegnaPrevista: r.data_consegna_prevista,
        dataCompletamento: r.data_completamento,
        note: r.note,
        storicoStati: r.storico_stati || [],
        notePubbliche: r.note_pubbliche,
        tempoStimato: r.tempo_stimato,
        messaggiCliente: r.messaggi_cliente || [],
        foto: r.foto || [],
        firme: r.firme || [],
        notificaWhatsapp: r.notifica_whatsapp !== false,
        urgente: r.urgente || false
      }));

      // Converte ordini
      const ordiniApp = (ordini || []).map(o => ({
        id: o.id,
        cliente: o.cliente,
        telefono: o.telefono,
        prodotto: o.prodotto,
        prodottoId: o.prodotto_id,
        quantita: o.quantita || 1,
        fornitore: o.fornitore,
        prezzo: parseFloat(o.prezzo) || 0,
        acconto: parseFloat(o.acconto) || 0,
        accontoConto: o.acconto_conto,
        stato: o.stato,
        note: o.note,
        dataOrdine: o.data_ordine,
        dataArrivo: o.data_arrivo,
        dataConsegna: o.data_consegna
      }));

      // Converte preventivi
      const preventiviApp = (preventivi || []).map(p => ({
        id: p.id,
        numero: p.numero,
        cliente: p.cliente,
        telefono: p.telefono,
        email: p.email,
        oggetto: p.oggetto,
        voci: p.voci || [],
        totale: parseFloat(p.totale) || 0,
        stato: p.stato,
        note: p.note,
        data: p.data,
        validitaGiorni: p.validita_giorni
      }));

      // Converte vendite
      const venditeApp = (vendite || []).map(v => ({
        id: v.id,
        cliente: v.cliente,
        voci: v.voci || [],
        totale: parseFloat(v.totale) || 0,
        metodoPagamento: v.metodo_pagamento,
        incassatoSu: v.incassato_su,
        note: v.note,
        data: v.data
      }));

      // Converte movimenti
      const movimentiApp = (movimenti || []).map(m => ({
        id: m.id,
        tipo: m.tipo,
        descrizione: m.descrizione,
        importo: parseFloat(m.importo) || 0,
        metodo: m.metodo,
        voce: m.voce,
        data: m.data
      }));

      // Converte abbonamenti
      const abbonamentiApp = (abbonamenti || []).map(a => ({
        id: a.id,
        nomeCliente: a.cliente,
        riferimento: a.servizio || '',
        telefono: a.note ? a.note.replace('Tel: ', '') : '',
        costo: parseFloat(a.importo) || 0,
        frequenza: a.frequenza || 'mensile',
        giornoScadenza: a.giorno_scadenza,
        meseCreazione: a.mese_creazione ?? 0,
        annoCreazione: a.anno_creazione ?? 2020,
        utente: '',
        password: '',
        statiMensili: a.stati_mensili || {}
      }));

      // Converte contratti
      const contrattiApp = (contratti || []).map(c => ({
        id: c.id,
        cliente: c.cliente,
        descrizione: c.tipo,
        costo: parseFloat(c.importo) || 0,
        scadenza: c.data_scadenza,
        linkDocumento: c.note ? c.note.replace('Link: ', '') : '',
        email: '',
        telefono: ''
      }));

      // Converte ricorrenze
      const ricorrenzeApp = (ricorrenze || []).map(r => ({
        id: r.id,
        tipo: r.tipo,
        nome: r.nome,
        importo: parseFloat(r.importo) || 0,
        frequenza: r.frequenza,
        giorno: r.giorno,
        categoria: r.categoria_id,
        attiva: r.attiva,
        ultimaApplicazione: r.ultima_applicazione
      }));

      // Converte promemoria
      const promemoriaApp = (promemoria || []).map(p => ({
        id: p.id,
        titolo: p.titolo,
        descrizione: p.descrizione,
        dataScadenza: p.data_scadenza,
        priorita: p.priorita,
        completato: p.completato,
        dataCompletamento: p.data_completamento
      }));

      // Converte file utili
      const fileUtiliApp = (fileUtili || []).map(f => ({
        id: f.id,
        nome: f.nome,
        url: f.url,
        tipo: f.tipo,
        cartella: f.cartella,
        note: f.note
      }));

      // Costruisci oggetto data completo
      const loadedData = {
        categorieEntrate: categorieEntrate.length > 0 ? categorieEntrate : DEFAULT_CATEGORIE_ENTRATE,
        categorieUscite: categorieUscite.length > 0 ? categorieUscite : DEFAULT_CATEGORIE_USCITE,
        entrate: entrateApp,
        uscite: usciteApp,
        clientiLab: clientiApp,
        laboratorio: laboratorioApp,
        ordini: ordiniApp,
        preventivi: preventiviApp,
        vendite: venditeApp,
        movimenti: movimentiApp,
        abbonamenti: abbonamentiApp,
        contratti: contrattiApp,
        ricorrenze: ricorrenzeApp,
        promemoria: promemoriaApp,
        fileUtili: fileUtiliApp
      };

      // Leggi impostazioni
      console.log('📥 Impostazioni caricate da Supabase:', impostazioni);
      (impostazioni || []).forEach(imp => {
        if (imp.chiave && imp.valore !== null) {
          console.log(`   📌 ${imp.chiave}:`, imp.valore);
          loadedData[imp.chiave] = imp.valore;
        }
      });

      // FIX undo: fotografa lo stato appena caricato, così anche la PRIMA
      // modifica della sessione ha uno snapshot pulito da cui tornare indietro
      setDataConSnapshot({ ...DEFAULT_DATA, ...loadedData });
      console.log('✅ Dati caricati da Supabase:', {
        entrate: entrateApp.length,
        uscite: usciteApp.length,
        abbonamenti: abbonamentiApp.length,
        contratti: contrattiApp.length,
        laboratorio: laboratorioApp.length
      });
      
      // Backup locale
      localStorage.setItem('resoconto-backup', JSON.stringify(loadedData));
      
      // Il backup giornaliero ora è fatto dallo script Google (Backup GestPCCare) alle 21:
      // il gestionale legge solo l'esito dalla chiave 'ultimoBackup' delle impostazioni.
      
    } catch (e) { 
      console.error('❌ Errore caricamento da Supabase:', e);
      // Fallback a localStorage
      const local = localStorage.getItem('resoconto-backup'); 
      if (local) {
        try {
          const localData = JSON.parse(local);
          setDataConSnapshot({ ...DEFAULT_DATA, ...localData });
          console.log('Dati caricati da localStorage (errore Supabase)');
        } catch (e2) {
          setDataConSnapshot(DEFAULT_DATA);
        }
      } else {
        setDataConSnapshot(DEFAULT_DATA);
      }
    }
    setLoading(false);
  };

  // Cleanup del timeout al unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        // Salva eventuali dati pendenti prima di smontare
        if (pendingDataRef.current) {
          localStorage.setItem('resoconto-backup', JSON.stringify(pendingDataRef.current));
        }
      }
    };
  }, []);

  const saveCloud = useCallback(async (d) => {
    if (!userId) {
      console.warn('saveCloud: userId non disponibile');
      return;
    }
    
    // Salva immediatamente in localStorage (backup locale)
    try {
      localStorage.setItem('resoconto-backup', JSON.stringify(d));
    } catch (e) {
      console.error('Errore salvataggio localStorage:', e);
    }
    
    // Cancella il timeout precedente se esiste
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Memorizza i dati più recenti
    pendingDataRef.current = d;
    
    // Debounce: aspetta 1500ms prima di salvare su Supabase (aumentato per evitare troppe chiamate)
    saveTimeoutRef.current = setTimeout(async () => {
      const dataToSave = pendingDataRef.current;
      if (!dataToSave) return;
      
      setSaving(true);
      try { 
        // Salva tutte le modifiche su Supabase
        await saveToSupabase(dataToSave);
        console.log('✅ Salvataggio Supabase completato', new Date().toLocaleTimeString());
      } catch (e) {
        console.error('❌ Errore salvataggio Supabase:', e);
        // Mostra notifica all'utente
        alert('Errore durante il salvataggio. I dati sono salvati localmente.');
      }
      setSaving(false);
      pendingDataRef.current = null;
    }, 1500);
  }, [userId]);

  // Funzione per salvare tutti i dati su Supabase
  const saveToSupabase = async (d) => {
    if (!userId) return;

    // Funzione helper per sincronizzare una tabella (upsert + delete)
    const syncTable = async (tableName, records, idField = 'id') => {
      try {
        if (records.length > 0) {
          // Upsert dei record attuali
          const { error: upsertError } = await supabase.from(tableName).upsert(records, { onConflict: idField });
          if (upsertError) {
            console.error(`❌ Errore upsert ${tableName}:`, upsertError);
            console.error(`   Record che hanno causato errore:`, records.slice(0, 3)); // Mostra primi 3 record
            return;
          }
          console.log(`✅ Salvati ${records.length} record in ${tableName}`);
        }
        // NOTA: Non eliminiamo più automaticamente i record dal DB
        // Le eliminazioni vengono gestite solo tramite la funzione del() esplicita
        // Questo evita la perdita accidentale di dati se l'array locale è incompleto
      } catch (e) {
        console.error(`Errore sync ${tableName}:`, e);
      }
    };

    // Salva categorie
    const categorieEntrate = (d.categorieEntrate || []).map((c, idx) => ({
      id: c.id,
      user_id: userId,
      tipo: 'entrate',
      nome: c.nome,
      icon: c.icon || '📁',
      ordine: idx
    }));
    
    const categorieUscite = (d.categorieUscite || []).map((c, idx) => ({
      id: c.id,
      user_id: userId,
      tipo: 'uscite',
      nome: c.nome,
      icon: c.icon || '📁',
      ordine: idx
    }));

    // Sync categorie (gestione speciale per entrambi i tipi)
    const tutteCategorie = [...categorieEntrate, ...categorieUscite];
    await syncTable('categorie', tutteCategorie);

    // Salva entrate
    const entrate = (d.entrate || []).map(e => ({
      id: e.id,
      user_id: userId,
      categoria_id: e.categoria || null,
      nome: e.nome,
      importo: e.importo || 0,
      icon: e.icon || '💰',
      note: e.note || null,
      is_acconto: e.isAcconto || false,
      is_riparazione: e.isRiparazione || false,
      is_ordine: e.isOrdine || false,
      scheda_id: e.schedaId || null,
      ordine_id: e.ordineId || null,
      metodo_pagamento: e.metodoPagamento || null
    }));
    await syncTable('entrate', entrate);

    // Salva uscite
    const uscite = (d.uscite || []).map(u => ({
      id: u.id,
      user_id: userId,
      categoria_id: u.categoria || null,
      nome: u.nome,
      importo: u.importo || 0,
      icon: u.icon || '💸',
      note: u.note || null
    }));
    await syncTable('uscite', uscite);

    // Salva clienti
    const clienti = (d.clientiLab || []).map(c => ({
      id: c.id,
      user_id: userId,
      nome: c.nome,
      telefono: c.telefono || null,
      email: c.email || null,
      indirizzo: c.indirizzo || null,
      azienda: c.azienda || null,
      codice_fiscale: c.codiceFiscale || null,
      note: c.note || null
    }));
    await syncTable('clienti', clienti);

    // Salva riparazioni (laboratorio)
    const riparazioni = (d.laboratorio || []).map(r => ({
      id: r.id,
      user_id: userId,
      numero: r.numero,
      cliente_nome: r.cliente,
      telefono: r.telefono || null,
      email: r.email || null,
      dispositivo: r.dispositivo || null,
      marca: r.marca || null,
      modello: r.modello || null,
      seriale: r.seriale || null,
      accessori: r.accessori || null,
      problema: r.problema || null,
      diagnosi: r.diagnosi || null,
      soluzione: r.soluzione || null,
      stato: r.stato || 'nuovo',
      priorita: r.priorita || 'normale',
      preventivo: r.preventivo || 0,
      importo_finale: r.importoFinale || 0,
      costo_riparazione: r.costoRiparazione || 0,
      acconto: r.acconto || 0,
      acconto_conto: r.accontoConto || null,
      incassato: r.incassato || false,
      incassato_su: r.incassatoSu || null,
      data_ingresso: r.dataIngresso || new Date().toISOString(),
      data_consegna_prevista: r.dataConsegnaPrevista || null,
      data_completamento: r.dataCompletamento || null,
      note: r.note || null,
      storico_stati: r.storicoStati || [],
      note_pubbliche: r.notePubbliche || null,
      tempo_stimato: r.tempoStimato || null,
      messaggi_cliente: r.messaggiCliente || [],
      foto: r.foto || [],
      firme: r.firme || [],
      notifica_whatsapp: r.notificaWhatsapp !== false,
      urgente: r.urgente || false
    }));

    // Protezione firme: se una scheda ha firme "in attesa" in locale, controlla sul DB
    // che il cliente non abbia già firmato, altrimenti il salvataggio cancellerebbe la firma
    const idConFirmeInAttesa = riparazioni.filter(r => (r.firme || []).some(firmaInAttesa)).map(r => r.id);
    if (idConFirmeInAttesa.length > 0) {
      const { data: firmeDb, error: firmeErr } = await supabase
        .from('riparazioni')
        .select('id, firme')
        .in('id', idConFirmeInAttesa);
      if (firmeErr) {
        // Senza la verifica non possiamo sapere se il cliente ha firmato: meglio non toccare le firme
        console.error('❌ Verifica firme fallita, salto il salvataggio delle schede con firme in attesa:', firmeErr);
        const daSaltare = new Set(idConFirmeInAttesa);
        await syncTable('riparazioni', riparazioni.filter(r => !daSaltare.has(r.id)));
        throw new Error('Verifica firme non riuscita');
      }
      const mappaDb = {};
      (firmeDb || []).forEach(r => { mappaDb[r.id] = r.firme || []; });
      riparazioni.forEach(r => {
        if (mappaDb[r.id]) r.firme = unisciFirme(r.firme, mappaDb[r.id]);
      });
    }
    await syncTable('riparazioni', riparazioni);

    // Salva ordini
    const ordini = (d.ordini || []).map(o => ({
      id: o.id,
      user_id: userId,
      cliente: o.cliente,
      telefono: o.telefono || null,
      prodotto: o.prodotto,
      prodotto_id: o.prodottoId || null,
      quantita: o.quantita || 1,
      fornitore: o.fornitore || null,
      prezzo: o.prezzo || 0,
      acconto: o.acconto || 0,
      acconto_conto: o.accontoConto || null,
      stato: o.stato || 'in_attesa',
      note: o.note || null,
      data_ordine: o.dataOrdine || new Date().toISOString(),
      data_arrivo: o.dataArrivo || null,
      data_consegna: o.dataConsegna || null
    }));
    await syncTable('ordini', ordini);

    // Salva preventivi
    const preventivi = (d.preventivi || []).map(p => ({
      id: p.id,
      user_id: userId,
      numero: p.numero,
      cliente: p.cliente,
      telefono: p.telefono || null,
      email: p.email || null,
      oggetto: p.oggetto || null,
      voci: p.voci || [],
      totale: p.totale || 0,
      stato: p.stato || 'in_attesa',
      note: p.note || null,
      data: p.data || new Date().toISOString(),
      validita_giorni: p.validitaGiorni || 30
    }));
    await syncTable('preventivi', preventivi);

    // Salva vendite
    const vendite = (d.vendite || []).map(v => ({
      id: v.id,
      user_id: userId,
      cliente: v.cliente || 'Cliente Generico',
      voci: v.voci || [],
      totale: v.totale || 0,
      metodo_pagamento: v.metodoPagamento || 'contanti',
      incassato_su: v.incassatoSu || null,
      note: v.note || null,
      data: v.data || new Date().toISOString()
    }));
    await syncTable('vendite', vendite);

    // Salva movimenti
    const movimenti = (d.movimenti || []).map(m => ({
      id: m.id,
      user_id: userId,
      tipo: m.tipo,
      descrizione: m.descrizione,
      importo: m.importo || 0,
      metodo: m.metodo || null,
      voce: m.voce || null,
      data: m.data || new Date().toISOString()
    }));
    await syncTable('movimenti', movimenti);

    // Salva abbonamenti
    const abbonamenti = (d.abbonamenti || []).map(a => ({
      id: a.id,
      user_id: userId,
      cliente: a.nomeCliente || a.cliente,
      servizio: a.riferimento || a.servizio || null,
      importo: a.costo || a.importo || 0,
      frequenza: a.frequenza || 'mensile',
      giorno_scadenza: a.giornoScadenza || null,
      mese_creazione: a.meseCreazione ?? 0,
      anno_creazione: a.annoCreazione ?? new Date().getFullYear(),
      stati_mensili: a.statiMensili || {},
      data_scadenza: null,
      pagato: false,
      data_pagamento: null,
      incassato_su: null,
      note: a.telefono ? `Tel: ${a.telefono}` : null
    }));
    await syncTable('abbonamenti', abbonamenti);

    // Salva contratti
    const contratti = (d.contratti || []).map(c => ({
      id: c.id,
      user_id: userId,
      cliente: c.cliente,
      tipo: c.descrizione || c.tipo || null,
      importo: c.costo || c.importo || 0,
      data_inizio: null,
      data_scadenza: c.scadenza || c.dataScadenza || null,
      frequenza_rinnovo: 'annuale',
      note: c.linkDocumento ? `Link: ${c.linkDocumento}` : null,
      allegati: [],
      storico_rinnovi: []
    }));
    await syncTable('contratti', contratti);

    // Salva ricorrenze
    const ricorrenze = (d.ricorrenze || []).map(r => ({
      id: r.id,
      user_id: userId,
      tipo: r.tipo,
      nome: r.nome,
      importo: r.importo || 0,
      frequenza: r.frequenza || 'mensile',
      giorno: r.giorno || null,
      categoria_id: r.categoria || null,
      attiva: r.attiva !== false,
      ultima_applicazione: r.ultimaApplicazione || null
    }));
    await syncTable('ricorrenze', ricorrenze);

    // Salva promemoria
    const promemoria = (d.promemoria || []).map(p => ({
      id: p.id,
      user_id: userId,
      titolo: p.titolo,
      descrizione: p.descrizione || null,
      data_scadenza: p.dataScadenza || null,
      priorita: p.priorita || 'normale',
      completato: p.completato || false,
      data_completamento: p.dataCompletamento || null
    }));
    await syncTable('promemoria', promemoria);

    // Salva file utili
    const fileUtili = (d.fileUtili || []).map(f => ({
      id: f.id,
      user_id: userId,
      nome: f.nome,
      url: f.url || null,
      tipo: f.tipo || null,
      cartella: f.cartella || 'Generale',
      note: f.note || null
    }));
    await syncTable('file_utili', fileUtili);

    // Salva impostazioni (messaggi WhatsApp, ecc.)
    const impostazioniDaSalvare = [];
    
    // Messaggi WhatsApp abbonamenti
    if (d.messaggiWA) {
      console.log('💬 Salvataggio messaggiWA:', d.messaggiWA);
      impostazioniDaSalvare.push({
        id: `imp_messaggiWA_${userId}`,
        user_id: userId,
        chiave: 'messaggiWA',
        valore: d.messaggiWA
      });
    }
    
    // Messaggi WhatsApp laboratorio
    if (d.messaggiWALab) {
      console.log('💬 Salvataggio messaggiWALab:', d.messaggiWALab);
      impostazioniDaSalvare.push({
        id: `imp_messaggiWALab_${userId}`,
        user_id: userId,
        chiave: 'messaggiWALab',
        valore: d.messaggiWALab
      });
    }
    
    // Anni abbonamenti salvati
    if (d.anniAbbonamenti) {
      impostazioniDaSalvare.push({
        id: `imp_anniAbbonamenti_${userId}`,
        user_id: userId,
        chiave: 'anniAbbonamenti',
        valore: d.anniAbbonamenti
      });
    }
    
    // Contatore laboratorio
    if (d.laboratorioContatore !== undefined) {
      impostazioniDaSalvare.push({
        id: `imp_laboratorioContatore_${userId}`,
        user_id: userId,
        chiave: 'laboratorioContatore',
        valore: d.laboratorioContatore
      });
    }
    
    // Anno laboratorio
    if (d.laboratorioAnno !== undefined) {
      impostazioniDaSalvare.push({
        id: `imp_laboratorioAnno_${userId}`,
        user_id: userId,
        chiave: 'laboratorioAnno',
        valore: d.laboratorioAnno
      });
    }
    
    // Tab order
    if (d.tabOrder) {
      impostazioniDaSalvare.push({
        id: `imp_tabOrder_${userId}`,
        user_id: userId,
        chiave: 'tabOrder',
        valore: d.tabOrder
      });
    }

    // FIX: il magazzino non veniva salvato da nessuna parte (né tabella né
    // impostazioni), quindi il sync automatico ogni 2 minuti lo azzerava.
    // Viene persistito come impostazione chiave-valore, come tabOrder:
    // loadData rilegge automaticamente qualsiasi chiave presente in impostazioni.
    if (d.magazzino) {
      impostazioniDaSalvare.push({
        id: `imp_magazzino_${userId}`,
        user_id: userId,
        chiave: 'magazzino',
        valore: d.magazzino
      });
    }

    if (d.movimentiMagazzino) {
      impostazioniDaSalvare.push({
        id: `imp_movimentiMagazzino_${userId}`,
        user_id: userId,
        chiave: 'movimentiMagazzino',
        // Si conservano gli ultimi 500 movimenti per non far crescere troppo la riga
        valore: d.movimentiMagazzino.slice(-500)
      });
    }

    // Salva impostazioni con upsert (non usa syncTable perché le impostazioni sono chiave-valore)
    if (impostazioniDaSalvare.length > 0) {
      console.log('💾 Salvataggio impostazioni su Supabase:', impostazioniDaSalvare.length, 'record');
      
      // Prima elimina le impostazioni esistenti per questo utente
      const chiavi = impostazioniDaSalvare.map(i => i.chiave);
      const { error: deleteError } = await supabase
        .from('impostazioni')
        .delete()
        .eq('user_id', userId)
        .in('chiave', chiavi);
      
      if (deleteError) {
        console.error('❌ Errore eliminazione impostazioni:', deleteError);
      }
      
      // Poi inserisci le nuove
      const { error: insertError } = await supabase
        .from('impostazioni')
        .insert(impostazioniDaSalvare);
      
      if (insertError) {
        console.error('❌ Errore inserimento impostazioni:', insertError);
      } else {
        console.log('✅ Impostazioni salvate con successo');
      }
    }
  };

  // Salvataggio manuale forzato
  const salvaManualeNow = async () => {
    if (!data || !userId) return;
    
    setSalvataggioManuale('saving');
    
    // Cancella eventuali timeout pendenti
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    
    // Salva in localStorage
    localStorage.setItem('resoconto-backup', JSON.stringify(data));
    
    try {
      // Salva su Supabase
      await saveToSupabase(data);
      
      console.log('Salvataggio manuale completato', new Date().toLocaleTimeString());
      setSalvataggioManuale('success');
      setTimeout(() => setSalvataggioManuale(null), 3000);
    } catch (e) {
      console.error('Errore salvataggio manuale:', e);
      setSalvataggioManuale('error');
      setTimeout(() => setSalvataggioManuale(null), 3000);
    }
    
    pendingDataRef.current = null;
  };

  const save = (d) => {
    // Se stiamo facendo undo/redo, non salvare nello stack
    if (!isUndoRedoRef.current && data) {
      // Salva lo stato CORRENTE (prima della modifica) nello stack undo.
      // FIX: si usa la serializzazione fatta all'ultimo salvataggio, NON
      // JSON.stringify(data) fatto adesso. Le funzioni che spostano denaro
      // (upd, transfer, pay, transferParziale, payParziale, eseguiSomma,
      // QuickAddModal) lavorano su una copia superficiale e modificano gli
      // oggetti dentro data in place: rifotografare data qui catturava lo
      // stato già modificato e l'annulla non annullava nulla.
      const currentSnapshot = lastSavedSnapshotRef.current || JSON.stringify(data);
      undoStackRef.current.push(currentSnapshot);

      // Limita la dimensione dello stack
      if (undoStackRef.current.length > MAX_UNDO) {
        undoStackRef.current.shift();
      }

      // Pulisci lo stack redo (nuova azione invalida il redo)
      redoStackRef.current = [];

      // Forza aggiornamento UI per i pulsanti
      forceUpdate(n => n + 1);
    }
    isUndoRedoRef.current = false;

    // Fotografa il nuovo stato: sarà lo snapshot "pulito" del prossimo undo
    try { lastSavedSnapshotRef.current = JSON.stringify(d); } catch (e) { lastSavedSnapshotRef.current = null; }
    setData(d);
    saveCloud(d);
  };

  // UNDO: torna allo stato precedente
  const undo = () => {
    if (undoStackRef.current.length === 0) {
      console.log('⏪ Undo: niente da annullare');
      return;
    }
    
    // Salva lo stato corrente nello stack redo
    if (data) {
      redoStackRef.current.push(lastSavedSnapshotRef.current || JSON.stringify(data));
    }

    // Recupera l'ultimo stato dallo stack undo
    const prevState = undoStackRef.current.pop();
    const prevData = JSON.parse(prevState);
    lastSavedSnapshotRef.current = prevState;
    
    console.log('⏪ Undo eseguito - stack undo:', undoStackRef.current.length, 'redo:', redoStackRef.current.length);
    
    // Imposta flag per non salvare questa operazione nello stack
    isUndoRedoRef.current = true;
    setData(prevData);
    saveCloud(prevData);
    // FIX: undo/redo non passano da save(), che è l'unico punto dove il flag
    // veniva riabbassato. Senza questo reset la PROSSIMA modifica dell'utente
    // non entrava nello stack undo e non svuotava lo stack redo.
    isUndoRedoRef.current = false;
    forceUpdate(n => n + 1);
  };

  // REDO: ripristina stato dopo undo
  const redo = () => {
    if (redoStackRef.current.length === 0) {
      console.log('⏩ Redo: niente da ripristinare');
      return;
    }
    
    // Salva lo stato corrente nello stack undo
    if (data) {
      undoStackRef.current.push(lastSavedSnapshotRef.current || JSON.stringify(data));
    }

    // Recupera l'ultimo stato dallo stack redo
    const nextState = redoStackRef.current.pop();
    const nextData = JSON.parse(nextState);
    lastSavedSnapshotRef.current = nextState;
    
    console.log('⏩ Redo eseguito - stack undo:', undoStackRef.current.length, 'redo:', redoStackRef.current.length);
    
    // Imposta flag per non salvare questa operazione nello stack
    isUndoRedoRef.current = true;
    setData(nextData);
    saveCloud(nextData);
    // FIX: vedi commento in undo()
    isUndoRedoRef.current = false;
    forceUpdate(n => n + 1);
  };

  const canUndo = undoStackRef.current.length > 0;
  const canRedo = redoStackRef.current.length > 0;

  // Salvataggio automatico storico alle 20:00
  useEffect(() => {
    if (!auth || !data) return;

    const checkAndSaveStorico = () => {
      const now = new Date();
      const hour = now.getHours();
      const minute = now.getMinutes();
      
      // Verifica se già salvato oggi
      const todayStr = now.toISOString().split('T')[0];
      const lastSave = localStorage.getItem('resoconto-last-auto-storico');
      
      // Controlla se sono tra le 20:00 e le 20:05 e non abbiamo già salvato oggi
      if (hour === 20 && minute <= 5 && lastSave !== todayStr) {
        const m = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        const dataCompleta = now.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
        const oraCompleta = now.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
        const timestamp = now.getTime();
        
        // Entrate base
        const entrateBase = data.entrate.reduce((s, i) => s + (i.importo || 0), 0);
        const u = data.uscite.reduce((s, i) => s + (i.importo || 0), 0);
        
        // Calcola totali abbonamenti
        const meseCorrente = now.getMonth();
        const annoCorrente = now.getFullYear();
        const meseProssimo = (meseCorrente + 1) % 12;
        
        // Verifica se l'abbonamento è attivo in un determinato mese/anno
        // Considera sia la frequenza che la data di creazione
        const isAttivoInMese = (abb, mese, anno) => {
          const meseCreazione = abb.meseCreazione ?? 0;
          const annoCreazione = abb.annoCreazione ?? 2020; // Default per abbonamenti vecchi
          
          // Se il mese/anno richiesto è PRIMA della creazione, l'abbonamento non esisteva
          if (anno < annoCreazione || (anno === annoCreazione && mese < meseCreazione)) {
            return false;
          }
          
          const diffMesi = (mese - meseCreazione + 12) % 12;
          switch (abb.frequenza) {
            case 'mensile': return true;
            case 'trimestrale': return diffMesi % 3 === 0;
            case 'semestrale': return diffMesi % 6 === 0;
            case 'annuale': return mese === meseCreazione;
            default: return true;
          }
        };
        
        const getStatoMensile = (abb, mese, anno) => {
          const chiave = `${mese}-${anno}`;
          return abb.statiMensili?.[chiave] || { pagato: false };
        };
        
        const abbonamenti = data.abbonamenti || [];
        
        // Calcola arretrati (ultimi 12 mesi precedenti)
        // Solo quelli avvisati almeno una volta e non pagati
        let arretrati = 0;
        for (let i = 1; i <= 12; i++) {
          let mesePassato = meseCorrente - i;
          let annoPassato = annoCorrente;
          if (mesePassato < 0) {
            mesePassato += 12;
            annoPassato -= 1;
          }
          abbonamenti.forEach(abb => {
            if (isAttivoInMese(abb, mesePassato, annoPassato)) {
              const stato = getStatoMensile(abb, mesePassato, annoPassato);
              // Conta solo se avvisato (primo o secondo avviso) E non pagato
              if ((stato.avvisato || stato.secondoAvviso || stato.ultimoAvviso) && !stato.pagato) {
                arretrati += abb.costo || 0;
              }
            }
          });
        }
        
        const abbMeseCorrente = abbonamenti.filter(a => isAttivoInMese(a, meseCorrente, annoCorrente));
        const daRicevereMeseCorrente = abbMeseCorrente
          .filter(a => !getStatoMensile(a, meseCorrente, annoCorrente).pagato)
          .reduce((s, a) => s + (a.costo || 0), 0);
        
        const annoProssimo = meseCorrente === 11 ? annoCorrente + 1 : annoCorrente;
        const abbMeseProssimo = abbonamenti.filter(a => isAttivoInMese(a, meseProssimo, annoProssimo));
        const daRicevereMeseProssimo = abbMeseProssimo
          .filter(a => !getStatoMensile(a, meseProssimo, annoProssimo).pagato)
          .reduce((s, a) => s + (a.costo || 0), 0);
        
        // Entrate totali = base + arretrati + mese corrente + mese prossimo (solo da ricevere)
        const e = entrateBase + arretrati + daRicevereMeseCorrente + daRicevereMeseProssimo;
        
        const rec = { 
          id: timestamp,
          mese: m, 
          dataCompleta, 
          oraCompleta,
          entrate: e, 
          uscite: u, 
          saldo: e - u, 
          auto: true,
          entrateBase,
          arretrati,
          lineeMeseCorrente: daRicevereMeseCorrente,
          lineeMeseProssimo: daRicevereMeseProssimo
        };
        
        // Aggiungi il nuovo record (non sovrascrive)
        let newStorico = [...(data.storico || []), rec];
        
        // Ordina per ID (più recenti prima)
        newStorico.sort((a, b) => (b.id || 0) - (a.id || 0));
        
        // Mantieni solo gli ultimi 365 record
        if (newStorico.length > 365) {
          newStorico = newStorico.slice(0, 365);
        }
        
        // Salva prima il flag per evitare duplicati
        localStorage.setItem('resoconto-last-auto-storico', todayStr);
        
        // Poi salva i dati
        const newData = { ...data, storico: newStorico };
        setDataConSnapshot(newData);
        saveCloud(newData);
        
        console.log('✅ Storico salvato automaticamente alle 20:00 -', dataCompleta);
        
        // Mostra notifica all'utente
        if (Notification.permission === 'granted') {
          new Notification('PC CARE', { body: 'Storico salvato automaticamente!' });
        }
      }
    };

    // Controlla ogni 30 secondi per non perdere la finestra
    const interval = setInterval(checkAndSaveStorico, 30000);
    // Controlla subito all'avvio
    checkAndSaveStorico();

    return () => clearInterval(interval);
  }, [auth, data?.entrate, data?.uscite, data?.abbonamenti]);

  // Controlla contratti scaduti/in scadenza
  useEffect(() => {
    if (!data || !data.contratti || data.contratti.length === 0) return;
    const oggi = new Date();
    const fra30 = new Date(); fra30.setDate(fra30.getDate() + 30);
    
    const scaduti = data.contratti.filter(c => new Date(c.scadenza) < oggi);
    const inScadenza = data.contratti.filter(c => {
      const scad = new Date(c.scadenza);
      return scad >= oggi && scad <= fra30;
    });
    
    if (scaduti.length > 0 || inScadenza.length > 0) {
      setAlertContratti({ scaduti, inScadenza });
    }
  }, [data?.contratti?.length]);

  // Flag per tracciare se l'alert è già stato mostrato in questa sessione
  const [alertAbbMostrato, setAlertAbbMostrato] = useState(false);

  // Controlla abbonamenti in scadenza (entro 7 giorni, anche mese prossimo) - SOLO AL LOGIN
  useEffect(() => {
    if (!data || !data.abbonamenti || data.abbonamenti.length === 0) return;
    
    // Se l'alert è già stato mostrato in questa sessione, non mostrarlo di nuovo
    if (alertAbbMostrato) return;
    
    // Controlla se alert è disattivato nelle impostazioni
    if (data.alertAbbDisattivato) return;
    
    // Controlla se è stato posticipato
    const posticipo = localStorage.getItem('alertAbbPosticipato');
    if (posticipo) {
      const dataPosticipo = new Date(posticipo);
      if (new Date() < dataPosticipo) return; // Ancora in posticipo
      localStorage.removeItem('alertAbbPosticipato'); // Scaduto, rimuovi
    }
    
    const oggi = new Date();
    const meseCorrente = oggi.getMonth();
    const annoCorrente = oggi.getFullYear();
    const giornoOggi = oggi.getDate();
    
    // Calcola mese e anno prossimo
    const meseProssimo = (meseCorrente + 1) % 12;
    const annoProssimo = meseCorrente === 11 ? annoCorrente + 1 : annoCorrente;
    
    // Giorni rimanenti nel mese corrente
    const ultimoGiornoMese = new Date(annoCorrente, meseCorrente + 1, 0).getDate();
    const giorniRimanentiMese = ultimoGiornoMese - giornoOggi;
    
    // Funzione per verificare se un abbonamento è attivo in un mese
    const isAttivoInMese = (abb, mese, anno) => {
      const meseCreazione = abb.meseCreazione ?? 0;
      const annoCreazione = abb.annoCreazione ?? 2020;
      
      // Se il mese/anno richiesto è PRIMA della creazione, l'abbonamento non esisteva
      if (anno < annoCreazione || (anno === annoCreazione && mese < meseCreazione)) {
        return false;
      }
      
      const diffMesi = (mese - meseCreazione + 12) % 12;
      switch (abb.frequenza) {
        case 'mensile': return true;
        case 'trimestrale': return diffMesi % 3 === 0;
        case 'semestrale': return diffMesi % 6 === 0;
        case 'annuale': return mese === meseCreazione;
        default: return true;
      }
    };
    
    // Filtra abbonamenti attivi nel mese corrente
    const abbMeseCorrente = data.abbonamenti.filter(a => isAttivoInMese(a, meseCorrente, annoCorrente));
    
    // Filtra abbonamenti attivi nel mese prossimo
    const abbMeseProssimo = data.abbonamenti.filter(a => isAttivoInMese(a, meseProssimo, annoProssimo));
    
    // Trova quelli in scadenza entro 7 giorni nel MESE CORRENTE e NON ancora pagati
    const inScadenzaMeseCorrente = abbMeseCorrente.filter(a => {
      const giornoScadenza = a.giornoScadenza || 1;
      const giorniMancanti = giornoScadenza - giornoOggi;
      const chiaveStato = `${meseCorrente}-${annoCorrente}`;
      const stato = a.statiMensili?.[chiaveStato] || {};
      
      // Mostra solo se: scadenza entro 7 giorni, non ancora passata, e non pagato
      return giorniMancanti >= 0 && giorniMancanti <= 7 && !stato.pagato;
    });
    
    // Trova quelli in scadenza entro 7 giorni nel MESE PROSSIMO e NON ancora pagati
    // (solo se siamo a fine mese, es: oggi 30 gen, mostra quelli del 1-6 febbraio)
    const inScadenzaMeseProssimo = abbMeseProssimo.filter(a => {
      const giornoScadenza = a.giornoScadenza || 1;
      // Calcola quanti giorni mancano: giorni rimasti nel mese + giorno scadenza mese prossimo
      const giorniMancanti = giorniRimanentiMese + giornoScadenza;
      const chiaveStato = `${meseProssimo}-${annoProssimo}`;
      const stato = a.statiMensili?.[chiaveStato] || {};
      
      // Mostra solo se: scadenza entro 7 giorni e non pagato
      return giorniMancanti <= 7 && !stato.pagato;
    }).map(a => ({
      ...a,
      _meseProssimo: true,
      _meseRiferimento: meseProssimo,
      _annoRiferimento: annoProssimo
    }));
    
    // Unisci gli in scadenza di entrambi i mesi
    const inScadenza = [...inScadenzaMeseCorrente, ...inScadenzaMeseProssimo];
    
    // Trova quelli già scaduti nel mese corrente e NON pagati
    const scaduti = abbMeseCorrente.filter(a => {
      const giornoScadenza = a.giornoScadenza || 1;
      const chiaveStato = `${meseCorrente}-${annoCorrente}`;
      const stato = a.statiMensili?.[chiaveStato] || {};
      
      return giornoScadenza < giornoOggi && !stato.pagato;
    });
    
    if (scaduti.length > 0 || inScadenza.length > 0) {
      setAlertAbbonamenti({ scaduti, inScadenza, meseCorrente, annoCorrente, meseProssimo, annoProssimo });
      setAlertAbbMostrato(true); // Marca come mostrato per questa sessione
    } else {
      setAlertAbbMostrato(true); // Anche se non ci sono alert, marca come controllato
    }
  }, [data?.abbonamenti, data?.alertAbbDisattivato, alertAbbMostrato]);

  // =============================================
  // AVVISO FIRMA CLIENTE (v2.4.6)
  // =============================================
  const [avvisiFirma, setAvvisiFirma] = useState([]); // [{ key, cliente, schedaId, tipo, dataFirma }]
  const dataRef = useRef(null);
  useEffect(() => { dataRef.current = data; }, [data]);

  // Ogni 20 secondi (e quando torni sulla finestra) controlla se il cliente ha firmato
  useEffect(() => {
    if (!auth || !userId) return;
    let attivo = true;
    const controllaFirme = async () => {
      const d = dataRef.current;
      if (!d || !d.laboratorio) return;
      const ids = d.laboratorio.filter(s => (s.firme || []).some(firmaInAttesa)).map(s => s.id);
      if (ids.length === 0) return;
      const { data: righe, error } = await supabase.from('riparazioni').select('id, firme').in('id', ids);
      if (!attivo || error || !righe) return;
      const mappa = {};
      righe.forEach(r => { mappa[r.id] = r.firme || []; });
      const cambiate = ids.filter(id => {
        const loc = (d.laboratorio.find(s => s.id === id) || {}).firme || [];
        return loc.some(f => firmaInAttesa(f) && (mappa[id] || []).some(x => x && x.firma && x.tipo === f.tipo && x.data === f.data));
      });
      if (cambiate.length === 0) return;
      setData(prev => {
        if (!prev) return prev;
        const nuovo = {
          ...prev,
          laboratorio: (prev.laboratorio || []).map(s => cambiate.includes(s.id) ? { ...s, firme: unisciFirme(s.firme, mappa[s.id]) } : s)
        };
        try {
          const json = JSON.stringify(nuovo);
          localStorage.setItem('resoconto-backup', json);
          lastSavedSnapshotRef.current = json; // la firma arrivata dal cliente non è una modifica da annullare
        } catch (e) {}
        return nuovo;
      });
    };
    controllaFirme();
    const intervallo = setInterval(controllaFirme, 20000);
    const alRitorno = () => { if (document.visibilityState === 'visible') controllaFirme(); };
    document.addEventListener('visibilitychange', alRitorno);
    window.addEventListener('focus', alRitorno);
    return () => {
      attivo = false;
      clearInterval(intervallo);
      document.removeEventListener('visibilitychange', alRitorno);
      window.removeEventListener('focus', alRitorno);
    };
  }, [auth, userId]);

  // Quando compare una firma cliente nuova (anche firmata mentre il gestionale era chiuso) mostra l'avviso
  useEffect(() => {
    const lab = data?.laboratorio;
    if (!lab) return;
    const firmate = [];
    lab.forEach(s => (s.firme || []).forEach(f => {
      if (f && f.richiesta && f.firma) firmate.push({ key: chiaveFirma(s.id, f), cliente: s.cliente, schedaId: s.id, tipo: f.tipo, dataFirma: f.dataFirma });
    }));
    const giaNotificate = leggiFirmeNotificate();
    if (giaNotificate === null) {
      // Primo avvio di questa versione: le firme già presenti non vanno notificate
      scriviFirmeNotificate(firmate.map(f => f.key));
      return;
    }
    const nuove = firmate.filter(f => !giaNotificate.includes(f.key));
    if (nuove.length === 0) return;
    scriviFirmeNotificate([...giaNotificate, ...nuove.map(f => f.key)]);
    setAvvisiFirma(prev => [...prev, ...nuove.filter(n => !prev.some(p => p.key === n.key))]);
    suonoFirma();
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        nuove.forEach(n => new Notification('✍️ Firma ricevuta', { body: (n.cliente || 'Il cliente') + ' ha firmato: ' + (n.tipo || 'autorizzazione') + ' (scheda #' + n.schedaId + ')', icon: '/logo.jpg', tag: n.key }));
      }
    } catch (e) {}
  }, [data?.laboratorio]);

  // Controlla promemoria e mostra notifiche
  const [alertPromemoria, setAlertPromemoria] = useState(null);
  const [alertPosticipatoId, setAlertPosticipatoId] = useState(null); // ID del promemoria posticipato (non mostrare alert ma mostra badge)
  
  // Calcola promemoria scaduti per il badge (sempre aggiornato)
  const promemoriaScadutiNonCompletati = useMemo(() => {
    if (!data?.promemoria || data.promemoria.length === 0) return [];
    const ora = new Date();
    return data.promemoria.filter(p => {
      if (p.completato) return false;
      try {
        let dataPromemoria;
        
        // Supporto vecchio formato (dataOra) e nuovo formato (dataAlert + oraAlert)
        if (p.dataOra) {
          dataPromemoria = new Date(p.dataOra);
        } else if (p.dataAlert) {
          const [anno, mese, giorno] = p.dataAlert.split('-').map(Number);
          const [ore, minuti] = (p.oraAlert || '09:00').split(':').map(Number);
          dataPromemoria = new Date(anno, mese - 1, giorno, ore, minuti);
        } else {
          return false;
        }
        
        return dataPromemoria <= ora;
      } catch (e) {
        return false;
      }
    });
  }, [data?.promemoria]);

  // Effetto per mostrare l'alert (solo per quelli non ancora notificati e non posticipati)
  useEffect(() => {
    if (!data?.promemoria || data.promemoria.length === 0) return;
    if (alertPromemoria) return; // C'è già un alert aperto
    
    console.log('🔔 DEBUG - Promemoria nel data:', data.promemoria);
    
    const ora = new Date();
    console.log('🔔 DEBUG - Ora attuale:', ora.toLocaleString());
    
    const daMostrare = data.promemoria.find(p => {
      console.log('🔔 DEBUG - Controllo:', p.titolo, '| completato:', p.completato, '| notificato:', p.notificato, '| posticipato:', p.id === alertPosticipatoId);
      
      if (p.completato) {
        console.log('  -> Skip: completato');
        return false;
      }
      if (p.notificato) {
        console.log('  -> Skip: già notificato');
        return false;
      }
      // Skip se è stato posticipato manualmente
      if (p.id === alertPosticipatoId) {
        console.log('  -> Skip: posticipato');
        return false;
      }
      
      try {
        let dataPromemoria;
        
        // Supporto vecchio formato (dataOra) e nuovo formato (dataAlert + oraAlert)
        if (p.dataOra) {
          // Vecchio formato: "2026-01-25T08:00"
          dataPromemoria = new Date(p.dataOra);
        } else if (p.dataAlert) {
          // Nuovo formato
          const [anno, mese, giorno] = p.dataAlert.split('-').map(Number);
          const [ore, minuti] = (p.oraAlert || '09:00').split(':').map(Number);
          dataPromemoria = new Date(anno, mese - 1, giorno, ore, minuti);
        } else {
          console.log('  -> Skip: no data');
          return false;
        }
        
        const isScaduto = dataPromemoria <= ora;
        console.log('  -> Data promemoria:', dataPromemoria.toLocaleString(), '| Scaduto:', isScaduto);
        return isScaduto;
      } catch (e) {
        console.log('  -> Errore:', e);
        return false;
      }
    });

    if (daMostrare) {
      console.log('🔔 MOSTRO ALERT per:', daMostrare.titolo);
      setAlertPromemoria(daMostrare);
      
      // Notifica browser
      if (Notification.permission === 'granted') {
        new Notification('🔔 Promemoria', { body: daMostrare.titolo, icon: '/logo.jpg' });
      }
    } else {
      console.log('🔔 Nessun promemoria da mostrare');
    }
  }, [data?.promemoria, alertPromemoria, alertPosticipatoId]);

  // Richiedi permesso notifiche all'avvio
  useEffect(() => {
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Funzioni per gestire l'alert
  const completaPromemoria = (id) => {
    save({ ...data, promemoria: data.promemoria.map(p => p.id === id ? { ...p, completato: true, notificato: true } : p) });
    setAlertPromemoria(null);
  };

  const posticipaPromemoria = (id, ore = 1) => {
    const p = data.promemoria.find(pr => pr.id === id);
    if (!p) return;
    
    let nuovaDataObj;
    
    // Supporto vecchio formato (dataOra) e nuovo formato (dataAlert + oraAlert)
    if (p.dataOra) {
      nuovaDataObj = new Date(p.dataOra);
    } else if (p.dataAlert) {
      const [anno, mese, giorno] = p.dataAlert.split('-').map(Number);
      const [oraOrig, minOrig] = (p.oraAlert || '09:00').split(':').map(Number);
      nuovaDataObj = new Date(anno, mese - 1, giorno, oraOrig, minOrig);
    } else {
      return;
    }
    
    nuovaDataObj.setHours(nuovaDataObj.getHours() + ore);
    
    // Salva nel nuovo formato
    const nuovaData = `${nuovaDataObj.getFullYear()}-${String(nuovaDataObj.getMonth() + 1).padStart(2, '0')}-${String(nuovaDataObj.getDate()).padStart(2, '0')}`;
    const nuovaOra = `${String(nuovaDataObj.getHours()).padStart(2, '0')}:${String(nuovaDataObj.getMinutes()).padStart(2, '0')}`;
    
    save({ ...data, promemoria: data.promemoria.map(pr => pr.id === id ? { ...pr, dataAlert: nuovaData, oraAlert: nuovaOra, dataOra: undefined, notificato: false } : pr) });
    setAlertPromemoria(null);
  };

  const posticipaPromemoriaGiorni = (id, giorni = 1) => {
    const p = data.promemoria.find(pr => pr.id === id);
    if (!p) return;
    
    let nuovaDataObj;
    
    if (p.dataOra) {
      nuovaDataObj = new Date(p.dataOra);
    } else if (p.dataAlert) {
      const [anno, mese, giorno] = p.dataAlert.split('-').map(Number);
      const [oraOrig, minOrig] = (p.oraAlert || '09:00').split(':').map(Number);
      nuovaDataObj = new Date(anno, mese - 1, giorno, oraOrig, minOrig);
    } else {
      return;
    }
    
    nuovaDataObj.setDate(nuovaDataObj.getDate() + giorni);
    
    const nuovaData = `${nuovaDataObj.getFullYear()}-${String(nuovaDataObj.getMonth() + 1).padStart(2, '0')}-${String(nuovaDataObj.getDate()).padStart(2, '0')}`;
    const nuovaOra = `${String(nuovaDataObj.getHours()).padStart(2, '0')}:${String(nuovaDataObj.getMinutes()).padStart(2, '0')}`;
    
    save({ ...data, promemoria: data.promemoria.map(pr => pr.id === id ? { ...pr, dataAlert: nuovaData, oraAlert: nuovaOra, dataOra: undefined, notificato: false } : pr) });
    setAlertPromemoria(null);
  };

  const chiudiAlertPromemoria = () => {
    if (alertPromemoria) {
      save({ ...data, promemoria: data.promemoria.map(p => p.id === alertPromemoria.id ? { ...p, notificato: true } : p) });
    }
    setAlertPromemoria(null);
  };

  // Funzione per posticipare alert abbonamenti
  const posticipaAlertAbb = (giorni) => {
    const nuovaData = new Date();
    nuovaData.setDate(nuovaData.getDate() + giorni);
    localStorage.setItem('alertAbbPosticipato', nuovaData.toISOString());
    setAlertAbbonamenti(null);
  };

  const downloadBackup = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `resoconto_backup_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };

  const restoreBackup = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const restored = JSON.parse(ev.target.result);
          if (restored.entrate && restored.uscite) { if (window.confirm('Ripristinare?')) { save(restored); alert('OK!'); } }
        } catch (err) { alert('Errore'); }
      };
      reader.readAsText(file);
    }
  };

  const salvaStorico = () => {
    const oggi = new Date();
    const m = `${oggi.getFullYear()}-${String(oggi.getMonth() + 1).padStart(2, '0')}`;
    const dataCompleta = oggi.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const oraCompleta = oggi.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    const timestamp = oggi.getTime(); // ID unico basato sul timestamp
    
    // Entrate base
    const entrateBase = data.entrate.reduce((s, i) => s + (i.importo || 0), 0);
    const u = data.uscite.reduce((s, i) => s + (i.importo || 0), 0);
    
    // Calcola totali abbonamenti
    const meseCorrente = oggi.getMonth();
    const annoCorrente = oggi.getFullYear();
    const meseProssimo = (meseCorrente + 1) % 12;
    
    const isAttivoInMese = (abb, mese, anno) => {
      const meseCreazione = abb.meseCreazione ?? 0;
      const annoCreazione = abb.annoCreazione ?? 2020;
      
      // Se il mese/anno richiesto è PRIMA della creazione, l'abbonamento non esisteva
      if (anno < annoCreazione || (anno === annoCreazione && mese < meseCreazione)) {
        return false;
      }
      
      const diffMesi = (mese - meseCreazione + 12) % 12;
      switch (abb.frequenza) {
        case 'mensile': return true;
        case 'trimestrale': return diffMesi % 3 === 0;
        case 'semestrale': return diffMesi % 6 === 0;
        case 'annuale': return mese === meseCreazione;
        default: return true;
      }
    };
    
    const getStatoMensile = (abb, mese, anno) => {
      const chiave = `${mese}-${anno}`;
      return abb.statiMensili?.[chiave] || { pagato: false };
    };
    
    const abbonamenti = data.abbonamenti || [];
    
    // Calcola arretrati (ultimi 12 mesi precedenti)
    // Solo quelli avvisati almeno una volta e non pagati
    let arretrati = 0;
    for (let i = 1; i <= 12; i++) {
      let mesePassato = meseCorrente - i;
      let annoPassato = annoCorrente;
      if (mesePassato < 0) {
        mesePassato += 12;
        annoPassato -= 1;
      }
      abbonamenti.forEach(abb => {
        if (isAttivoInMese(abb, mesePassato, annoPassato)) {
          const stato = getStatoMensile(abb, mesePassato, annoPassato);
          // Conta solo se avvisato (primo o secondo avviso) E non pagato
          if ((stato.avvisato || stato.secondoAvviso || stato.ultimoAvviso) && !stato.pagato) {
            arretrati += abb.costo || 0;
          }
        }
      });
    }
    
    const abbMeseCorrente = abbonamenti.filter(a => isAttivoInMese(a, meseCorrente, annoCorrente));
    const daRicevereMeseCorrente = abbMeseCorrente
      .filter(a => !getStatoMensile(a, meseCorrente, annoCorrente).pagato)
      .reduce((s, a) => s + (a.costo || 0), 0);
    
    const annoProssimo = meseCorrente === 11 ? annoCorrente + 1 : annoCorrente;
    const abbMeseProssimo = abbonamenti.filter(a => isAttivoInMese(a, meseProssimo, annoProssimo));
    const daRicevereMeseProssimo = abbMeseProssimo
      .filter(a => !getStatoMensile(a, meseProssimo, annoProssimo).pagato)
      .reduce((s, a) => s + (a.costo || 0), 0);
    
    // Entrate totali = base + arretrati + mese corrente + mese prossimo (solo da ricevere)
    const e = entrateBase + arretrati + daRicevereMeseCorrente + daRicevereMeseProssimo;
    
    let storico = [...(data.storico || [])];
    
    // Crea nuovo record con ID unico
    const rec = { 
      id: timestamp,
      mese: m, 
      dataCompleta, 
      oraCompleta,
      entrate: e, 
      uscite: u, 
      saldo: e - u,
      // Dettaglio per trasparenza
      entrateBase,
      arretrati,
      lineeMeseCorrente: daRicevereMeseCorrente,
      lineeMeseProssimo: daRicevereMeseProssimo
    };
    
    // Aggiungi sempre il nuovo record (non sovrascrive)
    storico.push(rec);
    
    // Ordina per data (più recenti prima)
    storico.sort((a, b) => (b.id || 0) - (a.id || 0));
    
    // Mantieni solo gli ultimi 365 record
    if (storico.length > 365) {
      storico = storico.slice(0, 365);
    }
    
    save({ ...data, storico });
    alert(`Storico salvato: ${dataCompleta} alle ${oraCompleta}\nTotale resoconti salvati: ${storico.length}/365`);
  };

  const applicaRicorrenze = () => {
    const oggi = new Date();
    const giornoOggi = oggi.getDate();
    const meseOggi = oggi.getMonth();
    const annoOggi = oggi.getFullYear();
    const chiaveOggi = `${annoOggi}-${meseOggi}-${giornoOggi}`;
    
    let d = { ...data };
    let cnt = 0;
    let ricorrenzeAggiornate = [...(data.ricorrenze || [])];
    
    (data.ricorrenze || []).forEach((r, index) => {
      if (r.giorno === giornoOggi) {
        const ultimaApplicazione = r.ultimaApplicazione || '';
        
        if (ultimaApplicazione === chiaveOggi) {
          // Già applicata oggi
          return;
        }
        
        const arr = r.tipo === 'entrata' ? d.entrate : d.uscite;
        const v = arr.find(x => x.nome === r.nome);
        if (v) { 
          v.importo = (v.importo || 0) + r.importo; 
          cnt++;
          
          // Aggiorna la data di ultima applicazione
          ricorrenzeAggiornate[index] = {
            ...r,
            ultimaApplicazione: chiaveOggi
          };
        }
      }
    });
    
    if (cnt > 0) { 
      d.ricorrenze = ricorrenzeAggiornate;
      save(d); 
      alert(`✅ ${cnt} ricorrenze applicate!`); 
    } else {
      // Controlla se ci sono ricorrenze per oggi già applicate
      const ricorrenzeOggi = (data.ricorrenze || []).filter(r => r.giorno === giornoOggi);
      if (ricorrenzeOggi.length > 0 && ricorrenzeOggi.every(r => r.ultimaApplicazione === chiaveOggi)) {
        alert('ℹ️ Le ricorrenze di oggi sono già state applicate');
      } else {
        alert('ℹ️ Nessuna ricorrenza prevista per oggi (giorno ' + giornoOggi + ')');
      }
    }
  };

  const rinnovaContratto = (c) => {
    const nuova = new Date(c.scadenza);
    nuova.setMonth(nuova.getMonth() + 12);
    save({ ...data, contratti: data.contratti.map(x => x.id === c.id ? { ...x, scadenza: nuova.toISOString().split('T')[0] } : x) });
    alert(`Rinnovato fino al ${fmtDate(nuova)}`);
  };

  // Calcolo totali abbonamenti per arretrati, mese corrente e prossimo
  const abbTotali = useMemo(() => {
    if (!data?.abbonamenti) return { arretrati: 0, meseCorrente: 0, daRicevereMeseCorrente: 0, meseProssimo: 0 };
    
    const oggi = new Date();
    const meseCorrente = oggi.getMonth();
    const annoCorrente = oggi.getFullYear();
    const meseProssimo = (meseCorrente + 1) % 12;
    const annoProssimo = meseCorrente === 11 ? annoCorrente + 1 : annoCorrente;
    
    // Funzione per verificare se un abbonamento è attivo in un mese
    const isAttivoInMese = (abb, mese, anno) => {
      const meseCreazione = abb.meseCreazione ?? 0;
      const annoCreazione = abb.annoCreazione ?? 2020;
      
      // Se il mese/anno richiesto è PRIMA della creazione, l'abbonamento non esisteva
      if (anno < annoCreazione || (anno === annoCreazione && mese < meseCreazione)) {
        return false;
      }
      
      const diffMesi = (mese - meseCreazione + 12) % 12;
      switch (abb.frequenza) {
        case 'mensile': return true;
        case 'trimestrale': return diffMesi % 3 === 0;
        case 'semestrale': return diffMesi % 6 === 0;
        case 'annuale': return mese === meseCreazione;
        default: return true;
      }
    };
    
    // Funzione per ottenere stato mensile
    const getStatoMensile = (abb, mese, anno) => {
      const chiave = `${mese}-${anno}`;
      return abb.statiMensili?.[chiave] || { avvisato: false, secondoAvviso: false, ultimoAvviso: false, pagato: false };
    };
    
    // Calcola arretrati (ultimi 12 mesi precedenti al mese corrente)
    // Solo quelli avvisati almeno una volta e non pagati
    let arretrati = 0;
    for (let i = 1; i <= 12; i++) {
      let mesePassato = meseCorrente - i;
      let annoPassato = annoCorrente;
      if (mesePassato < 0) {
        mesePassato += 12;
        annoPassato -= 1;
      }
      
      // Per ogni abbonamento, controlla se era attivo in quel mese, avvisato e non pagato
      data.abbonamenti.forEach(abb => {
        if (isAttivoInMese(abb, mesePassato, annoPassato)) {
          const stato = getStatoMensile(abb, mesePassato, annoPassato);
          // Conta solo se avvisato (primo o secondo avviso) E non pagato
          if ((stato.avvisato || stato.secondoAvviso || stato.ultimoAvviso) && !stato.pagato) {
            arretrati += abb.costo || 0;
          }
        }
      });
    }
    
    // Filtra abbonamenti attivi nel mese corrente
    const abbMeseCorrente = data.abbonamenti.filter(a => isAttivoInMese(a, meseCorrente, annoCorrente));
    const totaleMeseCorrente = abbMeseCorrente.reduce((s, a) => s + (a.costo || 0), 0);
    const daRicevereMeseCorrente = abbMeseCorrente
      .filter(a => !getStatoMensile(a, meseCorrente, annoCorrente).pagato)
      .reduce((s, a) => s + (a.costo || 0), 0);
    
    // Filtra abbonamenti attivi nel mese prossimo (escludi già pagati in anticipo)
    const abbMeseProssimo = data.abbonamenti.filter(a => isAttivoInMese(a, meseProssimo, annoProssimo));
    const totaleMeseProssimo = abbMeseProssimo.reduce((s, a) => s + (a.costo || 0), 0);
    const daRicevereMeseProssimo = abbMeseProssimo
      .filter(a => !getStatoMensile(a, meseProssimo, annoProssimo).pagato)
      .reduce((s, a) => s + (a.costo || 0), 0);
    
    // Calcola anticipi (mesi futuri già pagati, dal mese prossimo in poi)
    let anticipi = 0;
    for (let i = 1; i <= 12; i++) {
      let meseFuturo = (meseCorrente + i) % 12;
      let annoFuturo = annoCorrente + Math.floor((meseCorrente + i) / 12);
      
      data.abbonamenti.forEach(abb => {
        if (isAttivoInMese(abb, meseFuturo, annoFuturo)) {
          const stato = getStatoMensile(abb, meseFuturo, annoFuturo);
          if (stato.pagato) {
            anticipi += abb.costo || 0;
          }
        }
      });
    }
    
    return { 
      arretrati,
      meseCorrente: totaleMeseCorrente, 
      daRicevereMeseCorrente,
      meseProssimo: totaleMeseProssimo,
      daRicevereMeseProssimo,
      anticipi
    };
  }, [data]);

  // Usa promemoriaScadutiNonCompletati calcolato sopra
  const promemoriaScaduti = promemoriaScadutiNonCompletati;

  const totals = useMemo(() => {
    if (!data) return { e: 0, u: 0, s: 0 };
    const e = data.entrate.reduce((s, i) => s + (i.importo || 0), 0);
    const u = data.uscite.reduce((s, i) => s + (i.importo || 0), 0);
    // Aggiungi arretrati + mese corrente + mese prossimo (solo da ricevere, esclusi anticipi già pagati)
    const eConAbb = e + (abbTotali?.arretrati || 0) + (abbTotali?.daRicevereMeseCorrente || 0) + (abbTotali?.daRicevereMeseProssimo || 0);
    return { e: eConAbb, u, s: eConAbb - u, eBase: e };
  }, [data, abbTotali]);

  const searchRes = useMemo(() => {
    if (!search || !data) return [];
    const q = search.toLowerCase();
    const res = [];
    data.entrate.forEach(e => { if (e.nome.toLowerCase().includes(q)) res.push({ t: 'Entrata', ...e }); });
    data.uscite.forEach(e => { if (e.nome.toLowerCase().includes(q)) res.push({ t: 'Uscita', ...e }); });
    (data.contratti || []).forEach(c => { if (c.cliente.toLowerCase().includes(q)) res.push({ t: 'Contratto', nome: c.cliente, ...c }); });
    return res.slice(0, 15);
  }, [search, data]);

  // Funzione per registrare movimenti nello storico
  const registraMovimento = (tipo, descrizione, importo, metodo = '') => {
    const movimento = {
      id: Date.now().toString(),
      data: new Date().toISOString(),
      tipo,
      descrizione,
      importo,
      metodo
    };
    const movimenti = [...(data.movimenti || []), movimento];
    // Mantieni max 1000 movimenti
    if (movimenti.length > 1000) movimenti.shift();
    return movimenti;
  };

  // FIX: si copia anche l'array della sezione, altrimenti d[sec] resta lo stesso
  // array di data[sec] e l'assegnazione per indice modifica lo stato React in place.
  const upd = (sec, id, f, v) => { const arr = [...(data[sec] || [])]; const i = arr.findIndex(x => x.id === id); if (i >= 0) { arr[i] = { ...arr[i], [f]: v }; save({ ...data, [sec]: arr }); } };
  const add = (sec, item) => { save({ ...data, [sec]: [...(data[sec] || []), { ...item, id: Date.now().toString() }] }); setAddModal(null); };
  
  // Mappa sezioni locali a tabelle Supabase
  const sectionToTable = {
    entrate: 'entrate',
    uscite: 'uscite',
    laboratorio: 'riparazioni',
    ordini: 'ordini',
    preventivi: 'preventivi',
    vendite: 'vendite',
    abbonamenti: 'abbonamenti',
    contratti: 'contratti',
    ricorrenze: 'ricorrenze',
    promemoria: 'promemoria',
    fileUtili: 'file_utili',
    clientiLab: 'clienti',
    movimenti: 'movimenti'
  };
  
  const del = async (sec, id) => {
    // FIX: prima si aggiorna lo stato locale, POI si cancella dal database.
    // Con l'await davanti, due eliminazioni ravvicinate leggevano entrambe lo
    // stesso `data` dalla closure e la prima riga riappariva.
    save({ ...data, [sec]: (data[sec] || []).filter(x => x.id !== id) });

    // Elimina da Supabase
    const tableName = sectionToTable[sec];
    if (tableName && userId) {
      // FIX: try/catch oltre al controllo di `error`, perché con la rete caduta
      // la promise viene rigettata e l'utente non vedrebbe alcun avviso
      try {
        const { error } = await supabase.from(tableName).delete().eq('id', id).eq('user_id', userId);
        if (error) throw error;
        console.log(`✅ Eliminato ${id} da ${tableName}`);
      } catch (errDel) {
        console.error(`❌ Errore eliminazione da ${tableName}:`, errDel);
        alert(`Attenzione: l'elemento è stato tolto dall'elenco ma NON dal server.\nPotrebbe riapparire alla prossima sincronizzazione.`);
      }
    }
  };

  // FIX: svuotamento completo di una sezione. Serve per "Svuota storico", che
  // prima azzerava solo l'array locale: syncTable non cancella nulla dal
  // database, quindi al primo sync tornavano tutti i movimenti.
  const delAll = async (sec) => {
    save({ ...data, [sec]: [] });

    const tableName = sectionToTable[sec];
    if (tableName && userId) {
      try {
        const { error } = await supabase.from(tableName).delete().eq('user_id', userId);
        if (error) throw error;
        console.log(`✅ Svuotata tabella ${tableName}`);
      } catch (errDel) {
        console.error(`❌ Errore svuotamento ${tableName}:`, errDel);
        alert(`Attenzione: l'elenco è stato svuotato a schermo ma NON sul server.\nI dati potrebbero riapparire alla prossima sincronizzazione.`);
        return false;
      }
    }
    return true;
  };
  
  const transfer = (fromId, toName) => { 
    const d = { ...data }; 
    const from = d.entrate.find(x => x.id === fromId); 
    const to = d.entrate.find(x => x.nome === toName); 
    if (from && to && from.importo > 0) { 
      const importo = from.importo;
      to.importo = (to.importo || 0) + importo; 
      from.importo = 0; 
      // Registra movimento
      d.movimenti = registraMovimento('trasferimento', `${from.nome} → ${to.nome}`, importo, `Da: ${from.nome}`);
      save(d); 
    } 
  };
  
  const pay = (uscitaId, accName) => { 
    const d = { ...data }; 
    const usc = d.uscite.find(x => x.id === uscitaId); 
    const acc = d.entrate.find(x => x.nome === accName); 
    if (usc && acc && usc.importo > 0) { 
      const importo = usc.importo;
      acc.importo = (acc.importo || 0) - importo; 
      usc.importo = 0; 
      // Registra movimento
      d.movimenti = registraMovimento('uscita', `${usc.nome}`, importo, `Pagato da: ${acc.nome}`);
      save(d); 
    } 
  };
  
  // Conti rapidi (definiti qui per essere usati in transferParziale e payParziale)
  const CONTI_RAPIDI_BASE = [
    { nome: 'BANCA CARIPARMA PC CARE', icon: '🏦' },
    { nome: 'CASSA NEGOZIO', icon: '💵' },
    { nome: 'POSTEPAY', icon: '💳' },
    { nome: 'PAYPAL', icon: '🅿️' },
    { nome: 'REVOLUT', icon: '💜' },
    { nome: 'BUNQ', icon: '🟢' },
    { nome: 'DA INCASSARE', icon: '📋' }
  ];
  
  // Trasferimento parziale tra entrate
  const transferParziale = (fromId, toName, importo) => { 
    const d = { ...data }; 
    const fromIndex = d.entrate.findIndex(x => x.id === fromId);
    const from = d.entrate[fromIndex];
    
    if (!from) {
      alert('Voce di origine non trovata');
      return;
    }
    
    // Caso speciale: DA INCASSARE - sposta la voce intera nella categoria "Da Incassare"
    if (toName === 'DA INCASSARE') {
      // Trova o crea la categoria "Da Incassare"
      let catDaIncassare = (d.categorieEntrate || []).find(c => c.nome === 'Da Incassare' || c.nome === 'DA INCASSARE');
      if (!catDaIncassare) {
        catDaIncassare = {
          id: 'cat_da_incassare',
          nome: 'Da Incassare',
          icon: '📋',
          color: '#f59e0b'
        };
        d.categorieEntrate = [...(d.categorieEntrate || []), catDaIncassare];
      }
      
      // Sposta la voce nella categoria Da Incassare (mantiene nome, importo, tutto)
      d.entrate[fromIndex] = { ...from, categoria: catDaIncassare.id };
      
      // Registra movimento
      const movimento = {
        id: Date.now().toString(),
        data: new Date().toISOString(),
        tipo: 'trasferimento',
        descrizione: from.nome + ' → Da Incassare',
        importo: from.importo || 0,
        metodo: 'Spostato a Da Incassare'
      };
      d.movimenti = [...(d.movimenti || []), movimento];
      save(d);
      return;
    }
    
    // Caso normale: trasferimento tra conti
    const importoEffettivo = Math.min(importo, from.importo || 0);
    if (importoEffettivo <= 0) {
      alert('Importo non valido o saldo insufficiente');
      return;
    }
    
    let to = d.entrate.find(x => x.nome === toName); 
    
    // Se il conto di destinazione non esiste, crealo
    if (!to) {
      const iconaConto = CONTI_RAPIDI_BASE.find(c => c.nome === toName)?.icon || '💰';
      to = {
        id: 'conto_' + Date.now(),
        nome: toName,
        importo: 0,
        icon: iconaConto,
        categoria: (d.categorieEntrate || [])[0]?.id || null
      };
      d.entrate = [...d.entrate, to];
    }
    
    to.importo = (to.importo || 0) + importoEffettivo; 
    from.importo = (from.importo || 0) - importoEffettivo; 
    // Registra movimento
    const movimento = {
      id: Date.now().toString(),
      data: new Date().toISOString(),
      tipo: 'trasferimento',
      descrizione: from.nome + ' → ' + to.nome,
      importo: importoEffettivo,
      metodo: 'Da: ' + from.nome
    };
    d.movimenti = [...(d.movimenti || []), movimento];
    save(d); 
  };
  
  // Pagamento parziale da un conto
  const payParziale = (uscitaId, accName, importo) => { 
    const d = { ...data }; 
    const usc = d.uscite.find(x => x.id === uscitaId); 
    let acc = d.entrate.find(x => x.nome === accName); 
    
    // Se il conto non esiste, crealo
    if (!acc) {
      const iconaConto = CONTI_RAPIDI_BASE.find(c => c.nome === accName)?.icon || '💰';
      acc = {
        id: 'conto_' + Date.now(),
        nome: accName,
        importo: 0,
        icon: iconaConto,
        categoria: (d.categorieEntrate || [])[0]?.id || null
      };
      d.entrate = [...d.entrate, acc];
    }
    
    if (usc && acc && importo > 0) { 
      const importoEffettivo = Math.min(importo, usc.importo || 0);
      if (importoEffettivo <= 0) {
        alert('Importo non valido o saldo insufficiente');
        return;
      }
      acc.importo = (acc.importo || 0) - importoEffettivo; 
      usc.importo = (usc.importo || 0) - importoEffettivo; 
      // Registra movimento
      const movimento = {
        id: Date.now().toString(),
        data: new Date().toISOString(),
        tipo: 'uscita',
        descrizione: usc.nome,
        importo: importoEffettivo,
        metodo: 'Pagato da: ' + acc.nome
      };
      d.movimenti = [...(d.movimenti || []), movimento];
      save(d); 
    } 
  };
  const addCat = (type, cat) => { const k = type === 'entrate' ? 'categorieEntrate' : 'categorieUscite'; save({ ...data, [k]: [...data[k], { ...cat, id: Date.now().toString() }] }); setCatModal(null); };
  // FIX: la categoria veniva rimossa solo dall'array locale. La tabella
  // `categorie` è in sola scrittura (syncTable fa solo upsert), quindi al primo
  // sync la categoria eliminata riappariva.
  const delCat = async (type, id) => {
    if (!window.confirm('Eliminare?')) return;
    const k = type === 'entrate' ? 'categorieEntrate' : 'categorieUscite';
    save({ ...data, [k]: (data[k] || []).filter(c => c.id !== id) });
    if (userId && supabase) {
      try {
        const { error } = await supabase.from('categorie').delete().eq('id', id).eq('user_id', userId);
        if (error) throw error;
      } catch (errDel) {
        console.error('❌ Errore eliminazione categoria:', errDel);
        alert('Attenzione: la categoria è stata tolta dall\'elenco ma NON dal server.\nPotrebbe riapparire alla prossima sincronizzazione.');
      }
    }
  };
  const moveCat = (type, id, dir) => { const k = type === 'entrate' ? 'categorieEntrate' : 'categorieUscite'; const arr = [...data[k]]; const i = arr.findIndex(c => c.id === id); if (dir === 'up' && i > 0) [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]]; if (dir === 'down' && i < arr.length - 1) [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]]; save({ ...data, [k]: arr }); };

  // =============================================
  // PAGINA PUBBLICA - STATO RIPARAZIONE
  // =============================================
  const isStatoPage = window.location.pathname === '/stato' || window.location.pathname === '/stato/';
  
  const [statoRicerca, setStatoRicerca] = useState({ numeroScheda: '', telefono: '' });
  const [statoRisultato, setStatoRisultato] = useState(null); // null, 'loading', 'not_found', { scheda }
  const [statoErrore, setStatoErrore] = useState('');
  
  const cercaStatoRiparazione = async () => {
    const { numeroScheda, telefono } = statoRicerca;
    
    if (!numeroScheda.trim() || !telefono.trim()) {
      setStatoErrore('Inserisci sia il numero scheda che il numero di telefono');
      return;
    }
    
    setStatoRisultato('loading');
    setStatoErrore('');
    
    try {
      // Normalizza il numero scheda (rimuovi # se presente, spazi, etc)
      let numSchedaNorm = numeroScheda.trim().replace('#', '').replace(/\s/g, '');
      
      // Se l'utente ha inserito solo il numero (es: 2591), aggiungi l'anno corrente
      if (/^\d+$/.test(numSchedaNorm) && numSchedaNorm.length <= 4) {
        const anno = new Date().getFullYear();
        numSchedaNorm = `${anno}-${numSchedaNorm}`;
      }
      
      // Normalizza il telefono (ultime 10 cifre)
      const normalizzaTel = (tel) => (tel || '').replace(/\D/g, '').slice(-10);
      const telNormalizzato = normalizzaTel(telefono);
      
      console.log('Ricerca scheda con ID:', numSchedaNorm, 'Tel:', telNormalizzato);
      
      // Usa fetch diretto con la chiave anonima (più affidabile per query pubbliche)
      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/riparazioni?id=eq.${encodeURIComponent(numSchedaNorm)}&select=id,cliente_nome,telefono,dispositivo,marca,modello,problema,diagnosi,stato,data_ingresso,preventivo,importo_finale,note_pubbliche,tempo_stimato,messaggi_cliente,foto,firme`,
        {
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        console.error('Errore HTTP:', response.status);
        
        // Se 404 o errore, prova ricerca parziale
        const soloNumero = numSchedaNorm.split('-').pop();
        console.log('Provo ricerca con LIKE per:', soloNumero);
        
        const response2 = await fetch(
          `${SUPABASE_URL}/rest/v1/riparazioni?id=like.*${encodeURIComponent(soloNumero)}&select=id,cliente_nome,telefono,dispositivo,marca,modello,problema,diagnosi,stato,data_ingresso,preventivo,importo_finale,note_pubbliche,tempo_stimato,messaggi_cliente,foto,firme&limit=10`,
          {
            headers: {
              'apikey': SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (!response2.ok) {
          console.error('Anche ricerca parziale fallita');
          setStatoRisultato('not_found');
          return;
        }
        
        const schede = await response2.json();
        console.log('Schede trovate (parziale):', schede);
        
        if (!schede || schede.length === 0) {
          setStatoRisultato('not_found');
          return;
        }
        
        // Verifica telefono
        const schedaTrovata = schede.find(s => {
          const telScheda = normalizzaTel(s.telefono);
          return telScheda.slice(-6) === telNormalizzato.slice(-6);
        });
        
        if (schedaTrovata) {
          setStatoRisultato({ scheda: schedaTrovata });
        } else {
          setStatoRisultato('not_found');
        }
        return;
      }
      
      const schede = await response.json();
      console.log('Schede trovate:', schede);
      
      if (!schede || schede.length === 0) {
        // Prova anni precedenti
        const annoCorrente = new Date().getFullYear();
        const soloNumero = numSchedaNorm.split('-').pop();
        
        for (let anno = annoCorrente; anno >= annoCorrente - 2; anno--) {
          const idProva = `${anno}-${soloNumero}`;
          console.log('Provo con ID:', idProva);
          
          const resp = await fetch(
            `${SUPABASE_URL}/rest/v1/riparazioni?id=eq.${encodeURIComponent(idProva)}&select=id,cliente_nome,telefono,dispositivo,marca,modello,problema,diagnosi,stato,data_ingresso,preventivo,importo_finale,note_pubbliche,tempo_stimato,messaggi_cliente,foto,firme`,
            {
              headers: {
                'apikey': SUPABASE_ANON_KEY,
                'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          if (resp.ok) {
            const dati = await resp.json();
            if (dati && dati.length > 0) {
              const telScheda = normalizzaTel(dati[0].telefono);
              if (telScheda.slice(-6) === telNormalizzato.slice(-6)) {
                setStatoRisultato({ scheda: dati[0] });
                return;
              }
            }
          }
        }
        
        setStatoRisultato('not_found');
        return;
      }
      
      // Verifica che il telefono corrisponda
      const schedaTrovata = schede.find(s => {
        const telScheda = normalizzaTel(s.telefono);
        return telScheda.slice(-6) === telNormalizzato.slice(-6);
      });
      
      if (schedaTrovata) {
        console.log('Scheda trovata:', schedaTrovata);
        setStatoRisultato({ scheda: schedaTrovata });
      } else {
        console.log('Telefono non corrisponde');
        setStatoRisultato('not_found');
      }
    } catch (e) {
      console.error('Errore:', e);
      setStatoRisultato('not_found');
    }
  };
  
  // Pagina pubblica stato riparazione
  if (isStatoPage) {
    const STATI_PUBBLICI = {
      'nuovo': { label: 'Ricevuto', color: '#ec4899', icon: '📥', descrizione: 'Il dispositivo è stato preso in carico' },
      'in_lavorazione': { label: 'In Lavorazione', color: '#3b82f6', icon: '🔧', descrizione: 'I nostri tecnici stanno lavorando sul dispositivo' },
      'in_attesa': { label: 'In Attesa', color: '#f59e0b', icon: '⏳', descrizione: 'In attesa di diagnosi o conferma' },
      'attesa_pezzi': { label: 'In Attesa Ricambi', color: '#8b5cf6', icon: '📦', descrizione: 'Stiamo aspettando i pezzi di ricambio' },
      'ricontatto': { label: 'Da Ricontattare', color: '#06b6d4', icon: '📞', descrizione: 'Abbiamo bisogno di contattarti' },
      'non_riparabile': { label: 'Non Riparabile', color: '#ef4444', icon: '❌', descrizione: 'Purtroppo il dispositivo non è riparabile' },
      'pronto': { label: 'PRONTO PER IL RITIRO', color: '#22c55e', icon: '✅', descrizione: 'Il dispositivo è pronto! Puoi venire a ritirarlo' },
      'consegnato': { label: 'Consegnato', color: '#64748b', icon: '🏠', descrizione: 'Dispositivo già ritirato' }
    };
    
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', fontFamily: '"Inter",sans-serif', padding: 20 }}>
        <div style={{ maxWidth: 500, margin: '0 auto', paddingTop: 40 }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <img src="/logo.jpg" alt="PC Care" style={{ width: 80, height: 80, borderRadius: 16, marginBottom: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }} />
            <h1 style={{ fontSize: 24, fontWeight: 800, color: '#fff', margin: 0 }}>PC CARE NAPOLI</h1>
            <p style={{ color: '#94a3b8', marginTop: 8 }}>Verifica lo stato della tua riparazione</p>
          </div>
          
          {/* Form di ricerca */}
          <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 20, padding: 24, border: '1px solid rgba(255,255,255,0.1)', marginBottom: 24 }}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 8, textTransform: 'uppercase' }}>Numero Scheda</label>
              <input
                type="text"
                placeholder="Es: 12345"
                value={statoRicerca.numeroScheda}
                onChange={e => setStatoRicerca({ ...statoRicerca, numeroScheda: e.target.value })}
                onKeyDown={e => e.key === 'Enter' && cercaStatoRiparazione()}
                style={{ width: '100%', padding: '14px 16px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 12, color: '#fff', fontSize: 16, boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 8, textTransform: 'uppercase' }}>Numero di Telefono</label>
              <input
                type="tel"
                placeholder="Es: 3331234567"
                value={statoRicerca.telefono}
                onChange={e => setStatoRicerca({ ...statoRicerca, telefono: e.target.value })}
                onKeyDown={e => e.key === 'Enter' && cercaStatoRiparazione()}
                style={{ width: '100%', padding: '14px 16px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 12, color: '#fff', fontSize: 16, boxSizing: 'border-box' }}
              />
            </div>
            
            {statoErrore && (
              <div style={{ background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: 12, marginBottom: 16, color: '#fca5a5', fontSize: 14 }}>
                {statoErrore}
              </div>
            )}
            
            <button
              onClick={cercaStatoRiparazione}
              disabled={statoRisultato === 'loading'}
              style={{ width: '100%', padding: 16, background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', border: 'none', borderRadius: 12, color: '#fff', fontSize: 16, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            >
              {statoRisultato === 'loading' ? (
                <>
                  <span style={{ width: 20, height: 20, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></span>
                  Ricerca in corso...
                </>
              ) : (
                <>🔍 Cerca Riparazione</>
              )}
            </button>
          </div>
          
          {/* Risultato */}
          {statoRisultato === 'not_found' && (
            <div style={{ background: 'rgba(239,68,68,0.1)', borderRadius: 16, padding: 24, border: '1px solid rgba(239,68,68,0.3)', textAlign: 'center' }}>
              <span style={{ fontSize: 48 }}>🔍</span>
              <h3 style={{ color: '#fca5a5', margin: '16px 0 8px', fontSize: 18 }}>Nessuna riparazione trovata</h3>
              <p style={{ color: '#94a3b8', fontSize: 14, margin: 0 }}>Verifica il numero scheda e il numero di telefono inseriti</p>
            </div>
          )}
          
          {statoRisultato?.scheda && (
            <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 20, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>
              {/* Stato principale */}
              {(() => {
                const s = statoRisultato.scheda;
                const statoInfo = STATI_PUBBLICI[s.stato] || STATI_PUBBLICI['nuovo'];
                const isPronto = s.stato === 'pronto';
                
                return (
                  <>
                    <div style={{ 
                      padding: 24, 
                      background: isPronto ? 'linear-gradient(135deg, #22c55e, #16a34a)' : `linear-gradient(135deg, ${statoInfo.color}33, ${statoInfo.color}11)`,
                      borderBottom: '1px solid rgba(255,255,255,0.1)',
                      textAlign: 'center'
                    }}>
                      <span style={{ fontSize: 56 }}>{statoInfo.icon}</span>
                      <h2 style={{ 
                        color: isPronto ? '#fff' : statoInfo.color, 
                        fontSize: isPronto ? 24 : 20, 
                        fontWeight: 800, 
                        margin: '16px 0 8px',
                        textTransform: 'uppercase',
                        letterSpacing: isPronto ? 2 : 0
                      }}>
                        {statoInfo.label}
                      </h2>
                      <p style={{ color: isPronto ? 'rgba(255,255,255,0.9)' : '#94a3b8', fontSize: 14, margin: 0 }}>
                        {statoInfo.descrizione}
                      </p>
                    </div>
                    
                    {/* Dettagli */}
                    <div style={{ padding: 24 }}>
                      <div style={{ display: 'grid', gap: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                          <span style={{ color: '#94a3b8' }}>N° Scheda</span>
                          <span style={{ color: '#fff', fontWeight: 600 }}>{s.id}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                          <span style={{ color: '#94a3b8' }}>Dispositivo</span>
                          <span style={{ color: '#fff', fontWeight: 600 }}>{s.dispositivo || '-'}</span>
                        </div>
                        {(s.marca || s.modello) && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                            <span style={{ color: '#94a3b8' }}>Marca/Modello</span>
                            <span style={{ color: '#fff', fontWeight: 600 }}>{[s.marca, s.modello].filter(Boolean).join(' ')}</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                          <span style={{ color: '#94a3b8' }}>Data Ingresso</span>
                          <span style={{ color: '#fff', fontWeight: 600 }}>{s.data_ingresso ? new Date(s.data_ingresso).toLocaleDateString('it-IT') : '-'}</span>
                        </div>
                        {(s.importo_finale > 0 || s.preventivo > 0) && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                            <span style={{ color: '#94a3b8' }}>Importo</span>
                            <span style={{ color: '#22c55e', fontWeight: 700, fontSize: 18 }}>€ {parseFloat(s.importo_finale || s.preventivo).toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Tempo stimato */}
                      {s.tempo_stimato && (
                        <div style={{ marginTop: 16, padding: 12, background: 'rgba(59,130,246,0.1)', borderRadius: 10, border: '1px solid rgba(59,130,246,0.3)' }}>
                          <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>⏱️ Tempo stimato</div>
                          <div style={{ color: '#60a5fa', fontWeight: 600, fontSize: 14 }}>
                            {s.tempo_stimato === 'stesso_giorno' ? 'Stesso giorno' :
                             s.tempo_stimato === '1_giorno' ? '1 giorno' :
                             s.tempo_stimato === '2_3_giorni' ? '2-3 giorni' :
                             s.tempo_stimato === '1_settimana' ? '1 settimana' :
                             s.tempo_stimato === '2_settimane' ? '2 settimane' :
                             s.tempo_stimato === 'da_valutare' ? 'Da valutare dopo diagnosi' :
                             s.tempo_stimato.startsWith('data:') ? `📅 Previsto: ${new Date(s.tempo_stimato.replace('data:', '')).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}` :
                             s.tempo_stimato}
                          </div>
                        </div>
                      )}
                      
                      {/* Foto dispositivo */}
                      {s.foto && s.foto.length > 0 && (
                        <div style={{ marginTop: 16 }}>
                          <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 8 }}>📷 Foto dispositivo ({s.foto.length})</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                            {s.foto.map((foto, idx) => {
                              // Le foto possono essere stringhe JSON o oggetti
                              let fotoObj = foto;
                              if (typeof foto === 'string') {
                                try { fotoObj = JSON.parse(foto); } catch(e) { fotoObj = { data: foto }; }
                              }
                              const fotoSrc = fotoObj.data || fotoObj;
                              return (
                                <div 
                                  key={idx} 
                                  style={{ paddingTop: '100%', position: 'relative', borderRadius: 8, overflow: 'hidden', cursor: 'pointer', background: '#1e293b' }}
                                  onClick={() => {
                                    const w = window.open('', '_blank');
                                    w.document.write(`<html><head><title>Foto ${idx + 1}</title><style>body{margin:0;background:#000;display:flex;align-items:center;justify-content:center;min-height:100vh}img{max-width:100%;max-height:100vh;object-fit:contain}</style></head><body><img src="${fotoSrc}"/></body></html>`);
                                    w.document.close();
                                  }}
                                >
                                  <img 
                                    src={fotoSrc}
                                    alt={`Foto ${idx + 1}`}
                                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Firme richieste - in attesa */}
                      {(() => {
                        const firmeDaFare = (s.firme || []).filter(f => f.richiesta && !f.firma);
                        const firmeCompletate = (s.firme || []).filter(f => f.firma);
                        
                        return (
                          <>
                            {firmeDaFare.length > 0 && (
                              <div style={{ marginTop: 16, padding: 16, background: 'rgba(245,158,11,0.15)', borderRadius: 12, border: '2px solid rgba(245,158,11,0.5)' }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: '#fbbf24', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                                  ⚠️ AUTORIZZAZIONE RICHIESTA
                                </div>
                                {firmeDaFare.map((firma, idx) => (
                                  <div key={idx} style={{ marginBottom: 16, padding: 16, background: 'rgba(0,0,0,0.2)', borderRadius: 10 }}>
                                    <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', marginBottom: 8 }}>{firma.tipo}</div>
                                    
                                    {/* Testo del modulo - scrollabile */}
                                    {firma.descrizione && (
                                      <div style={{ 
                                        maxHeight: 200, 
                                        overflow: 'auto', 
                                        padding: 12, 
                                        background: 'rgba(255,255,255,0.95)', 
                                        borderRadius: 8, 
                                        marginBottom: 12,
                                        fontSize: 11,
                                        lineHeight: 1.5,
                                        color: '#1e293b',
                                        fontFamily: 'system-ui',
                                        whiteSpace: 'pre-wrap'
                                      }}>
                                        {firma.descrizione}
                                      </div>
                                    )}
                                    
                                    <div style={{ fontSize: 12, color: '#fbbf24', marginBottom: 8, fontWeight: 500 }}>
                                      ✍️ Firma qui sotto per confermare:
                                    </div>
                                    
                                    <div style={{ marginBottom: 8 }}>
                                      <canvas 
                                        id={`firma-canvas-cliente-${idx}`}
                                        width={300}
                                        height={120}
                                        style={{ width: '100%', height: 120, background: '#fff', border: '2px solid #fbbf24', borderRadius: 8, touchAction: 'none' }}
                                        onMouseDown={(e) => {
                                          const canvas = e.target;
                                          const ctx = canvas.getContext('2d');
                                          ctx.beginPath();
                                          const rect = canvas.getBoundingClientRect();
                                          ctx.moveTo((e.clientX - rect.left) * (canvas.width / rect.width), (e.clientY - rect.top) * (canvas.height / rect.height));
                                          canvas.isDrawing = true;
                                        }}
                                        onMouseMove={(e) => {
                                          const canvas = e.target;
                                          if (!canvas.isDrawing) return;
                                          const ctx = canvas.getContext('2d');
                                          const rect = canvas.getBoundingClientRect();
                                          ctx.lineTo((e.clientX - rect.left) * (canvas.width / rect.width), (e.clientY - rect.top) * (canvas.height / rect.height));
                                          ctx.strokeStyle = '#000';
                                          ctx.lineWidth = 2;
                                          ctx.stroke();
                                        }}
                                        onMouseUp={(e) => { e.target.isDrawing = false; }}
                                        onMouseLeave={(e) => { e.target.isDrawing = false; }}
                                        onTouchStart={(e) => {
                                          const canvas = e.target;
                                          const ctx = canvas.getContext('2d');
                                          ctx.beginPath();
                                          const rect = canvas.getBoundingClientRect();
                                          const touch = e.touches[0];
                                          ctx.moveTo((touch.clientX - rect.left) * (canvas.width / rect.width), (touch.clientY - rect.top) * (canvas.height / rect.height));
                                          canvas.isDrawing = true;
                                        }}
                                        onTouchMove={(e) => {
                                          e.preventDefault();
                                          const canvas = e.target;
                                          if (!canvas.isDrawing) return;
                                          const ctx = canvas.getContext('2d');
                                          const rect = canvas.getBoundingClientRect();
                                          const touch = e.touches[0];
                                          ctx.lineTo((touch.clientX - rect.left) * (canvas.width / rect.width), (touch.clientY - rect.top) * (canvas.height / rect.height));
                                          ctx.strokeStyle = '#000';
                                          ctx.lineWidth = 2;
                                          ctx.stroke();
                                        }}
                                        onTouchEnd={(e) => { e.target.isDrawing = false; }}
                                      />
                                    </div>
                                    
                                    <div style={{ display: 'flex', gap: 8 }}>
                                      <button
                                        onClick={() => {
                                          const canvas = document.getElementById(`firma-canvas-cliente-${idx}`);
                                          const ctx = canvas.getContext('2d');
                                          ctx.clearRect(0, 0, canvas.width, canvas.height);
                                        }}
                                        style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.2)', border: 'none', borderRadius: 8, color: '#fca5a5', fontSize: 12, cursor: 'pointer' }}
                                      >
                                        🗑️ Cancella
                                      </button>
                                      <button
                                        onClick={async () => {
                                          const canvas = document.getElementById(`firma-canvas-cliente-${idx}`);
                                          const firmaBase64 = canvas.toDataURL('image/png');
                                          
                                          // Aggiorna la firma nel DB
                                          const firmeAggiornate = [...(s.firme || [])];
                                          const firmaIdx = firmeAggiornate.findIndex(f => f.tipo === firma.tipo && f.richiesta && !f.firma);
                                          if (firmaIdx >= 0) {
                                            firmeAggiornate[firmaIdx] = {
                                              ...firmeAggiornate[firmaIdx],
                                              firma: firmaBase64,
                                              dataFirma: new Date().toISOString()
                                            };
                                          }
                                          
                                          try {
                                            const risposta = await fetch(
                                              `${SUPABASE_URL}/rest/v1/riparazioni?id=eq.${encodeURIComponent(s.id)}`,
                                              {
                                                method: 'PATCH',
                                                headers: {
                                                  'apikey': SUPABASE_ANON_KEY,
                                                  'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
                                                  'Content-Type': 'application/json',
                                                  'Prefer': 'return=minimal'
                                                },
                                                body: JSON.stringify({ firme: firmeAggiornate })
                                              }
                                            );
                                            if (!risposta.ok) throw new Error('HTTP ' + risposta.status);
                                            
                                            setStatoRisultato(prev => ({
                                              ...prev,
                                              scheda: { ...prev.scheda, firme: firmeAggiornate }
                                            }));
                                            
                                            alert('✅ Firma salvata con successo!');
                                          } catch (err) {
                                            console.error('Errore salvataggio firma:', err);
                                            alert('Errore nel salvataggio della firma');
                                          }
                                        }}
                                        style={{ flex: 1, padding: '8px 12px', background: 'linear-gradient(135deg, #22c55e, #16a34a)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                                      >
                                        ✓ Conferma e Firma
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Firme completate */}
                            {firmeCompletate.length > 0 && (
                              <div style={{ marginTop: 16 }}>
                                <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 8 }}>✅ Autorizzazioni firmate</div>
                                {firmeCompletate.map((firma, idx) => (
                                  <div key={idx} style={{ padding: 10, background: 'rgba(34,197,94,0.1)', borderRadius: 8, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span style={{ fontSize: 18 }}>✅</span>
                                    <div style={{ flex: 1 }}>
                                      <div style={{ fontSize: 12, fontWeight: 600, color: '#86efac' }}>{firma.tipo}</div>
                                      <div style={{ fontSize: 10, color: '#94a3b8' }}>Firmato il {new Date(firma.dataFirma || firma.data).toLocaleString('it-IT')}</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        );
                      })()}
                      
                      {/* Note pubbliche */}
                      {s.note_pubbliche && (
                        <div style={{ marginTop: 16, padding: 12, background: 'rgba(34,197,94,0.1)', borderRadius: 10, border: '1px solid rgba(34,197,94,0.3)' }}>
                          <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>📢 Aggiornamento dal tecnico</div>
                          <div style={{ color: '#86efac', fontSize: 14 }}>{s.note_pubbliche}</div>
                        </div>
                      )}
                      
                      {/* Messaggio ritiro */}
                      {isPronto && (
                        <div style={{ marginTop: 20, padding: 16, background: 'rgba(34,197,94,0.15)', borderRadius: 12, border: '1px solid rgba(34,197,94,0.3)' }}>
                          <p style={{ color: '#86efac', fontSize: 14, margin: 0, textAlign: 'center' }}>
                            📍 <strong>Vieni a ritirare il tuo dispositivo!</strong><br/>
                            <span style={{ color: '#94a3b8' }}>Vico Acitillo 148, Napoli • Tel: 081.191.73.939</span>
                          </p>
                        </div>
                      )}
                      
                      {/* Chat / Messaggi */}
                      <div style={{ marginTop: 20 }}>
                        <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                          💬 Messaggi
                        </div>
                        
                        {/* Lista messaggi */}
                        <div style={{ maxHeight: 200, overflow: 'auto', marginBottom: 12 }}>
                          {(s.messaggi_cliente && s.messaggi_cliente.length > 0) ? (
                            s.messaggi_cliente.map((msg, idx) => (
                              <div key={idx} style={{ 
                                padding: 10, 
                                marginBottom: 8, 
                                borderRadius: 10, 
                                background: msg.mittente === 'cliente' ? 'rgba(59,130,246,0.15)' : 'rgba(139,92,246,0.15)',
                                borderLeft: '3px solid ' + (msg.mittente === 'cliente' ? '#3b82f6' : '#8b5cf6')
                              }}>
                                <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4 }}>
                                  {msg.mittente === 'cliente' ? '👤 Tu' : '🔧 PC Care'} • {new Date(msg.data).toLocaleString('it-IT')}
                                </div>
                                <div style={{ fontSize: 13, color: '#e2e8f0' }}>{msg.messaggio}</div>
                              </div>
                            ))
                          ) : (
                            <div style={{ textAlign: 'center', padding: 16, color: '#64748b', fontSize: 12 }}>
                              Nessun messaggio. Hai domande? Scrivici qui sotto!
                            </div>
                          )}
                        </div>
                        
                        {/* Form nuovo messaggio */}
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input
                            type="text"
                            id="nuovo-messaggio-cliente"
                            placeholder="Scrivi un messaggio..."
                            style={{ flex: 1, padding: '12px 14px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 10, color: '#fff', fontSize: 14 }}
                            onKeyDown={async (e) => {
                              if (e.key === 'Enter' && e.target.value.trim()) {
                                const messaggio = e.target.value.trim();
                                e.target.value = '';
                                
                                const nuovoMessaggio = {
                                  data: new Date().toISOString(),
                                  mittente: 'cliente',
                                  messaggio
                                };
                                
                                const messaggiAggiornati = [...(s.messaggi_cliente || []), nuovoMessaggio];
                                
                                // Salva su Supabase
                                try {
                                  await fetch(
                                    `${SUPABASE_URL}/rest/v1/riparazioni?id=eq.${encodeURIComponent(s.id)}`,
                                    {
                                      method: 'PATCH',
                                      headers: {
                                        'apikey': SUPABASE_ANON_KEY,
                                        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
                                        'Content-Type': 'application/json',
                                        'Prefer': 'return=minimal'
                                      },
                                      body: JSON.stringify({ messaggi_cliente: messaggiAggiornati })
                                    }
                                  );
                                  
                                  // Aggiorna lo stato locale
                                  setStatoRisultato(prev => ({
                                    ...prev,
                                    scheda: { ...prev.scheda, messaggi_cliente: messaggiAggiornati }
                                  }));
                                } catch (err) {
                                  console.error('Errore invio messaggio:', err);
                                  alert('Errore nell\'invio del messaggio');
                                }
                              }
                            }}
                          />
                          <button
                            onClick={async () => {
                              const input = document.getElementById('nuovo-messaggio-cliente');
                              if (!input.value.trim()) return;
                              
                              const messaggio = input.value.trim();
                              input.value = '';
                              
                              const nuovoMessaggio = {
                                data: new Date().toISOString(),
                                mittente: 'cliente',
                                messaggio
                              };
                              
                              const messaggiAggiornati = [...(s.messaggi_cliente || []), nuovoMessaggio];
                              
                              try {
                                await fetch(
                                  `${SUPABASE_URL}/rest/v1/riparazioni?id=eq.${encodeURIComponent(s.id)}`,
                                  {
                                    method: 'PATCH',
                                    headers: {
                                      'apikey': SUPABASE_ANON_KEY,
                                      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
                                      'Content-Type': 'application/json',
                                      'Prefer': 'return=minimal'
                                    },
                                    body: JSON.stringify({ messaggi_cliente: messaggiAggiornati })
                                  }
                                );
                                
                                setStatoRisultato(prev => ({
                                  ...prev,
                                  scheda: { ...prev.scheda, messaggi_cliente: messaggiAggiornati }
                                }));
                              } catch (err) {
                                console.error('Errore invio messaggio:', err);
                                alert('Errore nell\'invio del messaggio');
                              }
                            }}
                            style={{ padding: '12px 16px', background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', border: 'none', borderRadius: 10, color: '#fff', cursor: 'pointer' }}
                          >
                            📤
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
          
          {/* Footer */}
          <div style={{ textAlign: 'center', marginTop: 40, color: '#64748b', fontSize: 12 }}>
            <p>📍 Vico Acitillo 148, Napoli</p>
            <p>📞 081.191.73.939</p>
            <a href="/" style={{ color: '#3b82f6', textDecoration: 'none', marginTop: 16, display: 'inline-block' }}>← Torna al sito</a>
          </div>
        </div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if (checking) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: bg }}><div style={{ width: 40, height: 40, border: '3px solid #1e293b', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>;

  if (!auth) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: bg, padding: 20, fontFamily: '"Inter",sans-serif' }}>
        <div style={{ background: dark ? 'rgba(30,41,59,0.9)' : '#fff', borderRadius: 24, padding: '48px 40px', width: '100%', maxWidth: 380, textAlign: 'center', border: '1px solid ' + bord }}>
          <img src="/logo.jpg" alt="PC Care" style={{ width: 80, height: 80, borderRadius: 16, marginBottom: 16 }} />
          <h1 style={{ fontSize: 24, fontWeight: 800, color: text, margin: 0 }}>GESTIONE PC CARE</h1>
          <p style={{ color: muted, marginTop: 8, marginBottom: 36 }}>Accedi per continuare</p>
          <LoginForm onLogin={login} dark={dark} />
        </div>
      </div>
    );
  }

  if (loading || !data) return <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: bg }}><div style={{ width: 40, height: 40, border: '3px solid #1e293b', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div><p style={{ color: muted, marginTop: 16 }}>Caricamento...</p><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>;

  // Tab disponibili (per ora tutti, la logica permessi è in ConfigTab)
  const userAllowedTabs = ['home', 'resoconto', 'laboratorio', 'clienti', 'ordini', 'magazzino', 'preventivi', 'cassa', 'weekend', 'abbonamenti', 'contratti', 'ricorrenze', 'storico', 'promemoria', 'fileUtili', 'config'];

  return (
    <div style={{ minHeight: '100vh', background: bg, fontFamily: '"Inter",sans-serif', color: text, display: 'flex' }}>
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes slideInLeft { from { transform: translateX(-100%); } to { transform: translateX(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        input[type=number]::-webkit-outer-spin-button,
        input[type=number]::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type=number] {
          -moz-appearance: textfield;
        }
        .resoconto-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }
        @media (max-width: 768px) {
          .resoconto-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      {/* Overlay per chiudere sidebar (solo se aperta e NON pinnata) */}
      {sidebarOpen && !sidebarPinned && (
        <div 
          onClick={() => setSidebarOpen(false)}
          style={{ 
            position: 'fixed', 
            inset: 0, 
            background: 'rgba(0,0,0,0.5)', 
            zIndex: 998,
            animation: 'fadeIn 0.2s ease'
          }} 
        />
      )}

      {/* Sidebar Laterale */}
      <aside style={{ 
        position: sidebarPinned ? 'relative' : 'fixed',
        top: 0,
        left: sidebarPinned ? 0 : (sidebarOpen ? 0 : -280),
        width: 280,
        minWidth: 280,
        height: sidebarPinned ? 'auto' : '100vh',
        background: dark ? '#0f172a' : '#fff',
        borderRight: '1px solid ' + bord,
        zIndex: sidebarPinned ? 1 : 999,
        transition: sidebarPinned ? 'none' : 'left 0.3s ease',
        display: sidebarPinned ? 'flex' : (sidebarOpen ? 'flex' : 'flex'),
        flexDirection: 'column',
        boxShadow: (sidebarOpen && !sidebarPinned) ? '4px 0 20px rgba(0,0,0,0.2)' : 'none',
        flexShrink: 0
      }}>
        {/* Header Sidebar con pulsante Pin */}
        <div style={{ padding: '20px 16px', borderBottom: '1px solid ' + bord, background: dark ? 'rgba(59,130,246,0.1)' : 'rgba(59,130,246,0.05)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <img src="/logo.jpg" alt="PC Care" style={{ width: 50, height: 50, borderRadius: 12 }} />
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#3b82f6' }}>GestPCCARE</div>
                <div style={{ fontSize: 11, color: muted }}>👤 {user}</div>
              </div>
            </div>
            {/* Pulsante Pin/Unpin */}
            <button
              onClick={() => {
                const newPinned = !sidebarPinned;
                setSidebarPinned(newPinned);
                localStorage.setItem('sidebar-pinned', newPinned);
                if (newPinned) setSidebarOpen(true);
              }}
              style={{
                padding: '8px',
                background: sidebarPinned ? 'rgba(59,130,246,0.2)' : (dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'),
                border: sidebarPinned ? '1px solid rgba(59,130,246,0.5)' : '1px solid ' + bord,
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title={sidebarPinned ? 'Sblocca menu' : 'Fissa menu'}
            >
              {sidebarPinned ? '📌' : '📍'}
            </button>
          </div>
        </div>

        {/* Menu Items */}
        <div style={{ flex: 1, overflow: 'auto', padding: '12px 8px' }}>
          {(() => {
            const allTabs = [
              { id: 'home', icon: '🏠', label: 'Home', color: '#6366f1' },
              { id: 'resoconto', icon: '📋', label: 'Resoconto', color: '#3b82f6' },
              { id: 'laboratorio', icon: '🔧', label: 'Laboratorio', color: '#f97316' },
              { id: 'clienti', icon: '👥', label: 'Clienti', color: '#8b5cf6' },
              { id: 'ordini', icon: '📦', label: 'Ordini', color: '#a855f7' },
              { id: 'magazzino', icon: '🏪', label: 'Magazzino', color: '#14b8a6' },
              { id: 'preventivi', icon: '📝', label: 'Preventivi', color: '#06b6d4' },
              { id: 'cassa', icon: '🧾', label: 'Cassa', color: '#10b981' },
              { id: 'abbonamenti', icon: '📅', label: 'Abbonamenti', color: '#f59e0b' },
              { id: 'contratti', icon: '📑', label: 'Contratti', color: '#22c55e' },
              { id: 'weekend', icon: '🗓️', label: 'Weekend', color: '#8b5cf6' },
              { id: 'ricorrenze', icon: '🔄', label: 'Ricorrenze', color: '#06b6d4' },
              { id: 'storico', icon: '📈', label: 'Storico', color: '#ec4899' },
              { id: 'promemoria', icon: '🔔', label: 'Promemoria', color: '#8b5cf6' },
              { id: 'fileUtili', icon: '📁', label: 'File Utili', color: '#10b981' },
              { id: 'config', icon: '⚙️', label: 'Impostazioni', color: '#6366f1' },
            ];
            
            // Usa tabOrder dai dati se disponibile
            const savedOrder = data?.tabOrder || [];
            
            // Ordina i tab secondo tabOrder, mettendo quelli non presenti alla fine
            const orderedTabs = [...allTabs].sort((a, b) => {
              const idxA = savedOrder.indexOf(a.id);
              const idxB = savedOrder.indexOf(b.id);
              // Se entrambi sono in savedOrder, ordina per posizione
              if (idxA !== -1 && idxB !== -1) return idxA - idxB;
              // Se solo uno è in savedOrder, quello viene prima
              if (idxA !== -1) return -1;
              if (idxB !== -1) return 1;
              // Altrimenti mantieni ordine originale
              return 0;
            });
            
            return orderedTabs.filter(t => userAllowedTabs.includes(t.id)).map(t => (
              <button 
                key={t.id}
                className="sidebar-item"
                onClick={() => { setTab(t.id); if (!sidebarPinned) setSidebarOpen(false); }}
                style={{ 
                  width: '100%',
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 12, 
                  padding: '12px 16px', 
                  background: tab === t.id ? (t.color + '20') : 'transparent', 
                  border: 'none', 
                  borderRadius: 10, 
                  color: tab === t.id ? t.color : (dark ? '#e2e8f0' : '#334155'),
                  fontSize: 14, 
                  fontWeight: tab === t.id ? 600 : 500,
                  cursor: 'pointer',
                  textAlign: 'left',
                  marginBottom: 4,
                  borderLeft: tab === t.id ? ('3px solid ' + t.color) : '3px solid transparent'
                }}
              >
                <span style={{ fontSize: 18 }}>{t.icon}</span>
                <span>{t.label}</span>
              </button>
            ));
          })()}
        </div>

        {/* Footer Sidebar */}
        <div style={{ padding: 16, borderTop: '1px solid ' + bord }}>
          <button onClick={logout} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 12, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, color: '#ef4444', fontWeight: 600, cursor: 'pointer' }}>
            🚪 Esci
          </button>
        </div>
      </aside>

      {/* Contenuto principale */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh', minWidth: 0, overflow: 'auto' }}>
        {/* Header Fisso */}
        <header style={{ background: dark ? 'linear-gradient(135deg,rgba(59,130,246,0.15),rgba(139,92,246,0.1))' : 'linear-gradient(135deg,rgba(59,130,246,0.1),rgba(139,92,246,0.05))', borderBottom: '1px solid ' + bord, padding: '12px 16px', position: 'sticky', top: 0, zIndex: 100, backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {/* Hamburger Menu - nascosto se sidebar pinnata */}
              {!sidebarPinned && (
                <button 
                  onClick={() => setSidebarOpen(true)}
                  style={{ 
                    padding: '10px 12px', 
                    background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', 
                    border: 'none', 
                    borderRadius: 10, 
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4
                  }}
                >
                  <span style={{ width: 20, height: 2, background: dark ? '#e2e8f0' : '#334155', borderRadius: 1 }}></span>
                  <span style={{ width: 20, height: 2, background: dark ? '#e2e8f0' : '#334155', borderRadius: 1 }}></span>
                  <span style={{ width: 20, height: 2, background: dark ? '#e2e8f0' : '#334155', borderRadius: 1 }}></span>
                </button>
              )}
              <div>
                <h1 className="header-title" style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>
                  {tab === 'home' ? 'PC CARE NAPOLI' : 
                   tab === 'resoconto' ? '📋 Resoconto' :
                 tab === 'laboratorio' ? '🔧 Laboratorio' :
                 tab === 'clienti' ? '👥 Clienti' :
                 tab === 'ordini' ? '📦 Ordini' :
                 tab === 'magazzino' ? '🏪 Magazzino' :
                 tab === 'preventivi' ? '📝 Preventivi' :
                 tab === 'cassa' ? '🧾 Cassa' :
                 tab === 'abbonamenti' ? '📅 Abbonamenti' :
                 tab === 'contratti' ? '📑 Contratti' :
                 tab === 'weekend' ? '🗓️ Weekend' :
                 tab === 'ricorrenze' ? '🔄 Ricorrenze' :
                 tab === 'storico' ? '📈 Storico' :
                 tab === 'promemoria' ? '🔔 Promemoria' :
                 tab === 'fileUtili' ? '📁 File Utili' :
                 tab === 'config' ? '⚙️ Impostazioni' : 'GestPCCARE'}
              </h1>
              {saving && <span style={{ fontSize: 11, color: '#f59e0b' }}>● Sincronizzazione...</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
            {tab === 'resoconto' && (
              <div className="header-totals" style={{ display: 'flex', gap: 8, marginRight: 8 }}>
                <div style={{ textAlign: 'right' }}><span style={{ display: 'block', fontSize: 9, color: muted }}>IN</span><span style={{ fontSize: 14, fontWeight: 700, color: '#22c55e' }}>{fmtCur(totals.e)}</span></div>
                <div style={{ textAlign: 'right' }}><span style={{ display: 'block', fontSize: 9, color: muted }}>OUT</span><span style={{ fontSize: 14, fontWeight: 700, color: '#ef4444' }}>{fmtCur(totals.u)}</span></div>
                <div style={{ textAlign: 'right', padding: '6px 10px', background: dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', borderRadius: 8 }}><span style={{ display: 'block', fontSize: 9, color: muted }}>SALDO</span><span style={{ fontSize: 14, fontWeight: 700, color: totals.s >= 0 ? '#22c55e' : '#ef4444' }}>{fmtCur(totals.s)}</span></div>
              </div>
            )}
            {/* Pulsanti Undo/Redo */}
            <div style={{ display: 'flex', gap: 3, background: dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', borderRadius: 8, padding: 3 }}>
              <button onClick={undo} disabled={!canUndo} className="action-btn" style={{ padding: '6px 8px', background: canUndo ? (dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)') : 'transparent', border: 'none', borderRadius: 6, fontSize: 14, cursor: canUndo ? 'pointer' : 'not-allowed', opacity: canUndo ? 1 : 0.3 }} title={`Annulla (${undoStackRef.current.length})`}>↩️</button>
              <button onClick={redo} disabled={!canRedo} className="action-btn" style={{ padding: '6px 8px', background: canRedo ? (dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)') : 'transparent', border: 'none', borderRadius: 6, fontSize: 14, cursor: canRedo ? 'pointer' : 'not-allowed', opacity: canRedo ? 1 : 0.3 }} title={`Ripristina (${redoStackRef.current.length})`}>↪️</button>
            </div>
            {/* Campanella Promemoria */}
            <div style={{ position: 'relative' }}>
              <button 
                onClick={() => setShowPromemoriaDropdown(!showPromemoriaDropdown)} 
                className="action-btn" 
                style={{ 
                  padding: '8px 10px', 
                  background: promemoriaScaduti.length > 0 ? 'rgba(139,92,246,0.3)' : (dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'), 
                  border: 'none', 
                  borderRadius: 8, 
                  fontSize: 14, 
                  cursor: 'pointer',
                  position: 'relative'
                }}
                title={promemoriaScaduti.length > 0 ? `${promemoriaScaduti.length} promemoria scaduti` : 'Promemoria'}
              >
                🔔
                {promemoriaScaduti.length > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    background: '#ef4444',
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 700,
                    minWidth: 16,
                    height: 16,
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 4px'
                  }}>
                    {promemoriaScaduti.length}
                  </span>
                )}
              </button>
              {/* Dropdown promemoria */}
              {showPromemoriaDropdown && (
                <div style={{ 
                  position: 'absolute', 
                  top: '100%', 
                  right: 0, 
                  marginTop: 8,
                  background: dark ? '#1e293b' : '#fff', 
                  border: '1px solid ' + bord, 
                  borderRadius: 12, 
                  width: 320,
                  maxHeight: 400,
                  overflow: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
                }}>
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid ' + bord, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>🔔 Promemoria</span>
                    <button onClick={() => { setShowPromemoriaDropdown(false); setTab('promemoria'); }} style={{ background: 'rgba(139,92,246,0.2)', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, color: '#8b5cf6', cursor: 'pointer' }}>Vedi tutti</button>
                  </div>
                  {promemoriaScaduti.length === 0 ? (
                    <div style={{ padding: 24, textAlign: 'center', color: muted }}>
                      <span style={{ fontSize: 32, display: 'block', marginBottom: 8, opacity: 0.5 }}>✓</span>
                      <p style={{ margin: 0, fontSize: 13 }}>Nessun promemoria scaduto</p>
                    </div>
                  ) : (
                    <div style={{ padding: 8 }}>
                      {promemoriaScaduti.slice(0, 5).map(p => (
                        <div key={p.id} style={{ 
                          padding: 12, 
                          background: dark ? 'rgba(239,68,68,0.1)' : 'rgba(239,68,68,0.05)', 
                          borderRadius: 8, 
                          marginBottom: 6,
                          borderLeft: '3px solid #ef4444'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                            <div style={{ flex: 1 }}>
                              <span style={{ fontSize: 13, fontWeight: 600, display: 'block' }}>{p.titolo}</span>
                              <span style={{ fontSize: 11, color: muted }}>
                                📅 {new Date(p.dataAlert).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })} 🕐 {p.oraAlert || '09:00'}
                              </span>
                            </div>
                            <button 
                              onClick={() => {
                                save({ ...data, promemoria: data.promemoria.map(pr => pr.id === p.id ? { ...pr, completato: true } : pr) });
                              }}
                              style={{ background: 'rgba(34,197,94,0.2)', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 11, color: '#22c55e', cursor: 'pointer' }}
                              title="Segna completato"
                            >
                              ✓
                            </button>
                          </div>
                        </div>
                      ))}
                      {promemoriaScaduti.length > 5 && (
                        <p style={{ textAlign: 'center', fontSize: 11, color: muted, margin: '8px 0 0' }}>
                          +{promemoriaScaduti.length - 5} altri...
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <button onClick={() => setShowSearch(!showSearch)} className="action-btn" style={{ padding: '8px 10px', background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 8, fontSize: 14, cursor: 'pointer' }}>🔍</button>
            <button 
              onClick={salvaManualeNow} 
              disabled={salvataggioManuale === 'saving'}
              className="action-btn"
              style={{ 
                padding: '8px 10px', 
                background: salvataggioManuale === 'success' ? 'rgba(34,197,94,0.3)' : 
                           salvataggioManuale === 'error' ? 'rgba(239,68,68,0.3)' : 
                           salvataggioManuale === 'saving' ? 'rgba(245,158,11,0.3)' :
                           'rgba(34,197,94,0.2)', 
                border: 'none', 
                borderRadius: 8, 
                fontSize: 14, 
                cursor: salvataggioManuale === 'saving' ? 'wait' : 'pointer'
              }}
              title="Salva"
            >
              {salvataggioManuale === 'saving' ? '⏳' : salvataggioManuale === 'success' ? '✅' : salvataggioManuale === 'error' ? '❌' : '💾'}
            </button>
            {(() => {
              // Esito del backup automatico giornaliero (scritto dallo script Google su Supabase)
              const ub = data?.ultimoBackup;
              const quando = ub?.data ? new Date(ub.data) : null;
              const ore = quando ? (Date.now() - quando.getTime()) / 3600000 : null;
              const stato = !ub ? 'nessuno' : ub.esito !== 'ok' ? 'errore' : ore > 27 ? 'vecchio' : 'ok';
              const dataTesto = quando ? quando.toLocaleString('it-IT', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
              const titolo = stato === 'ok' ? 'Backup OK: ' + dataTesto
                : stato === 'errore' ? 'Ultimo backup NON riuscito (' + dataTesto + ')'
                : stato === 'vecchio' ? 'Backup mancante: l\'ultimo riuscito è del ' + dataTesto
                : 'Nessuna informazione sul backup automatico';
              const colori = { ok: 'rgba(34,197,94,0.3)', errore: 'rgba(239,68,68,0.35)', vecchio: 'rgba(245,158,11,0.3)', nessuno: 'rgba(100,116,139,0.2)' };
              const icone = { ok: '☁️✓', errore: '☁️✗', vecchio: '☁️⚠️', nessuno: '☁️?' };
              return (
                <button
                  type="button"
                  onClick={() => {
                    let msg = '💾 BACKUP AUTOMATICO GIORNALIERO\n\n' + titolo;
                    if (ub?.righe) msg += '\n\nRighe salvate:\n' + Object.entries(ub.righe).map(([k, v]) => '• ' + k + ': ' + v).join('\n');
                    if (ub?.file) msg += '\n\nFile: ' + ub.file + '\n(Google Drive → cartella Backup_PCCare)';
                    if (stato === 'errore' && ub?.errore) msg += '\n\nErrore: ' + ub.errore;
                    if (stato !== 'ok') msg += '\n\nControlla su script.google.com → progetto "Backup GestPCCare" → Esecuzioni.';
                    alert(msg);
                  }}
                  className="action-btn"
                  style={{ padding: '8px 10px', background: colori[stato], border: 'none', borderRadius: 8, fontSize: 14, cursor: 'pointer' }}
                  title={titolo}
                >
                  {icone[stato]}
                </button>
              );
            })()}
            <button onClick={logout} className="action-btn" style={{ padding: '8px 10px', background: 'rgba(239,68,68,0.2)', border: 'none', borderRadius: 8, color: '#ef4444', fontSize: 14, cursor: 'pointer' }}>🚪</button>
          </div>
        </div>
        {showSearch && (
          <div style={{ margin: '12px 0 0', position: 'relative' }}>
            <input type="text" placeholder="Cerca..." value={search} onChange={e => setSearch(e.target.value)} style={{ ...inputStyle, fontSize: 16 }} autoFocus />
            {search && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: dark ? '#1e293b' : '#fff', border: '1px solid ' + bord, borderRadius: 10, marginTop: 4, maxHeight: 300, overflow: 'auto', zIndex: 100 }}>
                {searchRes.length === 0 ? <p style={{ padding: 16, textAlign: 'center', color: muted }}>Nessun risultato</p> : searchRes.map((r, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid ' + bord }}>
                    <span style={{ fontSize: 10, padding: '2px 6px', background: 'rgba(59,130,246,0.2)', color: '#3b82f6', borderRadius: 4 }}>{r.t}</span>
                    <span style={{ flex: 1, fontSize: 14 }}>{r.nome}</span>
                    {r.importo !== undefined && <span style={{ fontSize: 14, fontWeight: 600, color: '#22c55e' }}>{fmtCur(r.importo)}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </header>

      {/* Notifica Backup Drive */}
      {backupNotification && (
        <div style={{
          position: 'fixed',
          top: 80,
          right: 20,
          zIndex: 9999,
          background: backupNotification === 'loading' ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' :
                     backupNotification === 'success' ? 'linear-gradient(135deg, #22c55e, #16a34a)' :
                     'linear-gradient(135deg, #ef4444, #dc2626)',
          borderRadius: 12,
          padding: '14px 20px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          minWidth: 280,
          animation: 'slideIn 0.3s ease'
        }}>
          {backupNotification === 'loading' && (
            <span style={{ fontSize: 20, animation: 'spin 1s linear infinite' }}>🔄</span>
          )}
          {backupNotification === 'success' && <span style={{ fontSize: 20 }}>☁️</span>}
          {backupNotification === 'error' && <span style={{ fontSize: 20 }}>⚠️</span>}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>
              {backupNotification === 'loading' ? 'Backup in corso...' : 
               backupNotification === 'success' ? 'Backup completato' : 'Errore backup'}
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)', marginTop: 2 }}>{backupMessage}</div>
          </div>
          <button 
            onClick={() => setBackupNotification(null)}
            style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6, padding: '4px 8px', color: '#fff', cursor: 'pointer', fontSize: 12 }}
          >✕</button>
        </div>
      )}

      {/* Main */}
      <main className="main-content" style={{ flex: 1, padding: '0 16px 20px', overflow: 'auto' }}>
        {tab === 'home' && <HomeTab dark={dark} bgCard={bgCard} bgInput={bgInput} bord={bord} muted={muted} />}
        {tab === 'resoconto' && <ResocontoTab data={data} save={save} upd={upd} del={del} transfer={transfer} pay={pay} transferParziale={transferParziale} payParziale={payParziale} setAddModal={setAddModal} setEditModal={setEditModal} dark={dark} bgCard={bgCard} bgInput={bgInput} bord={bord} muted={muted} abbTotali={abbTotali} />}
        {tab === 'laboratorio' && <LaboratorioTab data={data} save={save} del={del} dark={dark} bgCard={bgCard} bgInput={bgInput} bord={bord} muted={muted} userId={userId} supabase={supabase} loadData={loadData} />}
        {tab === 'clienti' && <ClientiTab data={data} save={save} del={del} dark={dark} bgCard={bgCard} bgInput={bgInput} bord={bord} muted={muted} />}
        {tab === 'ordini' && <OrdiniTab data={data} save={save} dark={dark} bgCard={bgCard} bgInput={bgInput} bord={bord} muted={muted} supabase={supabase} userId={userId} />}
        {tab === 'magazzino' && <MagazzinoTab data={data} save={save} dark={dark} bgCard={bgCard} bgInput={bgInput} bord={bord} muted={muted} />}
        {tab === 'preventivi' && <PreventiviTab data={data} save={save} del={del} dark={dark} bgCard={bgCard} bgInput={bgInput} bord={bord} muted={muted} />}
        {tab === 'cassa' && <CassaTab data={data} save={save} del={del} dark={dark} bgCard={bgCard} bgInput={bgInput} bord={bord} muted={muted} />}
        {tab === 'weekend' && <WeekendTab data={data} save={save} del={del} dark={dark} bgCard={bgCard} bgInput={bgInput} bord={bord} muted={muted} />}
        {tab === 'abbonamenti' && <AbbonamentiTab data={data} save={save} dark={dark} bgCard={bgCard} bgInput={bgInput} bord={bord} muted={muted} supabase={supabase} userId={userId} />}
        {tab === 'contratti' && <ContrattiTab data={data} del={del} rinnovaContratto={rinnovaContratto} setAddModal={setAddModal} setEditModal={setEditModal} dark={dark} bgCard={bgCard} bgInput={bgInput} bord={bord} muted={muted} upd={upd} />}
        {tab === 'ricorrenze' && <RicorrenzeTab data={data} del={del} applicaRicorrenze={applicaRicorrenze} setAddModal={setAddModal} dark={dark} bgCard={bgCard} bgInput={bgInput} bord={bord} muted={muted} save={save} />}
        {tab === 'storico' && <StoricoTab data={data} save={save} del={del} delAll={delAll} dark={dark} bgCard={bgCard} bgInput={bgInput} bord={bord} muted={muted} />}
        {tab === 'promemoria' && <PromemoriaTab data={data} save={save} del={del} dark={dark} bgCard={bgCard} bgInput={bgInput} bord={bord} muted={muted} />}
        {tab === 'fileUtili' && <FileUtiliTab data={data} save={save} del={del} dark={dark} bgCard={bgCard} bgInput={bgInput} bord={bord} muted={muted} uploadFileToStorage={uploadFileToStorage} downloadFileFromStorage={downloadFileFromStorage} deleteFileFromStorage={deleteFileFromStorage} />}
        {tab === 'config' && <ConfigTab data={data} save={save} delCat={delCat} moveCat={moveCat} setCatModal={setCatModal} exportExcel={exportExcel} downloadBackup={downloadBackup} restoreBackup={restoreBackup} theme={theme} setTheme={setTheme} dark={dark} bgCard={bgCard} bgInput={bgInput} bord={bord} muted={muted} loadData={loadData} text={text} currentUser={user} getUsers={getUsers} />}
      </main>
      </div>{/* Fine wrapper contenuto principale */}

      {/* Modals */}
      {editModal && <EditModal modal={editModal} setModal={setEditModal} upd={upd} dark={dark} inputStyle={inputStyle} labelStyle={labelStyle} />}
      {addModal && <AddModalComp modal={addModal} setModal={setAddModal} add={add} data={data} dark={dark} inputStyle={inputStyle} labelStyle={labelStyle} />}
      {catModal && <CatModalComp modal={catModal} setModal={setCatModal} addCat={addCat} data={data} dark={dark} inputStyle={inputStyle} labelStyle={labelStyle} />}
      {showPrint && <PrintPreview data={data} totals={totals} onClose={() => setShowPrint(false)} />}
      
      {/* Alert Contratti Scaduti */}
      {alertContratti && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setAlertContratti(null)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 500, maxHeight: '80vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '20px', borderBottom: '1px solid ' + bord, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 32 }}>⚠️</span>
              <div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: dark ? '#fff' : '#1e293b' }}>Attenzione Contratti!</h2>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: muted }}>Hai contratti che richiedono attenzione</p>
              </div>
            </div>
            <div style={{ padding: 20 }}>
              {alertContratti.scaduti.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 8 }}>🔴 Scaduti ({alertContratti.scaduti.length})</h3>
                  {alertContratti.scaduti.map(c => (
                    <div key={c.id} style={{ background: 'rgba(239,68,68,0.1)', borderRadius: 10, padding: '12px 16px', marginBottom: 8, borderLeft: '4px solid #ef4444' }}>
                      <div style={{ fontWeight: 600 }}>{c.cliente}</div>
                      <div style={{ fontSize: 12, color: muted }}>{c.descrizione} • Scaduto il {fmtDate(c.scadenza)}</div>
                    </div>
                  ))}
                </div>
              )}
              {alertContratti.inScadenza.length > 0 && (
                <div>
                  <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: 8 }}>🟡 In scadenza nei prossimi 30 giorni ({alertContratti.inScadenza.length})</h3>
                  {alertContratti.inScadenza.map(c => {
                    const giorni = Math.ceil((new Date(c.scadenza) - new Date()) / (1000 * 60 * 60 * 24));
                    return (
                      <div key={c.id} style={{ background: 'rgba(245,158,11,0.1)', borderRadius: 10, padding: '12px 16px', marginBottom: 8, borderLeft: '4px solid #f59e0b' }}>
                        <div style={{ fontWeight: 600 }}>{c.cliente}</div>
                        <div style={{ fontSize: 12, color: muted }}>{c.descrizione} • Scade tra {giorni} giorni ({fmtDate(c.scadenza)})</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={{ padding: '16px 20px', borderTop: '1px solid ' + bord, display: 'flex', gap: 12 }}>
              <button onClick={() => { setAlertContratti(null); setTab('contratti'); }} style={{ flex: 1, padding: 12, background: 'linear-gradient(135deg,#3b82f6,#2563eb)', border: 'none', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>📑 Vai ai Contratti</button>
              <button onClick={() => setAlertContratti(null)} style={{ flex: 1, padding: 12, background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 10, color: muted, cursor: 'pointer' }}>Chiudi</button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Abbonamenti in Scadenza */}
      {alertAbbonamenti && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setAlertAbbonamenti(null)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 500, maxHeight: '80vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '20px', borderBottom: '1px solid ' + bord, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 32 }}>📅</span>
              <div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: dark ? '#fff' : '#1e293b' }}>Abbonamenti in Scadenza!</h2>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: muted }}>Hai abbonamenti da riscuotere questo mese</p>
              </div>
            </div>
            <div style={{ padding: 20 }}>
              {alertAbbonamenti.scaduti.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 8 }}>🔴 Scaduti e non pagati ({alertAbbonamenti.scaduti.length})</h3>
                  {alertAbbonamenti.scaduti.map(a => (
                    <div key={a.id} style={{ background: 'rgba(239,68,68,0.1)', borderRadius: 10, padding: '12px 16px', marginBottom: 8, borderLeft: '4px solid #ef4444' }}>
                      <div style={{ fontWeight: 600 }}>{a.nomeCliente}</div>
                      <div style={{ fontSize: 12, color: muted }}>{a.riferimento || 'Abbonamento'} • Scaduto il giorno {a.giornoScadenza} • €{a.costo}</div>
                    </div>
                  ))}
                </div>
              )}
              {alertAbbonamenti.inScadenza.length > 0 && (
                <div>
                  <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: 8 }}>🟡 In scadenza nei prossimi 7 giorni ({alertAbbonamenti.inScadenza.length})</h3>
                  {alertAbbonamenti.inScadenza.map(a => {
                    const oggi = new Date();
                    const giornoOggi = oggi.getDate();
                    const ultimoGiornoMese = new Date(oggi.getFullYear(), oggi.getMonth() + 1, 0).getDate();
                    const giorniRimanentiMese = ultimoGiornoMese - giornoOggi;
                    // Se è del mese prossimo, calcola i giorni mancanti correttamente
                    const giorniMancanti = a._meseProssimo 
                      ? giorniRimanentiMese + a.giornoScadenza 
                      : a.giornoScadenza - giornoOggi;
                    const mesiNomi = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
                    const meseLabel = a._meseProssimo ? mesiNomi[a._meseRiferimento] : mesiNomi[oggi.getMonth()];
                    return (
                      <div key={a.id} style={{ background: 'rgba(245,158,11,0.1)', borderRadius: 10, padding: '12px 16px', marginBottom: 8, borderLeft: '4px solid #f59e0b' }}>
                        <div style={{ fontWeight: 600 }}>{a.nomeCliente}</div>
                        <div style={{ fontSize: 12, color: muted }}>{a.riferimento || 'Abbonamento'} • Scade tra {giorniMancanti} giorni ({a.giornoScadenza} {meseLabel}) • €{a.costo}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={{ padding: '16px 20px', borderTop: '1px solid ' + bord, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', gap: 12 }}>
                <button onClick={() => { setAlertAbbonamenti(null); setTab('abbonamenti'); }} style={{ flex: 1, padding: 12, background: 'linear-gradient(135deg,#3b82f6,#2563eb)', border: 'none', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>📅 Vai agli Abbonamenti</button>
                <button onClick={() => setAlertAbbonamenti(null)} style={{ flex: 1, padding: 12, background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 10, color: muted, cursor: 'pointer' }}>Chiudi</button>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button onClick={() => posticipaAlertAbb(1)} style={{ padding: '8px 12px', background: 'transparent', border: '1px solid ' + bord, borderRadius: 8, color: muted, fontSize: 11, cursor: 'pointer' }}>⏰ Ricorda domani</button>
                <button onClick={() => posticipaAlertAbb(3)} style={{ padding: '8px 12px', background: 'transparent', border: '1px solid ' + bord, borderRadius: 8, color: muted, fontSize: 11, cursor: 'pointer' }}>⏰ Tra 3 giorni</button>
                <button onClick={() => posticipaAlertAbb(7)} style={{ padding: '8px 12px', background: 'transparent', border: '1px solid ' + bord, borderRadius: 8, color: muted, fontSize: 11, cursor: 'pointer' }}>⏰ Tra 1 settimana</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Avviso firma cliente ricevuta */}
      {avvisiFirma.length > 0 && (
        <div style={{ position: 'fixed', top: 16, right: 16, left: 16, zIndex: 1200, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10, pointerEvents: 'none' }}>
          {avvisiFirma.map(a => (
            <div key={a.key} style={{ pointerEvents: 'auto', width: '100%', maxWidth: 380, background: dark ? '#14532d' : '#f0fdf4', border: '2px solid #22c55e', borderRadius: 14, padding: 14, boxShadow: '0 10px 30px rgba(0,0,0,0.35)', color: dark ? '#dcfce7' : '#14532d' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ fontSize: 26, lineHeight: 1 }}>✍️</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>Firma ricevuta</div>
                  <div style={{ fontSize: 13, marginTop: 2 }}><strong>{a.cliente || 'Il cliente'}</strong> ha firmato <em>{a.tipo || 'l\'autorizzazione'}</em></div>
                  <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>Scheda #{a.schedaId}{a.dataFirma ? ' · ' + new Date(a.dataFirma).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}</div>
                </div>
                <button onClick={() => setAvvisiFirma(prev => prev.filter(p => p.key !== a.key))} style={{ background: 'transparent', border: 'none', fontSize: 20, color: 'inherit', cursor: 'pointer', lineHeight: 1 }} title="Chiudi">×</button>
              </div>
              <button onClick={() => { setTab('laboratorio'); setAvvisiFirma(prev => prev.filter(p => p.key !== a.key)); }} style={{ marginTop: 10, width: '100%', padding: '8px 12px', background: '#22c55e', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                🔧 Vai al Laboratorio
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Alert Promemoria */}
      {alertPromemoria && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, padding: 20 }} onClick={() => chiudiAlertPromemoria()}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 400, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '24px', textAlign: 'center', background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' }}>
              <span style={{ fontSize: 48, display: 'block', marginBottom: 12 }}>🔔</span>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#fff' }}>Promemoria!</h2>
            </div>
            <div style={{ padding: 24 }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 600 }}>{alertPromemoria.titolo}</h3>
              {alertPromemoria.descrizione && <p style={{ margin: '0 0 16px', fontSize: 14, color: muted }}>{alertPromemoria.descrizione}</p>}
              <div style={{ fontSize: 12, color: muted, display: 'flex', alignItems: 'center', gap: 8 }}>
                {alertPromemoria.dataOra ? (
                  <span>📅 {new Date(alertPromemoria.dataOra).toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                ) : (
                  <>
                    <span>📅 {alertPromemoria.dataAlert}</span>
                    <span>🕐 {alertPromemoria.oraAlert || '09:00'}</span>
                  </>
                )}
              </div>
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid ' + bord, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', gap: 12 }}>
                <button 
                  onClick={() => completaPromemoria(alertPromemoria.id)} 
                  style={{ flex: 1, padding: 12, background: 'linear-gradient(135deg,#22c55e,#16a34a)', border: 'none', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer' }}
                >
                  ✓ Completato
                </button>
                <button 
                  onClick={() => { 
                    setAlertPosticipatoId(alertPromemoria.id); // Ricorda che è stato posticipato
                    setAlertPromemoria(null); // Chiudi alert ma badge rimane!
                  }} 
                  style={{ flex: 1, padding: 12, background: 'rgba(100,116,139,0.2)', border: '1px solid ' + bord, borderRadius: 10, color: muted, fontWeight: 600, cursor: 'pointer' }}
                >
                  Posticipa
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <button onClick={() => posticipaPromemoria(alertPromemoria.id, 1)} style={{ padding: '8px 12px', background: 'transparent', border: '1px solid ' + bord, borderRadius: 8, color: muted, fontSize: 11, cursor: 'pointer' }}>⏰ +1 ora</button>
                <button onClick={() => posticipaPromemoria(alertPromemoria.id, 3)} style={{ padding: '8px 12px', background: 'transparent', border: '1px solid ' + bord, borderRadius: 8, color: muted, fontSize: 11, cursor: 'pointer' }}>⏰ +3 ore</button>
                <button onClick={() => posticipaPromemoriaGiorni(alertPromemoria.id, 1)} style={{ padding: '8px 12px', background: 'transparent', border: '1px solid ' + bord, borderRadius: 8, color: muted, fontSize: 11, cursor: 'pointer' }}>⏰ Domani</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LoginForm({ onLogin, dark }) {
  const rememberedUser = localStorage.getItem('resoconto-remembered-user') || '';
  const [u, setU] = useState(rememberedUser);
  const [p, setP] = useState('');
  const [err, setErr] = useState('');
  const [load, setLoad] = useState(false);
  const [rememberMe, setRememberMe] = useState(!!rememberedUser);
  const inputStyle = { width: '100%', padding: '14px 16px', background: dark ? 'rgba(15,23,42,0.8)' : '#f8fafc', border: dark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', borderRadius: 12, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 15, outline: 'none', boxSizing: 'border-box' };
  
  const submit = (e) => {
    e.preventDefault();
    setLoad(true);
    setErr('');
    // FIX: onLogin è una funzione async e restituisce SEMPRE una Promise, che è
    // sempre truthy. Senza await il ramo di errore non veniva mai eseguito e con
    // le credenziali sbagliate non compariva alcun messaggio.
    setTimeout(async () => {
      try {
        const ok = await onLogin(u, p);
        if (!ok) {
          setErr('Credenziali non valide');
        } else if (!rememberMe) {
          // Se non vuole essere ricordato, rimuovi l'utente memorizzato
          localStorage.removeItem('resoconto-remembered-user');
        }
      } catch (errLogin) {
        console.error('Errore login:', errLogin);
        setErr('Impossibile contattare il server, riprova');
      } finally {
        setLoad(false);
      }
    }, 500);
  };
  
  const clearRememberedUser = () => {
    localStorage.removeItem('resoconto-remembered-user');
    setU('');
    setRememberMe(false);
  };
  
  return (
    <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ textAlign: 'left' }}>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#94a3b8', marginBottom: 8 }}>👤 Username</label>
        {rememberedUser ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ 
              flex: 1, 
              padding: '14px 16px', 
              background: dark ? 'rgba(34,197,94,0.1)' : 'rgba(34,197,94,0.05)', 
              border: '1px solid rgba(34,197,94,0.3)', 
              borderRadius: 12, 
              color: '#22c55e', 
              fontSize: 15,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <span>✓</span>
              <span>{rememberedUser}</span>
            </div>
            <button 
              type="button"
              onClick={clearRememberedUser}
              style={{ 
                padding: '14px 16px', 
                background: dark ? 'rgba(255,255,255,0.1)' : '#f1f5f9', 
                border: 'none', 
                borderRadius: 12, 
                color: '#64748b', 
                fontSize: 14,
                cursor: 'pointer'
              }}
              title="Cambia utente"
            >
              ✕
            </button>
          </div>
        ) : (
          <input type="text" value={u} onChange={e => setU(e.target.value)} style={inputStyle} required autoFocus />
        )}
      </div>
      <div style={{ textAlign: 'left' }}>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#94a3b8', marginBottom: 8 }}>🔒 Password</label>
        <input type="password" value={p} onChange={e => setP(e.target.value)} style={inputStyle} required autoFocus={!!rememberedUser} />
      </div>
      {!rememberedUser && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <input 
            type="checkbox" 
            id="rememberMe"
            checked={rememberMe} 
            onChange={e => setRememberMe(e.target.checked)}
            style={{ width: 18, height: 18, cursor: 'pointer' }}
          />
          <label htmlFor="rememberMe" style={{ fontSize: 13, color: '#94a3b8', cursor: 'pointer' }}>
            Ricordami su questo dispositivo
          </label>
        </div>
      )}
      {err && <p style={{ color: '#ef4444', fontSize: 13, margin: 0, padding: 12, background: 'rgba(239,68,68,0.1)', borderRadius: 8 }}>{err}</p>}
      <button type="submit" style={{ padding: 16, background: 'linear-gradient(135deg,#3b82f6,#2563eb)', border: 'none', borderRadius: 12, color: '#fff', fontSize: 16, fontWeight: 600, cursor: 'pointer' }}>{load ? '⏳...' : '🚀 Accedi'}</button>
    </form>
  );
}

function HomeTab({ dark, bgCard, bgInput, bord, muted }) {
  return (
    <section style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      minHeight: 'calc(100vh - 120px)',
      padding: '40px 20px'
    }}>
      {/* Logo Grande */}
      <img 
        src="/logo.jpg" 
        alt="PC Care Napoli" 
        style={{ 
          width: 180, 
          height: 180, 
          borderRadius: 30, 
          marginBottom: 30,
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
        }} 
      />
      
      {/* Dati Aziendali */}
      <div style={{ 
        textAlign: 'center',
        background: bgCard,
        borderRadius: 20,
        padding: '30px 40px',
        border: '1px solid ' + bord,
        maxWidth: 500,
        width: '100%'
      }}>
        <h1 style={{ 
          margin: '0 0 8px', 
          fontSize: 28, 
          fontWeight: 800, 
          color: '#3b82f6'
        }}>
          PC CARE NAPOLI
        </h1>
        <h2 style={{ 
          margin: '0 0 20px', 
          fontSize: 16, 
          fontWeight: 600, 
          color: muted
        }}>
          di Rispoli Oreste
        </h2>
        
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 12,
          fontSize: 14,
          color: dark ? '#e2e8f0' : '#334155'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}>
            <span>📍</span>
            <span>Vico Acitillo, 148 - 80127 Napoli (NA)</span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <span>📞</span>
            <a href="tel:08119173939" style={{ color: '#3b82f6', textDecoration: 'none' }}>081 191 73 939</a>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <span>✉️</span>
            <a href="mailto:info@pccarenapoli.it" style={{ color: '#3b82f6', textDecoration: 'none' }}>info@pccarenapoli.it</a>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}>
            <span>🌐</span>
            <a href="https://www.pccarenapoli.it" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'none' }}>www.pccarenapoli.it</a>
          </div>
        </div>
        
        {/* Dati Fiscali */}
        <div style={{ 
          marginTop: 24, 
          paddingTop: 20, 
          borderTop: '1px solid ' + bord,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 12,
          fontSize: 12,
          color: muted,
          textAlign: 'left'
        }}>
          <div>
            <strong style={{ color: dark ? '#e2e8f0' : '#334155' }}>Partita IVA:</strong><br />
            07880741215
          </div>
          <div>
            <strong style={{ color: dark ? '#e2e8f0' : '#334155' }}>Codice Fiscale:</strong><br />
            RSPRST89M18F839J
          </div>
          <div>
            <strong style={{ color: dark ? '#e2e8f0' : '#334155' }}>IBAN:</strong><br />
            IT79L0623003543000057371135
          </div>
          <div>
            <strong style={{ color: dark ? '#e2e8f0' : '#334155' }}>Codice Univoco:</strong><br />
            KUPCRMI
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <p style={{ 
        marginTop: 40, 
        fontSize: 12, 
        color: muted,
        textAlign: 'center'
      }}>
        App creata e sviluppata da <strong>Oreste Rispoli</strong>
      </p>
    </section>
  );
}

function ResocontoTab({ data, save, upd, del, transfer, pay, transferParziale, payParziale, setAddModal, setEditModal, dark, bgCard, bgInput, bord, muted, abbTotali }) {
  const [importoModal, setImportoModal] = useState(null);
  const [sommaModal, setSommaModal] = useState(null);
  const [saldoTempModal, setSaldoTempModal] = useState(false);
  const [selezioneEntrate, setSelezioneEntrate] = useState({});
  const [selezioneUscite, setSelezioneUscite] = useState({});
  const [trasferimentoModal, setTrasferimentoModal] = useState(null); // { tipo: 'transfer'|'pay', item, section }
  
  // Conti rapidi (gli stessi degli abbonamenti)
  const CONTI_RAPIDI = [
    { nome: 'BANCA CARIPARMA PC CARE', icon: '🏦' },
    { nome: 'CASSA NEGOZIO', icon: '💵' },
    { nome: 'POSTEPAY', icon: '💳' },
    { nome: 'PAYPAL', icon: '🅿️' },
    { nome: 'REVOLUT', icon: '💜' },
    { nome: 'BUNQ', icon: '🟢' }
  ];

  // Nomi mesi per visualizzazione
  const NOMI_MESI = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
  const oggi = new Date();
  const meseCorrenteNome = NOMI_MESI[oggi.getMonth()];
  const meseProssimoNome = NOMI_MESI[(oggi.getMonth() + 1) % 12];

  // Applica l'importo predefinito (somma al valore attuale) - per uscite
  const applicaImporto = (itemId) => {
    const item = data.uscite.find(i => i.id === itemId);
    if (!item) return;
    const importoPredefinito = item.importoPredefinito || 0;
    if (importoPredefinito === 0) {
      setImportoModal({ id: itemId, nome: item.nome, importoPredefinito: '', section: 'uscite' });
      return;
    }
    upd('uscite', itemId, 'importo', (item.importo || 0) + importoPredefinito);
  };

  // Salva l'importo predefinito
  const salvaImportoPredefinito = () => {
    if (!importoModal) return;
    const valore = parseFloat(importoModal.importoPredefinito) || 0;
    upd(importoModal.section, importoModal.id, 'importoPredefinito', valore);
    setImportoModal(null);
    if (valore > 0) {
      alert(`Importo predefinito di €${valore} salvato per "${importoModal.nome}".\nOra clicca 🔄 per sommare questo valore!`);
    }
  };

  // Apri modal per sommare/sottrarre un valore
  const apriSommaModal = (section, item, sottrai = false) => {
    setSommaModal({ section, id: item.id, nome: item.nome, importoAttuale: item.importo || 0, valoreDaSommare: '', sottrai });
  };

  // Somma/Sottrai il valore e registra nel log movimenti
  const eseguiSomma = () => {
    if (!sommaModal) return;
    let valore = parseFloat(sommaModal.valoreDaSommare) || 0;
    if (valore === 0) { setSommaModal(null); return; }
    
    const isSottrazione = sommaModal.sottrai;
    
    // Calcola nuovo importo
    const importoVariazione = isSottrazione ? -Math.abs(valore) : Math.abs(valore);
    const nuovoImporto = sommaModal.importoAttuale + importoVariazione;
    
    // Aggiorna voce
    const d = { ...data };
    const i = d[sommaModal.section].findIndex(x => x.id === sommaModal.id);
    if (i >= 0) { 
      d[sommaModal.section][i] = { ...d[sommaModal.section][i], importo: nuovoImporto }; 
    }
    
    // Registra movimento nello storico SE c'è una descrizione
    if (sommaModal.descrizione && sommaModal.descrizione.trim()) {
      const movimento = {
        id: Date.now().toString(),
        data: new Date().toISOString(),
        tipo: sommaModal.section === 'entrate' ? 'entrata' : 'uscita',
        descrizione: sommaModal.descrizione.trim(),
        importo: Math.abs(valore),
        metodo: `${isSottrazione ? 'Sottratto da' : 'Aggiunto a'}: ${sommaModal.nome}`,
        voce: sommaModal.nome
      };
      d.movimenti = [...(d.movimenti || []), movimento];
    }
    
    save(d);
    setSommaModal(null);
  };

  // Genera PDF Backup Resoconto
  const generaPdfResoconto = () => {
    // Calcola totale abbonamenti (solo quelli pagati)
    const abbonamenti = data.abbonamenti || [];
    const totAbbonamenti = abbonamenti
      .filter(a => a.pagato)
      .reduce((s, a) => s + (a.importo || 0), 0);
    
    // Entrate normali (esclude voci riparazione temporanee)
    const entrateNormali = data.entrate.filter(e => !e.isRiparazione && !e.isOrdine);
    const totEntrate = entrateNormali.reduce((s, i) => s + (i.importo || 0), 0);
    const totEntrateConAbb = totEntrate + totAbbonamenti;
    
    const totUscite = data.uscite.reduce((s, i) => s + (i.importo || 0), 0);
    const saldo = totEntrateConAbb - totUscite;
    const oggi = new Date().toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    
    // Raggruppa abbonamenti pagati per mese
    const abbPagatiPerMese = {};
    abbonamenti.filter(a => a.pagato).forEach(a => {
      const mese = a.mese || 'Sconosciuto';
      if (!abbPagatiPerMese[mese]) abbPagatiPerMese[mese] = [];
      abbPagatiPerMese[mese].push(a);
    });
    
    const printWindow = window.open('', '_blank');
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Backup Resoconto - ${oggi}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 30px; max-width: 800px; margin: 0 auto; }
          h1 { color: #1e293b; font-size: 24px; border-bottom: 3px solid #3b82f6; padding-bottom: 10px; }
          h2 { color: #3b82f6; font-size: 16px; margin-top: 25px; margin-bottom: 10px; }
          h3 { color: #f59e0b; font-size: 14px; margin-top: 20px; margin-bottom: 8px; }
          .summary { display: flex; gap: 20px; margin: 20px 0; padding: 20px; background: #f8fafc; border-radius: 10px; flex-wrap: wrap; }
          .summary-item { text-align: center; min-width: 100px; }
          .summary-label { font-size: 11px; color: #64748b; }
          .summary-value { font-size: 20px; font-weight: 700; }
          .entrate { color: #22c55e; }
          .abbonamenti { color: #f59e0b; }
          .uscite { color: #ef4444; }
          .saldo { color: ${saldo >= 0 ? '#22c55e' : '#ef4444'}; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 20px; }
          th, td { border: 1px solid #e2e8f0; padding: 8px; text-align: left; }
          th { background: #f1f5f9; font-weight: 600; }
          .importo { text-align: right; font-weight: 600; }
          .categoria { background: #f8fafc; font-weight: 600; }
          .footer { margin-top: 30px; font-size: 11px; color: #94a3b8; text-align: center; border-top: 1px solid #e2e8f0; padding-top: 15px; }
          .mese-header { background: #fef3c7; font-weight: 600; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <h1>📊 BACKUP RESOCONTO</h1>
        <p style="color: #64748b; font-size: 13px;">Generato il ${oggi}</p>
        
        <div class="summary">
          <div class="summary-item">
            <div class="summary-label">ENTRATE</div>
            <div class="summary-value entrate">€${totEntrate.toFixed(2)}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">ABBONAMENTI</div>
            <div class="summary-value abbonamenti">€${totAbbonamenti.toFixed(2)}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">TOT. ENTRATE</div>
            <div class="summary-value entrate">€${totEntrateConAbb.toFixed(2)}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">USCITE</div>
            <div class="summary-value uscite">€${totUscite.toFixed(2)}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">SALDO</div>
            <div class="summary-value saldo">€${saldo.toFixed(2)}</div>
          </div>
        </div>
        
        <h2>↓ ENTRATE</h2>
        <table>
          <thead><tr><th>Categoria</th><th>Voce</th><th>Note</th><th class="importo">Importo</th></tr></thead>
          <tbody>
            ${(data.categorieEntrate || []).map(cat => {
              const items = entrateNormali.filter(i => i.categoria === cat.id);
              if (items.length === 0) return '';
              return items.map((item, idx) => `
                <tr>
                  ${idx === 0 ? `<td rowspan="${items.length}" class="categoria">${cat.icon} ${cat.nome}</td>` : ''}
                  <td>${item.nome}</td>
                  <td>${item.note || '-'}</td>
                  <td class="importo" style="color: #22c55e;">€${(item.importo || 0).toFixed(2)}</td>
                </tr>
              `).join('');
            }).join('')}
          </tbody>
        </table>
        
        ${totAbbonamenti > 0 ? `
        <h2>📅 ABBONAMENTI PAGATI</h2>
        <table>
          <thead><tr><th>Mese</th><th>Cliente</th><th>Tipo</th><th class="importo">Importo</th></tr></thead>
          <tbody>
            ${Object.entries(abbPagatiPerMese).map(([mese, abbs]) => 
              abbs.map((a, idx) => `
                <tr>
                  ${idx === 0 ? `<td rowspan="${abbs.length}" class="mese-header">${mese}</td>` : ''}
                  <td>${a.cliente || '-'}</td>
                  <td>${a.tipo || '-'}</td>
                  <td class="importo" style="color: #f59e0b;">€${(a.importo || 0).toFixed(2)}</td>
                </tr>
              `).join('')
            ).join('')}
            <tr style="background: #fef3c7; font-weight: 700;">
              <td colspan="3">TOTALE ABBONAMENTI</td>
              <td class="importo" style="color: #f59e0b;">€${totAbbonamenti.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
        ` : ''}
        
        <h2>↑ USCITE</h2>
        <table>
          <thead><tr><th>Categoria</th><th>Voce</th><th>Note</th><th class="importo">Importo</th></tr></thead>
          <tbody>
            ${(data.categorieUscite || []).map(cat => {
              const items = data.uscite.filter(i => i.categoria === cat.id);
              if (items.length === 0) return '';
              return items.map((item, idx) => `
                <tr>
                  ${idx === 0 ? `<td rowspan="${items.length}" class="categoria">${cat.icon} ${cat.nome}</td>` : ''}
                  <td>${item.nome}</td>
                  <td>${item.note || '-'}</td>
                  <td class="importo" style="color: #ef4444;">€${(item.importo || 0).toFixed(2)}</td>
                </tr>
              `).join('');
            }).join('')}
          </tbody>
        </table>
        
        <div class="footer">
          GESTIONE PC CARE - Backup generato automaticamente
        </div>
        <script>window.print();</script>
      </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const [quickAddModal, setQuickAddModal] = useState(null); // { tipo: 'entrate' | 'uscite' }

  return (
    <div>
      {/* Pulsanti rapidi + Entrata / - Uscita */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <button 
          onClick={() => setQuickAddModal({ tipo: 'entrate' })}
          style={{ 
            flex: 1,
            padding: '16px 20px', 
            background: 'linear-gradient(135deg, #22c55e, #16a34a)', 
            border: 'none', 
            borderRadius: 12, 
            color: '#fff', 
            fontSize: 16, 
            fontWeight: 700, 
            cursor: 'pointer', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: 10,
            boxShadow: '0 4px 12px rgba(34,197,94,0.3)'
          }}
        >
          <span style={{ fontSize: 24 }}>+</span>
          <span>Entrata</span>
        </button>
        <button 
          onClick={() => setQuickAddModal({ tipo: 'uscite' })}
          style={{ 
            flex: 1,
            padding: '16px 20px', 
            background: 'linear-gradient(135deg, #ef4444, #dc2626)', 
            border: 'none', 
            borderRadius: 12, 
            color: '#fff', 
            fontSize: 16, 
            fontWeight: 700, 
            cursor: 'pointer', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: 10,
            boxShadow: '0 4px 12px rgba(239,68,68,0.3)'
          }}
        >
          <span style={{ fontSize: 24 }}>−</span>
          <span>Uscita</span>
        </button>
      </div>

      {/* Modal Quick Add */}
      {quickAddModal && (
        <QuickAddModal
          tipo={quickAddModal.tipo}
          vociEntrate={data.entrate.filter(e => !e.isRiparazione && !e.isOrdine)}
          vociUscite={data.uscite}
          categorieEntrate={data.categorieEntrate}
          categorieUscite={data.categorieUscite}
          onAdd={(voceId, importo, sezione, sottrai, descrizioneOp) => {
            // sezione = 'entrate' o 'uscite'
            // sottrai = true se deve sottrarre (preleva da conto)
            // descrizioneOp = descrizione opzionale per lo storico
            const voce = data[sezione].find(v => v.id === voceId);
            if (!voce) return;
            
            const importoFinale = sottrai ? -Math.abs(importo) : importo;
            const nuovoImporto = (voce.importo || 0) + importoFinale;
            
            // Aggiorna dati
            const d = { ...data };
            const i = d[sezione].findIndex(x => x.id === voceId);
            if (i >= 0) d[sezione][i] = { ...d[sezione][i], importo: nuovoImporto };
            
            // Registra movimento nello storico
            const tipoMovimento = sottrai ? 'uscita' : (sezione === 'uscite' ? 'uscita' : 'entrata');
            const descrizioneMovimento = descrizioneOp 
              ? `${descrizioneOp} (${voce.nome})`
              : `${sottrai ? 'Prelievo da' : (sezione === 'uscite' ? 'Aggiunta a' : 'Aggiunta a')} ${voce.nome}`;
            
            const movimento = {
              id: Date.now().toString(),
              data: new Date().toISOString(),
              tipo: tipoMovimento,
              descrizione: descrizioneMovimento,
              importo: Math.abs(importo),
              metodo: 'Inserimento rapido',
              voce: voce.nome
            };
            d.movimenti = [...(d.movimenti || []), movimento];
            
            save(d);
            setQuickAddModal(null);
          }}
          onClose={() => setQuickAddModal(null)}
          dark={dark}
          bgCard={bgCard}
          bgInput={bgInput}
          bord={bord}
          muted={muted}
        />
      )}

      {/* Pulsanti Azioni */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16, gap: 10 }}>
        <button onClick={() => { setSelezioneEntrate({}); setSelezioneUscite({}); setSaldoTempModal(true); }} style={{ padding: '10px 16px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 10, color: '#3b82f6', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
          🧮 Saldo Temporaneo
        </button>
        <button onClick={generaPdfResoconto} style={{ padding: '10px 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, color: '#ef4444', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
          📄 Backup PDF Resoconto
        </button>
      </div>
      
      {/* LINEE ABBONAMENTI - Sezione riepilogo */}
      <section style={{ background: bgCard, borderRadius: 16, border: '1px solid ' + bord, overflow: 'hidden', marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid ' + bord }}>
          <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#8b5cf6' }}>📅 LINEE ABBONAMENTI</h2>
          <span style={{ fontSize: 11, color: '#22c55e', fontWeight: 500 }}>✓ Inclusi nel saldo</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, padding: 16 }}>
          {/* Arretrati (mesi precedenti non pagati) */}
          <div style={{ background: bgInput, borderRadius: 12, padding: 14, borderLeft: '4px solid #ef4444' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <span style={{ fontSize: 18 }}>⚠️</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: muted, textTransform: 'uppercase' }}>Arretrati</span>
            </div>
            <div style={{ fontSize: 10, color: abbTotali?.arretrati > 0 ? '#ef4444' : '#22c55e', marginBottom: 4 }}>
              {abbTotali?.arretrati > 0 ? '⏰ Non pagati' : '✓ Nessuno'}
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: abbTotali?.arretrati > 0 ? '#ef4444' : '#22c55e' }}>{fmtCur(abbTotali?.arretrati || 0)}</div>
          </div>
          
          {/* Linee Mese Corrente (da ricevere) */}
          <div style={{ background: bgInput, borderRadius: 12, padding: 14, borderLeft: '4px solid #f59e0b' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <span style={{ fontSize: 18 }}>📦</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: muted, textTransform: 'uppercase' }}>{meseCorrenteNome}</span>
            </div>
            <div style={{ fontSize: 10, color: '#22c55e', marginBottom: 4 }}>✓ Da ricevere</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#f59e0b' }}>{fmtCur(abbTotali?.daRicevereMeseCorrente || 0)}</div>
            <div style={{ fontSize: 9, color: muted, marginTop: 2 }}>Tot: {fmtCur(abbTotali?.meseCorrente || 0)}</div>
          </div>
          
          {/* Linee Mese Prossimo (da ricevere) */}
          <div style={{ background: bgInput, borderRadius: 12, padding: 14, borderLeft: '4px solid #3b82f6' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <span style={{ fontSize: 18 }}>📦</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: muted, textTransform: 'uppercase' }}>{meseProssimoNome}</span>
            </div>
            <div style={{ fontSize: 10, color: '#22c55e', marginBottom: 4 }}>✓ Da ricevere</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#3b82f6' }}>{fmtCur(abbTotali?.daRicevereMeseProssimo || 0)}</div>
            <div style={{ fontSize: 9, color: muted, marginTop: 2 }}>Tot: {fmtCur(abbTotali?.meseProssimo || 0)}</div>
          </div>
          
          {/* Anticipi (mesi futuri già pagati) */}
          <div style={{ background: bgInput, borderRadius: 12, padding: 14, borderLeft: '4px solid #22c55e' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <span style={{ fontSize: 18 }}>💰</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: muted, textTransform: 'uppercase' }}>Anticipi</span>
            </div>
            <div style={{ fontSize: 10, color: abbTotali?.anticipi > 0 ? '#22c55e' : muted, marginBottom: 4 }}>
              {abbTotali?.anticipi > 0 ? '✓ Già incassati' : 'Nessun anticipo'}
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: abbTotali?.anticipi > 0 ? '#22c55e' : muted }}>{fmtCur(abbTotali?.anticipi || 0)}</div>
          </div>
        </div>
      </section>
      
      <div className="resoconto-grid">
      {/* ENTRATE */}
      <section style={{ background: bgCard, borderRadius: 16, border: '1px solid ' + bord, overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid ' + bord }}>
          <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#22c55e' }}>↓ ENTRATE</h2>
          <span style={{ fontSize: 18, fontWeight: 700, color: '#22c55e' }}>{fmtCur(data.entrate.reduce((s, i) => s + (i.importo || 0), 0))}</span>
        </div>
        <div style={{ padding: 8 }}>
          {(data.categorieEntrate || []).map(cat => {
            const items = data.entrate.filter(i => i.categoria === cat.id);
            if (items.length === 0) return null;
            return (
              <div key={cat.id} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', borderRadius: 8, borderLeft: `3px solid ${cat.color}`, marginBottom: 4 }}>
                  <span style={{ fontSize: 14 }}>{cat.icon}</span>
                  <span style={{ flex: 1, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: muted }}>{cat.nome}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: cat.color }}>{fmtCur(items.reduce((s, i) => s + (i.importo || 0), 0))}</span>
                </div>
                {items.map(item => (
                  <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px' }}>
                    <div style={{ flex: 1 }}><span style={{ display: 'block', fontSize: 13, fontWeight: 500 }}>{item.nome}</span>{item.note && <span style={{ display: 'block', fontSize: 11, color: muted }}>{item.note}</span>}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <button onClick={() => apriSommaModal('entrate', item, false)} style={{ background: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 6, fontSize: 12, cursor: 'pointer', padding: '4px 8px', fontWeight: 700, color: '#22c55e' }} title="Somma un valore">+</button>
                      <button onClick={() => apriSommaModal('entrate', item, true)} style={{ background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, fontSize: 12, cursor: 'pointer', padding: '4px 8px', fontWeight: 700, color: '#ef4444' }} title="Sottrai un valore">−</button>
                      <button onClick={() => setTrasferimentoModal({ tipo: 'transfer', item, section: 'entrate' })} style={{ background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, fontSize: 12, cursor: 'pointer', padding: '4px 8px' }} title="Trasferisci a...">↗️</button>
                      <input type="number" value={item.importo || ''} onChange={e => upd('entrate', item.id, 'importo', parseFloat(e.target.value) || 0)} style={{ width: 100, padding: '6px 10px', background: bgInput, border: '1px solid ' + bord, borderRadius: 6, color: '#22c55e', fontSize: 16, fontWeight: 700, textAlign: 'right', outline: 'none' }} />
                      <button onClick={() => setEditModal({ section: 'entrate', item, categories: data.categorieEntrate })} style={{ background: 'transparent', border: 'none', fontSize: 12, cursor: 'pointer', opacity: 0.5 }}>✏️</button>
                      <button onClick={() => del('entrate', item.id)} style={{ background: 'transparent', border: 'none', fontSize: 16, color: '#ef4444', cursor: 'pointer', opacity: 0.5 }}>×</button>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
          {/* Voci senza categoria */}
          {(() => {
            const catIds = (data.categorieEntrate || []).map(c => c.id);
            const orfane = data.entrate.filter(i => !i.categoria || !catIds.includes(i.categoria));
            if (orfane.length === 0) return null;
            return (
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', borderRadius: 8, borderLeft: '3px solid #64748b', marginBottom: 4 }}>
                  <span style={{ fontSize: 14 }}>📁</span>
                  <span style={{ flex: 1, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: muted }}>Senza categoria</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#64748b' }}>{fmtCur(orfane.reduce((s, i) => s + (i.importo || 0), 0))}</span>
                </div>
                {orfane.map(item => (
                  <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px' }}>
                    <div style={{ flex: 1 }}><span style={{ display: 'block', fontSize: 13, fontWeight: 500 }}>{item.nome}</span>{item.note && <span style={{ display: 'block', fontSize: 11, color: muted }}>{item.note}</span>}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <button onClick={() => apriSommaModal('entrate', item, false)} style={{ background: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 6, fontSize: 12, cursor: 'pointer', padding: '4px 8px', fontWeight: 700, color: '#22c55e' }} title="Somma un valore">+</button>
                      <button onClick={() => apriSommaModal('entrate', item, true)} style={{ background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, fontSize: 12, cursor: 'pointer', padding: '4px 8px', fontWeight: 700, color: '#ef4444' }} title="Sottrai un valore">−</button>
                      <button onClick={() => setTrasferimentoModal({ tipo: 'transfer', item, section: 'entrate' })} style={{ background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, fontSize: 12, cursor: 'pointer', padding: '4px 8px' }} title="Trasferisci a...">↗️</button>
                      <input type="number" value={item.importo || ''} onChange={e => upd('entrate', item.id, 'importo', parseFloat(e.target.value) || 0)} style={{ width: 100, padding: '6px 10px', background: bgInput, border: '1px solid ' + bord, borderRadius: 6, color: '#22c55e', fontSize: 16, fontWeight: 700, textAlign: 'right', outline: 'none' }} />
                      <button onClick={() => setEditModal({ section: 'entrate', item, categories: data.categorieEntrate })} style={{ background: 'transparent', border: 'none', fontSize: 12, cursor: 'pointer', opacity: 0.5 }}>✏️</button>
                      <button onClick={() => del('entrate', item.id)} style={{ background: 'transparent', border: 'none', fontSize: 16, color: '#ef4444', cursor: 'pointer', opacity: 0.5 }}>×</button>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
          <button onClick={() => setAddModal({ section: 'entrate', type: 'categorized', categories: data.categorieEntrate })} style={{ width: '100%', padding: 10, background: 'transparent', border: `1px dashed ${bord}`, borderRadius: 8, color: muted, fontSize: 12, cursor: 'pointer', marginTop: 8 }}>+ Aggiungi voce</button>
        </div>
      </section>

      {/* USCITE */}
      <section style={{ background: bgCard, borderRadius: 16, border: '1px solid ' + bord, overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid ' + bord }}>
          <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#ef4444' }}>↑ USCITE</h2>
          <span style={{ fontSize: 18, fontWeight: 700, color: '#ef4444' }}>{fmtCur(data.uscite.reduce((s, i) => s + (i.importo || 0), 0))}</span>
        </div>
        <div style={{ padding: 8 }}>
          {(data.categorieUscite || []).map(cat => {
            const items = data.uscite.filter(i => i.categoria === cat.id);
            if (items.length === 0) return null;
            return (
              <div key={cat.id} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', borderRadius: 8, borderLeft: `3px solid ${cat.color}`, marginBottom: 4 }}>
                  <span style={{ fontSize: 14 }}>{cat.icon}</span>
                  <span style={{ flex: 1, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: muted }}>{cat.nome}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: cat.color }}>{fmtCur(items.reduce((s, i) => s + (i.importo || 0), 0))}</span>
                </div>
                {items.map(item => (
                  <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px' }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ display: 'block', fontSize: 13, fontWeight: 500 }}>{item.nome}</span>
                      <span style={{ display: 'block', fontSize: 11, color: muted }}>
                        {item.note && `${item.note} • `}
                        {item.importoPredefinito ? `+€${item.importoPredefinito}/click` : ''}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <button onClick={() => apriSommaModal('uscite', item, false)} style={{ background: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 6, fontSize: 12, cursor: 'pointer', padding: '4px 8px', fontWeight: 700, color: '#22c55e' }} title="Somma un valore">+</button>
                      <button onClick={() => apriSommaModal('uscite', item, true)} style={{ background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, fontSize: 12, cursor: 'pointer', padding: '4px 8px', fontWeight: 700, color: '#ef4444' }} title="Sottrai un valore">−</button>
                      <button 
                        onClick={() => applicaImporto(item.id)} 
                        onContextMenu={(e) => { e.preventDefault(); setImportoModal({ id: item.id, nome: item.nome, importoPredefinito: item.importoPredefinito || '', section: 'uscite' }); }}
                        style={{ background: item.importoPredefinito ? 'rgba(139,92,246,0.3)' : 'rgba(139,92,246,0.2)', border: '1px solid rgba(139,92,246,0.3)', borderRadius: 6, fontSize: 12, cursor: 'pointer', padding: '4px 6px', position: 'relative' }} 
                        title={item.importoPredefinito ? `Click: +€${item.importoPredefinito} | Tasto destro: modifica` : 'Click: imposta importo predefinito'}
                      >
                        🔄{item.importoPredefinito ? <span style={{ fontSize: 9, position: 'absolute', top: -4, right: -4, background: '#8b5cf6', color: '#fff', borderRadius: '50%', width: 14, height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</span> : null}
                      </button>
                      <button onClick={() => setTrasferimentoModal({ tipo: 'pay', item, section: 'uscite' })} style={{ background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, fontSize: 12, cursor: 'pointer', padding: '4px 8px' }} title="Paga da...">💸</button>
                      <input type="number" value={item.importo || ''} onChange={e => upd('uscite', item.id, 'importo', parseFloat(e.target.value) || 0)} style={{ width: 100, padding: '6px 10px', background: bgInput, border: '1px solid ' + bord, borderRadius: 6, color: '#ef4444', fontSize: 16, fontWeight: 700, textAlign: 'right', outline: 'none' }} />
                      <button onClick={() => setEditModal({ section: 'uscite', item, categories: data.categorieUscite })} style={{ background: 'transparent', border: 'none', fontSize: 12, cursor: 'pointer', opacity: 0.5 }}>✏️</button>
                      <button onClick={() => del('uscite', item.id)} style={{ background: 'transparent', border: 'none', fontSize: 16, color: '#ef4444', cursor: 'pointer', opacity: 0.5 }}>×</button>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
          {/* Voci senza categoria */}
          {(() => {
            const catIds = (data.categorieUscite || []).map(c => c.id);
            const orfane = data.uscite.filter(i => !i.categoria || !catIds.includes(i.categoria));
            if (orfane.length === 0) return null;
            return (
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', borderRadius: 8, borderLeft: '3px solid #64748b', marginBottom: 4 }}>
                  <span style={{ fontSize: 14 }}>📁</span>
                  <span style={{ flex: 1, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: muted }}>Senza categoria</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#64748b' }}>{fmtCur(orfane.reduce((s, i) => s + (i.importo || 0), 0))}</span>
                </div>
                {orfane.map(item => (
                  <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px' }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ display: 'block', fontSize: 13, fontWeight: 500 }}>{item.nome}</span>
                      <span style={{ display: 'block', fontSize: 11, color: muted }}>
                        {item.note && `${item.note} • `}
                        {item.importoPredefinito ? `+€${item.importoPredefinito}/click` : ''}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <button onClick={() => apriSommaModal('uscite', item, false)} style={{ background: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 6, fontSize: 12, cursor: 'pointer', padding: '4px 8px', fontWeight: 700, color: '#22c55e' }} title="Somma un valore">+</button>
                      <button onClick={() => apriSommaModal('uscite', item, true)} style={{ background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, fontSize: 12, cursor: 'pointer', padding: '4px 8px', fontWeight: 700, color: '#ef4444' }} title="Sottrai un valore">−</button>
                      <button 
                        onClick={() => applicaImporto(item.id)} 
                        onContextMenu={(e) => { e.preventDefault(); setImportoModal({ id: item.id, nome: item.nome, importoPredefinito: item.importoPredefinito || '', section: 'uscite' }); }}
                        style={{ background: item.importoPredefinito ? 'rgba(139,92,246,0.3)' : 'rgba(139,92,246,0.2)', border: '1px solid rgba(139,92,246,0.3)', borderRadius: 6, fontSize: 12, cursor: 'pointer', padding: '4px 6px', position: 'relative' }} 
                        title={item.importoPredefinito ? `Click: +€${item.importoPredefinito} | Tasto destro: modifica` : 'Click: imposta importo predefinito'}
                      >
                        🔄{item.importoPredefinito ? <span style={{ fontSize: 9, position: 'absolute', top: -4, right: -4, background: '#8b5cf6', color: '#fff', borderRadius: '50%', width: 14, height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</span> : null}
                      </button>
                      <button onClick={() => setTrasferimentoModal({ tipo: 'pay', item, section: 'uscite' })} style={{ background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, fontSize: 12, cursor: 'pointer', padding: '4px 8px' }} title="Paga da...">💸</button>
                      <input type="number" value={item.importo || ''} onChange={e => upd('uscite', item.id, 'importo', parseFloat(e.target.value) || 0)} style={{ width: 100, padding: '6px 10px', background: bgInput, border: '1px solid ' + bord, borderRadius: 6, color: '#ef4444', fontSize: 16, fontWeight: 700, textAlign: 'right', outline: 'none' }} />
                      <button onClick={() => setEditModal({ section: 'uscite', item, categories: data.categorieUscite })} style={{ background: 'transparent', border: 'none', fontSize: 12, cursor: 'pointer', opacity: 0.5 }}>✏️</button>
                      <button onClick={() => del('uscite', item.id)} style={{ background: 'transparent', border: 'none', fontSize: 16, color: '#ef4444', cursor: 'pointer', opacity: 0.5 }}>×</button>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
          <button onClick={() => setAddModal({ section: 'uscite', type: 'categorized', categories: data.categorieUscite })} style={{ width: '100%', padding: 10, background: 'transparent', border: `1px dashed ${bord}`, borderRadius: 8, color: muted, fontSize: 12, cursor: 'pointer', marginTop: 8 }}>+ Aggiungi voce</button>
        </div>
      </section>

      {/* Modal Imposta Importo Predefinito */}
      {importoModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setImportoModal(null)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 400, padding: 24 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>🔄 Imposta Importo Predefinito</h3>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: muted, marginBottom: 6 }}>Voce</label>
              <input type="text" value={importoModal.nome} readOnly style={{ width: '100%', padding: '12px 14px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: muted, marginBottom: 6 }}>Importo da sommare ad ogni click (€)</label>
              <input type="number" value={importoModal.importoPredefinito} onChange={e => setImportoModal({ ...importoModal, importoPredefinito: e.target.value })} placeholder="Es: 50" style={{ width: '100%', padding: '12px 14px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} autoFocus />
            </div>
            <p style={{ fontSize: 12, color: muted, marginBottom: 20 }}>💡 Ogni volta che clicchi 🔄, questo importo verrà sommato al totale della voce.</p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setImportoModal(null)} style={{ flex: 1, padding: 12, background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 10, color: muted, cursor: 'pointer' }}>Annulla</button>
              <button onClick={salvaImportoPredefinito} style={{ flex: 1, padding: 12, background: 'linear-gradient(135deg,#8b5cf6,#7c3aed)', border: 'none', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>✓ Salva</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Somma/Sottrai Valore */}
      {sommaModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setSommaModal(null)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 400, padding: 24 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>{sommaModal.sottrai ? '➖ Sottrai Valore' : '➕ Somma Valore'}</h3>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: muted, marginBottom: 6 }}>Voce</label>
              <input type="text" value={sommaModal.nome} readOnly style={{ width: '100%', padding: '12px 14px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: muted, marginBottom: 6 }}>Importo attuale</label>
              <div style={{ padding: '12px 14px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, fontSize: 18, fontWeight: 700, color: sommaModal.section === 'entrate' ? '#22c55e' : '#ef4444' }}>{fmtCur(sommaModal.importoAttuale)}</div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: muted, marginBottom: 6 }}>Valore da {sommaModal.sottrai ? 'sottrarre' : 'sommare'} (€)</label>
              <input type="number" value={sommaModal.valoreDaSommare} onChange={e => setSommaModal({ ...sommaModal, valoreDaSommare: e.target.value })} placeholder="Es: 100" style={{ width: '100%', padding: '12px 14px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 16, boxSizing: 'border-box', outline: 'none' }} autoFocus />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: muted, marginBottom: 6 }}>Descrizione (opzionale - per storico)</label>
              <input 
                type="text" 
                value={sommaModal.descrizione || ''} 
                onChange={e => setSommaModal({ ...sommaModal, descrizione: e.target.value })} 
                placeholder="Es: Pagamento cliente Mario Rossi" 
                style={{ width: '100%', padding: '12px 14px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14, boxSizing: 'border-box', outline: 'none' }} 
              />
              <p style={{ margin: '6px 0 0', fontSize: 11, color: muted }}>💡 Se compili, il movimento verrà registrato nello storico</p>
            </div>
            {sommaModal.valoreDaSommare && (
              <div style={{ padding: 12, background: sommaModal.sottrai ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)', borderRadius: 10, marginBottom: 20, textAlign: 'center' }}>
                <span style={{ fontSize: 12, color: muted }}>Nuovo totale: </span>
                <span style={{ fontSize: 18, fontWeight: 700, color: sommaModal.section === 'entrate' ? '#22c55e' : '#ef4444' }}>{fmtCur(sommaModal.importoAttuale + (sommaModal.sottrai ? -Math.abs(parseFloat(sommaModal.valoreDaSommare) || 0) : (parseFloat(sommaModal.valoreDaSommare) || 0)))}</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setSommaModal(null)} style={{ flex: 1, padding: 12, background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 10, color: muted, cursor: 'pointer' }}>Annulla</button>
              <button onClick={eseguiSomma} style={{ flex: 1, padding: 12, background: sommaModal.sottrai ? 'linear-gradient(135deg,#ef4444,#dc2626)' : 'linear-gradient(135deg,#22c55e,#16a34a)', border: 'none', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>✓ {sommaModal.sottrai ? 'Sottrai' : 'Somma'}</button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal Saldo Temporaneo */}
      {saldoTempModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setSaldoTempModal(false)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 800, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            {/* Header con saldo in tempo reale */}
            {(() => {
              const entrate = data?.entrate || [];
              const uscite = data?.uscite || [];
              let totEntrate = entrate.filter(e => selezioneEntrate[e.id]).reduce((s, e) => s + (e.importo || 0), 0);
              if (selezioneEntrate['abb_mese_corrente']) totEntrate += abbTotali?.daRicevereMeseCorrente || 0;
              if (selezioneEntrate['abb_mese_prossimo']) totEntrate += abbTotali?.daRicevereMeseProssimo || 0;
              const totUscite = uscite.filter(u => selezioneUscite[u.id]).reduce((s, u) => s + (u.importo || 0), 0);
              const saldo = totEntrate - totUscite;
              const numEntrateSelez = entrate.filter(e => selezioneEntrate[e.id]).length + (selezioneEntrate['abb_mese_corrente'] ? 1 : 0) + (selezioneEntrate['abb_mese_prossimo'] ? 1 : 0);
              const numUsciteSelez = uscite.filter(u => selezioneUscite[u.id]).length;
              
              return (
                <div style={{ padding: '16px 24px', background: saldo >= 0 ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', borderBottom: '1px solid ' + bord }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>🧮 Saldo Temporaneo</h3>
                      <p style={{ margin: '4px 0 0', fontSize: 12, color: muted }}>{numEntrateSelez} entrate • {numUsciteSelez} uscite selezionate</p>
                    </div>
                    <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontSize: 11, color: muted }}>Entrate</span>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#22c55e' }}>+€{totEntrate.toFixed(2)}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontSize: 11, color: muted }}>Uscite</span>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#ef4444' }}>-€{totUscite.toFixed(2)}</div>
                      </div>
                      <div style={{ background: saldo >= 0 ? '#22c55e' : '#ef4444', borderRadius: 12, padding: '8px 16px', minWidth: 100, textAlign: 'center' }}>
                        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.8)' }}>SALDO</span>
                        <div style={{ fontSize: 22, fontWeight: 800, color: '#fff' }}>€{saldo.toFixed(2)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
            
            {/* Barra azioni rapide */}
            <div style={{ padding: '12px 24px', borderBottom: '1px solid ' + bord, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button 
                onClick={() => {
                  const entrate = data?.entrate || [];
                  const newSel = {};
                  entrate.forEach(e => { newSel[e.id] = true; });
                  newSel['abb_mese_corrente'] = true;
                  newSel['abb_mese_prossimo'] = true;
                  setSelezioneEntrate(newSel);
                  const uscite = data?.uscite || [];
                  const newSelU = {};
                  uscite.forEach(u => { newSelU[u.id] = true; });
                  setSelezioneUscite(newSelU);
                }}
                style={{ padding: '8px 14px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 8, color: '#3b82f6', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              >
                ✓ Seleziona tutto
              </button>
              <button 
                onClick={() => { setSelezioneEntrate({}); setSelezioneUscite({}); }}
                style={{ padding: '8px 14px', background: 'rgba(100,116,139,0.1)', border: '1px solid rgba(100,116,139,0.3)', borderRadius: 8, color: '#64748b', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              >
                ✗ Deseleziona tutto
              </button>
              <button 
                onClick={() => {
                  const entrate = data?.entrate || [];
                  const newSel = {};
                  entrate.forEach(e => { newSel[e.id] = true; });
                  newSel['abb_mese_corrente'] = true;
                  newSel['abb_mese_prossimo'] = true;
                  setSelezioneEntrate(newSel);
                  setSelezioneUscite({});
                }}
                style={{ padding: '8px 14px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 8, color: '#22c55e', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              >
                📈 Solo entrate
              </button>
              <button 
                onClick={() => {
                  setSelezioneEntrate({});
                  const uscite = data?.uscite || [];
                  const newSelU = {};
                  uscite.forEach(u => { newSelU[u.id] = true; });
                  setSelezioneUscite(newSelU);
                }}
                style={{ padding: '8px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#ef4444', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              >
                📉 Solo uscite
              </button>
            </div>
            
            {/* Contenuto scrollabile */}
            <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                {/* Colonna Entrate */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, padding: '8px 12px', background: 'rgba(34,197,94,0.1)', borderRadius: 8 }}>
                    <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#22c55e' }}>📈 Entrate ({(data?.entrate || []).length + ((abbTotali?.daRicevereMeseCorrente || 0) > 0 ? 1 : 0) + ((abbTotali?.daRicevereMeseProssimo || 0) > 0 ? 1 : 0)})</h4>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#22c55e' }}>
                      €{((data?.entrate || []).reduce((s, e) => s + (e.importo || 0), 0) + (abbTotali?.daRicevereMeseCorrente || 0) + (abbTotali?.daRicevereMeseProssimo || 0)).toFixed(2)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 350, overflow: 'auto' }}>
                    {/* Voce speciale: Abbonamenti mese corrente */}
                    {(abbTotali?.daRicevereMeseCorrente || 0) > 0 && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: selezioneEntrate['abb_mese_corrente'] ? 'rgba(249,115,22,0.15)' : bgInput, border: '2px solid ' + (selezioneEntrate['abb_mese_corrente'] ? '#f97316' : 'transparent'), borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s' }}>
                        <input 
                          type="checkbox" 
                          checked={!!selezioneEntrate['abb_mese_corrente']} 
                          onChange={() => setSelezioneEntrate(prev => ({ ...prev, 'abb_mese_corrente': !prev['abb_mese_corrente'] }))}
                          style={{ width: 20, height: 20, accentColor: '#f97316' }}
                        />
                        <span style={{ fontSize: 18 }}>📅</span>
                        <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>Abb. mese corrente da ricevere</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#f97316' }}>€{(abbTotali?.daRicevereMeseCorrente || 0).toFixed(2)}</span>
                      </label>
                    )}
                    {/* Voce speciale: Abbonamenti mese prossimo */}
                    {(abbTotali?.daRicevereMeseProssimo || 0) > 0 && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: selezioneEntrate['abb_mese_prossimo'] ? 'rgba(139,92,246,0.15)' : bgInput, border: '2px solid ' + (selezioneEntrate['abb_mese_prossimo'] ? '#8b5cf6' : 'transparent'), borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s' }}>
                        <input 
                          type="checkbox" 
                          checked={!!selezioneEntrate['abb_mese_prossimo']} 
                          onChange={() => setSelezioneEntrate(prev => ({ ...prev, 'abb_mese_prossimo': !prev['abb_mese_prossimo'] }))}
                          style={{ width: 20, height: 20, accentColor: '#8b5cf6' }}
                        />
                        <span style={{ fontSize: 18 }}>📆</span>
                        <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>Abb. mese prossimo da ricevere</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#8b5cf6' }}>€{(abbTotali?.daRicevereMeseProssimo || 0).toFixed(2)}</span>
                      </label>
                    )}
                    {(data?.entrate || []).map(e => (
                      <label key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: selezioneEntrate[e.id] ? 'rgba(34,197,94,0.1)' : bgInput, border: '2px solid ' + (selezioneEntrate[e.id] ? '#22c55e' : 'transparent'), borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s' }}>
                        <input 
                          type="checkbox" 
                          checked={!!selezioneEntrate[e.id]} 
                          onChange={() => setSelezioneEntrate(prev => ({ ...prev, [e.id]: !prev[e.id] }))}
                          style={{ width: 20, height: 20, accentColor: '#22c55e' }}
                        />
                        <span style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.nome}</span>
                        <span style={{ fontSize: 14, fontWeight: 600, color: '#22c55e' }}>€{(e.importo || 0).toFixed(2)}</span>
                      </label>
                    ))}
                    {(data?.entrate || []).length === 0 && (
                      <p style={{ textAlign: 'center', color: muted, padding: 20 }}>Nessuna entrata</p>
                    )}
                  </div>
                </div>
                
                {/* Colonna Uscite */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, padding: '8px 12px', background: 'rgba(239,68,68,0.1)', borderRadius: 8 }}>
                    <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#ef4444' }}>📉 Uscite ({(data?.uscite || []).length})</h4>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#ef4444' }}>
                      €{(data?.uscite || []).reduce((s, u) => s + (u.importo || 0), 0).toFixed(2)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 350, overflow: 'auto' }}>
                    {(data?.uscite || []).map(u => (
                      <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: selezioneUscite[u.id] ? 'rgba(239,68,68,0.1)' : bgInput, border: '2px solid ' + (selezioneUscite[u.id] ? '#ef4444' : 'transparent'), borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s' }}>
                        <input 
                          type="checkbox" 
                          checked={!!selezioneUscite[u.id]} 
                          onChange={() => setSelezioneUscite(prev => ({ ...prev, [u.id]: !prev[u.id] }))}
                          style={{ width: 20, height: 20, accentColor: '#ef4444' }}
                        />
                        <span style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.nome}</span>
                        <span style={{ fontSize: 14, fontWeight: 600, color: '#ef4444' }}>€{(u.importo || 0).toFixed(2)}</span>
                      </label>
                    ))}
                    {(data?.uscite || []).length === 0 && (
                      <p style={{ textAlign: 'center', color: muted, padding: 20 }}>Nessuna uscita</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div style={{ padding: '12px 24px', borderTop: '1px solid ' + bord }}>
              <button onClick={() => setSaldoTempModal(false)} style={{ width: '100%', padding: 14, background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 10, color: muted, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Chiudi</button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal Trasferimento/Pagamento - v3 CON CONFERME */}
      {trasferimentoModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setTrasferimentoModal(null)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 450, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid ' + bord, background: trasferimentoModal.tipo === 'transfer' ? 'rgba(59,130,246,0.1)' : 'rgba(239,68,68,0.1)' }}>
              <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 28 }}>{trasferimentoModal.tipo === 'transfer' ? '↗️' : '💸'}</span>
                {trasferimentoModal.tipo === 'transfer' ? 'Trasferisci' : 'Paga Spesa'}
              </h3>
              <p style={{ margin: '8px 0 0', fontSize: 14, color: muted }}>
                {trasferimentoModal.tipo === 'transfer' ? 'Da:' : 'Spesa:'} <strong style={{ color: dark ? '#fff' : '#1e293b' }}>{trasferimentoModal.item.nome}</strong>
              </p>
              <p style={{ margin: '4px 0 0', fontSize: 18, fontWeight: 700, color: trasferimentoModal.tipo === 'transfer' ? '#22c55e' : '#ef4444' }}>
                {trasferimentoModal.tipo === 'transfer' ? 'Saldo' : 'Importo'}: €{(trasferimentoModal.item.importo || 0).toFixed(2)}
              </p>
            </div>
            
            <div style={{ padding: 20 }}>
              {/* Input importo */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: muted, marginBottom: 8 }}>
                  IMPORTO DA {trasferimentoModal.tipo === 'transfer' ? 'TRASFERIRE' : 'PAGARE'}
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1, position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 18, fontWeight: 700, color: trasferimentoModal.tipo === 'transfer' ? '#3b82f6' : '#ef4444' }}>€</span>
                    <input
                      type="number"
                      value={trasferimentoModal.importo || ''}
                      onChange={e => setTrasferimentoModal({ ...trasferimentoModal, importo: e.target.value })}
                      placeholder="0.00"
                      style={{ 
                        width: '100%', 
                        padding: '14px 14px 14px 36px', 
                        background: bgInput, 
                        border: '2px solid ' + (trasferimentoModal.tipo === 'transfer' ? '#3b82f6' : '#ef4444') + '50', 
                        borderRadius: 12, 
                        color: trasferimentoModal.tipo === 'transfer' ? '#3b82f6' : '#ef4444', 
                        fontSize: 20, 
                        fontWeight: 700, 
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setTrasferimentoModal({ ...trasferimentoModal, importo: (trasferimentoModal.item.importo || 0).toString() })}
                    style={{ 
                      padding: '14px 20px', 
                      background: trasferimentoModal.tipo === 'transfer' ? '#3b82f6' : '#ef4444', 
                      border: 'none', 
                      borderRadius: 12, 
                      color: '#fff', 
                      fontWeight: 700, 
                      cursor: 'pointer',
                      fontSize: 13
                    }}
                  >
                    TUTTO
                  </button>
                </div>
              </div>
              
              {/* TRASFERIMENTO: Conti rapidi destinazione */}
              {trasferimentoModal.tipo === 'transfer' && (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: muted, marginBottom: 10 }}>
                      TRASFERISCI A (CONTI RAPIDI)
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                      {CONTI_RAPIDI.filter(c => c.nome !== trasferimentoModal.item.nome).map(conto => {
                        const contoEsistente = data.entrate.find(e => e.nome === conto.nome);
                        return (
                          <button 
                            key={conto.nome}
                            type="button"
                            onClick={() => {
                              const importo = parseFloat(trasferimentoModal.importo) || trasferimentoModal.item.importo || 0;
                              if (importo <= 0) { alert('Inserisci un importo valido'); return; }
                              const nomeCorto = conto.nome.replace('BANCA CARIPARMA ', '').replace(' PC CARE', '');
                              if (!window.confirm(`Confermi trasferimento di €${importo.toFixed(2)} a ${nomeCorto}?`)) return;
                              transferParziale(trasferimentoModal.item.id, conto.nome, importo);
                              setTrasferimentoModal(null);
                            }}
                            style={{ 
                              padding: '14px 8px', 
                              background: dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)', 
                              border: '2px solid ' + bord, 
                              borderRadius: 12, 
                              cursor: 'pointer',
                              textAlign: 'center'
                            }}
                          >
                            <span style={{ display: 'block', fontSize: 22, marginBottom: 4 }}>{conto.icon}</span>
                            <span style={{ display: 'block', fontSize: 9, fontWeight: 600, color: dark ? '#e2e8f0' : '#1e293b', lineHeight: 1.2 }}>
                              {conto.nome.replace('BANCA CARIPARMA ', '').replace(' PC CARE', '')}
                            </span>
                            {contoEsistente && (
                              <span style={{ display: 'block', fontSize: 9, color: '#22c55e', marginTop: 2 }}>
                                €{(contoEsistente.importo || 0).toFixed(0)}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Altri conti (entrate) */}
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: muted, marginBottom: 8 }}>
                      ALTRI CONTI
                    </label>
                    <div style={{ maxHeight: 150, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {data.entrate
                        .filter(e => e.nome !== trasferimentoModal.item.nome && !CONTI_RAPIDI.some(c => c.nome === e.nome))
                        .sort((a, b) => (b.importo || 0) - (a.importo || 0))
                        .map(entrata => (
                          <button 
                            key={entrata.id}
                            type="button"
                            onClick={() => {
                              const importo = parseFloat(trasferimentoModal.importo) || trasferimentoModal.item.importo || 0;
                              if (importo <= 0) { alert('Inserisci un importo valido'); return; }
                              if (!window.confirm(`Confermi trasferimento di €${importo.toFixed(2)} a ${entrata.nome}?`)) return;
                              transferParziale(trasferimentoModal.item.id, entrata.nome, importo);
                              setTrasferimentoModal(null);
                            }}
                            style={{ 
                              padding: '12px 14px', 
                              background: bgInput, 
                              border: '1px solid ' + bord, 
                              borderRadius: 10, 
                              cursor: 'pointer',
                              textAlign: 'left',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span>{entrata.icon || '💰'}</span>
                              <span style={{ fontSize: 13, color: dark ? '#e2e8f0' : '#1e293b' }}>{entrata.nome}</span>
                            </span>
                            <span style={{ fontSize: 13, color: '#22c55e', fontWeight: 600 }}>€{(entrata.importo || 0).toFixed(2)}</span>
                          </button>
                        ))}
                    </div>
                  </div>
                  
                  {/* Tasto Da Incassare */}
                  <div style={{ marginBottom: 16, paddingTop: 16, borderTop: '1px solid ' + bord }}>
                    <button 
                      type="button"
                      onClick={() => {
                        const importo = parseFloat(trasferimentoModal.importo) || trasferimentoModal.item.importo || 0;
                        if (importo <= 0) { alert('Inserisci un importo valido'); return; }
                        if (!window.confirm(`Confermi spostamento di €${importo.toFixed(2)} a "DA INCASSARE"?`)) return;
                        transferParziale(trasferimentoModal.item.id, 'DA INCASSARE', importo);
                        setTrasferimentoModal(null);
                      }}
                      style={{ 
                        width: '100%',
                        padding: '16px', 
                        background: 'linear-gradient(135deg, #f59e0b, #d97706)', 
                        border: 'none', 
                        borderRadius: 12, 
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 10
                      }}
                    >
                      <span style={{ fontSize: 24 }}>📋</span>
                      <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>SPOSTA A "DA INCASSARE"</span>
                    </button>
                  </div>
                </>
              )}
              
              {/* PAGAMENTO: Da quale conto pagare */}
              {trasferimentoModal.tipo === 'pay' && (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: muted, marginBottom: 10 }}>
                      PAGA DA (CONTI RAPIDI)
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                      {CONTI_RAPIDI.map(conto => {
                        const contoEsistente = data.entrate.find(e => e.nome === conto.nome);
                        return (
                          <button 
                            key={conto.nome}
                            type="button"
                            onClick={() => {
                              const importo = parseFloat(trasferimentoModal.importo) || trasferimentoModal.item.importo || 0;
                              if (importo <= 0) { alert('Inserisci un importo valido'); return; }
                              const nomeCorto = conto.nome.replace('BANCA CARIPARMA ', '').replace(' PC CARE', '');
                              if (!window.confirm(`Confermi pagamento di €${importo.toFixed(2)} da ${nomeCorto}?`)) return;
                              payParziale(trasferimentoModal.item.id, conto.nome, importo);
                              setTrasferimentoModal(null);
                            }}
                            style={{ 
                              padding: '14px 8px', 
                              background: dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)', 
                              border: '2px solid ' + bord, 
                              borderRadius: 12, 
                              cursor: 'pointer',
                              textAlign: 'center'
                            }}
                          >
                            <span style={{ display: 'block', fontSize: 22, marginBottom: 4 }}>{conto.icon}</span>
                            <span style={{ display: 'block', fontSize: 9, fontWeight: 600, color: dark ? '#e2e8f0' : '#1e293b', lineHeight: 1.2 }}>
                              {conto.nome.replace('BANCA CARIPARMA ', '').replace(' PC CARE', '')}
                            </span>
                            {contoEsistente && (
                              <span style={{ display: 'block', fontSize: 9, color: '#22c55e', marginTop: 2 }}>
                                €{(contoEsistente.importo || 0).toFixed(0)}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Altri conti da cui pagare */}
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: muted, marginBottom: 8 }}>
                      ALTRI CONTI DA CUI PAGARE
                    </label>
                    <div style={{ maxHeight: 120, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {data.entrate
                        .filter(e => !CONTI_RAPIDI.some(c => c.nome === e.nome))
                        .sort((a, b) => (b.importo || 0) - (a.importo || 0))
                        .map(entrata => (
                          <button 
                            key={entrata.id}
                            type="button"
                            onClick={() => {
                              const importo = parseFloat(trasferimentoModal.importo) || trasferimentoModal.item.importo || 0;
                              if (importo <= 0) { alert('Inserisci un importo valido'); return; }
                              if (!window.confirm(`Confermi pagamento di €${importo.toFixed(2)} da ${entrata.nome}?`)) return;
                              payParziale(trasferimentoModal.item.id, entrata.nome, importo);
                              setTrasferimentoModal(null);
                            }}
                            style={{ 
                              padding: '10px 12px', 
                              background: bgInput, 
                              border: '1px solid ' + bord, 
                              borderRadius: 8, 
                              cursor: 'pointer',
                              textAlign: 'left',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span>{entrata.icon || '💰'}</span>
                              <span style={{ fontSize: 12, color: dark ? '#e2e8f0' : '#1e293b' }}>{entrata.nome}</span>
                            </span>
                            <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 600 }}>€{(entrata.importo || 0).toFixed(2)}</span>
                          </button>
                        ))}
                    </div>
                  </div>
                  
                  {/* Somma ad altre uscite */}
                  <div style={{ marginBottom: 16, paddingTop: 16, borderTop: '1px solid ' + bord }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: muted, marginBottom: 8 }}>
                      OPPURE SOMMA AD ALTRA USCITA
                    </label>
                    <div style={{ maxHeight: 120, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {data.uscite
                        .filter(u => u.id !== trasferimentoModal.item.id)
                        .sort((a, b) => (b.importo || 0) - (a.importo || 0))
                        .map(uscita => (
                          <button 
                            key={uscita.id}
                            type="button"
                            onClick={() => {
                              const importo = parseFloat(trasferimentoModal.importo) || trasferimentoModal.item.importo || 0;
                              if (importo <= 0) { alert('Inserisci un importo valido'); return; }
                              if (!window.confirm(`Confermi spostamento di €${importo.toFixed(2)} verso "${uscita.nome}"?`)) return;
                              // Trasferisci importo da questa uscita a un'altra uscita
                              const d = { ...data };
                              const idxDest = d.uscite.findIndex(x => x.id === uscita.id);
                              const idxOrig = d.uscite.findIndex(x => x.id === trasferimentoModal.item.id);
                              if (idxDest >= 0 && idxOrig >= 0) {
                                const importoEffettivo = Math.min(importo, d.uscite[idxOrig].importo || 0);
                                d.uscite[idxDest] = { ...d.uscite[idxDest], importo: (d.uscite[idxDest].importo || 0) + importoEffettivo };
                                d.uscite[idxOrig] = { ...d.uscite[idxOrig], importo: Math.max(0, (d.uscite[idxOrig].importo || 0) - importoEffettivo) };
                                // Registra movimento
                                const movimento = {
                                  id: Date.now().toString(),
                                  data: new Date().toISOString(),
                                  tipo: 'trasferimento',
                                  descrizione: trasferimentoModal.item.nome + ' → ' + uscita.nome,
                                  importo: importoEffettivo,
                                  metodo: 'Trasferimento tra uscite'
                                };
                                d.movimenti = [...(d.movimenti || []), movimento];
                                save(d);
                              }
                              setTrasferimentoModal(null);
                            }}
                            style={{ 
                              padding: '10px 12px', 
                              background: 'rgba(239,68,68,0.05)', 
                              border: '1px solid rgba(239,68,68,0.2)', 
                              borderRadius: 8, 
                              cursor: 'pointer',
                              textAlign: 'left',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span>{uscita.icon || '💸'}</span>
                              <span style={{ fontSize: 12, color: dark ? '#e2e8f0' : '#1e293b' }}>{uscita.nome}</span>
                            </span>
                            <span style={{ fontSize: 12, color: '#ef4444', fontWeight: 600 }}>€{(uscita.importo || 0).toFixed(2)}</span>
                          </button>
                        ))}
                    </div>
                  </div>
                </>
              )}
              
              {/* Pulsante Annulla */}
              <button 
                type="button"
                onClick={() => setTrasferimentoModal(null)} 
                style={{ 
                  width: '100%', 
                  padding: 14, 
                  background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', 
                  border: 'none', 
                  borderRadius: 12, 
                  color: muted, 
                  fontSize: 14, 
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

function ContrattiTab({ data, del, rinnovaContratto, setAddModal, setEditModal, dark, bgCard, bgInput, bord, muted, upd }) {
  const [messaggioModal, setMessaggioModal] = useState(null);
  const [messaggioTesto, setMessaggioTesto] = useState('');
  const [tipoMessaggio, setTipoMessaggio] = useState('email'); // 'email' o 'whatsapp'

  const DEFAULT_MESSAGGIO = `Gentile Cliente,

La informiamo che il Suo contratto "{DESCRIZIONE}" è in scadenza il {SCADENZA}.

La preghiamo di contattarci per procedere al rinnovo.

Cordiali saluti,
PC Care`;

  const DEFAULT_WHATSAPP = `Gentile Cliente, La informiamo che il Suo contratto "{DESCRIZIONE}" è in scadenza il {SCADENZA}. La preghiamo di contattarci per procedere al rinnovo. Cordiali saluti, PC Care`;

  const apriMessaggioModal = (contratto, tipo) => {
    const template = tipo === 'whatsapp' ? DEFAULT_WHATSAPP : DEFAULT_MESSAGGIO;
    const msg = template
      .replace('{DESCRIZIONE}', contratto.descrizione || '')
      .replace('{SCADENZA}', fmtDate(contratto.scadenza));
    setMessaggioTesto(msg);
    setTipoMessaggio(tipo);
    setMessaggioModal(contratto);
  };

  const inviaMessaggio = () => {
    if (!messaggioModal) return;
    
    if (tipoMessaggio === 'email') {
      if (!messaggioModal.email) {
        alert('Email cliente non presente. Modifica il contratto per aggiungere l\'email.');
        return;
      }
      const oggetto = encodeURIComponent(`Scadenza contratto: ${messaggioModal.descrizione}`);
      const corpo = encodeURIComponent(messaggioTesto);
      window.open(`mailto:${messaggioModal.email}?subject=${oggetto}&body=${corpo}`, '_blank');
    } else {
      if (!messaggioModal.telefono) {
        alert('Telefono cliente non presente. Modifica il contratto per aggiungere il telefono.');
        return;
      }
      // Pulisci il numero di telefono (rimuovi spazi, trattini, etc.)
      let telefono = messaggioModal.telefono.replace(/[\s\-\(\)]/g, '');
      // Se inizia con 0, sostituisci con +39 (Italia)
      if (telefono.startsWith('0')) {
        telefono = '+39' + telefono.substring(1);
      }
      // Se non inizia con +, aggiungi +39
      if (!telefono.startsWith('+')) {
        telefono = '+39' + telefono;
      }
      const testo = encodeURIComponent(messaggioTesto);
      window.open(`https://wa.me/${telefono.replace('+', '')}?text=${testo}`, '_blank');
    }
    setMessaggioModal(null);
  };

  return (
    <section style={{ background: bgCard, borderRadius: 16, border: '1px solid ' + bord, padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>📑 CONTRATTI</h2>
        <span style={{ color: muted }}>{(data.contratti || []).length} contratti</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: 12, marginBottom: 20 }}>
        <div style={{ background: bgInput, borderRadius: 12, padding: 16, textAlign: 'center' }}><span style={{ display: 'block', fontSize: 24 }}>🔴</span><span style={{ display: 'block', fontSize: 11, color: muted }}>Scaduti</span><span style={{ display: 'block', fontSize: 18, fontWeight: 700 }}>{(data.contratti || []).filter(c => new Date(c.scadenza) < new Date()).length}</span></div>
        <div style={{ background: bgInput, borderRadius: 12, padding: 16, textAlign: 'center' }}><span style={{ display: 'block', fontSize: 24 }}>🟡</span><span style={{ display: 'block', fontSize: 11, color: muted }}>30gg</span><span style={{ display: 'block', fontSize: 18, fontWeight: 700 }}>{(data.contratti || []).filter(c => { const s = new Date(c.scadenza); const o = new Date(); const f = new Date(); f.setDate(f.getDate() + 30); return s >= o && s <= f; }).length}</span></div>
        <div style={{ background: bgInput, borderRadius: 12, padding: 16, textAlign: 'center' }}><span style={{ display: 'block', fontSize: 24 }}>🟢</span><span style={{ display: 'block', fontSize: 11, color: muted }}>Attivi</span><span style={{ display: 'block', fontSize: 18, fontWeight: 700 }}>{(data.contratti || []).filter(c => new Date(c.scadenza) > new Date()).length}</span></div>
        <div style={{ background: bgInput, borderRadius: 12, padding: 16, textAlign: 'center' }}><span style={{ display: 'block', fontSize: 24 }}>💰</span><span style={{ display: 'block', fontSize: 11, color: muted }}>Totale</span><span style={{ display: 'block', fontSize: 18, fontWeight: 700 }}>{fmtCur((data.contratti || []).reduce((s, c) => s + (c.costo || 0), 0))}</span></div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {(data.contratti || []).length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: muted }}>Nessun contratto</div> : (data.contratti || []).sort((a, b) => new Date(a.scadenza) - new Date(b.scadenza)).map(c => {
          const diff = Math.ceil((new Date(c.scadenza) - new Date()) / (1000 * 60 * 60 * 24));
          const col = diff < 0 ? '#ef4444' : diff <= 30 ? '#f59e0b' : '#22c55e';
          return (
            <div key={c.id} style={{ background: bgInput, borderRadius: 12, padding: 16, borderLeft: `4px solid ${col}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{c.cliente}</h3>
                  <p style={{ margin: '4px 0 0', fontSize: 13, color: muted }}>{c.descrizione}</p>
                  {c.email && <p style={{ margin: '2px 0 0', fontSize: 11, color: '#3b82f6' }}>✉️ {c.email}</p>}
                  {c.telefono && <p style={{ margin: '2px 0 0', fontSize: 11, color: '#25D366' }}>📱 {c.telefono}</p>}
                </div>
                <div style={{ textAlign: 'right' }}><span style={{ display: 'block', fontSize: 12, fontWeight: 500, color: col }}>{diff < 0 ? `Scaduto ${Math.abs(diff)}gg` : `${diff}gg`}</span><span style={{ display: 'block', fontSize: 18, fontWeight: 700, color: '#22c55e' }}>{fmtCur(c.costo)}</span></div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12, borderTop: '1px solid ' + bord }}>
                <span style={{ fontSize: 13, color: muted }}>📅 {fmtDate(c.scadenza)}</span>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button onClick={() => apriMessaggioModal(c, 'email')} style={{ background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, color: '#3b82f6', fontSize: 11, padding: '6px 10px', cursor: 'pointer' }} title="Invia email">✉️</button>
                  <button onClick={() => apriMessaggioModal(c, 'whatsapp')} style={{ background: 'rgba(37,211,102,0.2)', border: '1px solid rgba(37,211,102,0.3)', borderRadius: 6, color: '#25D366', fontSize: 11, padding: '6px 10px', cursor: 'pointer' }} title="Invia WhatsApp">💬</button>
                  
                  {/* Download Documento */}
                  {c.linkDocumento && (
                    <button onClick={() => window.open(c.linkDocumento, '_blank')} style={{ background: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 6, color: '#22c55e', fontSize: 11, padding: '6px 10px', cursor: 'pointer' }} title="Apri documento">📄</button>
                  )}
                  
                  <button onClick={() => rinnovaContratto(c)} style={{ background: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 6, color: '#22c55e', fontSize: 11, padding: '6px 10px', cursor: 'pointer' }} title="Rinnova">🔄</button>
                  <button onClick={() => { const t = encodeURIComponent(`Scadenza: ${c.cliente}`); const d = c.scadenza.replace(/-/g, ''); window.open(`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${t}&dates=${d}/${d}`, '_blank'); }} style={{ background: 'rgba(66,133,244,0.2)', border: '1px solid rgba(66,133,244,0.3)', borderRadius: 6, color: '#4285f4', fontSize: 11, padding: '6px 10px', cursor: 'pointer' }} title="Aggiungi a Calendar">📆</button>
                  <button onClick={() => setEditModal({ section: 'contratti', item: c })} style={{ background: 'transparent', border: 'none', fontSize: 12, cursor: 'pointer', opacity: 0.5 }} title="Modifica">✏️</button>
                  <button onClick={() => del('contratti', c.id)} style={{ background: 'transparent', border: 'none', fontSize: 16, color: '#ef4444', cursor: 'pointer', opacity: 0.5 }} title="Elimina">×</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <button onClick={() => setAddModal({ section: 'contratti', type: 'contratto' })} style={{ display: 'block', width: '100%', padding: 14, background: 'linear-gradient(135deg,#22c55e,#16a34a)', border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 16 }}>+ Nuovo Contratto</button>

      {/* Modal Messaggio (Email/WhatsApp) */}
      {messaggioModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setMessaggioModal(null)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 500, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '20px', borderBottom: '1px solid ' + bord }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{tipoMessaggio === 'email' ? '✉️ Invia Email' : '💬 Invia WhatsApp'}</h3>
              <p style={{ margin: '8px 0 0', fontSize: 13, color: muted }}>
                A: {tipoMessaggio === 'email' 
                  ? (messaggioModal.email || <span style={{ color: '#ef4444' }}>Email non presente!</span>)
                  : (messaggioModal.telefono || <span style={{ color: '#ef4444' }}>Telefono non presente!</span>)
                }
              </p>
            </div>
            <div style={{ padding: 20 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: muted, marginBottom: 8 }}>Messaggio</label>
              <textarea 
                value={messaggioTesto} 
                onChange={e => setMessaggioTesto(e.target.value)} 
                style={{ width: '100%', minHeight: 200, padding: 14, background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14, resize: 'vertical', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} 
              />
              <p style={{ fontSize: 11, color: muted, marginTop: 8 }}>💡 Puoi modificare il messaggio prima di inviare</p>
            </div>
            <div style={{ display: 'flex', gap: 12, padding: 20, borderTop: '1px solid ' + bord }}>
              <button onClick={() => setMessaggioModal(null)} style={{ flex: 1, padding: 12, background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 10, color: muted, cursor: 'pointer' }}>Annulla</button>
              <button onClick={inviaMessaggio} style={{ flex: 1, padding: 12, background: tipoMessaggio === 'email' ? 'linear-gradient(135deg,#3b82f6,#2563eb)' : 'linear-gradient(135deg,#25D366,#128C7E)', border: 'none', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
                {tipoMessaggio === 'email' ? '📧 Apri Client Email' : '💬 Apri WhatsApp'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function RicorrenzeTab({ data, del, applicaRicorrenze, setAddModal, dark, bgCard, bgInput, bord, muted, save }) {
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ nome: '', tipo: 'uscita', importo: '', giorno: 1 });
  
  const oggi = new Date();
  const giornoOggi = oggi.getDate();
  const meseOggi = oggi.getMonth();
  const annoOggi = oggi.getFullYear();
  const chiaveOggi = `${annoOggi}-${meseOggi}-${giornoOggi}`;
  
  const ricorrenze = data.ricorrenze || [];
  const ricorrenzeOggi = ricorrenze.filter(r => r.giorno === giornoOggi);
  const tutteApplicate = ricorrenzeOggi.length > 0 && ricorrenzeOggi.every(r => r.ultimaApplicazione === chiaveOggi);
  
  // Calcola totali
  const totaleEntrate = ricorrenze.filter(r => r.tipo === 'entrata').reduce((sum, r) => sum + (r.importo || 0), 0);
  const totaleUscite = ricorrenze.filter(r => r.tipo === 'uscita').reduce((sum, r) => sum + (r.importo || 0), 0);
  const saldo = totaleEntrate - totaleUscite;
  
  const startEdit = (r) => {
    setEditingId(r.id);
    setEditForm({ nome: r.nome, tipo: r.tipo, importo: r.importo, giorno: r.giorno });
  };
  
  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ nome: '', tipo: 'uscita', importo: '', giorno: 1 });
  };
  
  const saveEdit = () => {
    if (!editForm.nome || !editForm.importo) {
      alert('Compila tutti i campi');
      return;
    }
    const updatedRicorrenze = ricorrenze.map(r => 
      r.id === editingId 
        ? { ...r, nome: editForm.nome, tipo: editForm.tipo, importo: parseFloat(editForm.importo) || 0, giorno: parseInt(editForm.giorno) || 1 }
        : r
    );
    save({ ...data, ricorrenze: updatedRicorrenze });
    cancelEdit();
  };
  
  return (
    <section style={{ background: bgCard, borderRadius: 16, border: '1px solid ' + bord, padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>🔄 RICORRENZE MENSILI</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {ricorrenzeOggi.length > 0 && (
            <span style={{ 
              fontSize: 11, 
              padding: '4px 10px', 
              borderRadius: 20, 
              background: tutteApplicate ? 'rgba(34,197,94,0.2)' : 'rgba(245,158,11,0.2)',
              color: tutteApplicate ? '#22c55e' : '#f59e0b'
            }}>
              {tutteApplicate ? '✓ Oggi applicate' : `${ricorrenzeOggi.length} da applicare oggi`}
            </span>
          )}
          <button onClick={applicaRicorrenze} style={{ padding: '8px 16px', background: 'linear-gradient(135deg,#3b82f6,#2563eb)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>▶️ Applica Oggi</button>
        </div>
      </div>
      
      {/* Totali */}
      {ricorrenze.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
          <div style={{ background: 'rgba(34,197,94,0.1)', borderRadius: 10, padding: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: muted, marginBottom: 4 }}>Entrate Mensili</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#22c55e' }}>{fmtCur(totaleEntrate)}</div>
          </div>
          <div style={{ background: 'rgba(239,68,68,0.1)', borderRadius: 10, padding: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: muted, marginBottom: 4 }}>Uscite Mensili</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#ef4444' }}>{fmtCur(totaleUscite)}</div>
          </div>
          <div style={{ background: saldo >= 0 ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', borderRadius: 10, padding: 12, textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: muted, marginBottom: 4 }}>Saldo Mensile</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: saldo >= 0 ? '#22c55e' : '#ef4444' }}>{fmtCur(saldo)}</div>
          </div>
        </div>
      )}
      
      <p style={{ fontSize: 12, color: muted, marginBottom: 16 }}>Le ricorrenze si applicano automaticamente nel giorno specificato (al login). Clicca ✏️ per modificare.</p>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {ricorrenze.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: muted }}>Nessuna ricorrenza</div>
        ) : ricorrenze.map(r => {
          const isOggi = r.giorno === giornoOggi;
          const applicataOggi = r.ultimaApplicazione === chiaveOggi;
          const isEditing = editingId === r.id;
          
          if (isEditing) {
            return (
              <div key={r.id} style={{ 
                background: dark ? 'rgba(59,130,246,0.1)' : 'rgba(59,130,246,0.05)', 
                borderRadius: 10, 
                padding: 16, 
                border: '2px solid #3b82f6'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: muted, marginBottom: 4 }}>Voce</label>
                    <input 
                      type="text" 
                      value={editForm.nome} 
                      onChange={e => setEditForm({ ...editForm, nome: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', background: bgInput, border: '1px solid ' + bord, borderRadius: 8, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14, boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: muted, marginBottom: 4 }}>Tipo</label>
                    <select 
                      value={editForm.tipo} 
                      onChange={e => setEditForm({ ...editForm, tipo: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', background: bgInput, border: '1px solid ' + bord, borderRadius: 8, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14 }}
                    >
                      <option value="entrata">↓ Entrata</option>
                      <option value="uscita">↑ Uscita</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: muted, marginBottom: 4 }}>Importo (€)</label>
                    <input 
                      type="number" 
                      value={editForm.importo} 
                      onChange={e => setEditForm({ ...editForm, importo: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', background: bgInput, border: '1px solid ' + bord, borderRadius: 8, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14, boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: muted, marginBottom: 4 }}>Giorno del mese (1-31)</label>
                    <input 
                      type="number" 
                      min="1" 
                      max="31" 
                      value={editForm.giorno} 
                      onChange={e => setEditForm({ ...editForm, giorno: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', background: bgInput, border: '1px solid ' + bord, borderRadius: 8, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14, boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button onClick={cancelEdit} style={{ padding: '8px 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#ef4444', fontSize: 12, cursor: 'pointer' }}>Annulla</button>
                  <button onClick={saveEdit} style={{ padding: '8px 16px', background: 'linear-gradient(135deg,#22c55e,#16a34a)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>💾 Salva</button>
                </div>
              </div>
            );
          }
          
          return (
            <div key={r.id} style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              background: isOggi ? (applicataOggi ? 'rgba(34,197,94,0.1)' : 'rgba(245,158,11,0.1)') : bgInput, 
              borderRadius: 10, 
              padding: '12px 16px', 
              borderLeft: `4px solid ${r.tipo === 'entrata' ? '#22c55e' : '#ef4444'}` 
            }}>
              <div>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{r.nome}</span>
                  {isOggi && (
                    <span style={{ 
                      fontSize: 9, 
                      padding: '2px 6px', 
                      borderRadius: 4, 
                      background: applicataOggi ? '#22c55e' : '#f59e0b',
                      color: '#fff'
                    }}>
                      {applicataOggi ? '✓ APPLICATA' : 'OGGI'}
                    </span>
                  )}
                </span>
                <span style={{ display: 'block', fontSize: 11, color: muted }}>{r.tipo === 'entrata' ? '↓ Entrata' : '↑ Uscita'} • Giorno {r.giorno}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: r.tipo === 'entrata' ? '#22c55e' : '#ef4444' }}>{fmtCur(r.importo)}</span>
                <button onClick={() => startEdit(r)} style={{ background: 'rgba(59,130,246,0.1)', border: 'none', borderRadius: 6, padding: '6px 8px', fontSize: 12, cursor: 'pointer' }} title="Modifica">✏️</button>
                <button onClick={() => del('ricorrenze', r.id)} style={{ background: 'rgba(239,68,68,0.1)', border: 'none', borderRadius: 6, padding: '6px 8px', fontSize: 14, color: '#ef4444', cursor: 'pointer' }} title="Elimina">×</button>
              </div>
            </div>
          );
        })}
      </div>
      <button onClick={() => setAddModal({ section: 'ricorrenze', type: 'ricorrenza' })} style={{ display: 'block', width: '100%', padding: 14, background: 'linear-gradient(135deg,#22c55e,#16a34a)', border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 16 }}>+ Nuova Ricorrenza</button>
    </section>
  );
}

function StoricoTab({ data, save, del, delAll, dark, bgCard, bgInput, bord, muted }) {
  const [filtroMese, setFiltroMese] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [mesiAperti, setMesiAperti] = useState({});
  
  const nomiMesi = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
  
  // Tipi di movimento con icone e colori
  const tipiMovimento = {
    entrata: { icon: '↓', color: '#22c55e', label: 'Entrata' },
    uscita: { icon: '↑', color: '#ef4444', label: 'Uscita' },
    trasferimento: { icon: '↔', color: '#3b82f6', label: 'Trasferimento' },
    abbonamento: { icon: '📅', color: '#8b5cf6', label: 'Abbonamento' },
    riparazione: { icon: '🔧', color: '#f59e0b', label: 'Riparazione' },
    // FIX: senza questa voce gli storni venivano disegnati con icona e colore
    // di un'entrata, pur avendo importo negativo
    storno: { icon: '↩', color: '#f97316', label: 'Storno' }
  };
  
  // Ottieni tutti i movimenti ordinati per data (più recente prima)
  const movimenti = (data.movimenti || [])
    .filter(m => !filtroTipo || m.tipo === filtroTipo)
    .sort((a, b) => new Date(b.data) - new Date(a.data));
  
  // Raggruppa movimenti per mese/anno
  const movimentiPerMese = movimenti.reduce((acc, m) => {
    const d = new Date(m.data);
    const chiave = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, '0')}`;
    const label = `${nomiMesi[d.getMonth()]} ${d.getFullYear()}`;
    if (!acc[chiave]) acc[chiave] = { label, chiave, movimenti: [], totEntrate: 0, totUscite: 0 };
    acc[chiave].movimenti.push(m);
    if (m.tipo === 'storno') {
      // FIX: lo storno riduce le entrate del mese, non è né entrata né uscita
      acc[chiave].totEntrate -= Math.abs(m.importo || 0);
    } else if (m.tipo === 'entrata' || (m.tipo === 'abbonamento') || (m.tipo === 'riparazione' && m.importo > 0)) {
      acc[chiave].totEntrate += Math.abs(m.importo || 0);
    } else if (m.tipo === 'uscita') {
      acc[chiave].totUscite += Math.abs(m.importo || 0);
    }
    return acc;
  }, {});
  
  // Ordina mesi (più recente prima)
  const mesiOrdinati = Object.values(movimentiPerMese)
    .filter(m => !filtroMese || m.chiave === filtroMese)
    .sort((a, b) => b.chiave.localeCompare(a.chiave));
  
  // Lista mesi per filtro
  const mesiDisponibili = Object.values(movimentiPerMese).sort((a, b) => b.chiave.localeCompare(a.chiave));
  
  // Toggle mese aperto/chiuso
  const toggleMese = (chiave) => {
    setMesiAperti(prev => ({ ...prev, [chiave]: !prev[chiave] }));
  };
  
  // Formatta data/ora
  const formatDataOra = (dataISO) => {
    const d = new Date(dataISO);
    return {
      data: d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }),
      ora: d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
    };
  };
  
  // Elimina movimento
  const eliminaMovimento = (id) => {
    if (window.confirm('Eliminare questo movimento dallo storico?')) {
      // FIX: passa da del(), che cancella anche dal database
      del('movimenti', id);
    }
  };

  // Svuota storico
  const svuotaStorico = () => {
    if (window.confirm('Eliminare TUTTI i movimenti dallo storico? Questa azione è irreversibile.')) {
      // FIX: passa da delAll(), che svuota anche la tabella su Supabase. Prima
      // azzerava solo l'array locale e i movimenti tornavano al primo sync.
      delAll('movimenti');
    }
  };

  // Apri mese corrente di default
  const oggi = new Date();
  const meseCorrente = `${oggi.getFullYear()}-${String(oggi.getMonth()).padStart(2, '0')}`;
  if (mesiAperti[meseCorrente] === undefined && movimentiPerMese[meseCorrente]) {
    setMesiAperti(prev => ({ ...prev, [meseCorrente]: true }));
  }

  return (
    <section style={{ background: bgCard, borderRadius: 16, border: '1px solid ' + bord, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid ' + bord, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>📜 REGISTRO MOVIMENTI</h2>
          <p style={{ margin: '4px 0 0', fontSize: 11, color: muted }}>{movimenti.length} movimenti registrati</p>
        </div>
        <button onClick={svuotaStorico} style={{ padding: '6px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#ef4444', fontSize: 11, cursor: 'pointer' }}>🗑️ Svuota</button>
      </div>
      
      {/* Filtri */}
      <div style={{ padding: '12px 20px', borderBottom: '1px solid ' + bord, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', background: dark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
        <select 
          value={filtroMese} 
          onChange={e => setFiltroMese(e.target.value)}
          style={{ padding: '6px 12px', background: bgInput, border: '1px solid ' + bord, borderRadius: 6, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 12 }}
        >
          <option value="">Tutti i mesi</option>
          {mesiDisponibili.map(m => (
            <option key={m.chiave} value={m.chiave}>{m.label} ({m.movimenti.length})</option>
          ))}
        </select>
        
        <select 
          value={filtroTipo} 
          onChange={e => setFiltroTipo(e.target.value)}
          style={{ padding: '6px 12px', background: bgInput, border: '1px solid ' + bord, borderRadius: 6, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 12 }}
        >
          <option value="">Tutti i tipi</option>
          {Object.entries(tipiMovimento).map(([k, v]) => (
            <option key={k} value={k}>{v.icon} {v.label}</option>
          ))}
        </select>
        
        {(filtroMese || filtroTipo) && (
          <button onClick={() => { setFiltroMese(''); setFiltroTipo(''); }} style={{ padding: '4px 10px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#ef4444', fontSize: 11, cursor: 'pointer' }}>✕ Reset</button>
        )}
      </div>
      
      {/* Lista movimenti per mese */}
      <div style={{ padding: 16 }}>
        {mesiOrdinati.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: muted }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
            <div>Nessun movimento registrato</div>
            <div style={{ fontSize: 11, marginTop: 8 }}>I movimenti verranno registrati automaticamente</div>
          </div>
        ) : mesiOrdinati.map(meseData => {
          const isAperto = mesiAperti[meseData.chiave] !== false;
          const saldo = meseData.totEntrate - meseData.totUscite;
          
          return (
            <div key={meseData.chiave} style={{ marginBottom: 12, background: bgInput, borderRadius: 12, overflow: 'hidden' }}>
              {/* Header mese */}
              <div 
                onClick={() => toggleMese(meseData.chiave)} 
                style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: isAperto ? '1px solid ' + bord : 'none' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 16 }}>{isAperto ? '▼' : '▶'}</span>
                  <span style={{ fontWeight: 700, fontSize: 14 }}>{meseData.label}</span>
                  <span style={{ fontSize: 11, color: muted, background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', padding: '2px 8px', borderRadius: 10 }}>{meseData.movimenti.length}</span>
                </div>
                <div style={{ display: 'flex', gap: 16, fontSize: 12 }}>
                  <span style={{ color: '#22c55e', fontWeight: 600 }}>+€{meseData.totEntrate.toFixed(2)}</span>
                  <span style={{ color: '#ef4444', fontWeight: 600 }}>-€{meseData.totUscite.toFixed(2)}</span>
                  <span style={{ color: saldo >= 0 ? '#22c55e' : '#ef4444', fontWeight: 700 }}>=€{saldo.toFixed(2)}</span>
                </div>
              </div>
              
              {/* Lista movimenti del mese */}
              {isAperto && (
                <div style={{ maxHeight: 400, overflow: 'auto' }}>
                  {meseData.movimenti.map(m => {
                    const tipo = tipiMovimento[m.tipo] || tipiMovimento.entrata;
                    const { data: dataStr, ora } = formatDataOra(m.data);
                    const isPositivo = m.tipo === 'entrata' || m.tipo === 'abbonamento' || (m.tipo === 'riparazione' && m.importo > 0);
                    
                    return (
                      <div key={m.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid ' + (dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'), gap: 12 }}>
                        {/* Icona tipo */}
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: tipo.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: tipo.color, flexShrink: 0 }}>
                          {tipo.icon}
                        </div>
                        
                        {/* Data/Ora */}
                        <div style={{ minWidth: 55, textAlign: 'center', flexShrink: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600 }}>{dataStr}</div>
                          <div style={{ fontSize: 10, color: muted }}>{ora}</div>
                        </div>
                        
                        {/* Descrizione */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.descrizione}</div>
                          {m.metodo && <div style={{ fontSize: 11, color: muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.metodo}</div>}
                        </div>
                        
                        {/* Importo */}
                        <div style={{ fontWeight: 700, fontSize: 14, color: isPositivo ? '#22c55e' : '#ef4444', flexShrink: 0 }}>
                          {isPositivo ? '+' : '-'}€{Math.abs(m.importo || 0).toFixed(2)}
                        </div>
                        
                        {/* Elimina */}
                        <button onClick={() => eliminaMovimento(m.id)} style={{ background: 'transparent', border: 'none', fontSize: 14, color: '#ef4444', cursor: 'pointer', opacity: 0.4, flexShrink: 0 }}>×</button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// Modal per aggiunta rapida entrate/uscite
function QuickAddModal({ tipo, vociEntrate, vociUscite, categorieEntrate, categorieUscite, onAdd, onClose, dark, bgCard, bgInput, bord, muted }) {
  const [selectedVoce, setSelectedVoce] = useState('');
  const [importo, setImporto] = useState('');
  const [descrizione, setDescrizione] = useState(''); // NUOVO: descrizione per storico
  const [searchVoce, setSearchVoce] = useState('');
  const [showAllVoci, setShowAllVoci] = useState(false);
  // Per le uscite: tab per scegliere se sottrarre da entrate o sommare a uscite
  const [subTipo, setSubTipo] = useState('entrate'); // 'entrate' = sottrai da conto, 'uscite' = somma a uscita
  
  const isEntrata = tipo === 'entrate';
  const isUscitaDaEntrate = !isEntrata && subTipo === 'entrate';
  const isUscitaVera = !isEntrata && subTipo === 'uscite';
  
  const color = isEntrata ? '#22c55e' : '#ef4444';
  const icon = isEntrata ? '+' : '−';
  const titolo = isEntrata ? 'Aggiungi Entrata' : (isUscitaDaEntrate ? 'Preleva da Conto' : 'Registra Uscita');
  const descrizioneHeader = isEntrata 
    ? 'Seleziona conto e importo da aggiungere' 
    : (isUscitaDaEntrate ? 'Sottrai importo da un conto' : 'Somma importo a una voce uscite');
  
  // Voci e categorie in base alla selezione
  const voci = (isEntrata || isUscitaDaEntrate) ? vociEntrate : vociUscite;
  const categorie = (isEntrata || isUscitaDaEntrate) ? categorieEntrate : categorieUscite;
  
  // Voci rapide predefinite (cerca per nome) - solo per entrate
  const vociRapideDef = [
    { nome: 'BANCA CARIPARMA PC CARE', icon: '🏦' },
    { nome: 'CASSA NEGOZIO', icon: '💵' },
    { nome: 'POSTEPAY', icon: '💳' },
    { nome: 'PAYPAL', icon: '🅿️' },
    { nome: 'REVOLUT', icon: '💜' },
    { nome: 'BUNQ', icon: '🟢' },
  ];
  
  // Trova le voci rapide tra quelle disponibili (solo per entrate)
  const vociRapide = (isEntrata || isUscitaDaEntrate) ? vociRapideDef.map(er => {
    const voce = voci.find(v => v.nome?.toUpperCase().includes(er.nome.split(' ')[0]));
    return voce ? { ...voce, iconRapida: er.icon } : null;
  }).filter(Boolean) : [];
  
  // Raggruppa voci per categoria
  const vociPerCategoria = {};
  (categorie || []).forEach(cat => {
    const items = voci.filter(v => v.categoria === cat.id);
    if (items.length > 0) {
      vociPerCategoria[cat.id] = { ...cat, items };
    }
  });
  
  // Voci senza categoria
  const catIds = (categorie || []).map(c => c.id);
  const vociOrfane = voci.filter(v => !v.categoria || !catIds.includes(v.categoria));
  
  // Filtra per ricerca
  const matchSearch = (voce) => {
    if (!searchVoce) return true;
    return voce.nome?.toLowerCase().includes(searchVoce.toLowerCase());
  };
  
  // Reset selezione quando cambia subTipo
  const cambiaSubTipo = (nuovoTipo) => {
    setSubTipo(nuovoTipo);
    setSelectedVoce('');
    setSearchVoce('');
  };
  
  const conferma = () => {
    if (!selectedVoce || !importo) {
      alert('Seleziona una voce e inserisci un importo');
      return;
    }
    
    // Trova il nome della voce selezionata
    const voceSelezionata = voci.find(v => v.id === selectedVoce);
    const nomeVoce = voceSelezionata?.nome || 'voce';
    const importoNum = parseFloat(importo) || 0;
    
    // Messaggio di conferma
    let messaggio;
    if (isEntrata) {
      messaggio = `Confermi aggiunta di €${importoNum.toFixed(2)} a "${nomeVoce}"?`;
    } else if (isUscitaDaEntrate) {
      messaggio = `Confermi prelievo di €${importoNum.toFixed(2)} da "${nomeVoce}"?`;
    } else {
      messaggio = `Confermi registrazione spesa di €${importoNum.toFixed(2)} su "${nomeVoce}"?`;
    }
    
    if (!window.confirm(messaggio)) return;
    
    // Passa info su quale sezione aggiornare + descrizione
    onAdd(selectedVoce, importoNum, isUscitaVera ? 'uscite' : 'entrate', isUscitaDaEntrate, descrizione.trim());
  };
  
  // Click su voce rapida
  const clickVoceRapida = (voceId) => {
    if (selectedVoce === voceId && importo) {
      // Secondo click = conferma
      conferma();
    } else {
      setSelectedVoce(voceId);
    }
  };
  
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={onClose}>
      <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 450, maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <div style={{ padding: '20px', borderBottom: '1px solid ' + bord, background: 'linear-gradient(135deg, ' + color + '20, transparent)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 32, width: 50, height: 50, borderRadius: '50%', background: color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{icon}</span>
            <div>
              <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{titolo}</h3>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: muted }}>{descrizioneHeader}</p>
            </div>
          </div>
        </div>
        
        {/* Tab per uscite: scegli se sottrarre da entrate o sommare a uscite */}
        {!isEntrata && (
          <div style={{ display: 'flex', gap: 8, padding: '12px 20px', borderBottom: '1px solid ' + bord, background: dark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.03)' }}>
            <button
              onClick={() => cambiaSubTipo('entrate')}
              style={{
                flex: 1,
                padding: '10px 12px',
                background: subTipo === 'entrate' ? '#ef4444' : 'transparent',
                border: '1px solid ' + (subTipo === 'entrate' ? '#ef4444' : bord),
                borderRadius: 8,
                color: subTipo === 'entrate' ? '#fff' : muted,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              💵 Preleva da Conto
            </button>
            <button
              onClick={() => cambiaSubTipo('uscite')}
              style={{
                flex: 1,
                padding: '10px 12px',
                background: subTipo === 'uscite' ? '#f59e0b' : 'transparent',
                border: '1px solid ' + (subTipo === 'uscite' ? '#f59e0b' : bord),
                borderRadius: 8,
                color: subTipo === 'uscite' ? '#fff' : muted,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              📝 Registra Spesa
            </button>
          </div>
        )}
        
        {/* Importo */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid ' + bord }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: muted, marginBottom: 6, textTransform: 'uppercase' }}>
            Importo da {isEntrata ? 'aggiungere' : (isUscitaDaEntrate ? 'sottrarre' : 'registrare')}
          </label>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 20, fontWeight: 700, color: isUscitaVera ? '#f59e0b' : color }}>€</span>
            <input 
              type="number" 
              value={importo}
              onChange={e => setImporto(e.target.value)}
              placeholder="0.00"
              autoFocus
              style={{ 
                width: '100%', 
                padding: '14px 14px 14px 40px', 
                background: bgInput, 
                border: `2px solid ${isUscitaVera ? '#f59e0b' : color}50`, 
                borderRadius: 12, 
                color: isUscitaVera ? '#f59e0b' : color, 
                fontSize: 24, 
                fontWeight: 700, 
                textAlign: 'right',
                outline: 'none'
              }} 
            />
          </div>
          
          {/* Campo descrizione per storico */}
          <div style={{ marginTop: 12 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: muted, marginBottom: 6, textTransform: 'uppercase' }}>
              Descrizione (per storico)
            </label>
            <input 
              type="text" 
              value={descrizione}
              onChange={e => setDescrizione(e.target.value)}
              placeholder="Es: Incasso cliente Rossi, Pagamento fornitore..."
              style={{ 
                width: '100%', 
                padding: '12px 14px', 
                background: bgInput, 
                border: '1px solid ' + bord, 
                borderRadius: 10, 
                color: dark ? '#e2e8f0' : '#1e293b', 
                fontSize: 14,
                outline: 'none',
                boxSizing: 'border-box'
              }} 
            />
          </div>
        </div>
        
        {/* ENTRATE o PRELEVA: Pulsanti rapidi */}
        {(isEntrata || isUscitaDaEntrate) && !showAllVoci && (
          <div style={{ padding: '16px 20px', flex: 1, overflow: 'auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
              {vociRapide.map(voce => (
                <button
                  key={voce.id}
                  onClick={() => clickVoceRapida(voce.id)}
                  style={{
                    padding: '14px 12px',
                    background: selectedVoce === voce.id ? color + "20" : (dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'),
                    border: selectedVoce === voce.id ? `2px solid ${color}` : '1px solid ' + bord,
                    borderRadius: 12,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 6,
                    transition: 'all 0.15s'
                  }}
                >
                  <span style={{ fontSize: 24 }}>{voce.iconRapida}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: selectedVoce === voce.id ? color : (dark ? '#e2e8f0' : '#1e293b'), textAlign: 'center' }}>
                    {voce.nome?.replace('BANCA ', '').replace(' PC CARE', '')}
                  </span>
                  <span style={{ fontSize: 12, color: muted }}>€{(voce.importo || 0).toFixed(2)}</span>
                </button>
              ))}
            </div>
            
            {/* Pulsante "Altra voce" */}
            <button
              onClick={() => setShowAllVoci(true)}
              style={{
                width: '100%',
                marginTop: 12,
                padding: '12px',
                background: 'transparent',
                border: `1px dashed ${bord}`,
                borderRadius: 10,
                color: muted,
                fontSize: 13,
                cursor: 'pointer'
              }}
            >
              📋 Scegli altra voce...
            </button>
          </div>
        )}
        
        {/* Lista completa voci (per uscite vere, o "Altre voci" per entrate/preleva) */}
        {(isUscitaVera || showAllVoci) && (
          <>
            {/* Ricerca */}
            <div style={{ padding: '12px 20px', borderBottom: '1px solid ' + bord }}>
              {(isEntrata || isUscitaDaEntrate) && showAllVoci && (
                <button
                  onClick={() => setShowAllVoci(false)}
                  style={{ marginBottom: 10, padding: '6px 12px', background: 'transparent', border: '1px solid ' + bord, borderRadius: 6, color: muted, fontSize: 12, cursor: 'pointer' }}
                >
                  ← Torna ai conti rapidi
                </button>
              )}
              <input 
                type="text"
                value={searchVoce}
                onChange={e => setSearchVoce(e.target.value)}
                placeholder={isUscitaVera ? "🔍 Cerca spesa..." : "🔍 Cerca voce..."}
                style={{ 
                  width: '100%', 
                  padding: '10px 14px', 
                  background: bgInput, 
                  border: '1px solid ' + bord, 
                  borderRadius: 8, 
                  color: dark ? '#e2e8f0' : '#1e293b',
                  fontSize: 14,
                  outline: 'none'
                }} 
              />
            </div>
            
            {/* Lista voci */}
            <div style={{ flex: 1, overflow: 'auto', padding: '8px 12px' }}>
              {Object.values(vociPerCategoria).map(cat => {
                const itemsFiltrati = cat.items.filter(matchSearch);
                if (itemsFiltrati.length === 0) return null;
                return (
                  <div key={cat.id} style={{ marginBottom: 12 }}>
                    <div style={{ padding: '6px 8px', fontSize: 10, fontWeight: 700, color: muted, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>{cat.icon}</span> {cat.nome}
                    </div>
                    {itemsFiltrati.map(voce => (
                      <div 
                        key={voce.id}
                        onClick={() => setSelectedVoce(voce.id)}
                        style={{ 
                          padding: '12px 14px', 
                          background: selectedVoce === voce.id ? color + "20" : 'transparent',
                          border: selectedVoce === voce.id ? `2px solid ${color}` : `1px solid transparent`,
                          borderRadius: 10, 
                          marginBottom: 4,
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'all 0.15s'
                        }}
                      >
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 500 }}>{voce.nome}</div>
                          {voce.note && <div style={{ fontSize: 11, color: muted }}>{voce.note}</div>}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600, color }}>€{(voce.importo || 0).toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                );
              })}
              
              {/* Voci orfane */}
              {vociOrfane.filter(matchSearch).length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ padding: '6px 8px', fontSize: 10, fontWeight: 700, color: muted, textTransform: 'uppercase' }}>📁 Altro</div>
                  {vociOrfane.filter(matchSearch).map(voce => (
                    <div 
                      key={voce.id}
                      onClick={() => setSelectedVoce(voce.id)}
                      style={{ 
                        padding: '12px 14px', 
                        background: selectedVoce === voce.id ? color + "20" : 'transparent',
                        border: selectedVoce === voce.id ? `2px solid ${color}` : `1px solid transparent`,
                        borderRadius: 10, 
                        marginBottom: 4,
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{voce.nome}</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color }}>€{(voce.importo || 0).toFixed(2)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
        
        {/* Footer */}
        <div style={{ display: 'flex', gap: 12, padding: '16px 20px', borderTop: '1px solid ' + bord }}>
          <button onClick={onClose} style={{ flex: 1, padding: 14, background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 10, color: muted, fontSize: 14, cursor: 'pointer' }}>Annulla</button>
          <button 
            onClick={conferma}
            disabled={!selectedVoce || !importo}
            style={{ 
              flex: 2, 
              padding: 14, 
              background: selectedVoce && importo 
                ? (isUscitaVera ? 'linear-gradient(135deg, #f59e0b, #d97706)' : `linear-gradient(135deg, ${color}, ${isEntrata ? '#16a34a' : '#dc2626'})`)
                : '#64748b',
              border: 'none', 
              borderRadius: 10, 
              color: '#fff', 
              fontSize: 14, 
              fontWeight: 600, 
              cursor: selectedVoce && importo ? 'pointer' : 'not-allowed',
              opacity: selectedVoce && importo ? 1 : 0.5
            }}
          >
            {isEntrata ? '+' : (isUscitaVera ? '+' : '−')} {isEntrata ? 'Aggiungi' : (isUscitaVera ? 'Registra' : 'Sottrai')} €{importo || '0.00'}
          </button>
        </div>
      </div>
    </div>
  );
}

function WeekendTab({ data, save, del, dark, bgCard, bgInput, bord, muted }) {
  const [newItem, setNewItem] = useState({ tipo: 'uscita', descrizione: '', importo: '', voceRiferimentoId: '', voceRiferimentoNome: '', voceRiferimentoTipo: '' });
  const weekend = data.weekend || [];
  const totEntrate = weekend.filter(w => w.tipo === 'entrata').reduce((s, w) => s + (w.importo || 0), 0);
  const totUscite = weekend.filter(w => w.tipo === 'uscita').reduce((s, w) => s + (w.importo || 0), 0);

  // Liste voci per il dropdown
  const vociEntrate = data.entrate || [];
  const vociUscite = data.uscite || [];

  const addWeekendItem = () => {
    if (!newItem.descrizione || !newItem.importo) return;
    const item = { 
      id: Date.now().toString(), 
      tipo: newItem.tipo, 
      descrizione: newItem.descrizione, 
      importo: parseFloat(newItem.importo) || 0, 
      voceRiferimentoId: newItem.voceRiferimentoId || null,
      voceRiferimentoNome: newItem.voceRiferimentoNome || 'Manuale',
      voceRiferimentoTipo: newItem.voceRiferimentoTipo || null, // 'entrate' o 'uscite'
      data: new Date().toISOString() 
    };
    save({ ...data, weekend: [...weekend, item] });
    setNewItem({ tipo: 'uscita', descrizione: '', importo: '', voceRiferimentoId: '', voceRiferimentoNome: '', voceRiferimentoTipo: '' });
  };

  const handleVoceChange = (e) => {
    const selectedValue = e.target.value;
    if (selectedValue === '') {
      setNewItem({ ...newItem, voceRiferimentoId: '', voceRiferimentoNome: 'Manuale', voceRiferimentoTipo: '' });
    } else {
      // Il valore è nel formato "tipo:id" (es. "entrate:123" o "uscite:456")
      const [tipo, id] = selectedValue.split(':');
      const lista = tipo === 'entrate' ? vociEntrate : vociUscite;
      const voce = lista.find(v => v.id === id);
      setNewItem({ 
        ...newItem, 
        voceRiferimentoId: id, 
        voceRiferimentoNome: voce ? voce.nome : '', 
        voceRiferimentoTipo: tipo 
      });
    }
  };

  const clearWeekend = () => {
    if (window.confirm('Svuotare la lista del weekend?')) save({ ...data, weekend: [] });
  };

  const applyToResoconto = () => {
    if (weekend.length === 0) { alert('Nessuna voce da applicare'); return; }
    let newData = { ...data };
    let applied = 0;
    let manual = 0;
    const remainingWeekend = [];

    weekend.forEach(w => {
      // Se è manuale, lascia nella lista
      if (!w.voceRiferimentoId || !w.voceRiferimentoTipo) {
        remainingWeekend.push(w);
        manual++;
        return;
      }

      // Trova la voce di riferimento
      const lista = w.voceRiferimentoTipo === 'entrate' ? newData.entrate : newData.uscite;
      const voce = lista.find(v => v.id === w.voceRiferimentoId);
      
      if (voce) {
        if (w.voceRiferimentoTipo === 'entrate') {
          // Riferimento a ENTRATA
          if (w.tipo === 'uscita') {
            // Uscita su Entrata → SOTTRAE
            voce.importo = (voce.importo || 0) - w.importo;
          } else {
            // Entrata su Entrata → AGGIUNGE
            voce.importo = (voce.importo || 0) + w.importo;
          }
        } else {
          // Riferimento a USCITA
          // Sia Entrata che Uscita → AGGIUNGE sempre
          voce.importo = (voce.importo || 0) + w.importo;
        }
        applied++;
      } else {
        // Voce non trovata, lascia nella lista
        remainingWeekend.push(w);
        manual++;
      }
    });

    newData.weekend = remainingWeekend;
    save(newData);
    
    if (applied > 0 && manual > 0) {
      alert(`✅ ${applied} voci applicate al resoconto!\n📝 ${manual} voci manuali da gestire.`);
    } else if (applied > 0) {
      alert(`✅ ${applied} voci applicate al resoconto!`);
    } else {
      alert('📝 Tutte le voci sono manuali. Gestiscile singolarmente.');
    }
  };

  // Calcola l'azione che verrà eseguita
  const getAzionePreview = () => {
    if (!newItem.voceRiferimentoId) return null;
    if (newItem.voceRiferimentoTipo === 'entrate') {
      return newItem.tipo === 'uscita' ? '−' : '+';
    } else {
      return '+';
    }
  };

  return (
    <section style={{ background: bgCard, borderRadius: 16, border: '1px solid ' + bord, padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>🗓️ SPESE WEEKEND</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={applyToResoconto} style={{ padding: '8px 16px', background: 'linear-gradient(135deg,#3b82f6,#2563eb)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>📋 Applica a Resoconto</button>
          <button onClick={clearWeekend} style={{ padding: '8px 16px', background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#ef4444', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>🗑️ Svuota</button>
        </div>
      </div>

      {/* Riepilogo */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        <div style={{ background: bgInput, borderRadius: 12, padding: 16, textAlign: 'center' }}>
          <span style={{ display: 'block', fontSize: 11, color: muted }}>ENTRATE</span>
          <span style={{ display: 'block', fontSize: 20, fontWeight: 700, color: '#22c55e' }}>{fmtCur(totEntrate)}</span>
        </div>
        <div style={{ background: bgInput, borderRadius: 12, padding: 16, textAlign: 'center' }}>
          <span style={{ display: 'block', fontSize: 11, color: muted }}>USCITE</span>
          <span style={{ display: 'block', fontSize: 20, fontWeight: 700, color: '#ef4444' }}>{fmtCur(totUscite)}</span>
        </div>
        <div style={{ background: bgInput, borderRadius: 12, padding: 16, textAlign: 'center' }}>
          <span style={{ display: 'block', fontSize: 11, color: muted }}>SALDO</span>
          <span style={{ display: 'block', fontSize: 20, fontWeight: 700, color: totEntrate - totUscite >= 0 ? '#22c55e' : '#ef4444' }}>{fmtCur(totEntrate - totUscite)}</span>
        </div>
      </div>

      {/* Form aggiunta */}
      <div style={{ background: bgInput, borderRadius: 12, padding: 16, marginBottom: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: muted, marginBottom: 4 }}>Tipo</label>
            <select value={newItem.tipo} onChange={e => setNewItem({ ...newItem, tipo: e.target.value })} style={{ width: '100%', padding: '10px 12px', background: dark ? 'rgba(15,23,42,0.6)' : '#fff', border: '1px solid ' + bord, borderRadius: 8, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14 }}>
              <option value="uscita">↑ Uscita</option>
              <option value="entrata">↓ Entrata</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: muted, marginBottom: 4 }}>Importo (€)</label>
            <input type="number" placeholder="0.00" value={newItem.importo} onChange={e => setNewItem({ ...newItem, importo: e.target.value })} style={{ width: '100%', padding: '10px 12px', background: dark ? 'rgba(15,23,42,0.6)' : '#fff', border: '1px solid ' + bord, borderRadius: 8, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={{ display: 'block', fontSize: 11, color: muted, marginBottom: 4 }}>Voce di riferimento (per applicazione automatica)</label>
            <select 
              value={newItem.voceRiferimentoId ? `${newItem.voceRiferimentoTipo}:${newItem.voceRiferimentoId}` : ''} 
              onChange={handleVoceChange} 
              style={{ width: '100%', padding: '10px 12px', background: dark ? 'rgba(15,23,42,0.6)' : '#fff', border: '1px solid ' + bord, borderRadius: 8, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14 }}
            >
              <option value="">📝 Manuale (applica a mano)</option>
              <optgroup label="──── ENTRATE ────">
                {vociEntrate.map(v => (
                  <option key={`entrate:${v.id}`} value={`entrate:${v.id}`}>↓ {v.nome}</option>
                ))}
              </optgroup>
              <optgroup label="──── USCITE ────">
                {vociUscite.map(v => (
                  <option key={`uscite:${v.id}`} value={`uscite:${v.id}`}>↑ {v.nome}</option>
                ))}
              </optgroup>
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <input type="text" placeholder="Descrizione (es. Cena sabato, Benzina...)" value={newItem.descrizione} onChange={e => setNewItem({ ...newItem, descrizione: e.target.value })} style={{ flex: 1, padding: '10px 12px', background: dark ? 'rgba(15,23,42,0.6)' : '#fff', border: '1px solid ' + bord, borderRadius: 8, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14, outline: 'none' }} />
          <button onClick={addWeekendItem} style={{ padding: '10px 20px', background: 'linear-gradient(135deg,#22c55e,#16a34a)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>+ Aggiungi</button>
        </div>
        {/* Anteprima azione */}
        {newItem.voceRiferimentoId && newItem.importo && (
          <div style={{ marginTop: 12, padding: 10, background: dark ? 'rgba(59,130,246,0.1)' : 'rgba(59,130,246,0.05)', borderRadius: 8, fontSize: 12, color: '#3b82f6' }}>
            💡 Applicherà: <strong>{getAzionePreview()}{fmtCur(parseFloat(newItem.importo) || 0)}</strong> a <strong>{newItem.voceRiferimentoNome}</strong> ({newItem.voceRiferimentoTipo})
          </div>
        )}
      </div>

      {/* Legenda */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, fontSize: 11, color: muted, flexWrap: 'wrap' }}>
        <span>📝 Manuale</span>
        <span>|</span>
        <span>Entrata→Entrate: +</span>
        <span>Uscita→Entrate: −</span>
        <span>|</span>
        <span>Entrata→Uscite: +</span>
        <span>Uscita→Uscite: +</span>
      </div>

      {/* Lista */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {weekend.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: muted }}>Nessuna voce per questo weekend.<br />Aggiungi le spese qui sopra!</div>
        ) : weekend.slice().reverse().map(w => {
          // Calcola l'azione
          let azione = '';
          if (w.voceRiferimentoId && w.voceRiferimentoTipo) {
            if (w.voceRiferimentoTipo === 'entrate') {
              azione = w.tipo === 'uscita' ? '−' : '+';
            } else {
              azione = '+';
            }
          }
          return (
            <div key={w.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: dark ? 'rgba(15,23,42,0.6)' : '#fff', borderRadius: 10, padding: '12px 16px', borderLeft: `4px solid ${w.tipo === 'entrata' ? '#22c55e' : '#ef4444'}` }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{w.descrizione}</span>
                  <span style={{ fontSize: 10, padding: '2px 6px', background: w.voceRiferimentoId ? (w.voceRiferimentoTipo === 'entrate' ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)') : 'rgba(139,92,246,0.2)', color: w.voceRiferimentoId ? (w.voceRiferimentoTipo === 'entrate' ? '#22c55e' : '#ef4444') : '#8b5cf6', borderRadius: 4 }}>
                    {azione} → {w.voceRiferimentoNome || 'Manuale'}
                  </span>
                </div>
                <span style={{ display: 'block', fontSize: 11, color: muted }}>
                  {w.tipo === 'entrata' ? '🟢 Entrata' : '🔴 Uscita'} • {new Date(w.data).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 18, fontWeight: 700, color: w.tipo === 'entrata' ? '#22c55e' : '#ef4444' }}>{fmtCur(w.importo)}</span>
                <button onClick={() => save({ ...data, weekend: weekend.filter(x => x.id !== w.id) })} style={{ background: 'transparent', border: 'none', fontSize: 16, color: '#ef4444', cursor: 'pointer', opacity: 0.5 }}>×</button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function AbbonamentiTab({ data, save, dark, bgCard, bgInput, bord, muted, supabase, userId }) {
  const [editModal, setEditModal] = useState(null);
  const [credenzialiModal, setCredenzialiModal] = useState(null);
  const [whatsappModal, setWhatsappModal] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [importRows, setImportRows] = useState(() => Array(5).fill(null).map(() => ({ nomeCliente: '', riferimento: '', telefono: '', costo: '', giornoScadenza: '', frequenza: 'mensile', meseCreazione: new Date().getMonth(), utente: '', password: '' })));
  const [copiato, setCopiato] = useState(false);
  const [searchAbb, setSearchAbb] = useState('');
  const [showSearchAbb, setShowSearchAbb] = useState(false);
  const [filtroFrequenza, setFiltroFrequenza] = useState('tutti'); // tutti, mensile, trimestrale, semestrale, annuale
  const [mesiAperti, setMesiAperti] = useState(() => {
    const oggi = new Date();
    const meseCorrente = oggi.getMonth();
    const annoCorrente = oggi.getFullYear();
    const meseProssimo = (meseCorrente + 1) % 12;
    const annoProssimo = meseCorrente === 11 ? annoCorrente + 1 : annoCorrente;
    return {
      [`${meseCorrente}-${annoCorrente}`]: true,
      [`${meseProssimo}-${annoProssimo}`]: true
    };
  });
  const [anniAperti, setAnniAperti] = useState(() => ({ [new Date().getFullYear()]: true })); // Anno corrente aperto di default
  const [mostraImporti, setMostraImporti] = useState(false); // Mostra/nascondi importi nei menu a tendina
  const [pagamentoModal, setPagamentoModal] = useState(null); // { abbonamento, mese, anno }
  const [confermaEliminaAnno, setConfermaEliminaAnno] = useState(null); // Anno da eliminare

  // Conti predefiniti per il pagamento
  const CONTI_PAGAMENTO = [
    { nome: 'BANCA CARIPARMA PC CARE', icon: '🏦' },
    { nome: 'CASSA NEGOZIO', icon: '💵' },
    { nome: 'POSTEPAY', icon: '💳' },
    { nome: 'PAYPAL', icon: '🅿️' },
    { nome: 'REVOLUT', icon: '💜' }
  ];

  // Toggle apertura mese
  const toggleMese = (chiave) => {
    setMesiAperti(prev => ({ ...prev, [chiave]: !prev[chiave] }));
  };
  
  // Toggle apertura anno
  const toggleAnno = (anno) => {
    setAnniAperti(prev => ({ ...prev, [anno]: !prev[anno] }));
  };

  // Apri/Chiudi tutti
  const apriTuttiMesi = () => {
    const nuoviStati = {};
    getAnniDisponibili().forEach(anno => {
      getMesiAnno(anno).forEach(({ mese }) => {
        nuoviStati[`${mese}-${anno}`] = true;
      });
    });
    setMesiAperti(nuoviStati);
    // Apri anche tutti gli anni
    const anniAperti = {};
    getAnniDisponibili().forEach(anno => { anniAperti[anno] = true; });
    setAnniAperti(anniAperti);
  };

  const chiudiTuttiMesi = () => {
    setMesiAperti({});
    // Chiudi anche tutti gli anni tranne il corrente
    setAnniAperti({ [new Date().getFullYear()]: true });
  };

  const abbonamenti = data.abbonamenti || [];

  // Filtra abbonamenti per frequenza
  const abbonamentiFiltratiFrq = filtroFrequenza === 'tutti' 
    ? abbonamenti 
    : abbonamenti.filter(a => a.frequenza === filtroFrequenza);

  // Filtra abbonamenti per ricerca (dopo filtro frequenza)
  const abbonamentiFiltrati = searchAbb.trim() 
    ? abbonamentiFiltratiFrq.filter(a => 
        a.nomeCliente?.toLowerCase().includes(searchAbb.toLowerCase()) ||
        a.riferimento?.toLowerCase().includes(searchAbb.toLowerCase()) ||
        a.telefono?.includes(searchAbb)
      )
    : abbonamentiFiltratiFrq;

  // Messaggi WhatsApp (da impostazioni o predefiniti)
  // Messaggio info abbonamento - forza nuovo formato se contiene vecchio
  const getInfoAbbonamentoMsg = () => {
    const saved = data.messaggiWA?.infoAbbonamento;
    // Se contiene il vecchio formato, usa il nuovo
    if (saved && (saved.includes('giorno {GIORNO} di ogni mese') || saved.includes('RIEPILOGO ABBONAMENTO'))) {
      return `*INFO ABBONAMENTO*\n\nCliente: *{CLIENTE}*\nServizio: {RIFERIMENTO}\nTipo: {FREQUENZA}\nScadenza: ogni {GIORNO} di {MESI_SCADENZA}\nCosto: €{COSTO}\n\nPC Care Napoli - 081.191.73.939`;
    }
    return saved || `*INFO ABBONAMENTO*\n\nCliente: *{CLIENTE}*\nServizio: {RIFERIMENTO}\nTipo: {FREQUENZA}\nScadenza: ogni {GIORNO} di {MESI_SCADENZA}\nCosto: €{COSTO}\n\nPC Care Napoli - 081.191.73.939`;
  };

  const MESSAGGI_WA = {
    primo: data.messaggiWA?.primo || `Gentile {CLIENTE}, le ricordiamo che il suo abbonamento "{RIFERIMENTO}" scade il giorno {GIORNO} di questo mese. Importo: €{COSTO}. Cordiali saluti, PC Care`,
    secondo: data.messaggiWA?.secondo || `Gentile {CLIENTE}, SECONDO AVVISO: il suo abbonamento "{RIFERIMENTO}" risulta in scadenza il giorno {GIORNO}. La preghiamo di provvedere al pagamento di €{COSTO}. Cordiali saluti, PC Care`,
    ultimo: data.messaggiWA?.ultimo || `Gentile {CLIENTE}, ULTIMO AVVISO: il pagamento dell'abbonamento "{RIFERIMENTO}" risulta ancora in sospeso nonostante i precedenti solleciti. In assenza di regolarizzazione entro 7 giorni, saremo costretti a procedere con la sospensione del servizio. Importo dovuto: €{COSTO}. Per informazioni: 081.191.73.939. PC CARE NAPOLI`,
    conferma: data.messaggiWA?.conferma || `Gentile {CLIENTE}, confermiamo la ricezione del pagamento per l'abbonamento "{RIFERIMENTO}". Grazie! PC Care`,
    datiPagamento: data.messaggiWA?.datiPagamento || `Gentile {CLIENTE}, di seguito i dati per il pagamento dell'abbonamento "{RIFERIMENTO}" (€{COSTO}):\n\nIBAN: IT00X0000000000000000000000\nIntestato a: PC Care Napoli\nCausale: {RIFERIMENTO} - {CLIENTE}\n\nCordiali saluti, PC Care`,
    infoAbbonamento: getInfoAbbonamentoMsg()
  };

  // Ottieni anni disponibili (dall'anno più vecchio negli stati fino a anno corrente + 1)
  const getAnniDisponibili = () => {
    const oggi = new Date();
    const annoCorrente = oggi.getFullYear();
    const anniSet = new Set([annoCorrente, annoCorrente + 1]); // Sempre anno corrente e prossimo
    
    // Aggiungi anni dagli stati salvati
    abbonamenti.forEach(abb => {
      if (abb.statiMensili) {
        Object.keys(abb.statiMensili).forEach(chiave => {
          const [, anno] = chiave.split('-');
          if (anno) anniSet.add(parseInt(anno));
        });
      }
    });
    
    // Aggiungi anni salvati esplicitamente
    if (data.anniAbbonamenti) {
      data.anniAbbonamenti.forEach(a => anniSet.add(a));
    }
    
    return Array.from(anniSet).sort((a, b) => a - b); // Ordine crescente (più vecchio prima)
  };
  
  // Ottieni i 12 mesi di un anno specifico
  const getMesiAnno = (anno) => {
    const mesi = [];
    const nomiMesi = ['GENNAIO', 'FEBBRAIO', 'MARZO', 'APRILE', 'MAGGIO', 'GIUGNO', 
                      'LUGLIO', 'AGOSTO', 'SETTEMBRE', 'OTTOBRE', 'NOVEMBRE', 'DICEMBRE'];
    for (let i = 0; i < 12; i++) {
      mesi.push({ mese: i, anno, label: `${nomiMesi[i]} ${anno}` });
    }
    return mesi;
  };
  
  // Funzione legacy per compatibilità
  const getMesi = () => {
    const mesi = [];
    const oggi = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(oggi.getFullYear(), oggi.getMonth() + i, 1);
      mesi.push({ mese: d.getMonth(), anno: d.getFullYear(), label: d.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' }).toUpperCase() });
    }
    return mesi;
  };
  
  // Elimina dati di un anno specifico
  const eliminaAnno = (annoEliminare) => {
    const abbList = data.abbonamenti || [];
    const abbAggiornati = abbList.map(abb => {
      if (!abb.statiMensili) return abb;
      const nuoviStati = {};
      Object.entries(abb.statiMensili).forEach(([chiave, valore]) => {
        const [, anno] = chiave.split('-');
        if (parseInt(anno) !== annoEliminare) {
          nuoviStati[chiave] = valore;
        }
      });
      return { ...abb, statiMensili: nuoviStati };
    });
    
    // Rimuovi anno dalla lista anni salvati
    const anniSalvati = (data.anniAbbonamenti || []).filter(a => a !== annoEliminare);
    
    save({ ...data, abbonamenti: abbAggiornati, anniAbbonamenti: anniSalvati });
    setConfermaEliminaAnno(null);
  };

  // Controlla se un abbonamento è attivo in un determinato mese
  // meseCreazione = mese di partenza (0-11), la frequenza determina ogni quanti mesi si ripete
  // Calcola il giorno di scadenza effettivo per un mese (gestisce feb 29/30/31)
  const getGiornoScadenzaEffettivo = (giornoScadenza, mese, anno) => {
    // Calcola l'ultimo giorno del mese
    const ultimoGiorno = new Date(anno, mese + 1, 0).getDate();
    // Se il giorno scadenza supera l'ultimo giorno del mese, usa l'ultimo giorno
    return Math.min(giornoScadenza, ultimoGiorno);
  };

  const isAttivoInMese = (abb, mese, anno) => {
    const meseCreazione = abb.meseCreazione ?? 0;
    const annoCreazione = abb.annoCreazione ?? 2020;
    
    // Se il mese/anno richiesto è PRIMA della creazione, l'abbonamento non esisteva
    if (anno < annoCreazione || (anno === annoCreazione && mese < meseCreazione)) {
      return false;
    }
    
    // Calcola la differenza in mesi (ciclica)
    const diffMesi = (mese - meseCreazione + 12) % 12;
    
    switch (abb.frequenza) {
      case 'mensile': return true;
      case 'trimestrale': return diffMesi % 3 === 0;
      case 'semestrale': return diffMesi % 6 === 0;
      case 'annuale': return mese === meseCreazione;
      default: return true;
    }
  };

  // Aggiungi/modifica abbonamento
  const salvaAbbonamento = (abb) => {
    const abbList = data.abbonamenti || [];
    if (abb.id) {
      // Modifica esistente - mantieni annoCreazione originale
      save({ ...data, abbonamenti: abbList.map(a => a.id === abb.id ? abb : a) });
    } else {
      // Nuovo abbonamento - aggiungi annoCreazione
      const nuovoAbb = { 
        ...abb, 
        id: Date.now().toString(),
        meseCreazione: abb.meseCreazione ?? new Date().getMonth(),
        annoCreazione: abb.annoCreazione ?? new Date().getFullYear()
      };
      save({ ...data, abbonamenti: [...abbList, nuovoAbb] });
    }
    setEditModal(null);
  };

  // Elimina abbonamento
  const eliminaAbbonamento = async (id) => {
    if (window.confirm('Eliminare questo abbonamento?')) {
      console.log('🗑️ Eliminazione abbonamento:', id);
      
      // Prima prepara l'array senza l'elemento
      const abbList = data.abbonamenti || [];
      const nuoviAbbonamenti = abbList.filter(a => a.id !== id);
      
      // Poi elimina da Supabase
      if (userId && supabase) {
        try {
          const { error } = await supabase
            .from('abbonamenti')
            .delete()
            .eq('id', id)
            .eq('user_id', userId);
          
          if (error) {
            console.error('❌ Errore eliminazione da Supabase:', error);
          } else {
            console.log('✅ Abbonamento eliminato da Supabase');
          }
        } catch (e) {
          console.error('❌ Errore:', e);
        }
      }
      
      // Aggiorna lo stato locale
      save({ ...data, abbonamenti: nuoviAbbonamenti });
    }
  };

  // Ottieni lo stato mensile di un abbonamento
  const getStatoMensile = (abb, mese, anno) => {
    const chiave = `${mese}-${anno}`;
    return abb.statiMensili?.[chiave] || { avvisato: false, secondoAvviso: false, ultimoAvviso: false, pagato: false };
  };

  // Toggle stato per un mese specifico
  const toggleStato = (id, campo, mese, anno) => {
    // Se si sta cliccando su "pagato", apri il modal per scegliere il conto
    if (campo === 'pagato') {
      const abb = abbonamenti.find(a => a.id === id);
      const stato = getStatoMensile(abb, mese, anno);
      
      // Se sta togliendo il pagato, fallo direttamente senza modal
      if (stato.pagato) {
        const chiave = `${mese}-${anno}`;
        // FIX: togliere la spunta "pagato" deve anche STORNARE i soldi dal conto
        // su cui erano stati incassati. Prima veniva azzerato solo il flag: i soldi
        // restavano sul conto, il mese tornava in "da ricevere" e veniva ri-sommato
        // nei totali, gonfiando il saldo di una cifra pari al costo ogni volta.
        const contoStorno = stato.incassatoSu;
        const importoStorno = (stato.importoIncassato !== undefined && stato.importoIncassato !== null)
          ? stato.importoIncassato
          : (abb?.costo || 0);

        // FIX: se il conto è stato rinominato o eliminato la sottrazione non
        // troverebbe nulla, ma il movimento negativo verrebbe comunque registrato:
        // registro movimenti e saldi divergerebbero in silenzio.
        const contoEsiste = contoStorno ? (data.entrate || []).some(e => e.nome === contoStorno) : false;
        if (contoStorno && !contoEsiste) {
          alert(`Il conto "${contoStorno}" su cui era stato incassato non esiste più.\n\nLa spunta non verrà tolta: rinomina il conto come prima, oppure correggi il saldo a mano.`);
          return;
        }

        if (!contoStorno && importoStorno > 0) {
          // Abbonamenti marcati pagati prima di questa correzione non sanno su
          // quale conto erano finiti: si avvisa invece di stornare a caso.
          // Con "Solo Segna Pagato" importoIncassato è 0 e questo avviso non compare,
          // perché non c'era stato alcun incasso da stornare.
          if (!window.confirm(
            'Questo pagamento non ha memorizzato su quale conto era stato incassato ' +
            '(segnato come pagato con una versione precedente).\n\n' +
            'La spunta verrà tolta ma i soldi NON verranno stornati da nessun conto: ' +
            'controlla tu il saldo.\n\nProcedere?'
          )) return;
        } else if (contoStorno && importoStorno > 0) {
          if (!window.confirm(`Togliendo il pagato verranno stornati €${importoStorno.toFixed(2)} da "${contoStorno}". Procedere?`)) return;
        }

        const nomiMesiStorno = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
        let nuoveEntrateStorno = data.entrate || [];
        let nuoviMovimentiStorno = data.movimenti || [];

        if (contoStorno && contoEsiste && importoStorno > 0) {
          nuoveEntrateStorno = nuoveEntrateStorno.map(e =>
            e.nome === contoStorno ? { ...e, importo: (e.importo || 0) - importoStorno } : e
          );
          nuoviMovimentiStorno = [...nuoviMovimentiStorno, {
            id: Date.now().toString(),
            data: new Date().toISOString(),
            tipo: 'storno',
            descrizione: `STORNO ${abb?.nomeCliente || 'Abbonamento'} - ${abb?.riferimento || 'Abbonamento'} (${nomiMesiStorno[mese]} ${anno})`,
            importo: -importoStorno,
            metodo: `Stornato da: ${contoStorno}`
          }];
        }

        save({
          ...data,
          entrate: nuoveEntrateStorno,
          movimenti: nuoviMovimentiStorno,
          abbonamenti: (data.abbonamenti || []).map(a => {
            if (a.id !== id) return a;
            const statiMensili = { ...(a.statiMensili || {}) };
            const statoAttuale = statiMensili[chiave] || { avvisato: false, secondoAvviso: false, ultimoAvviso: false, pagato: false };
            statiMensili[chiave] = { ...statoAttuale, pagato: false, dataPagato: null, incassatoSu: null, importoIncassato: null };
            return { ...a, statiMensili };
          })
        });
        return;
      }
      
      // Altrimenti apri il modal
      setPagamentoModal({ abbonamento: abb, mese, anno });
      return;
    }
    
    // Per altri campi (avvisato, secondoAvviso), comportamento normale con data
    const chiave = `${mese}-${anno}`;
    const abbList = data.abbonamenti || [];
    const oggi = new Date().toISOString();
    save({ 
      ...data, 
      abbonamenti: abbList.map(a => {
        if (a.id !== id) return a;
        const statiMensili = { ...(a.statiMensili || {}) };
        const statoAttuale = statiMensili[chiave] || { avvisato: false, secondoAvviso: false, ultimoAvviso: false, pagato: false };
        const nuovoValore = !statoAttuale[campo];
        // Salva la data solo quando si attiva il flag
        const dataField = `data${campo.charAt(0).toUpperCase() + campo.slice(1)}`; // dataAvvisato, dataSecondoAvviso
        statiMensili[chiave] = { 
          ...statoAttuale, 
          [campo]: nuovoValore,
          [dataField]: nuovoValore ? oggi : null
        };
        return { ...a, statiMensili };
      })
    });
  };

  // Conferma pagamento e somma al conto selezionato
  const confermaPagamento = (contoNome) => {
    if (!pagamentoModal) return;
    
    const { abbonamento, mese, anno } = pagamentoModal;
    const chiave = `${mese}-${anno}`;
    const oggi = new Date().toISOString();
    const nomiMesi = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
    
    // Aggiorna lo stato dell'abbonamento come pagato con data
    const nuoviAbbonamenti = (data.abbonamenti || []).map(a => {
      if (a.id !== abbonamento.id) return a;
      const statiMensili = { ...(a.statiMensili || {}) };
      // FIX: si memorizza il conto e l'importo incassati, così togliendo la
      // spunta "pagato" è possibile stornare la cifra dal conto giusto.
      statiMensili[chiave] = { ...statiMensili[chiave], pagato: true, dataPagato: oggi, incassatoSu: contoNome, importoIncassato: abbonamento.costo || 0 };
      return { ...a, statiMensili };
    });
    
    // Trova il conto nelle entrate e somma l'importo
    const nuoveEntrate = data.entrate.map(e => {
      if (e.nome === contoNome) {
        return { ...e, importo: (e.importo || 0) + (abbonamento.costo || 0) };
      }
      return e;
    });
    
    // Registra movimento
    const movimento = {
      id: Date.now().toString(),
      data: oggi,
      tipo: 'abbonamento',
      descrizione: `${abbonamento.nomeCliente} - ${abbonamento.riferimento || 'Abbonamento'} (${nomiMesi[mese]} ${anno})`,
      importo: abbonamento.costo || 0,
      metodo: `Incassato su: ${contoNome}`
    };
    const movimenti = [...(data.movimenti || []), movimento];
    
    save({ ...data, abbonamenti: nuoviAbbonamenti, entrate: nuoveEntrate, movimenti });
    setPagamentoModal(null);
    
    // Apri WhatsApp automaticamente con messaggio conferma
    if (abbonamento.telefono) {
      setTimeout(() => apriWhatsApp(abbonamento, 'conferma'), 300);
    }
  };

  // Reset stati per un mese specifico (per un singolo abbonamento)
  const resetStati = (id, mese, anno) => {
    const chiave = `${mese}-${anno}`;
    const abbList = data.abbonamenti || [];
    save({ 
      ...data, 
      abbonamenti: abbList.map(a => {
        if (a.id !== id) return a;
        const statiMensili = { ...(a.statiMensili || {}) };
        statiMensili[chiave] = { avvisato: false, secondoAvviso: false, ultimoAvviso: false, pagato: false };
        return { ...a, statiMensili };
      })
    });
  };

  // Copia credenziali
  const copiaCredenziali = (abb) => {
    const testo = `Utente: ${abb.utente || 'N/D'}\nPassword: ${abb.password || 'N/D'}`;
    navigator.clipboard.writeText(testo);
    setCopiato(true);
    setTimeout(() => setCopiato(false), 2000);
  };

  // Apri WhatsApp
  const apriWhatsApp = (abb, tipo) => {
    if (!abb.telefono) { alert('Nessun numero di telefono!'); return; }
    const freqNomi = { mensile: 'Mensile', trimestrale: 'Trimestrale', semestrale: 'Semestrale', annuale: 'Annuale' };
    const nomiMesi = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
    
    // Calcola i mesi di scadenza in base alla frequenza e al mese di creazione
    const calcolaMesiScadenza = () => {
      const meseCreazione = abb.meseCreazione || 0;
      const freq = abb.frequenza || 'mensile';
      
      if (freq === 'mensile') return 'ogni mese';
      if (freq === 'annuale') return nomiMesi[meseCreazione];
      
      const intervallo = freq === 'trimestrale' ? 3 : 6; // semestrale = 6
      const mesi = [];
      for (let i = 0; i < 12; i += intervallo) {
        mesi.push(nomiMesi[(meseCreazione + i) % 12]);
      }
      return mesi.join(', ');
    };
    
    let msg = MESSAGGI_WA[tipo]
      .replace('{CLIENTE}', abb.nomeCliente)
      .replace('{RIFERIMENTO}', abb.riferimento || 'abbonamento')
      .replace('{GIORNO}', abb.giornoScadenza)
      .replace('{COSTO}', abb.costo)
      .replace('{FREQUENZA}', freqNomi[abb.frequenza] || 'Mensile')
      .replace('{MESI_SCADENZA}', calcolaMesiScadenza());
    
    let num = abb.telefono.replace(/[\s\-\(\)]/g, '');
    if (num.startsWith('0')) num = '+39' + num.substring(1);
    else if (!num.startsWith('+')) num = '+39' + num;
    
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, '_blank');
    setWhatsappModal(null);
  };

  const freqLabel = { mensile: 'M', trimestrale: 'T', semestrale: 'S', annuale: 'A' };
  const freqNome = { mensile: 'Mensile', trimestrale: 'Trimestrale', semestrale: 'Semestrale', annuale: 'Annuale' };

  // Genera PDF Backup Abbonamenti
  const generaPdfAbbonamenti = () => {
    const oggi = new Date().toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const meseCorrente = new Date().getMonth();
    const annoCorrente = new Date().getFullYear();
    const totaleAbbonamenti = abbonamenti.reduce((s, a) => s + (a.costo || 0), 0);
    
    const printWindow = window.open('', '_blank');
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Backup Abbonamenti - ${oggi}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 30px; max-width: 900px; margin: 0 auto; font-size: 11px; }
          h1 { color: #1e293b; font-size: 22px; border-bottom: 3px solid #f59e0b; padding-bottom: 10px; }
          h2 { color: #f59e0b; font-size: 14px; margin-top: 25px; margin-bottom: 10px; background: #fef3c7; padding: 8px 12px; border-radius: 6px; }
          .summary { display: flex; gap: 20px; margin: 20px 0; padding: 15px; background: #f8fafc; border-radius: 10px; flex-wrap: wrap; }
          .summary-item { text-align: center; min-width: 80px; }
          .summary-label { font-size: 10px; color: #64748b; }
          .summary-value { font-size: 18px; font-weight: 700; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          th, td { border: 1px solid #e2e8f0; padding: 6px 8px; text-align: left; }
          th { background: #f1f5f9; font-weight: 600; font-size: 10px; }
          .importo { text-align: right; font-weight: 600; }
          .centro { text-align: center; }
          .pagato { background: #dcfce7; }
          .avvisato { background: #fef9c3; }
          .check { color: #22c55e; font-weight: bold; }
          .footer { margin-top: 30px; font-size: 10px; color: #94a3b8; text-align: center; border-top: 1px solid #e2e8f0; padding-top: 15px; }
          .freq-badge { padding: 2px 6px; border-radius: 4px; font-size: 9px; font-weight: 600; }
          .freq-M { background: #dbeafe; color: #2563eb; }
          .freq-T { background: #f3e8ff; color: #7c3aed; }
          .freq-S { background: #fce7f3; color: #db2777; }
          .freq-A { background: #fee2e2; color: #dc2626; }
          @media print { body { padding: 15px; } }
        </style>
      </head>
      <body>
        <h1>📅 BACKUP ABBONAMENTI</h1>
        <p style="color: #64748b; font-size: 12px;">Generato il ${oggi}</p>
        
        <div class="summary">
          <div class="summary-item">
            <div class="summary-label">TOTALE ABBONAMENTI</div>
            <div class="summary-value" style="color: #f59e0b;">${abbonamenti.length}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">MENSILI</div>
            <div class="summary-value">${abbonamenti.filter(a => a.frequenza === 'mensile').length}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">TRIMESTRALI</div>
            <div class="summary-value">${abbonamenti.filter(a => a.frequenza === 'trimestrale').length}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">SEMESTRALI</div>
            <div class="summary-value">${abbonamenti.filter(a => a.frequenza === 'semestrale').length}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">ANNUALI</div>
            <div class="summary-value">${abbonamenti.filter(a => a.frequenza === 'annuale').length}</div>
          </div>
        </div>
        
        ${getMesi().map(({ mese, anno, label }) => {
          const abbMese = abbonamenti.filter(a => isAttivoInMese(a, mese, anno));
          if (abbMese.length === 0) return '';
          const totMese = abbMese.reduce((s, a) => s + (a.costo || 0), 0);
          const numPagati = abbMese.filter(a => getStatoMensile(a, mese, anno).pagato).length;
          
          return `
            <h2>📅 ${label} - ${abbMese.length} abbonamenti - €${totMese.toFixed(2)} ${numPagati > 0 ? `(✓ ${numPagati} pagati)` : ''}</h2>
            <table>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Riferimento</th>
                  <th>Telefono</th>
                  <th class="importo">€</th>
                  <th class="centro">Gg</th>
                  <th class="centro">Freq</th>
                  <th class="centro">Avv</th>
                  <th class="centro">2°Avv</th>
                  <th class="centro">Ult</th>
                  <th class="centro">Pagato</th>
                </tr>
              </thead>
              <tbody>
                ${abbMese.sort((a, b) => a.giornoScadenza - b.giornoScadenza).map(a => {
                  const stato = getStatoMensile(a, mese, anno);
                  const rowClass = stato.pagato ? 'pagato' : (stato.ultimoAvviso ? 'ultimoAvviso' : (stato.avvisato ? 'avvisato' : ''));
                  return `
                    <tr class="${rowClass}">
                      <td>${a.nomeCliente}</td>
                      <td>${a.riferimento || '-'}</td>
                      <td>${a.telefono || '-'}</td>
                      <td class="importo">€${a.costo}</td>
                      <td class="centro">${a.giornoScadenza}</td>
                      <td class="centro"><span class="freq-badge freq-${freqLabel[a.frequenza]}">${freqLabel[a.frequenza]}</span></td>
                      <td class="centro">${stato.avvisato ? '<span class="check">✓</span>' : ''}</td>
                      <td class="centro">${stato.secondoAvviso ? '<span class="check">✓</span>' : ''}</td>
                      <td class="centro">${stato.ultimoAvviso ? '<span class="check">✓</span>' : ''}</td>
                      <td class="centro">${stato.pagato ? '<span class="check">✓</span>' : ''}</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          `;
        }).join('')}
        
        <div class="footer">
          GESTIONE PC CARE - Backup Abbonamenti generato automaticamente
        </div>
        <script>window.print();</script>
      </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  return (
    <section style={{ background: bgCard, borderRadius: 16, border: '1px solid ' + bord, padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>📅 ABBONAMENTI</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={generaPdfAbbonamenti} style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#ef4444', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>📄 PDF</button>
          <button onClick={() => setShowSearchAbb(!showSearchAbb)} style={{ padding: '8px 12px', background: showSearchAbb ? 'rgba(59,130,246,0.3)' : (dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'), border: 'none', borderRadius: 8, fontSize: 14, cursor: 'pointer' }}>🔍</button>
          <button onClick={() => setImportModal(true)} style={{ padding: '8px 16px', background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 8, color: '#3b82f6', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>➕ Multiplo</button>
          <button onClick={() => setEditModal({ nomeCliente: '', riferimento: '', telefono: '', costo: '', giornoScadenza: '', frequenza: 'mensile', utente: '', password: '', meseCreazione: new Date().getMonth(), annoCreazione: new Date().getFullYear() })} style={{ padding: '8px 16px', background: 'linear-gradient(135deg,#22c55e,#16a34a)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>+ Nuovo</button>
        </div>
      </div>

      {/* Barra di ricerca */}
      {showSearchAbb && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ position: 'relative' }}>
            <input 
              type="text" 
              placeholder="Cerca per nome, riferimento o telefono..." 
              value={searchAbb} 
              onChange={e => setSearchAbb(e.target.value)} 
              style={{ width: '100%', padding: '12px 16px 12px 40px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14, boxSizing: 'border-box' }}
              autoFocus
            />
            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 16 }}>🔍</span>
            {searchAbb && (
              <button onClick={() => setSearchAbb('')} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', fontSize: 16, cursor: 'pointer', color: muted }}>×</button>
            )}
          </div>
          {searchAbb && (
            <p style={{ margin: '8px 0 0', fontSize: 12, color: muted }}>
              {abbonamentiFiltrati.length} risultat{abbonamentiFiltrati.length === 1 ? 'o' : 'i'} per "{searchAbb}"
            </p>
          )}
        </div>
      )}

      {/* Filtri Rapidi Frequenza */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { id: 'tutti', label: 'Tutti', icon: '📋' },
          { id: 'mensile', label: 'Mensili', icon: 'M' },
          { id: 'trimestrale', label: 'Trimestrali', icon: 'T' },
          { id: 'semestrale', label: 'Semestrali', icon: 'S' },
          { id: 'annuale', label: 'Annuali', icon: 'A' },
        ].map(f => {
          const count = f.id === 'tutti' ? abbonamenti.length : abbonamenti.filter(a => a.frequenza === f.id).length;
          const isActive = filtroFrequenza === f.id;
          return (
            <button 
              key={f.id}
              onClick={() => setFiltroFrequenza(f.id)}
              style={{ 
                padding: '8px 14px', 
                background: isActive ? 'linear-gradient(135deg,#3b82f6,#2563eb)' : (dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'),
                border: isActive ? 'none' : '1px solid ' + bord,
                borderRadius: 8, 
                color: isActive ? '#fff' : (dark ? '#e2e8f0' : '#1e293b'),
                fontSize: 12, 
                fontWeight: isActive ? 600 : 400,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                transition: 'all 0.2s'
              }}
            >
              <span>{f.icon}</span>
              <span>{f.label}</span>
              <span style={{ 
                background: isActive ? 'rgba(255,255,255,0.2)' : (dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'),
                padding: '2px 6px',
                borderRadius: 10,
                fontSize: 10,
                fontWeight: 600
              }}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Legenda e controlli accordion */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', gap: 16, fontSize: 11, color: muted, flexWrap: 'wrap' }}>
          <span>⬜ Attivo</span>
          <span>🟡 Avvisato</span>
          <span>🟠 2° Avviso</span>
          <span>🟢 Pagato</span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setMostraImporti(!mostraImporti)} style={{ padding: '6px 12px', background: mostraImporti ? 'rgba(34,197,94,0.2)' : (dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'), border: '1px solid ' + (mostraImporti ? '#22c55e' : bord), borderRadius: 6, color: mostraImporti ? '#22c55e' : muted, fontSize: 11, cursor: 'pointer' }}>{mostraImporti ? '👁️ Nascondi €' : '👁️ Mostra €'}</button>
          <button onClick={apriTuttiMesi} style={{ padding: '6px 12px', background: dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', border: '1px solid ' + bord, borderRadius: 6, color: muted, fontSize: 11, cursor: 'pointer' }}>▼ Espandi tutti</button>
          <button onClick={chiudiTuttiMesi} style={{ padding: '6px 12px', background: dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', border: '1px solid ' + bord, borderRadius: 6, color: muted, fontSize: 11, cursor: 'pointer' }}>▲ Chiudi tutti</button>
        </div>
      </div>

      {/* Anni e Mesi */}
      {getAnniDisponibili().map(annoCorrente => {
        const isAnnoAperto = anniAperti[annoCorrente] || false;
        const oggi = new Date();
        const annoOggi = oggi.getFullYear();
        
        // Calcola totali per l'anno
        const mesiAnno = getMesiAnno(annoCorrente);
        let totaleAnno = 0;
        let pagatiAnno = 0;
        let totaliAnno = 0;
        
        mesiAnno.forEach(({ mese, anno }) => {
          const abbMese = abbonamentiFiltrati.filter(a => isAttivoInMese(a, mese, anno));
          abbMese.forEach(a => {
            totaleAnno += a.costo || 0;
            totaliAnno++;
            if (getStatoMensile(a, mese, anno).pagato) pagatiAnno++;
          });
        });
        
        const arretratiAnno = totaliAnno - pagatiAnno;
        
        return (
          <div key={annoCorrente} style={{ marginBottom: 16 }}>
            {/* Header Anno */}
            <div 
              style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                padding: '16px 20px', 
                background: isAnnoAperto 
                  ? (dark ? 'rgba(139,92,246,0.2)' : 'rgba(139,92,246,0.15)') 
                  : (dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'),
                borderRadius: isAnnoAperto ? '16px 16px 0 0' : 16,
                border: `2px solid ${isAnnoAperto ? '#8b5cf6' : bord}`,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={() => toggleAnno(annoCorrente)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 18, transition: 'transform 0.2s', transform: isAnnoAperto ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                <span style={{ fontSize: 20, fontWeight: 800, color: isAnnoAperto ? '#8b5cf6' : (dark ? '#e2e8f0' : '#1e293b') }}>
                  📆 {annoCorrente}
                </span>
                {annoCorrente === annoOggi && (
                  <span style={{ padding: '4px 10px', background: '#22c55e', borderRadius: 12, fontSize: 10, fontWeight: 700, color: '#fff' }}>CORRENTE</span>
                )}
                <span style={{ 
                  padding: '4px 12px', 
                  background: arretratiAnno > 0 ? 'rgba(239,68,68,0.2)' : 'rgba(34,197,94,0.2)', 
                  borderRadius: 12, 
                  fontSize: 11, 
                  fontWeight: 600,
                  color: arretratiAnno > 0 ? '#ef4444' : '#22c55e'
                }}>
                  {pagatiAnno}/{totaliAnno} pagati
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {mostraImporti && (
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#8b5cf6' }}>€{totaleAnno.toFixed(0)}</span>
                )}
                {annoCorrente !== annoOggi && annoCorrente !== annoOggi + 1 && (
                  <button 
                    onClick={(e) => { e.stopPropagation(); setConfermaEliminaAnno(annoCorrente); }}
                    style={{ padding: '6px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#ef4444', fontSize: 11, cursor: 'pointer' }}
                  >
                    🗑️ Elimina
                  </button>
                )}
              </div>
            </div>
            
            {/* Mesi dell'anno */}
            {isAnnoAperto && (
              <div style={{ border: `2px solid #8b5cf6`, borderTop: 'none', borderRadius: '0 0 16px 16px', padding: 12, background: dark ? 'rgba(139,92,246,0.05)' : 'rgba(139,92,246,0.03)' }}>
                {getMesiAnno(annoCorrente).map(({ mese, anno, label }) => {
        const abbMese = abbonamentiFiltrati.filter(a => isAttivoInMese(a, mese, anno));
        if (abbMese.length === 0) return null;
        const totale = abbMese.reduce((s, a) => s + (a.costo || 0), 0);
        const chiaveMese = `${mese}-${anno}`;
        const isAperto = mesiAperti[chiaveMese] || false;
        
        // Conta pagati per badge
        const numPagati = abbMese.filter(a => getStatoMensile(a, mese, anno).pagato).length;
        
        // Calcola importo da ricevere (non pagati)
        const totaleDaRicevere = abbMese
          .filter(a => !getStatoMensile(a, mese, anno).pagato)
          .reduce((s, a) => s + (a.costo || 0), 0);
        
        // Ordina: prima per giorno scadenza, poi pagati in fondo
        const abbMeseOrdinati = [...abbMese].sort((a, b) => {
          const statA = getStatoMensile(a, mese, anno);
          const statB = getStatoMensile(b, mese, anno);
          if (statA.pagato !== statB.pagato) return statA.pagato ? 1 : -1;
          return a.giornoScadenza - b.giornoScadenza;
        });

        // Reset stati di tutti gli abbonamenti del mese
        const resetStatiMese = () => {
          const idsDelMese = abbMese.map(a => a.id);
          // FIX: il reset azzera anche il flag "pagato" senza stornare i soldi dai
          // conti. Si avvisa esplicitamente quando ci sono pagamenti che verrebbero
          // dimenticati, invece di falsare il saldo in silenzio.
          const giaPagati = abbMese.filter(a => (a.statiMensili || {})[chiaveMese]?.pagato);
          const totaleGiaPagato = giaPagati.reduce((s, a) => s + ((a.statiMensili || {})[chiaveMese]?.importoIncassato ?? a.costo ?? 0), 0);
          if (giaPagati.length > 0) {
            if (!window.confirm(
              `Attenzione: ${giaPagati.length} di questi abbonamenti risultano già PAGATI, per un totale di €${totaleGiaPagato.toFixed(2)}.\n\n` +
              `Il reset toglierà la spunta ma NON storna i soldi dai conti: quelle cifre resteranno sui conti e i mesi torneranno "da ricevere".\n\n` +
              `Per stornare correttamente, togli la spunta a uno a uno.\n\nResettare comunque tutti i ${abbMese.length} abbonamenti di ${label}?`
            )) return;
          } else if (!window.confirm(`Resettare gli stati di tutti i ${abbMese.length} abbonamenti di ${label}?`)) {
            return;
          }
          const abbList = data.abbonamenti || [];
          save({ 
            ...data, 
            abbonamenti: abbList.map(a => {
              if (!idsDelMese.includes(a.id)) return a;
              const statiMensili = { ...(a.statiMensili || {}) };
              statiMensili[chiaveMese] = { avvisato: false, secondoAvviso: false, ultimoAvviso: false, pagato: false };
              return { ...a, statiMensili };
            })
          });
        };

        // Stampa mese
        const stampaMese = () => {
          const printWindow = window.open('', '_blank');
          const html = `
            <!DOCTYPE html>
            <html>
            <head>
              <title>Abbonamenti - ${label}</title>
              <style>
                body { font-family: Arial, sans-serif; padding: 20px; }
                h1 { color: #3b82f6; font-size: 18px; margin-bottom: 5px; }
                .totale { color: #3b82f6; font-size: 14px; margin-bottom: 15px; }
                table { width: 100%; border-collapse: collapse; font-size: 12px; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                th { background: #f1f5f9; font-weight: 600; }
                .pagato { background: #dcfce7; }
                .stato { text-align: center; }
                .importo { text-align: right; }
                @media print { body { padding: 0; } }
              </style>
            </head>
            <body>
              <h1>📅 ${label}</h1>
              <div class="totale">Totale: €${totale.toFixed(2)}</div>
              <table>
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Riferimento</th>
                    <th>Telefono</th>
                    <th class="importo">€</th>
                    <th class="stato">Gg</th>
                    <th class="stato">Freq</th>
                    <th class="stato">Avv</th>
                    <th class="stato">2°</th>
                    <th class="stato">Ult</th>
                    <th class="stato">Pag</th>
                  </tr>
                </thead>
                <tbody>
                  ${abbMeseOrdinati.map(a => {
                    const stato = getStatoMensile(a, mese, anno);
                    return `
                    <tr class="${stato.pagato ? 'pagato' : ''}">
                      <td>${a.nomeCliente}</td>
                      <td>${a.riferimento || '-'}</td>
                      <td>${a.telefono || '-'}</td>
                      <td class="importo">${a.costo}</td>
                      <td class="stato">${a.giornoScadenza}</td>
                      <td class="stato">${freqLabel[a.frequenza] || 'M'}</td>
                      <td class="stato">${stato.avvisato ? '✓' : ''}</td>
                      <td class="stato">${stato.secondoAvviso ? '✓' : ''}</td>
                      <td class="stato">${stato.ultimoAvviso ? '✓' : ''}</td>
                      <td class="stato">${stato.pagato ? '✓' : ''}</td>
                    </tr>
                  `}).join('')}
                </tbody>
              </table>
              <script>window.print();</script>
            </body>
            </html>
          `;
          printWindow.document.write(html);
          printWindow.document.close();
        };
        
        return (
          <div key={`${mese}-${anno}`} style={{ marginBottom: 8 }}>
            {/* Header cliccabile */}
            <div 
              onClick={() => toggleMese(chiaveMese)}
              style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                padding: '14px 16px', 
                background: isAperto ? (dark ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.1)') : (dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
                borderRadius: isAperto ? '12px 12px 0 0' : 12,
                border: '1px solid ' + (isAperto ? '#3b82f6' : bord),
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 16, transition: 'transform 0.2s', transform: isAperto ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: isAperto ? '#3b82f6' : (dark ? '#e2e8f0' : '#1e293b') }}>📅 {label}</span>
                <span style={{ 
                  padding: '3px 10px', 
                  background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', 
                  borderRadius: 12, 
                  fontSize: 12, 
                  fontWeight: 600 
                }}>{abbMese.length}</span>
                {numPagati > 0 && (
                  <span style={{ 
                    padding: '3px 10px', 
                    background: 'rgba(34,197,94,0.2)', 
                    borderRadius: 12, 
                    fontSize: 11, 
                    fontWeight: 600,
                    color: '#22c55e'
                  }}>✓ {numPagati}/{abbMese.length}</span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {mostraImporti && (
                  <>
                    {totaleDaRicevere > 0 && totaleDaRicevere < totale && (
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#f59e0b' }}>
                        Da ricevere: {fmtCur(totaleDaRicevere)}
                      </span>
                    )}
                    <span style={{ fontSize: 15, fontWeight: 700, color: totaleDaRicevere === 0 ? '#22c55e' : '#3b82f6' }}>
                      Tot: {fmtCur(totale)}
                    </span>
                  </>
                )}
              </div>
            </div>
            
            {/* Contenuto espandibile */}
            {isAperto && (
              <div style={{ border: '1px solid ' + bord, borderTop: 'none', borderRadius: '0 0 12px 12px', overflow: 'hidden' }}>
                {/* Azioni mese */}
                <div style={{ display: 'flex', gap: 8, padding: '10px 12px', background: dark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)', borderBottom: '1px solid ' + bord }}>
                  <button onClick={(e) => { e.stopPropagation(); resetStatiMese(); }} style={{ padding: '5px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#ef4444', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>🔄 Reset Stati</button>
                  <button onClick={(e) => { e.stopPropagation(); stampaMese(); }} style={{ padding: '5px 12px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, color: '#3b82f6', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>🖨️ Stampa</button>
                </div>
                
                {/* Header tabella */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 70px 40px 40px 140px 90px', gap: 8, padding: '8px 12px', background: dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', fontSize: 10, fontWeight: 600, color: muted, textTransform: 'uppercase' }}>
                  <span>Cliente</span>
                  <span>Riferimento</span>
                  <span>📱 Tel</span>
                  <span style={{ textAlign: 'right' }}>€</span>
                  <span style={{ textAlign: 'center' }}>Gg</span>
                  <span style={{ textAlign: 'center' }}>Fr</span>
                  <span style={{ textAlign: 'center' }}>Stato</span>
                  <span style={{ textAlign: 'center' }}>Azioni</span>
                </div>
                
                {/* Righe */}
                {abbMeseOrdinati.map(abb => {
                  const stato = getStatoMensile(abb, mese, anno);
                  
                  // Calcola se scaduto o in scadenza (solo se non pagato)
                  let statusColor = 'inherit';
                  let statusBg = 'transparent';
                  let isScaduto = false;
                  let isInScadenza = false;
                  
                  if (!stato.pagato) {
                    const oggi = new Date();
                    oggi.setHours(0, 0, 0, 0);
                    const meseOggi = oggi.getMonth();
                    const annoOggi = oggi.getFullYear();
                    
                    // Calcola il giorno scadenza effettivo per questo mese (es. 31 -> 28 per febbraio)
                    const giornoEffettivo = getGiornoScadenzaEffettivo(abb.giornoScadenza || 1, mese, anno);
                    
                    // Data scadenza dell'abbonamento per il mese visualizzato
                    const dataScadenza = new Date(anno, mese, giornoEffettivo);
                    dataScadenza.setHours(0, 0, 0, 0);
                    const diffGiorni = Math.ceil((dataScadenza - oggi) / (1000 * 60 * 60 * 24));
                    
                    // Il mese visualizzato è passato, corrente o futuro?
                    const mesePrecedente = (anno < annoOggi) || (anno === annoOggi && mese < meseOggi);
                    
                    if (mesePrecedente) {
                      // Qualsiasi mese passato e non pagato = scaduto (rosso)
                      isScaduto = true;
                      statusColor = '#ef4444';
                      statusBg = dark ? 'rgba(239,68,68,0.15)' : 'rgba(239,68,68,0.1)';
                    } else if (diffGiorni < 0) {
                      // Scadenza già passata = scaduto (rosso)
                      isScaduto = true;
                      statusColor = '#ef4444';
                      statusBg = dark ? 'rgba(239,68,68,0.15)' : 'rgba(239,68,68,0.1)';
                    } else if (diffGiorni <= 7) {
                      // In scadenza entro 7 giorni (mese corrente o futuro) = arancione
                      isInScadenza = true;
                      statusColor = '#f59e0b';
                      statusBg = dark ? 'rgba(245,158,11,0.15)' : 'rgba(245,158,11,0.1)';
                    }
                    // Scadenza oltre 7 giorni: nessun colore speciale (resta neutro)
                  } else {
                    // Pagato = verde (per qualsiasi mese)
                    statusColor = '#22c55e';
                    statusBg = dark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.1)';
                  }
                  
                  const statusIcon = stato.pagato ? '✓ ' : isScaduto ? '⚠ ' : isInScadenza ? '⏰ ' : '';
                  
                  // Mostra il giorno effettivo per questo mese
                  const giornoMostrato = getGiornoScadenzaEffettivo(abb.giornoScadenza || 1, mese, anno);
                  const giornoModificato = giornoMostrato !== abb.giornoScadenza;
                  
                  return (
                    <div key={abb.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 70px 40px 40px 140px 90px', gap: 8, padding: '10px 12px', borderBottom: '1px solid ' + bord, alignItems: 'center', fontSize: 13, background: statusBg }}>
                      <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: statusColor }}>{statusIcon}{abb.nomeCliente}</span>
                      <span style={{ color: stato.pagato ? '#22c55e' : muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{abb.riferimento || '-'}</span>
                      <span style={{ color: stato.pagato ? '#22c55e' : (abb.telefono ? '#25D366' : muted), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{abb.telefono || '-'}</span>
                      <span style={{ textAlign: 'right', fontWeight: 600, color: statusColor }}>{abb.costo}</span>
                      <span style={{ textAlign: 'center', fontWeight: 600, color: statusColor }} title={giornoModificato ? `Originale: ${abb.giornoScadenza} → Adattato: ${giornoMostrato}` : ''}>{giornoMostrato}{giornoModificato ? '*' : ''}</span>
                      <span style={{ textAlign: 'center', fontSize: 11, padding: '2px 6px', background: stato.pagato ? 'rgba(34,197,94,0.2)' : isScaduto ? 'rgba(239,68,68,0.2)' : isInScadenza ? 'rgba(245,158,11,0.2)' : (dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'), borderRadius: 4, color: statusColor }}>{freqLabel[abb.frequenza] || 'M'}</span>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'center', alignItems: 'center' }}>
                        <button 
                          onClick={() => toggleStato(abb.id, 'avvisato', mese, anno)} 
                          title={stato.dataAvvisato ? `Avvisato: ${new Date(stato.dataAvvisato).toLocaleDateString('it-IT')}` : 'Primo avviso'}
                          style={{ width: 24, height: 24, border: `2px solid ${stato.avvisato ? '#eab308' : bord}`, borderRadius: 4, background: stato.avvisato ? '#eab308' : 'transparent', cursor: 'pointer', fontSize: 10, color: stato.avvisato ? '#fff' : 'transparent' }}
                        >✓</button>
                        <button 
                          onClick={() => toggleStato(abb.id, 'secondoAvviso', mese, anno)} 
                          title={stato.dataSecondoAvviso ? `2° Avviso: ${new Date(stato.dataSecondoAvviso).toLocaleDateString('it-IT')}` : 'Secondo avviso'}
                          style={{ width: 24, height: 24, border: `2px solid ${stato.secondoAvviso ? '#f97316' : bord}`, borderRadius: 4, background: stato.secondoAvviso ? '#f97316' : 'transparent', cursor: 'pointer', fontSize: 10, color: stato.secondoAvviso ? '#fff' : 'transparent' }}
                        >✓</button>
                        <button 
                          onClick={() => toggleStato(abb.id, 'ultimoAvviso', mese, anno)} 
                          title={stato.dataUltimoAvviso ? `Ultimo Avviso: ${new Date(stato.dataUltimoAvviso).toLocaleDateString('it-IT')}` : 'Ultimo avviso (sospensione)'}
                          style={{ width: 24, height: 24, border: `2px solid ${stato.ultimoAvviso ? '#ef4444' : bord}`, borderRadius: 4, background: stato.ultimoAvviso ? '#ef4444' : 'transparent', cursor: 'pointer', fontSize: 10, color: stato.ultimoAvviso ? '#fff' : 'transparent' }}
                        >✓</button>
                        <button 
                          onClick={() => toggleStato(abb.id, 'pagato', mese, anno)} 
                          title={stato.dataPagato ? `Pagato: ${new Date(stato.dataPagato).toLocaleDateString('it-IT')}` : 'Segna come pagato'}
                          style={{ width: 24, height: 24, border: `2px solid ${stato.pagato ? '#22c55e' : bord}`, borderRadius: 4, background: stato.pagato ? '#22c55e' : 'transparent', cursor: 'pointer', fontSize: 10, color: stato.pagato ? '#fff' : 'transparent' }}
                        >✓</button>
                        <button onClick={() => setWhatsappModal(abb)} style={{ background: 'transparent', border: 'none', fontSize: 14, cursor: 'pointer', opacity: abb.telefono ? 1 : 0.3 }} disabled={!abb.telefono}>💬</button>
                      </div>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                        <button onClick={() => setCredenzialiModal(abb)} style={{ background: 'transparent', border: 'none', fontSize: 12, cursor: 'pointer', opacity: (abb.utente || abb.password) ? 1 : 0.3 }}>🔑</button>
                        <button onClick={() => setEditModal(abb)} style={{ background: 'transparent', border: 'none', fontSize: 12, cursor: 'pointer', opacity: 0.6 }}>✏️</button>
                        <button onClick={() => eliminaAbbonamento(abb.id)} style={{ background: 'transparent', border: 'none', fontSize: 14, color: '#ef4444', cursor: 'pointer', opacity: 0.5 }}>×</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
              </div>
            )}
          </div>
        );
      })}

      {/* Modal conferma eliminazione anno */}
      {confermaEliminaAnno && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setConfermaEliminaAnno(null)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 400, padding: 24 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700, color: '#ef4444' }}>⚠️ Eliminare Anno {confermaEliminaAnno}?</h3>
            <p style={{ margin: '0 0 20px', fontSize: 14, color: muted }}>
              Verranno eliminati tutti gli stati (pagato, avvisato, ecc.) degli abbonamenti per l'anno {confermaEliminaAnno}.<br/><br/>
              <strong>Gli abbonamenti stessi NON verranno eliminati</strong> e continueranno ad apparire negli altri anni.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setConfermaEliminaAnno(null)} style={{ flex: 1, padding: 14, background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 10, color: muted, fontSize: 14, cursor: 'pointer' }}>Annulla</button>
              <button onClick={() => eliminaAnno(confermaEliminaAnno)} style={{ flex: 1, padding: 14, background: '#ef4444', border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>🗑️ Elimina</button>
            </div>
          </div>
        </div>
      )}

      {abbonamenti.length === 0 && (
        <div style={{ padding: 60, textAlign: 'center', color: muted }}>
          Nessun abbonamento.<br />Clicca "+ Nuovo" per aggiungere il primo!
        </div>
      )}

      {/* Modal Modifica/Aggiungi */}
      {editModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setEditModal(null)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 500, padding: 24, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700 }}>{editModal.id ? '✏️ Modifica' : '➕ Nuovo'} Abbonamento</h3>
            
            <div style={{ display: 'grid', gap: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: muted, marginBottom: 6 }}>Nome Cliente *</label>
                <input type="text" value={editModal.nomeCliente} onChange={e => setEditModal({ ...editModal, nomeCliente: e.target.value })} style={{ width: '100%', padding: '12px 14px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: muted, marginBottom: 6 }}>Riferimento</label>
                <input type="text" value={editModal.riferimento} onChange={e => setEditModal({ ...editModal, riferimento: e.target.value })} placeholder="Es: Antivirus, Office 365..." style={{ width: '100%', padding: '12px 14px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: muted, marginBottom: 6 }}>Telefono (WhatsApp)</label>
                <input type="tel" value={editModal.telefono} onChange={e => setEditModal({ ...editModal, telefono: e.target.value })} placeholder="333 1234567" style={{ width: '100%', padding: '12px 14px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: muted, marginBottom: 6 }}>Costo € *</label>
                  <input type="number" value={editModal.costo} onChange={e => setEditModal({ ...editModal, costo: e.target.value })} style={{ width: '100%', padding: '12px 14px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: muted, marginBottom: 6 }}>Giorno Scadenza *</label>
                  <input type="number" min="1" max="31" value={editModal.giornoScadenza} onChange={e => setEditModal({ ...editModal, giornoScadenza: e.target.value })} style={{ width: '100%', padding: '12px 14px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14, boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: muted, marginBottom: 6 }}>Frequenza *</label>
                  <select value={editModal.frequenza} onChange={e => setEditModal({ ...editModal, frequenza: e.target.value })} style={{ width: '100%', padding: '12px 14px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14 }}>
                    <option value="mensile">Mensile</option>
                    <option value="trimestrale">Trimestrale</option>
                    <option value="semestrale">Semestrale</option>
                    <option value="annuale">Annuale</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: muted, marginBottom: 6 }}>Mese Inizio *</label>
                  <select 
                    value={editModal.meseCreazione ?? new Date().getMonth()} 
                    onChange={e => setEditModal({ ...editModal, meseCreazione: parseInt(e.target.value) })} 
                    style={{ width: '100%', padding: '12px 14px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14 }}
                  >
                    {['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'].map((nome, i) => (
                      <option key={i} value={i}>{nome}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div style={{ borderTop: '1px solid ' + bord, paddingTop: 16, marginTop: 8 }}>
                <p style={{ fontSize: 12, color: muted, marginBottom: 12 }}>🔐 Credenziali (opzionale)</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: muted, marginBottom: 6 }}>Utente</label>
                    <input type="text" value={editModal.utente} onChange={e => setEditModal({ ...editModal, utente: e.target.value })} style={{ width: '100%', padding: '12px 14px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: muted, marginBottom: 6 }}>Password</label>
                    <input type="text" value={editModal.password} onChange={e => setEditModal({ ...editModal, password: e.target.value })} style={{ width: '100%', padding: '12px 14px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14, boxSizing: 'border-box' }} />
                  </div>
                </div>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              <button onClick={() => setEditModal(null)} style={{ flex: 1, padding: 14, background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 10, color: muted, fontSize: 14, cursor: 'pointer' }}>Annulla</button>
              <button onClick={() => salvaAbbonamento({ ...editModal, costo: parseFloat(editModal.costo) || 0, giornoScadenza: parseInt(editModal.giornoScadenza) || 1 })} style={{ flex: 1, padding: 14, background: 'linear-gradient(135deg,#22c55e,#16a34a)', border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>✓ Salva</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Credenziali */}
      {credenzialiModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => { setCredenzialiModal(null); setShowPassword(false); }}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 400, padding: 24 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700 }}>🔑 Credenziali</h3>
            <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>{credenzialiModal.nomeCliente}</p>
            
            <div style={{ background: bgInput, borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ marginBottom: 12 }}>
                <span style={{ display: 'block', fontSize: 11, color: muted, marginBottom: 4 }}>👤 Utente</span>
                <span style={{ fontSize: 14, fontFamily: 'monospace' }}>{credenzialiModal.utente || 'Non impostato'}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <span style={{ display: 'block', fontSize: 11, color: muted, marginBottom: 4 }}>🔒 Password</span>
                  <span style={{ fontSize: 14, fontFamily: 'monospace' }}>{showPassword ? (credenzialiModal.password || 'Non impostata') : '••••••••'}</span>
                </div>
                <button onClick={() => setShowPassword(!showPassword)} style={{ background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 14 }}>{showPassword ? '🙈' : '👁️'}</button>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => copiaCredenziali(credenzialiModal)} style={{ flex: 1, padding: 14, background: 'linear-gradient(135deg,#3b82f6,#2563eb)', border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>{copiato ? '✓ Copiato!' : '📋 Copia Credenziali'}</button>
              <button onClick={() => { setCredenzialiModal(null); setShowPassword(false); }} style={{ padding: 14, background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 10, color: muted, fontSize: 14, cursor: 'pointer' }}>Chiudi</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal WhatsApp */}
      {whatsappModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setWhatsappModal(null)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 400, padding: 24, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700 }}>💬 Invia WhatsApp</h3>
            <p style={{ fontSize: 14, marginBottom: 16 }}><strong>{whatsappModal.nomeCliente}</strong> • 📱 {whatsappModal.telefono}</p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button onClick={() => apriWhatsApp(whatsappModal, 'primo')} style={{ padding: 16, background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.3)', borderRadius: 12, textAlign: 'left', cursor: 'pointer' }}>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#eab308', marginBottom: 4 }}>🟡 Primo Avviso</span>
                <span style={{ fontSize: 12, color: muted }}>Promemoria scadenza abbonamento</span>
              </button>
              <button onClick={() => apriWhatsApp(whatsappModal, 'secondo')} style={{ padding: 16, background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.3)', borderRadius: 12, textAlign: 'left', cursor: 'pointer' }}>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#f97316', marginBottom: 4 }}>🟠 Secondo Avviso</span>
                <span style={{ fontSize: 12, color: muted }}>Sollecito pagamento</span>
              </button>
              <button onClick={() => apriWhatsApp(whatsappModal, 'ultimo')} style={{ padding: 16, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 12, textAlign: 'left', cursor: 'pointer' }}>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#ef4444', marginBottom: 4 }}>🔴 Ultimo Avviso</span>
                <span style={{ fontSize: 12, color: muted }}>Preavviso sospensione servizio</span>
              </button>
              <button onClick={() => apriWhatsApp(whatsappModal, 'conferma')} style={{ padding: 16, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 12, textAlign: 'left', cursor: 'pointer' }}>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#22c55e', marginBottom: 4 }}>🟢 Conferma Pagamento</span>
                <span style={{ fontSize: 12, color: muted }}>Ringraziamento per il pagamento</span>
              </button>
              <button onClick={() => apriWhatsApp(whatsappModal, 'datiPagamento')} style={{ padding: 16, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 12, textAlign: 'left', cursor: 'pointer' }}>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#3b82f6', marginBottom: 4 }}>🔵 Invio Dati Pagamento</span>
                <span style={{ fontSize: 12, color: muted }}>IBAN e coordinate bancarie</span>
              </button>
              <button onClick={() => apriWhatsApp(whatsappModal, 'infoAbbonamento')} style={{ padding: 16, background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.3)', borderRadius: 12, textAlign: 'left', cursor: 'pointer' }}>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#8b5cf6', marginBottom: 4 }}>🟣 Info Abbonamento</span>
                <span style={{ fontSize: 12, color: muted }}>Riepilogo completo: scadenza, costo, frequenza</span>
              </button>
            </div>
            
            <button onClick={() => setWhatsappModal(null)} style={{ width: '100%', marginTop: 16, padding: 14, background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 10, color: muted, fontSize: 14, cursor: 'pointer' }}>Annulla</button>
          </div>
        </div>
      )}

      {/* Modal Inserimento Multiplo */}
      {importModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setImportModal(false)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 900, padding: 24, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 700 }}>➕ Inserimento Multiplo</h3>
            <p style={{ fontSize: 12, color: muted, marginBottom: 16 }}>
              Copia una colonna da Excel e incollala nel campo corrispondente. I valori si distribuiranno automaticamente sulle righe.<br/>
              <span style={{ color: '#3b82f6' }}>Campi obbligatori: Cliente, Costo (€), Giorno (Gg)</span>
            </p>
            
            {/* Colonne per incollare */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, marginBottom: 20 }}>
              {[
                { key: 'nomeCliente', label: 'Cliente *', placeholder: 'Mario Rossi\nLuigi Verdi\n...' },
                { key: 'riferimento', label: 'Riferimento', placeholder: 'Antivirus\nOffice 365\n...' },
                { key: 'telefono', label: 'Telefono', placeholder: '333123456\n338765432\n...' },
                { key: 'costo', label: 'Costo € *', placeholder: '30\n50\n...' },
                { key: 'giornoScadenza', label: 'Giorno *', placeholder: '15\n20\n...' },
                { key: 'utente', label: 'Utente', placeholder: 'user1\nuser2\n...' },
                { key: 'password', label: 'Password', placeholder: 'pwd1\npwd2\n...' },
              ].map(col => (
                <div key={col.key}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: muted, marginBottom: 4 }}>{col.label}</label>
                  <textarea
                    placeholder={col.placeholder}
                    style={{ width: '100%', height: 120, padding: 8, background: bgInput, border: '1px solid ' + bord, borderRadius: 8, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 11, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'monospace' }}
                    value={importRows.map(r => r[col.key] || '').join('\n')}
                    onChange={e => {
                      const valori = e.target.value.split('\n');
                      const maxRighe = Math.max(valori.length, importRows.length);
                      const nuoveRighe = [];
                      for (let i = 0; i < maxRighe; i++) {
                        nuoveRighe.push({
                          ...(importRows[i] || { nomeCliente: '', riferimento: '', telefono: '', costo: '', giornoScadenza: '', frequenza: 'mensile', meseCreazione: new Date().getMonth(), utente: '', password: '' }),
                          [col.key]: valori[i] !== undefined ? valori[i] : (importRows[i]?.[col.key] || '')
                        });
                      }
                      setImportRows(nuoveRighe);
                    }}
                  />
                </div>
              ))}
              
              {/* Frequenza - dropdown singolo per tutti */}
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: muted, marginBottom: 4 }}>Frequenza (tutti)</label>
                <select 
                  style={{ width: '100%', padding: 10, background: bgInput, border: '1px solid ' + bord, borderRadius: 8, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 12 }}
                  value={importRows[0]?.frequenza || 'mensile'}
                  onChange={e => setImportRows(importRows.map(r => ({ ...r, frequenza: e.target.value })))}
                >
                  <option value="mensile">Mensile</option>
                  <option value="trimestrale">Trimestrale</option>
                  <option value="semestrale">Semestrale</option>
                  <option value="annuale">Annuale</option>
                </select>
                
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: muted, marginBottom: 4, marginTop: 12 }}>Mese Inizio (tutti)</label>
                <select 
                  style={{ width: '100%', padding: 10, background: bgInput, border: '1px solid ' + bord, borderRadius: 8, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 12 }}
                  value={importRows[0]?.meseCreazione ?? new Date().getMonth()}
                  onChange={e => setImportRows(importRows.map(r => ({ ...r, meseCreazione: parseInt(e.target.value) })))}
                >
                  {['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'].map((m, i) => <option key={i} value={i}>{m}</option>)}
                </select>
              </div>
            </div>
            
            {/* Anteprima */}
            <div style={{ background: dark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.03)', borderRadius: 12, padding: 12, marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: muted, marginBottom: 8 }}>📋 Anteprima ({importRows.filter(r => r.nomeCliente?.trim() && r.costo && r.giornoScadenza).length} righe valide)</div>
              <div style={{ maxHeight: 150, overflowY: 'auto', fontSize: 11 }}>
                {importRows.filter(r => r.nomeCliente?.trim()).slice(0, 10).map((r, i) => (
                  <div key={i} style={{ padding: '6px 0', borderBottom: '1px solid ' + bord, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600 }}>{r.nomeCliente}</span>
                    {r.riferimento && <span style={{ color: muted }}>• {r.riferimento}</span>}
                    {r.costo && <span style={{ color: '#22c55e' }}>• €{r.costo}</span>}
                    {r.giornoScadenza && <span style={{ color: '#f59e0b' }}>• Gg {r.giornoScadenza}</span>}
                  </div>
                ))}
                {importRows.filter(r => r.nomeCliente?.trim()).length > 10 && (
                  <div style={{ padding: '6px 0', color: muted, fontStyle: 'italic' }}>... e altri {importRows.filter(r => r.nomeCliente?.trim()).length - 10}</div>
                )}
              </div>
            </div>
            
            {/* Pulsanti */}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setImportRows(Array(5).fill(null).map(() => ({ nomeCliente: '', riferimento: '', telefono: '', costo: '', giornoScadenza: '', frequenza: 'mensile', meseCreazione: new Date().getMonth(), utente: '', password: '' })))} style={{ padding: '12px 20px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, color: '#ef4444', fontSize: 13, cursor: 'pointer' }}>🗑️ Svuota</button>
              <button onClick={() => { setImportModal(false); setImportRows(Array(5).fill(null).map(() => ({ nomeCliente: '', riferimento: '', telefono: '', costo: '', giornoScadenza: '', frequenza: 'mensile', meseCreazione: new Date().getMonth(), utente: '', password: '' }))); }} style={{ padding: '12px 24px', background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 10, color: muted, fontSize: 14, cursor: 'pointer' }}>Annulla</button>
              <button onClick={() => {
                const validi = importRows.filter(r => r.nomeCliente?.trim() && r.costo && r.giornoScadenza);
                if (validi.length === 0) { alert('Nessuna riga valida da salvare!'); return; }
                const nuovi = validi.map(r => ({
                  id: Date.now().toString() + Math.random(),
                  nomeCliente: (r.nomeCliente || '').trim(),
                  riferimento: (r.riferimento || '').trim(),
                  telefono: (r.telefono || '').trim(),
                  costo: parseFloat(r.costo) || 0,
                  giornoScadenza: parseInt(r.giornoScadenza) || 1,
                  frequenza: r.frequenza || 'mensile',
                  meseCreazione: r.meseCreazione ?? new Date().getMonth(),
                  annoCreazione: r.annoCreazione ?? new Date().getFullYear(),
                  utente: (r.utente || '').trim(),
                  password: (r.password || '').trim(),
                  statiMensili: {}
                }));
                const abbList = data.abbonamenti || [];
                save({ ...data, abbonamenti: [...abbList, ...nuovi] });
                setImportModal(false);
                setImportRows(Array(5).fill(null).map(() => ({ nomeCliente: '', riferimento: '', telefono: '', costo: '', giornoScadenza: '', frequenza: 'mensile', meseCreazione: new Date().getMonth(), utente: '', password: '' })));
                alert(`${nuovi.length} abbonamenti inseriti!`);
              }} style={{ padding: '12px 24px', background: 'linear-gradient(135deg,#22c55e,#16a34a)', border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>✓ Salva ({importRows.filter(r => r.nomeCliente?.trim() && r.costo && r.giornoScadenza).length})</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Pagamento - Selezione Conto */}
      {pagamentoModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setPagamentoModal(null)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 400, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '20px', borderBottom: '1px solid ' + bord }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>💰 Conferma Pagamento</h3>
              <p style={{ margin: '8px 0 0', fontSize: 13, color: muted }}>
                {pagamentoModal.abbonamento.nomeCliente} - <strong style={{ color: '#22c55e' }}>{fmtCur(pagamentoModal.abbonamento.costo)}</strong>
              </p>
            </div>
            <div style={{ padding: 16 }}>
              <p style={{ fontSize: 12, color: muted, marginBottom: 12 }}>Dove vuoi registrare il pagamento?</p>
              
              {/* Conti predefiniti */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {CONTI_PAGAMENTO.map(conto => {
                  const contoEsiste = data.entrate.some(e => e.nome === conto.nome);
                  return (
                    <button 
                      key={conto.nome}
                      onClick={() => confermaPagamento(conto.nome)}
                      disabled={!contoEsiste}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 12, 
                        padding: '14px 16px', 
                        background: contoEsiste ? (dark ? 'rgba(34,197,94,0.1)' : 'rgba(34,197,94,0.05)') : (dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
                        border: '1px solid ' + (contoEsiste ? 'rgba(34,197,94,0.3)' : bord),
                        borderRadius: 12, 
                        cursor: contoEsiste ? 'pointer' : 'not-allowed',
                        opacity: contoEsiste ? 1 : 0.5,
                        width: '100%',
                        textAlign: 'left'
                      }}
                    >
                      <span style={{ fontSize: 24 }}>{conto.icon}</span>
                      <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: dark ? '#e2e8f0' : '#1e293b' }}>{conto.nome}</span>
                      {contoEsiste && <span style={{ color: '#22c55e', fontSize: 12 }}>+{fmtCur(pagamentoModal.abbonamento.costo)}</span>}
                    </button>
                  );
                })}
              </div>

              {/* Separatore */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0' }}>
                <div style={{ flex: 1, height: 1, background: bord }}></div>
                <span style={{ fontSize: 11, color: muted }}>oppure scegli un altro conto</span>
                <div style={{ flex: 1, height: 1, background: bord }}></div>
              </div>

              {/* Altri conti */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 150, overflow: 'auto' }}>
                {data.entrate
                  .filter(e => !CONTI_PAGAMENTO.some(c => c.nome === e.nome))
                  .map(entrata => (
                    <button 
                      key={entrata.id}
                      onClick={() => confermaPagamento(entrata.nome)}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        padding: '10px 14px', 
                        background: dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                        border: '1px solid ' + bord,
                        borderRadius: 8, 
                        cursor: 'pointer',
                        width: '100%'
                      }}
                    >
                      <span style={{ fontSize: 13, color: dark ? '#e2e8f0' : '#1e293b' }}>{entrata.nome}</span>
                      <span style={{ color: '#22c55e', fontSize: 11 }}>+{fmtCur(pagamentoModal.abbonamento.costo)}</span>
                    </button>
                  ))
                }
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, padding: '16px 20px', borderTop: '1px solid ' + bord }}>
              <button onClick={() => setPagamentoModal(null)} style={{ flex: 1, padding: 12, background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 10, color: muted, cursor: 'pointer' }}>Annulla</button>
              <button 
                onClick={() => {
                  // Segna come pagato senza sommare a nessun conto
                  const { abbonamento, mese, anno } = pagamentoModal;
                  const chiave = `${mese}-${anno}`;
                  save({ 
                    ...data, 
                    abbonamenti: (data.abbonamenti || []).map(a => {
                      if (a.id !== abbonamento.id) return a;
                      const statiMensili = { ...(a.statiMensili || {}) };
                      // FIX: si marca esplicitamente che non c'è nulla da stornare,
                      // così togliendo poi la spunta non compare l'avviso sul conto mancante
                      statiMensili[chiave] = { ...statiMensili[chiave], pagato: true, incassatoSu: null, importoIncassato: 0 };
                      return { ...a, statiMensili };
                    })
                  });
                  setPagamentoModal(null);
                }} 
                style={{ flex: 1, padding: 12, background: 'rgba(245,158,11,0.2)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10, color: '#f59e0b', fontWeight: 500, cursor: 'pointer' }}
              >
                Solo Segna Pagato
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function PromemoriaTab({ data, save, del, dark, bgCard, bgInput, bord, muted }) {
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ titolo: '', descrizione: '', dataOra: '', ripeti: 'mai' });

  const promemoria = data.promemoria || [];

  // Ordina per data (più vicini prima)
  const promemoriaSorted = [...promemoria].sort((a, b) => new Date(a.dataOra) - new Date(b.dataOra));

  // Promemoria scaduti (data passata e non completati)
  const scaduti = promemoriaSorted.filter(p => new Date(p.dataOra) < new Date() && !p.completato);
  // Promemoria futuri
  const futuri = promemoriaSorted.filter(p => new Date(p.dataOra) >= new Date() && !p.completato);
  // Promemoria completati
  const completati = promemoriaSorted.filter(p => p.completato);

  const resetForm = () => {
    setForm({ titolo: '', descrizione: '', dataOra: '', ripeti: 'mai' });
    setEditingId(null);
  };

  const openNew = () => {
    resetForm();
    // Imposta data/ora predefinita a domani alle 9:00
    const domani = new Date();
    domani.setDate(domani.getDate() + 1);
    domani.setHours(9, 0, 0, 0);
    setForm({ ...form, dataOra: domani.toISOString().slice(0, 16) });
    setShowModal(true);
  };

  const openEdit = (p) => {
    setForm({ titolo: p.titolo, descrizione: p.descrizione || '', dataOra: p.dataOra, ripeti: p.ripeti || 'mai' });
    setEditingId(p.id);
    setShowModal(true);
  };

  const salva = () => {
    if (!form.titolo.trim() || !form.dataOra) {
      alert('Inserisci titolo e data/ora');
      return;
    }

    const nuovoPromemoria = {
      id: editingId || Date.now().toString(),
      titolo: form.titolo.trim(),
      descrizione: form.descrizione.trim(),
      dataOra: form.dataOra,
      ripeti: form.ripeti,
      completato: false,
      notificato: false
    };

    if (editingId) {
      save({ ...data, promemoria: promemoria.map(p => p.id === editingId ? nuovoPromemoria : p) });
    } else {
      save({ ...data, promemoria: [...promemoria, nuovoPromemoria] });
    }

    setShowModal(false);
    resetForm();
  };

  const elimina = (id) => {
    if (window.confirm('Eliminare questo promemoria?')) {
      // FIX: passa da del(), che cancella anche dal database. Prima filtrava solo
      // l'array locale e il promemoria tornava al primo sync automatico.
      del('promemoria', id);
    }
  };

  const toggleCompletato = (id) => {
    save({ ...data, promemoria: promemoria.map(p => p.id === id ? { ...p, completato: !p.completato } : p) });
  };

  const posticipa = (id, minuti) => {
    const p = promemoria.find(x => x.id === id);
    if (!p) return;
    const nuovaData = new Date(p.dataOra);
    nuovaData.setMinutes(nuovaData.getMinutes() + minuti);
    save({ ...data, promemoria: promemoria.map(x => x.id === id ? { ...x, dataOra: nuovaData.toISOString().slice(0, 16), notificato: false } : x) });
  };

  const formatDataOra = (dataOra) => {
    const d = new Date(dataOra);
    const oggi = new Date();
    const domani = new Date(); domani.setDate(domani.getDate() + 1);
    
    const isOggi = d.toDateString() === oggi.toDateString();
    const isDomani = d.toDateString() === domani.toDateString();
    
    const ora = d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    
    if (isOggi) return `Oggi alle ${ora}`;
    if (isDomani) return `Domani alle ${ora}`;
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getTimeRemaining = (dataOra) => {
    const diff = new Date(dataOra) - new Date();
    if (diff < 0) return 'Scaduto';
    
    const minuti = Math.floor(diff / 60000);
    const ore = Math.floor(minuti / 60);
    const giorni = Math.floor(ore / 24);
    
    if (giorni > 0) return `tra ${giorni}g`;
    if (ore > 0) return `tra ${ore}h`;
    return `tra ${minuti}min`;
  };

  const renderPromemoria = (p, isScaduto = false) => (
    <div key={p.id} style={{ 
      background: p.completato ? (dark ? 'rgba(34,197,94,0.1)' : 'rgba(34,197,94,0.05)') : (isScaduto ? (dark ? 'rgba(239,68,68,0.1)' : 'rgba(239,68,68,0.05)') : bgInput), 
      borderRadius: 12, 
      padding: 14, 
      borderLeft: `4px solid ${p.completato ? '#22c55e' : (isScaduto ? '#ef4444' : '#3b82f6')}` 
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <button 
              onClick={() => toggleCompletato(p.id)}
              style={{ 
                width: 22, height: 22, borderRadius: 6, 
                border: `2px solid ${p.completato ? '#22c55e' : bord}`, 
                background: p.completato ? '#22c55e' : 'transparent', 
                cursor: 'pointer', 
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 12
              }}
            >
              {p.completato && '✓'}
            </button>
            <span style={{ fontSize: 15, fontWeight: 600, textDecoration: p.completato ? 'line-through' : 'none', color: p.completato ? muted : 'inherit' }}>{p.titolo}</span>
          </div>
          {p.descrizione && <p style={{ margin: '4px 0 0 30px', fontSize: 12, color: muted }}>{p.descrizione}</p>}
          <div style={{ marginTop: 8, marginLeft: 30, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: isScaduto ? '#ef4444' : '#3b82f6', fontWeight: 500 }}>
              🕐 {formatDataOra(p.dataOra)}
            </span>
            {!p.completato && (
              <span style={{ fontSize: 10, padding: '2px 8px', background: isScaduto ? 'rgba(239,68,68,0.2)' : 'rgba(59,130,246,0.2)', borderRadius: 10, color: isScaduto ? '#ef4444' : '#3b82f6' }}>
                {getTimeRemaining(p.dataOra)}
              </span>
            )}
            {p.ripeti !== 'mai' && (
              <span style={{ fontSize: 10, padding: '2px 8px', background: 'rgba(139,92,246,0.2)', borderRadius: 10, color: '#8b5cf6' }}>
                🔄 {p.ripeti}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {isScaduto && !p.completato && (
            <>
              <button onClick={() => posticipa(p.id, 30)} style={{ padding: '6px 8px', background: 'rgba(245,158,11,0.2)', border: 'none', borderRadius: 6, color: '#f59e0b', fontSize: 10, cursor: 'pointer' }} title="Posticipa 30min">+30m</button>
              <button onClick={() => posticipa(p.id, 60)} style={{ padding: '6px 8px', background: 'rgba(245,158,11,0.2)', border: 'none', borderRadius: 6, color: '#f59e0b', fontSize: 10, cursor: 'pointer' }} title="Posticipa 1 ora">+1h</button>
              <button onClick={() => posticipa(p.id, 1440)} style={{ padding: '6px 8px', background: 'rgba(245,158,11,0.2)', border: 'none', borderRadius: 6, color: '#f59e0b', fontSize: 10, cursor: 'pointer' }} title="Posticipa 1 giorno">+1g</button>
            </>
          )}
          <button onClick={() => openEdit(p)} style={{ padding: '6px 8px', background: 'rgba(59,130,246,0.2)', border: 'none', borderRadius: 6, color: '#3b82f6', fontSize: 11, cursor: 'pointer' }}>✏️</button>
          <button onClick={() => elimina(p.id)} style={{ padding: '6px 8px', background: 'transparent', border: 'none', color: '#ef4444', fontSize: 14, cursor: 'pointer', opacity: 0.6 }}>×</button>
        </div>
      </div>
    </div>
  );

  return (
    <section style={{ background: bgCard, borderRadius: 16, border: '1px solid ' + bord, padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>🔔 PROMEMORIA</h2>
        <span style={{ fontSize: 12, color: muted }}>{futuri.length + scaduti.length} attivi</span>
      </div>

      {/* Scaduti */}
      {scaduti.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 12, fontWeight: 600, color: '#ef4444', marginBottom: 10, textTransform: 'uppercase' }}>⚠️ Scaduti ({scaduti.length})</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {scaduti.map(p => renderPromemoria(p, true))}
          </div>
        </div>
      )}

      {/* Prossimi */}
      {futuri.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 12, fontWeight: 600, color: '#3b82f6', marginBottom: 10, textTransform: 'uppercase' }}>📅 Prossimi ({futuri.length})</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {futuri.map(p => renderPromemoria(p, false))}
          </div>
        </div>
      )}

      {/* Completati */}
      {completati.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 12, fontWeight: 600, color: '#22c55e', marginBottom: 10, textTransform: 'uppercase' }}>✓ Completati ({completati.length})</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {completati.slice(0, 5).map(p => renderPromemoria(p, false))}
            {completati.length > 5 && (
              <div style={{ textAlign: 'center', color: muted, fontSize: 12, padding: 8 }}>
                ... e altri {completati.length - 5} completati
              </div>
            )}
          </div>
        </div>
      )}

      {promemoria.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: muted }}>
          <span style={{ fontSize: 48, display: 'block', marginBottom: 12, opacity: 0.5 }}>🔔</span>
          <p>Nessun promemoria</p>
          <p style={{ fontSize: 12 }}>Crea un promemoria per ricevere notifiche</p>
        </div>
      )}

      <button onClick={openNew} style={{ display: 'block', width: '100%', padding: 14, background: 'linear-gradient(135deg,#3b82f6,#2563eb)', border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 16 }}>+ Nuovo Promemoria</button>

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setShowModal(false)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 420, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '20px', borderBottom: '1px solid ' + bord }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{editingId ? '✏️ Modifica Promemoria' : '🔔 Nuovo Promemoria'}</h3>
            </div>
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6, color: muted }}>Titolo *</label>
                <input 
                  type="text" 
                  value={form.titolo} 
                  onChange={e => setForm({ ...form, titolo: e.target.value })}
                  placeholder="Es: Chiamare cliente"
                  style={{ width: '100%', padding: 12, background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14, boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6, color: muted }}>Descrizione</label>
                <textarea 
                  value={form.descrizione} 
                  onChange={e => setForm({ ...form, descrizione: e.target.value })}
                  placeholder="Dettagli opzionali..."
                  style={{ width: '100%', minHeight: 80, padding: 12, background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6, color: muted }}>Data e Ora *</label>
                <input 
                  type="datetime-local" 
                  value={form.dataOra} 
                  onChange={e => setForm({ ...form, dataOra: e.target.value })}
                  style={{ width: '100%', padding: 12, background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14, boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6, color: muted }}>Ripeti</label>
                <select 
                  value={form.ripeti} 
                  onChange={e => setForm({ ...form, ripeti: e.target.value })}
                  style={{ width: '100%', padding: 12, background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14 }}
                >
                  <option value="mai">Mai</option>
                  <option value="giornaliero">Ogni giorno</option>
                  <option value="settimanale">Ogni settimana</option>
                  <option value="mensile">Ogni mese</option>
                  <option value="annuale">Ogni anno</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, padding: '16px 20px', borderTop: '1px solid ' + bord }}>
              <button onClick={() => { setShowModal(false); resetForm(); }} style={{ flex: 1, padding: 12, background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 10, color: muted, cursor: 'pointer' }}>Annulla</button>
              <button onClick={salva} style={{ flex: 1, padding: 12, background: 'linear-gradient(135deg,#3b82f6,#2563eb)', border: 'none', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>💾 Salva</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// ==================== LABORATORIO TAB ====================
function LaboratorioTab({ data, save, del, dark, bgCard, bgInput, bord, muted, userId, supabase, loadData }) {
  console.log('🔧 LaboratorioTab - userId:', userId, 'supabase:', !!supabase);
  const [vista, setVista] = useState('attive'); // attive, completate, archivio, tutti
  const [searchLab, setSearchLab] = useState('');
  const [schedaModal, setSchedaModal] = useState(null); // null | 'nuova' | scheda esistente
  const [dettaglioModal, setDettaglioModal] = useState(null);
  const [incassoModal, setIncassoModal] = useState(null);
  const [stampaModal, setStampaModal] = useState(null);
  const [clienteModal, setClienteModal] = useState(null); // Per storico cliente
  const [clienteEditModal, setClienteEditModal] = useState(null); // Per modifica/nuovo cliente
  const [showFiltriAvanzati, setShowFiltriAvanzati] = useState(false);
  
  // Filtri avanzati
  const [filtroStato, setFiltroStato] = useState('');
  const [filtroDataDa, setFiltroDataDa] = useState('');
  const [filtroDataA, setFiltroDataA] = useState('');
  const [filtroImportoMin, setFiltroImportoMin] = useState('');
  const [filtroImportoMax, setFiltroImportoMax] = useState('');
  const [filtroDispositivo, setFiltroDispositivo] = useState('');
  
  const laboratorio = data.laboratorio || [];
  const clientiLab = data.clientiLab || [];
  
  // Stati possibili
  const STATI = {
    'nuovo': { label: 'Nuova', color: '#ec4899', icon: '🆕' },
    'in_lavorazione': { label: 'In lavorazione', color: '#3b82f6', icon: '🔵' },
    'in_attesa': { label: 'In attesa', color: '#f59e0b', icon: '🟡' },
    'attesa_pezzi': { label: 'Attesa pezzi', color: '#8b5cf6', icon: '🟣' },
    'ricontatto': { label: 'Richiesta ricontatto', color: '#06b6d4', icon: '📞' },
    'non_riparabile': { label: 'Non riparabile', color: '#ef4444', icon: '❌' },
    'pronto': { label: 'Pronta', color: '#22c55e', icon: '🟢' },
    'consegnato': { label: 'Consegnata', color: '#64748b', icon: '⚪' }
  };
  
  // Checklist standard per ingresso dispositivi
  const CHECKLIST_ITEMS = [
    { id: 'accensione', label: 'Si accende', icon: '⚡' },
    { id: 'display', label: 'Display OK', icon: '🖥️' },
    { id: 'tastiera', label: 'Tastiera OK', icon: '⌨️' },
    { id: 'touchpad', label: 'Touchpad/Mouse OK', icon: '🖱️' },
    { id: 'audio', label: 'Audio OK', icon: '🔊' },
    { id: 'wifi', label: 'WiFi OK', icon: '📶' },
    { id: 'batteria', label: 'Batteria OK', icon: '🔋' },
    { id: 'caricatore', label: 'Caricatore presente', icon: '🔌' },
    { id: 'graffi', label: 'Graffi/Danni visibili', icon: '⚠️' },
    { id: 'backup', label: 'Backup effettuato', icon: '💾' }
  ];
  
  // Tipi dispositivo
  const TIPI_DISPOSITIVO = ['PC Desktop', 'Notebook', 'Tablet', 'Smartphone', 'Stampante', 'Monitor', 'Altro'];
  
  // Messaggi WhatsApp predefiniti (usati come fallback)
  const MESSAGGI_DEFAULT = {
    'nuovo': 'Gentile {cliente}, abbiamo registrato la sua richiesta di assistenza. Analizzeremo il dispositivo e la contatteremo a breve. PC CARE NAPOLI - 081.191.73.939',
    'in_attesa': 'Gentile {cliente}, il suo dispositivo è stato preso in carico. La terremo aggiornata sullo stato della riparazione. PC CARE NAPOLI - 081.191.73.939',
    'in_lavorazione': 'Gentile {cliente}, il suo dispositivo è attualmente in lavorazione. PC CARE NAPOLI - 081.191.73.939',
    'attesa_pezzi': 'Gentile {cliente}, siamo in attesa dei pezzi di ricambio per il suo dispositivo. La contatteremo appena arrivati. PC CARE NAPOLI - 081.191.73.939',
    'ricontatto': 'Gentile {cliente}, abbiamo bisogno di ricontattarla riguardo il suo dispositivo. Può chiamarci al 081.191.73.939 o passare in negozio? Grazie. PC CARE NAPOLI',
    'non_riparabile': 'Gentile {cliente}, dopo un\'attenta analisi, purtroppo il suo dispositivo non risulta riparabile. Può passare a ritirarlo in negozio. Ci scusiamo per l\'inconveniente. PC CARE NAPOLI - 081.191.73.939',
    'pronto': 'Gentile {cliente}, il suo dispositivo è pronto per il ritiro! Può passare in Vico Acitillo 148, Napoli. PC CARE NAPOLI - 081.191.73.939',
    'consegnato': `Ciao {cliente}! 
È stato un piacere averti nel nostro negozio. 
La tua opinione è molto importante per noi e ci aiuterebbe tantissimo se volessi condividere la tua esperienza lasciandoci una breve recensione qui: https://g.page/r/CQc6dx4B7TMzEBE/review 
Ti siamo grati per il tuo tempo! ✨
PC CARE NAPOLI!`
  };
  
  // Usa messaggi salvati o default
  const MESSAGGI_WHATSAPP = data.messaggiLaboratorio || MESSAGGI_DEFAULT;
  
  // Stato per modal impostazioni messaggi
  const [showMessaggiModal, setShowMessaggiModal] = useState(false);
  
  // Stato per modal scelta messaggio WhatsApp
  const [whatsappLabModal, setWhatsappLabModal] = useState(null); // scheda da inviare
  
  // Genera ID scheda (ANNO-PROGRESSIVO)
  const generaIdScheda = () => {
    const anno = new Date().getFullYear();
    
    console.log(`🔧 generaIdScheda chiamato - laboratorio.length=${laboratorio.length}, laboratorioContatore=${data.laboratorioContatore}, laboratorioAnno=${data.laboratorioAnno}`);
    
    // Trova il numero massimo tra le schede esistenti per quest'anno
    let maxNumero = 0;
    laboratorio.forEach(s => {
      if (!s.id) return;
      
      // Gestisci ID nel formato corretto YYYY-NNN
      const parts = s.id.split('-');
      
      // Se l'ID è malformato (es: 2026-2026-0251), prova a estrarre comunque il numero
      let schedaAnno = null;
      let num = 0;
      
      if (parts.length === 2 && parts[0].length === 4) {
        // Formato corretto: 2026-001
        schedaAnno = parseInt(parts[0]);
        num = parseInt(parts[1]) || 0;
      } else if (parts.length === 3 && parts[0] === parts[1]) {
        // Formato errato: 2026-2026-0251 -> prendi l'ultimo numero
        schedaAnno = parseInt(parts[0]);
        num = parseInt(parts[2]) || 0;
      } else if (parts.length > 2) {
        // Altri formati errati: prendi il primo anno e l'ultimo numero
        schedaAnno = parseInt(parts[0]);
        num = parseInt(parts[parts.length - 1]) || 0;
      }
      
      // Usa anche il campo numero se presente
      if (s.numero && s.numero > num) {
        num = s.numero;
      }
      
      if (schedaAnno === anno && num > maxNumero) {
        maxNumero = num;
        console.log(`   📌 Trovata scheda ${s.id} con numero=${num}`);
      }
    });
    
    // Considera anche il contatore salvato nei dati (per sicurezza)
    const annoSalvato = data.laboratorioAnno || anno;
    if (annoSalvato === anno && (data.laboratorioContatore || 0) > maxNumero) {
      console.log(`   📌 Uso contatore salvato: ${data.laboratorioContatore} (maggiore di maxNumero ${maxNumero})`);
      maxNumero = data.laboratorioContatore;
    }
    
    const contatore = maxNumero + 1;
    
    console.log(`🔧 Generazione scheda: anno=${anno}, maxNumero=${maxNumero}, nuovo contatore=${contatore}`);
    
    return {
      id: `${anno}-${String(contatore).padStart(3, '0')}`,
      contatore,
      anno
    };
  };
  
  // Combina anagrafica clienti con statistiche dalle schede
  const clientiStats = useMemo(() => {
    // Mappa per statistiche dalle schede
    const statsMap = {};
    laboratorio.forEach(s => {
      const key = s.telefono?.trim();
      if (!key) return;
      if (!statsMap[key]) {
        statsMap[key] = {
          riparazioni: [],
          totaleSpeso: 0,
          ultimaVisita: null
        };
      }
      statsMap[key].riparazioni.push(s);
      statsMap[key].totaleSpeso += (s.importoFinale || s.preventivo || 0);
      const dataIngresso = new Date(s.dataIngresso);
      if (!statsMap[key].ultimaVisita || dataIngresso > statsMap[key].ultimaVisita) {
        statsMap[key].ultimaVisita = dataIngresso;
      }
    });
    
    // Combina con anagrafica
    return clientiLab.map(c => ({
      ...c,
      riparazioni: statsMap[c.telefono]?.riparazioni || [],
      totaleSpeso: statsMap[c.telefono]?.totaleSpeso || 0,
      ultimaVisita: statsMap[c.telefono]?.ultimaVisita || new Date(c.dataCreazione)
    })).sort((a, b) => (b.ultimaVisita || 0) - (a.ultimaVisita || 0));
  }, [laboratorio, clientiLab]);
  
  // Funzioni gestione clienti
  const salvaCliente = (cliente) => {
    let nuoviClienti = [...clientiLab];
    if (cliente.id) {
      // Modifica cliente esistente
      nuoviClienti = nuoviClienti.map(c => c.id === cliente.id ? { ...c, ...cliente } : c);
    } else {
      // Nuovo cliente
      nuoviClienti.push({
        ...cliente,
        id: Date.now().toString(),
        dataCreazione: new Date().toISOString()
      });
    }
    save({ ...data, clientiLab: nuoviClienti });
    setClienteEditModal(null);
  };
  
  const eliminaCliente = (clienteId) => {
    if (window.confirm('Eliminare questo cliente dall\'anagrafica? Le schede di riparazione NON verranno eliminate.')) {
      // FIX: passa da del(), che cancella anche dal database. Prima il cliente
      // eliminato dall'anagrafica del Laboratorio tornava al primo sync.
      del('clientiLab', clienteId);
      setClienteModal(null);
    }
  };
  
  // Ordinamento elenco (ricordato su questo dispositivo)
  const [ordinaLab, setOrdinaLab] = useState(() => {
    try { const v = JSON.parse(localStorage.getItem('pccare-ordina-lab')); if (v && v.campo) return v; } catch (e) {}
    return { campo: 'ingresso', verso: 'asc' };
  });
  useEffect(() => {
    try { localStorage.setItem('pccare-ordina-lab', JSON.stringify(ordinaLab)); } catch (e) {}
  }, [ordinaLab]);

  // Filtra schede per vista e filtri avanzati
  const schedeFiltrate = laboratorio.filter(s => {
    // Filtro ricerca testuale
    if (searchLab) {
      const q = searchLab.toLowerCase();
      const match = s.id?.toLowerCase().includes(q) ||
                    s.cliente?.toLowerCase().includes(q) ||
                    s.telefono?.includes(q) ||
                    s.dispositivo?.toLowerCase().includes(q) ||
                    s.marca?.toLowerCase().includes(q) ||
                    s.problema?.toLowerCase().includes(q) ||
                    s.diagnosi?.toLowerCase().includes(q) ||
                    s.note?.toLowerCase().includes(q);
      if (!match) return false;
    }
    
    // Filtro vista
    if (vista === 'nuove' && s.stato !== 'nuovo') return false;
    if (vista === 'attive' && !['in_attesa', 'in_lavorazione', 'attesa_pezzi', 'ricontatto'].includes(s.stato)) return false;
    if (vista === 'completate' && s.stato !== 'pronto') return false;
    if (vista === 'non_riparabili' && s.stato !== 'non_riparabile') return false;
    if (vista === 'archivio' && s.stato !== 'consegnato') return false;
    
    // Filtri avanzati
    if (filtroStato && s.stato !== filtroStato) return false;
    
    if (filtroDataDa) {
      const dataIngresso = new Date(s.dataIngresso);
      const dataDa = new Date(filtroDataDa);
      if (dataIngresso < dataDa) return false;
    }
    
    if (filtroDataA) {
      const dataIngresso = new Date(s.dataIngresso);
      const dataA = new Date(filtroDataA);
      dataA.setHours(23, 59, 59);
      if (dataIngresso > dataA) return false;
    }
    
    if (filtroImportoMin) {
      const importo = s.importoFinale || s.preventivo || 0;
      if (importo < parseFloat(filtroImportoMin)) return false;
    }
    
    if (filtroImportoMax) {
      const importo = s.importoFinale || s.preventivo || 0;
      if (importo > parseFloat(filtroImportoMax)) return false;
    }
    
    if (filtroDispositivo && s.dispositivo !== filtroDispositivo) return false;
    
    return true;
  }).sort((a, b) => {
    // Prima le urgenti
    if (a.urgente && !b.urgente) return -1;
    if (!a.urgente && b.urgente) return 1;
    const verso = ordinaLab.verso === 'desc' ? -1 : 1;
    if (ordinaLab.campo === 'consegna') {
      const ca = calcolaConsegnaPrevista(a);
      const cb = calcolaConsegnaPrevista(b);
      // Le schede senza data prevista vanno sempre in fondo
      if (ca && !cb) return -1;
      if (!ca && cb) return 1;
      if (ca && cb && ca - cb !== 0) return (ca - cb) * verso;
    }
    return (new Date(a.dataIngresso) - new Date(b.dataIngresso)) * verso;
  });
  
  // Contatori
  const countNuove = laboratorio.filter(s => s.stato === 'nuovo').length;
  const countAttive = laboratorio.filter(s => ['in_attesa', 'in_lavorazione', 'attesa_pezzi', 'ricontatto'].includes(s.stato)).length;
  const countPronte = laboratorio.filter(s => s.stato === 'pronto').length;
  const countNonRiparabili = laboratorio.filter(s => s.stato === 'non_riparabile').length;
  const countArchivio = laboratorio.filter(s => s.stato === 'consegnato').length;
  const countTotale = laboratorio.length;
  
  // Reset filtri avanzati
  const resetFiltri = () => {
    setFiltroStato('');
    setFiltroDataDa('');
    setFiltroDataA('');
    setFiltroImportoMin('');
    setFiltroImportoMax('');
    setFiltroDispositivo('');
    setSearchLab('');
  };
  
  // Verifica se ci sono filtri attivi
  const hasFiltriAttivi = filtroStato || filtroDataDa || filtroDataA || filtroImportoMin || filtroImportoMax || filtroDispositivo;
  
  // Salva nuova scheda
  const salvaScheda = (scheda) => {
    if (scheda.isNuova) {
      const { id, contatore, anno } = generaIdScheda();
      const nuovaScheda = {
        ...scheda,
        id,
        numero: contatore, // IMPORTANTE: numero progressivo per Supabase
        dataIngresso: new Date().toISOString(),
        stato: 'nuovo',
        storicoStati: [{ stato: 'nuovo', data: new Date().toISOString() }]
      };
      delete nuovaScheda.isNuova;
      
      // Salva o aggiorna cliente in anagrafica
      let nuoviClienti = [...(data.clientiLab || [])];
      const telefono = nuovaScheda.telefono?.trim();
      if (telefono) {
        const clienteEsistente = nuoviClienti.find(c => c.telefono === telefono);
        if (clienteEsistente) {
          // Aggiorna dati cliente se più recenti
          nuoviClienti = nuoviClienti.map(c => c.telefono === telefono ? {
            ...c,
            nome: nuovaScheda.cliente || c.nome,
            email: nuovaScheda.email || c.email,
            ultimaVisita: new Date().toISOString()
          } : c);
        } else {
          // Nuovo cliente
          nuoviClienti.push({
            id: Date.now().toString(),
            nome: nuovaScheda.cliente,
            telefono: telefono,
            email: nuovaScheda.email || '',
            dataCreazione: new Date().toISOString(),
            ultimaVisita: new Date().toISOString()
          });
        }
      }
      
      // Gestione voci in entrate
      let nuoveEntrate = [...data.entrate];
      let categorie = [...(data.categorieEntrate || [])];
      
      // Assicurati che la categoria riparazioni esista
      if (!categorie.find(c => c.id === 'riparazioni')) {
        categorie.push({ id: 'riparazioni', nome: 'Riparazioni', icon: '🔧', color: '#f59e0b' });
      }
      
      // Calcola importi
      const acconto = parseFloat(nuovaScheda.acconto) || 0;
      const importoTotale = nuovaScheda.importoFinale || nuovaScheda.preventivo || 0;
      // FIX: si scala solo l'acconto effettivamente registrato su un conto,
      // così il saldo qui e saldoDaIncassare() alla consegna coincidono sempre
      const saldo = importoTotale - accontoRegistrato(nuovaScheda);
      
      // Se c'è un acconto e un conto selezionato, somma l'acconto al conto
      if (acconto > 0 && nuovaScheda.accontoConto) {
        const contoId = nuovaScheda.accontoConto;
        const contoEsistente = nuoveEntrate.find(e => e.id === contoId);
        if (contoEsistente) {
          nuoveEntrate = nuoveEntrate.map(e => 
            e.id === contoId 
              ? { ...e, importo: (e.importo || 0) + acconto }
              : e
          );
        }
      }
      
      // Il saldo (importo - acconto) va in riparazioni come "da incassare" al ritiro
      if (saldo > 0) {
        const idEntrata = `lab_${id}`;
        nuoveEntrate.push({
          id: idEntrata,
          nome: `🔧 ${nuovaScheda.cliente || 'Riparazione'}`,
          importo: saldo,
          note: `Saldo scheda #${id}${accontoRegistrato(nuovaScheda) > 0 ? ` (acconto €${accontoRegistrato(nuovaScheda).toFixed(2)} già incassato)` : ''}`,
          categoria: 'riparazioni',
          isRiparazione: true,
          schedaId: id
        });
      }
      
      save({ 
        ...data, 
        laboratorio: [...laboratorio, nuovaScheda],
        laboratorioContatore: contatore,
        laboratorioAnno: anno,
        clientiLab: nuoviClienti,
        entrate: nuoveEntrate,
        categorieEntrate: categorie
      });
    } else {
      // Aggiorna anche anagrafica cliente se modificato
      let nuoviClienti = [...(data.clientiLab || [])];
      const telefono = scheda.telefono?.trim();
      if (telefono) {
        const clienteEsistente = nuoviClienti.find(c => c.telefono === telefono);
        if (clienteEsistente) {
          nuoviClienti = nuoviClienti.map(c => c.telefono === telefono ? {
            ...c,
            nome: scheda.cliente || c.nome,
            email: scheda.email || c.email
          } : c);
        } else {
          nuoviClienti.push({
            id: Date.now().toString(),
            nome: scheda.cliente,
            telefono: telefono,
            email: scheda.email || '',
            dataCreazione: new Date().toISOString(),
            ultimaVisita: new Date().toISOString()
          });
        }
      }
      
      // Trova scheda precedente per confrontare acconto
      const schedaPrecedente = laboratorio.find(s => s.id === scheda.id);
      const accontoPrecedente = parseFloat(schedaPrecedente?.acconto) || 0;
      const accontoNuovo = parseFloat(scheda.acconto) || 0;
      const contoPrecedente = schedaPrecedente?.accontoConto;
      const contoNuovo = scheda.accontoConto;
      
      // Gestione voci in entrate
      let nuoveEntrate = [...data.entrate];
      const idEntrata = `lab_${scheda.id}`;
      const importoTotale = scheda.importoFinale || scheda.preventivo || 0;
      // FIX: come alla creazione, si scala solo l'acconto registrato su un conto
      const saldo = importoTotale - accontoRegistrato(scheda);
      const voceEsistente = nuoveEntrate.find(e => e.id === idEntrata);
      
      if (scheda.stato !== 'consegnato') {
        // Gestione acconto: se cambia, aggiorna i conti
        if (accontoNuovo !== accontoPrecedente || contoNuovo !== contoPrecedente) {
          // Rimuovi dal conto precedente se c'era un acconto
          if (accontoPrecedente > 0 && contoPrecedente) {
            nuoveEntrate = nuoveEntrate.map(e => 
              e.id === contoPrecedente 
                ? { ...e, importo: (e.importo || 0) - accontoPrecedente }
                : e
            );
          }
          // Aggiungi al nuovo conto se c'è un acconto
          if (accontoNuovo > 0 && contoNuovo) {
            nuoveEntrate = nuoveEntrate.map(e => 
              e.id === contoNuovo 
                ? { ...e, importo: (e.importo || 0) + accontoNuovo }
                : e
            );
          }
        }
        
        // Aggiorna voce saldo
        if (saldo > 0) {
          if (voceEsistente) {
            nuoveEntrate = nuoveEntrate.map(e => e.id === idEntrata ? {
              ...e,
              nome: `🔧 ${scheda.cliente || 'Riparazione'}`,
              importo: saldo,
              note: `Saldo scheda #${scheda.id}${accontoRegistrato(scheda) > 0 ? ` (acconto €${accontoRegistrato(scheda).toFixed(2)} già incassato)` : ''}`
            } : e);
          } else {
            nuoveEntrate.push({
              id: idEntrata,
              nome: `🔧 ${scheda.cliente || 'Riparazione'}`,
              importo: saldo,
              note: `Saldo scheda #${scheda.id}${accontoRegistrato(scheda) > 0 ? ` (acconto €${accontoRegistrato(scheda).toFixed(2)} già incassato)` : ''}`,
              categoria: 'riparazioni',
              isRiparazione: true,
              schedaId: scheda.id
            });
          }
        } else if (voceEsistente) {
          nuoveEntrate = nuoveEntrate.filter(e => e.id !== idEntrata);
          // FIX: la voce va cancellata anche su Supabase, altrimenti torna al sync
          eliminaVoceEntrataSupabase(idEntrata);
        }
      }
      
      // Assicurati che la categoria riparazioni esista
      let categorie = [...(data.categorieEntrate || [])];
      if (!categorie.find(c => c.id === 'riparazioni')) {
        categorie.push({ id: 'riparazioni', nome: 'Riparazioni', icon: '🔧', color: '#f59e0b' });
      }
      
      save({ 
        ...data, 
        laboratorio: laboratorio.map(s => s.id === scheda.id ? scheda : s),
        clientiLab: nuoviClienti,
        entrate: nuoveEntrate,
        categorieEntrate: categorie
      });
    }
    setSchedaModal(null);
  };
  
  // Cambia stato
  const cambiaStato = (scheda, nuovoStato) => {
    const schedaAggiornata = {
      ...scheda,
      stato: nuovoStato,
      storicoStati: [...(scheda.storicoStati || []), { stato: nuovoStato, data: new Date().toISOString() }]
    };
    
    // Gestione timer lavorazione
    if (nuovoStato === 'in_lavorazione' && !scheda.timerStart) {
      schedaAggiornata.timerStart = Date.now();
    } else if (nuovoStato !== 'in_lavorazione' && scheda.timerStart) {
      // Accumula tempo lavorazione
      const tempoSessione = Date.now() - scheda.timerStart;
      schedaAggiornata.tempoLavorazione = (scheda.tempoLavorazione || 0) + tempoSessione;
      schedaAggiornata.timerStart = null;
    }
    
    let nuoveEntrate = [...data.entrate];
    const idEntrata = `lab_${scheda.id}`;
    
    if (nuovoStato === 'consegnato') {
      schedaAggiornata.dataConsegna = new Date().toISOString();
      // Se c'è un importo, apri modal incasso
      if (scheda.importoFinale > 0 || scheda.preventivo > 0) {
        setIncassoModal(schedaAggiornata);
        return;
      } else {
        // Rimuovi voce dalle entrate se importo 0
        nuoveEntrate = nuoveEntrate.filter(e => e.id !== idEntrata);
        // FIX: la voce va cancellata anche su Supabase, altrimenti torna al sync
        eliminaVoceEntrataSupabase(idEntrata);
      }
    }
    
    save({ ...data, laboratorio: laboratorio.map(s => s.id === scheda.id ? schedaAggiornata : s), entrate: nuoveEntrate });
    
    // Aggiorna il modal con la scheda aggiornata invece di chiuderlo
    setDettaglioModal(schedaAggiornata);
    
    // Invia WhatsApp automaticamente per il nuovo stato (se ha telefono)
    if (scheda.telefono && MESSAGGI_WHATSAPP[nuovoStato]) {
      setTimeout(() => inviaWhatsApp(schedaAggiornata, nuovoStato), 300);
    }
  };
  
  // Incassa e registra in entrate
  // FIX: syncTable esegue solo upsert e non cancella mai nulla dal database,
  // quindi ogni voce di entrata rimossa localmente va eliminata esplicitamente
  // da Supabase, altrimenti riappare al primo sync automatico.
  const eliminaVoceEntrataSupabase = async (idVoce) => {
    if (!userId || !supabase || !idVoce) return;
    try {
      await supabase.from('entrate').delete().eq('id', idVoce).eq('user_id', userId);
      console.log('   ✅ Voce entrata eliminata da Supabase:', idVoce);
    } catch (e) {
      console.error('❌ Errore eliminazione voce entrata:', idVoce, e);
    }
  };

  const confermaIncasso = async (scheda, contoId) => {
    // FIX: si incassa il SALDO, non l'importo pieno. L'acconto è già stato
    // sommato al conto al momento dell'accettazione della scheda (vedi salvaScheda),
    // e la voce "lab_" contiene già il solo saldo: sommare qui l'importo totale
    // faceva risultare in cassa l'acconto due volte.
    const importo = saldoDaIncassare(scheda);
    const oggi = new Date().toISOString();
    
    // Aggiorna scheda
    const schedaAggiornata = {
      ...scheda,
      stato: 'consegnato',
      dataConsegna: oggi,
      incassato: true,
      contoIncasso: contoId,
      storicoStati: [...(scheda.storicoStati || []), { stato: 'consegnato', data: oggi }]
    };
    
    // Trova il nome del conto
    const conto = data.entrate.find(e => e.id === contoId);
    const nomeContoIncasso = conto?.nome || 'Conto';
    
    // Rimuovi voce temporanea e aggiungi importo al conto scelto
    const idEntrata = `lab_${scheda.id}`;
    console.log('💰 Incasso - Rimozione voce entrata:', idEntrata);
    
    // Elimina da Supabase
    if (userId && supabase) {
      await supabase.from('entrate').delete().eq('id', idEntrata).eq('user_id', userId);
      console.log('   ✅ Eliminato da Supabase');
    }
    
    const nuoveEntrate = data.entrate
      .filter(e => e.id !== idEntrata) // Rimuovi voce temporanea
      .map(e => e.id === contoId ? { ...e, importo: (e.importo || 0) + importo } : e); // Somma al conto
    
    // Registra movimento
    const movimento = {
      id: Date.now().toString(),
      data: oggi,
      tipo: 'riparazione',
      descrizione: `${scheda.cliente} - ${scheda.dispositivo || 'Riparazione'} (#${scheda.numero})`,
      importo: importo,
      metodo: `Incassato su: ${nomeContoIncasso}`
    };
    const movimenti = [...(data.movimenti || []), movimento];
    
    save({ 
      ...data, 
      laboratorio: laboratorio.map(s => s.id === scheda.id ? schedaAggiornata : s),
      entrate: nuoveEntrate,
      movimenti
    });
    
    setIncassoModal(null);
    setDettaglioModal(null);
    
    // Invia WhatsApp automaticamente per consegnato (se ha telefono)
    if (scheda.telefono && MESSAGGI_WHATSAPP['consegnato']) {
      setTimeout(() => {
        const messaggio = MESSAGGI_WHATSAPP['consegnato'].replace('{cliente}', scheda.cliente || 'Cliente');
        const tel = (scheda.telefono || '').replace(/\D/g, '');
        if (tel) {
          window.open(`https://wa.me/39${tel}?text=${encodeURIComponent(messaggio)}`, '_blank');
        }
      }, 300);
    }
  };
  
  // Invia WhatsApp
  // Apri modal scelta messaggio WhatsApp
  const inviaWhatsApp = (scheda, stato) => {
    if (stato) {
      // Se viene passato uno stato specifico, invia direttamente
      const messaggio = MESSAGGI_WHATSAPP[stato]
        .replace('{cliente}', scheda.cliente || 'Cliente');
      const tel = (scheda.telefono || '').replace(/\D/g, '');
      if (tel) {
        window.open(`https://wa.me/39${tel}?text=${encodeURIComponent(messaggio)}`, '_blank');
      }
    } else {
      // Altrimenti apri il modal per scegliere
      setWhatsappLabModal(scheda);
    }
  };
  
  // Invia messaggio WhatsApp specifico
  const inviaWhatsAppMessaggio = (scheda, tipoMessaggio) => {
    const messaggio = MESSAGGI_WHATSAPP[tipoMessaggio]
      .replace('{cliente}', scheda.cliente || 'Cliente');
    const tel = (scheda.telefono || '').replace(/\D/g, '');
    if (tel) {
      window.open(`https://wa.me/39${tel}?text=${encodeURIComponent(messaggio)}`, '_blank');
    }
    setWhatsappLabModal(null);
  };
  
  // Elimina scheda
  const eliminaScheda = async (id) => {
    if (window.confirm('Eliminare questa scheda?')) {
      console.log(`🗑️ Tentativo eliminazione scheda: ${id}`);
      console.log(`🗑️ userId disponibile: ${userId}, supabase: ${!!supabase}`);
      
      // Trova la scheda per ottenere il numero
      const scheda = laboratorio.find(s => s.id === id);
      
      // Elimina da Supabase direttamente
      if (userId) {
        // Prova prima con l'ID
        let { error, data: deleted } = await supabase
          .from('riparazioni')
          .delete()
          .eq('id', id)
          .eq('user_id', userId)
          .select();
        
        console.log(`   Tentativo delete con id=${id}:`, deleted, error);
        
        // Se non ha funzionato e abbiamo il numero, prova con quello
        if ((!deleted || deleted.length === 0) && scheda?.numero) {
          console.log(`   Tentativo con numero=${scheda.numero}`);
          const result = await supabase
            .from('riparazioni')
            .delete()
            .eq('numero', scheda.numero)
            .eq('user_id', userId)
            .select();
          console.log(`   Risultato:`, result.data, result.error);
        }
        
        // Elimina anche l'entrata correlata se esiste
        const idEntrata = `lab_${id}`;
        await supabase.from('entrate').delete().eq('id', idEntrata).eq('user_id', userId);
        
        // Prova anche con scheda_id
        if (scheda?.numero) {
          await supabase.from('entrate').delete().eq('scheda_id', id).eq('user_id', userId);
        }
      }
      
      // Rimuovi dalla memoria locale
      const idEntrata = `lab_${id}`;
      const nuoveEntrate = (data.entrate || []).filter(e => e.id !== idEntrata && e.schedaId !== id);
      const nuovoLaboratorio = laboratorio.filter(s => s.id !== id);
      
      console.log(`   Schede prima: ${laboratorio.length}, dopo: ${nuovoLaboratorio.length}`);
      
      save({ ...data, laboratorio: nuovoLaboratorio, entrate: nuoveEntrate });
      setDettaglioModal(null);
    }
  };
  
  // Stampa etichetta termica 40x30mm
  const stampaEtichettaTermica = (scheda) => {
    const dataIngresso = new Date(scheda.dataIngresso).toLocaleDateString('it-IT');
    
    // Crea canvas per generare immagine
    const canvas = document.createElement('canvas');
    // 40mm x 30mm a 203 DPI (standard stampanti termiche) = 320 x 240 px
    canvas.width = 320;
    canvas.height = 240;
    const ctx = canvas.getContext('2d');
    
    // Sfondo bianco
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Testo nero
    ctx.fillStyle = '#000000';
    ctx.textBaseline = 'top';
    
    // Titolo - ID scheda
    ctx.font = 'bold 28px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('#' + scheda.id, canvas.width / 2, 10);
    
    // Linea separatore
    ctx.beginPath();
    ctx.moveTo(10, 45);
    ctx.lineTo(canvas.width - 10, 45);
    ctx.lineWidth = 2;
    ctx.stroke();
    
    // Dati - allineati a sinistra
    ctx.textAlign = 'left';
    ctx.font = 'bold 16px Arial';
    let y = 55;
    
    ctx.fillText('Data: ', 10, y);
    ctx.font = '16px Arial';
    ctx.fillText(dataIngresso, 70, y);
    y += 25;
    
    ctx.font = 'bold 16px Arial';
    ctx.fillText('Cliente: ', 10, y);
    ctx.font = '16px Arial';
    const cliente = (scheda.cliente || '-').substring(0, 20);
    ctx.fillText(cliente, 80, y);
    y += 25;
    
    ctx.font = 'bold 16px Arial';
    ctx.fillText('Tel: ', 10, y);
    ctx.font = '16px Arial';
    ctx.fillText(scheda.telefono || '-', 50, y);
    y += 25;
    
    ctx.font = 'bold 16px Arial';
    ctx.fillText('Disp: ', 10, y);
    ctx.font = '16px Arial';
    const disp = ((scheda.dispositivo || '') + ' ' + (scheda.marca || '')).substring(0, 22);
    ctx.fillText(disp, 60, y);
    y += 25;
    
    // Linea tratteggiata
    ctx.setLineDash([5, 3]);
    ctx.beginPath();
    ctx.moveTo(10, y);
    ctx.lineTo(canvas.width - 10, y);
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.setLineDash([]);
    y += 10;
    
    // Problema
    ctx.font = 'bold 14px Arial';
    ctx.fillText('Problema:', 10, y);
    y += 18;
    ctx.font = '13px Arial';
    const problema = (scheda.problema || '-').substring(0, 40);
    ctx.fillText(problema, 10, y);
    if ((scheda.problema || '').length > 40) {
      y += 16;
      ctx.fillText((scheda.problema || '').substring(40, 80) + '...', 10, y);
    }
    
    // Scarica PNG
    const link = document.createElement('a');
    link.download = 'etichetta_' + scheda.id + '.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  };
  
  // Stampa ricevuta (formato A4 con disclaimer)
  const stampaRicevuta = (scheda, tipo = 'ingresso') => {
    const dataIngresso = new Date(scheda.dataIngresso).toLocaleDateString('it-IT');
    const dataConsegna = scheda.dataConsegna ? new Date(scheda.dataConsegna).toLocaleDateString('it-IT') : '';
    
    // Calcola data consegna stimata
    const calcolaDataStimata = () => {
      if (!scheda.tempoStimato) return null;
      if (scheda.tempoStimato.startsWith('data:')) {
        return new Date(scheda.tempoStimato.replace('data:', '')).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
      }
      const oggi = new Date(scheda.dataIngresso || Date.now());
      switch (scheda.tempoStimato) {
        case 'stesso_giorno': return 'Stesso giorno';
        case '1_giorno': 
          oggi.setDate(oggi.getDate() + 1);
          return oggi.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
        case '2_3_giorni':
          oggi.setDate(oggi.getDate() + 3);
          return 'Entro ' + oggi.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
        case '1_settimana':
          oggi.setDate(oggi.getDate() + 7);
          return 'Entro ' + oggi.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
        case '2_settimane':
          oggi.setDate(oggi.getDate() + 14);
          return 'Entro ' + oggi.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
        case 'da_valutare': return 'Da comunicare';
        default: return null;
      }
    };
    const dataStimata = calcolaDataStimata();
    
    // HTML condizionali
    const serialeClienteHtml = scheda.seriale ? '<div style="font-size: 9px; color: #666;">S/N: ' + scheda.seriale + '</div>' : '';
    const accessoriClienteHtml = scheda.accessori ? '<div style="font-size: 9px; margin-top: 2px;">📦 Accessori: ' + scheda.accessori + '</div>' : '';
    
    // Checklist HTML
    let checklistHtml = '';
    if (scheda.checklist && Object.keys(scheda.checklist).length > 0) {
      const checklistLabels = {
        'accensione': 'Accensione', 'display': 'Display', 'tastiera': 'Tastiera', 'touchpad': 'Touchpad',
        'audio': 'Audio', 'wifi': 'WiFi', 'batteria': 'Batteria', 'caricatore': 'Caricatore',
        'graffi': 'Graffi/Danni', 'backup': 'Backup'
      };
      const items = Object.entries(scheda.checklist)
        .filter(([k, v]) => v !== null)
        .map(([key, val]) => {
          const label = checklistLabels[key] || key;
          const icon = val === true ? (key === 'graffi' ? '⚠️' : '✓') : '✗';
          const color = val === true ? (key === 'graffi' ? '#f59e0b' : '#22c55e') : '#ef4444';
          return '<span style="display:inline-block;padding:1px 4px;margin:1px;border-radius:3px;font-size:8px;background:' + color + '20;color:' + color + ';border:1px solid ' + color + '50;">' + icon + ' ' + label + '</span>';
        }).join('');
      if (items) {
        checklistHtml = '<div style="margin:6px 0;padding:6px;background:#f9f9f9;border:1px solid #ddd;border-radius:3px;"><div style="font-size:7px;font-weight:bold;color:#666;text-transform:uppercase;margin-bottom:3px;">📋 Checklist Ingresso</div><div>' + items + '</div>' + (scheda.noteChecklist ? '<div style="font-size:8px;color:#666;margin-top:4px;font-style:italic;">Note: ' + scheda.noteChecklist + '</div>' : '') + '</div>';
      }
    }
    
    let importoBoxHtml = '';
    if (tipo === 'consegna' && (scheda.importoFinale || scheda.preventivo)) {
      importoBoxHtml = '<div style="text-align:center;font-size:14px;font-weight:bold;padding:8px;background:#e8f5e9;border:2px solid #4caf50;margin:8px 0;border-radius:3px;">TOTALE: €' + (scheda.importoFinale || scheda.preventivo || 0).toFixed(2) + '</div>';
    } else if (tipo === 'ingresso' && scheda.preventivo) {
      importoBoxHtml = '<div style="margin:6px 0;padding:6px;background:#f0f9ff;border:1px solid #3b82f6;border-radius:3px;"><div style="font-size:7px;font-weight:bold;color:#3b82f6;text-transform:uppercase;">Preventivo stimato</div><div style="font-size:12px;font-weight:bold;color:#1e40af;">€' + scheda.preventivo.toFixed(2) + '</div></div>';
    }
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Impossibile aprire la finestra di stampa. Controlla che i popup non siano bloccati.');
      return;
    }
    
    const titoloRicevuta = tipo === 'ingresso' ? 'INGRESSO' : 'CONSEGNA';
    const titoloProblema = tipo === 'ingresso' ? 'Problema segnalato' : 'Intervento eseguito';
    const contenutoProblema = tipo === 'ingresso' ? (scheda.problema || '-') : (scheda.diagnosi || scheda.problema || '-');
    const campo4Label = tipo === 'consegna' ? 'Data Consegna' : 'Email';
    const campo4Value = tipo === 'consegna' ? dataConsegna : (scheda.email || '-');
    const footerMsg = tipo === 'consegna' ? 'Grazie per aver scelto PC CARE NAPOLI!' : 'Conservare questo documento per il ritiro del dispositivo.';
    
    printWindow.document.write('<!DOCTYPE html><html><head><title>Ricevuta ' + titoloRicevuta + ' - ' + scheda.id + '</title>' +
      '<style>' +
      '@page { size: A4; margin: 8mm; }' +
      '* { margin: 0; padding: 0; box-sizing: border-box; }' +
      'body { font-family: Arial, sans-serif; font-size: 10px; line-height: 1.3; }' +
      '.page { width: 100%; max-width: 194mm; margin: 0 auto; }' +
      '.section { border: 2px solid #333; padding: 12px; }' +
      '.header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 8px; margin-bottom: 8px; }' +
      '.header h1 { font-size: 18px; margin-bottom: 3px; }' +
      '.header .info { font-size: 9px; color: #666; }' +
      '.header .scheda-id { font-size: 18px; font-weight: bold; background: #f0f0f0; padding: 5px 15px; display: inline-block; margin-top: 6px; border: 2px solid #333; }' +
      '.grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin: 8px 0; }' +
      '.field-label { font-size: 8px; color: #666; text-transform: uppercase; font-weight: bold; }' +
      '.field-value { font-size: 11px; font-weight: 500; border-bottom: 1px dotted #ccc; min-height: 14px; padding: 2px 0; }' +
      '.highlight-box { background: #f9f9f9; border: 1px solid #ddd; padding: 8px; margin: 6px 0; border-radius: 3px; }' +
      '.highlight-box .title { font-size: 9px; font-weight: bold; text-transform: uppercase; margin-bottom: 3px; color: #333; }' +
      '.disclaimer { margin-top: 12px; padding: 10px; background: #fff9e6; border: 1px solid #f0c000; border-radius: 3px; }' +
      '.disclaimer h3 { font-size: 9px; font-weight: bold; margin-bottom: 6px; color: #856404; text-transform: uppercase; }' +
      '.disclaimer ul { font-size: 8px; color: #555; padding-left: 12px; }' +
      '.disclaimer li { margin-bottom: 3px; line-height: 1.35; }' +
      '.firma-section { margin-top: 12px; display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }' +
      '.firma-box { text-align: center; }' +
      '.firma-box .label { font-size: 8px; color: #666; margin-bottom: 20px; }' +
      '.firma-box .line { border-bottom: 1px solid #333; height: 20px; }' +
      '.footer { text-align: center; font-size: 9px; color: #666; margin-top: 10px; padding-top: 8px; border-top: 1px solid #ccc; }' +
      '@media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }' +
      '</style></head><body>' +
      '<div class="page"><div class="section">' +
      '<div class="header">' +
      '<h1>🖥️ PC CARE NAPOLI</h1>' +
      '<div class="info">di Rispoli Oreste - Vico Acitillo, 148 - 80127 Napoli (NA)</div>' +
      '<div class="info">Tel: 081.191.73.939 | info@pccarenapoli.it | www.pccarenapoli.it</div>' +
      '<div class="scheda-id">#' + scheda.id + '</div>' +
      '<div style="font-size: 10px; margin-top: 5px; font-weight: bold;">RICEVUTA DI ' + titoloRicevuta + '</div>' +
      '</div>' +
      '<div class="grid">' +
      '<div class="field"><div class="field-label">Cliente</div><div class="field-value">' + (scheda.cliente || '-') + '</div></div>' +
      '<div class="field"><div class="field-label">Telefono</div><div class="field-value">' + (scheda.telefono || '-') + '</div></div>' +
      '<div class="field"><div class="field-label">Data Ingresso</div><div class="field-value">' + dataIngresso + '</div></div>' +
      '<div class="field"><div class="field-label">' + campo4Label + '</div><div class="field-value">' + campo4Value + '</div></div>' +
      (dataStimata && tipo === 'ingresso' ? '<div class="field" style="grid-column: span 2;"><div class="field-label">📅 Data Consegna Stimata</div><div class="field-value" style="font-weight:bold;color:#2563eb;">' + dataStimata + '</div></div>' : '') +
      '</div>' +
      '<div class="highlight-box">' +
      '<div class="title">📱 Dispositivo</div>' +
      '<div style="font-size: 11px;"><strong>' + (scheda.dispositivo || '') + ' ' + (scheda.marca || '') + ' ' + (scheda.modello || '') + '</strong></div>' +
      serialeClienteHtml + accessoriClienteHtml +
      '</div>' +
      checklistHtml +
      '<div class="highlight-box">' +
      '<div class="title">🔧 ' + titoloProblema + '</div>' +
      '<div style="font-size: 10px;">' + contenutoProblema + '</div>' +
      '</div>' +
      importoBoxHtml +
      '<div class="disclaimer">' +
      '<h3>⚠️ Termini e Condizioni del Servizio</h3>' +
      '<ul>' +
      '<li><strong>PERDITA DATI:</strong> PC Care Napoli non è responsabile per eventuali perdite di dati durante l\'intervento di riparazione. Si consiglia al cliente di effettuare un backup preventivo dei propri dati prima della consegna del dispositivo.</li>' +
      '<li><strong>CONTENUTI DEL DISPOSITIVO:</strong> Il cliente dichiara che il dispositivo non contiene materiale illegale, incluso ma non limitato a materiale pedopornografico o altro contenuto illecito. PC Care Napoli è sollevata da qualsiasi responsabilità relativa ai contenuti presenti sul dispositivo.</li>' +
      '<li><strong>PREVENTIVO:</strong> Per interventi fino a €50,00 non verrà comunicato preventivo e si procederà direttamente alla riparazione. Per importi superiori a €50,00 verrà comunicato preventivo al cliente. In caso di mancata accettazione del preventivo, il cliente non dovrà corrispondere alcun importo.</li>' +
      '<li><strong>DISPOSITIVI NON RIPARABILI:</strong> In caso di dispositivo non riparabile o riparazione antieconomica, non verrà addebitato alcun costo al cliente.</li>' +
      '<li><strong>GARANZIA RICAMBI:</strong> I ricambi utilizzati hanno garanzia di 12 mesi. La garanzia non copre danni causati da uso improprio, cadute, liquidi, manomissioni o problemi software.</li>' +
      '<li><strong>GARANZIA INTERVENTO:</strong> L\'intervento effettuato ha garanzia di 90 giorni, limitatamente al difetto riparato.</li>' +
      '<li><strong>AMBITO DELL\'INTERVENTO:</strong> L\'intervento sarà effettuato esclusivamente sul difetto/problema indicato nella presente ricevuta. Eventuali altri difetti riscontrati non saranno di nostra responsabilità.</li>' +
      '<li><strong>PROBLEMI PREESISTENTI O SUCCESSIVI:</strong> PC Care Napoli non risponde di eventuali malfunzionamenti o problemi preesistenti non dichiarati, né di problemi che potrebbero manifestarsi successivamente all\'intervento e non direttamente correlati allo stesso.</li>' +
      '<li><strong>RITIRO:</strong> Il dispositivo dovrà essere ritirato entro 30 giorni dalla comunicazione di fine lavori. Trascorso tale termine, verranno addebitati costi di giacenza pari a €0,50 al giorno. I dispositivi non ritirati entro 90 giorni saranno considerati abbandonati e potranno essere smaltiti senza ulteriore preavviso.</li>' +
      '<li><strong>PRIVACY:</strong> Il cliente autorizza il trattamento dei dati personali ai sensi del GDPR per le finalità connesse al servizio di assistenza.</li>' +
      '<li><strong>LIMITAZIONE RESPONSABILITÀ:</strong> La responsabilità massima di PC Care Napoli è limitata al valore dell\'intervento effettuato.</li>' +
      '</ul></div>' +
      '<div class="firma-section">' +
      '<div class="firma-box"><div class="label">Firma del Cliente per accettazione</div><div class="line"></div></div>' +
      '<div class="firma-box"><div class="label">Timbro e Firma PC Care Napoli</div><div class="line"></div></div>' +
      '</div>' +
      '<div style="text-align: center; margin: 15px 0; padding: 12px; background: #f8f9fa; border: 1px solid #e2e8f0; border-radius: 8px;">' +
      '<div style="font-size: 10px; font-weight: bold; text-transform: uppercase; margin-bottom: 8px;">📱 Controlla lo stato della riparazione</div>' +
      '<img src="https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=https://gestpccare.it/stato" alt="QR Code" style="width: 80px; height: 80px;" />' +
      '<div style="font-size: 9px; color: #666; margin-top: 6px;">Scansiona il QR o vai su <strong>gestpccare.it/stato</strong></div>' +
      '<div style="font-size: 9px; color: #666;">Inserisci: Scheda <strong>#' + scheda.id + '</strong> + tuo numero di telefono</div>' +
      '</div>' +
      '<div class="footer">' +
      '<strong>' + footerMsg + '</strong>' +
      '<div style="margin-top: 3px; font-size: 8px;">Con la firma il cliente dichiara di aver letto e accettato i termini e le condizioni sopra riportate.</div>' +
      '</div>' +
      '</div></div>' +
      '<script>setTimeout(function(){window.print();},500);<\/script>' +
      '</body></html>'
    );
    printWindow.document.close();
  };
  // Conti disponibili per incasso (helper condiviso a livello di modulo)
  const contiDisponibili = getContiDisponibili(data);

  return (
    <section style={{ background: bgCard, borderRadius: 16, border: '1px solid ' + bord, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid ' + bord, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>🔧 LABORATORIO</h2>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: muted }}>
            {countNuove > 0 && <span style={{ color: '#ec4899' }}>{countNuove} nuove • </span>}
            {countAttive} in lavorazione • {countPronte} pronte • {countTotale} totali
            <span 
              style={{ marginLeft: 8, cursor: 'pointer', opacity: 0.5 }} 
              title={`Contatore: ${data.laboratorioContatore || 0} - Clicca per modificare`}
              onClick={() => {
                const nuovoVal = prompt('Imposta nuovo contatore (es: 99 per far partire da 100):', data.laboratorioContatore || 0);
                if (nuovoVal !== null && !isNaN(parseInt(nuovoVal))) {
                  save({ ...data, laboratorioContatore: parseInt(nuovoVal), laboratorioAnno: new Date().getFullYear() });
                  alert('Contatore impostato a ' + nuovoVal + '. La prossima scheda sarà #' + new Date().getFullYear() + '-' + String(parseInt(nuovoVal) + 1).padStart(3, '0'));
                }
              }}
            >
              [#{data.laboratorioAnno || new Date().getFullYear()}-{String((data.laboratorioContatore || 0) + 1).padStart(3, '0')}]
            </span>
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button 
            onClick={() => { 
              console.log('🔄 Sync manuale...'); 
              loadData(); 
            }}
            style={{ padding: '10px 14px', background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: '1px solid ' + bord, borderRadius: 10, color: muted, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
            title="Sincronizza dati da Supabase"
          >
            🔄
          </button>
          <button 
            onClick={() => setShowMessaggiModal(true)}
            style={{ padding: '10px 14px', background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: '1px solid ' + bord, borderRadius: 10, color: muted, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
            title="Modifica messaggi WhatsApp"
          >
            ⚙️ Messaggi
          </button>
          <button 
            onClick={() => setSchedaModal({ isNuova: true, dispositivo: 'Notebook', stato: 'nuovo' })}
            style={{ padding: '10px 20px', background: 'linear-gradient(135deg,#3b82f6,#2563eb)', border: 'none', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            + Nuova Scheda
          </button>
        </div>
      </div>
      
      {/* Filtri */}
      <div style={{ padding: '12px 20px', borderBottom: '1px solid ' + bord, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 4, background: dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', borderRadius: 8, padding: 4, flexWrap: 'wrap' }}>
          {[
            { id: 'nuove', label: `🆕 Nuove (${countNuove})`, color: '#ec4899' },
            { id: 'attive', label: `In corso (${countAttive})` },
            { id: 'completate', label: `Pronte (${countPronte})` },
            { id: 'non_riparabili', label: `❌ Non rip. (${countNonRiparabili})`, color: '#ef4444' },
            { id: 'archivio', label: `Archivio (${countArchivio})` },
            { id: 'tutti', label: `Tutti (${countTotale})` }
          ].map(v => (
            <button 
              key={v.id}
              onClick={() => { setVista(v.id); resetFiltri(); }}
              style={{ 
                padding: '8px 12px', 
                background: vista === v.id ? (v.color || '#3b82f6') : 'transparent', 
                border: 'none', 
                borderRadius: 6, 
                color: vista === v.id ? '#fff' : muted, 
                fontSize: 12, 
                fontWeight: 500,
                cursor: 'pointer' 
              }}
            >
              {v.label}
            </button>
          ))}
        </div>
        
        {vista !== 'clienti' && (
          <>
            <input 
              type="text"
              placeholder="🔍 Cerca cliente, ID, dispositivo..."
              value={searchLab}
              onChange={e => setSearchLab(e.target.value)}
              style={{ flex: 1, minWidth: 200, padding: '8px 12px', background: bgInput, border: '1px solid ' + bord, borderRadius: 8, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 13 }}
            />
            <button 
              onClick={() => setShowFiltriAvanzati(!showFiltriAvanzati)}
              style={{ 
                padding: '8px 12px', 
                background: hasFiltriAttivi ? 'rgba(59,130,246,0.2)' : (dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'), 
                border: hasFiltriAttivi ? '1px solid #3b82f6' : '1px solid ' + bord, 
                borderRadius: 8, 
                color: hasFiltriAttivi ? '#3b82f6' : muted, 
                cursor: 'pointer',
                fontSize: 12
              }}
            >
              🎛️ Filtri {hasFiltriAttivi && '•'}
            </button>
          </>
        )}
      </div>
      
      {/* Filtri Avanzati */}
      {showFiltriAvanzati && vista !== 'clienti' && (
        <div style={{ padding: '12px 20px', borderBottom: '1px solid ' + bord, background: dark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: muted, marginBottom: 4 }}>Stato</label>
              <select 
                value={filtroStato} 
                onChange={e => setFiltroStato(e.target.value)}
                style={{ width: '100%', padding: '8px', background: bgInput, border: '1px solid ' + bord, borderRadius: 6, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 12 }}
              >
                <option value="">Tutti</option>
                {Object.entries(STATI).map(([k, v]) => (
                  <option key={k} value={k}>{v.icon} {v.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: muted, marginBottom: 4 }}>Dispositivo</label>
              <select 
                value={filtroDispositivo} 
                onChange={e => setFiltroDispositivo(e.target.value)}
                style={{ width: '100%', padding: '8px', background: bgInput, border: '1px solid ' + bord, borderRadius: 6, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 12 }}
              >
                <option value="">Tutti</option>
                {TIPI_DISPOSITIVO.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: muted, marginBottom: 4 }}>Data da</label>
              <input 
                type="date" 
                value={filtroDataDa} 
                onChange={e => setFiltroDataDa(e.target.value)}
                style={{ width: '100%', padding: '8px', background: bgInput, border: '1px solid ' + bord, borderRadius: 6, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 12 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: muted, marginBottom: 4 }}>Data a</label>
              <input 
                type="date" 
                value={filtroDataA} 
                onChange={e => setFiltroDataA(e.target.value)}
                style={{ width: '100%', padding: '8px', background: bgInput, border: '1px solid ' + bord, borderRadius: 6, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 12 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: muted, marginBottom: 4 }}>Importo min €</label>
              <input 
                type="number" 
                value={filtroImportoMin} 
                onChange={e => setFiltroImportoMin(e.target.value)}
                placeholder="0"
                style={{ width: '100%', padding: '8px', background: bgInput, border: '1px solid ' + bord, borderRadius: 6, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 12 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: muted, marginBottom: 4 }}>Importo max €</label>
              <input 
                type="number" 
                value={filtroImportoMax} 
                onChange={e => setFiltroImportoMax(e.target.value)}
                placeholder="999"
                style={{ width: '100%', padding: '8px', background: bgInput, border: '1px solid ' + bord, borderRadius: 6, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 12 }}
              />
            </div>
          </div>
          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: muted }}>{schedeFiltrate.length} risultati</span>
            <button 
              onClick={resetFiltri}
              style={{ padding: '6px 12px', background: 'transparent', border: '1px solid ' + bord, borderRadius: 6, color: muted, fontSize: 11, cursor: 'pointer' }}
            >
              ✕ Reset filtri
            </button>
          </div>
        </div>
      )}
      
      {/* Lista schede */}
      <div style={{ padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: muted, marginRight: 2 }}>Ordina per:</span>
          {[{ k: 'ingresso', l: '📅 Ingresso' }, { k: 'consegna', l: '📦 Consegna' }].map(o => (
            <button
              key={o.k}
              type="button"
              onClick={() => setOrdinaLab(prev => prev.campo === o.k ? { ...prev, verso: prev.verso === 'asc' ? 'desc' : 'asc' } : { campo: o.k, verso: 'asc' })}
              title={ordinaLab.campo === o.k ? 'Clicca di nuovo per invertire l\'ordine' : ''}
              style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1px solid ' + (ordinaLab.campo === o.k ? '#3b82f6' : bord), background: ordinaLab.campo === o.k ? 'rgba(59,130,246,0.15)' : 'transparent', color: ordinaLab.campo === o.k ? '#3b82f6' : muted }}
            >
              {o.l}{ordinaLab.campo === o.k ? (ordinaLab.verso === 'asc' ? ' ↑' : ' ↓') : ''}
            </button>
          ))}
          <span style={{ fontSize: 11, color: muted }}>
            {ordinaLab.campo === 'ingresso'
              ? (ordinaLab.verso === 'asc' ? 'più vecchie prima' : 'più recenti prima')
              : (ordinaLab.verso === 'asc' ? 'consegne più vicine prima' : 'consegne più lontane prima')}
          </span>
        </div>
        {schedeFiltrate.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: muted }}>
            <span style={{ fontSize: 48, display: 'block', marginBottom: 12, opacity: 0.5 }}>🔧</span>
            <p>Nessuna scheda {vista === 'nuove' ? 'nuova' : vista === 'archivio' ? 'in archivio' : vista === 'completate' ? 'pronta' : vista === 'tutti' ? '' : 'in lavorazione'}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {schedeFiltrate.map(scheda => {
              const stato = STATI[scheda.stato] || STATI.nuovo;
              const dataConsegnaPrevista = calcolaConsegnaPrevista(scheda);
              return (
                <div 
                  key={scheda.id}
                  onClick={() => setDettaglioModal(scheda)}
                  style={{ 
                    background: scheda.urgente ? (dark ? 'rgba(239,68,68,0.1)' : 'rgba(239,68,68,0.05)') : bgInput, 
                    borderRadius: 12, 
                    padding: 16, 
                    cursor: 'pointer',
                    borderLeft: `4px solid ${scheda.urgente ? '#ef4444' : stato.color}`,
                    transition: 'transform 0.1s',
                    border: scheda.urgente ? '1px solid rgba(239,68,68,0.3)' : undefined
                  }}
                >
                  {/* Riga superiore: ID, Stato, Badge Urgente */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#3b82f6' }}>#{scheda.id}</span>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: stato.color + "20", color: stato.color, fontWeight: 500 }}>
                      {stato.icon} {stato.label}
                    </span>
                    {scheda.urgente && (
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: 'rgba(239,68,68,0.2)', color: '#ef4444', fontWeight: 700, animation: 'pulse 2s infinite' }}>
                        🚨 URGENTE
                      </span>
                    )}
                    {(scheda.firme || []).some(firmaInAttesa) ? (
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: 'rgba(245,158,11,0.2)', color: '#f59e0b', fontWeight: 600 }}>
                        ⏳ In attesa di firma
                      </span>
                    ) : (scheda.firme || []).some(f => f && f.richiesta && f.firma) ? (
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: 'rgba(34,197,94,0.2)', color: '#22c55e', fontWeight: 600 }}>
                        ✍️ Firmata
                      </span>
                    ) : null}
                  </div>
                  
                  {/* Cliente e dispositivo */}
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{scheda.cliente || 'Cliente'}</div>
                  <div style={{ fontSize: 13, color: muted, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <span>💻 {scheda.dispositivo} {scheda.marca}</span>
                    {scheda.telefono && <span>📱 {scheda.telefono}</span>}
                  </div>
                  
                  {/* Problema */}
                  {scheda.problema && <div style={{ fontSize: 12, color: muted, marginTop: 6, fontStyle: 'italic' }}>"{scheda.problema.substring(0, 60)}{scheda.problema.length > 60 ? '...' : ''}"</div>}
                  
                  {/* Riga inferiore: Date a sinistra, Prezzo a destra */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 12, paddingTop: 10, borderTop: '1px solid ' + bord }}>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontSize: 10, color: muted, textTransform: 'uppercase', marginBottom: 2 }}>Ingresso</div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>📅 {new Date(scheda.dataIngresso).toLocaleDateString('it-IT')}</div>
                      </div>
                      {dataConsegnaPrevista && (
                        <div>
                          <div style={{ fontSize: 10, color: muted, textTransform: 'uppercase', marginBottom: 2 }}>Consegna prevista</div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: dataConsegnaPrevista < new Date(new Date().setHours(0, 0, 0, 0)) ? '#ef4444' : '#22c55e' }}>
                            📦 {dataConsegnaPrevista.toLocaleDateString('it-IT')}
                          </div>
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {scheda.costoRiparazione > 0 && (
                        <div style={{ fontSize: 11, color: '#ef4444' }}>
                          💸 Costo: €{scheda.costoRiparazione.toFixed(2)}
                        </div>
                      )}
                      {(scheda.importoFinale || scheda.preventivo) > 0 && (
                        <div style={{ fontSize: 18, fontWeight: 700, color: '#22c55e' }}>
                          €{(scheda.importoFinale || scheda.preventivo || 0).toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Modal Storico Cliente */}
      {clienteModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setClienteModal(null)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 600, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '20px', borderBottom: '1px solid ' + bord, background: 'linear-gradient(135deg, rgba(59,130,246,0.2), transparent)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 12, color: muted, marginBottom: 4 }}>STORICO CLIENTE</div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{clienteModal.nome || 'Cliente'}</div>
                  <div style={{ fontSize: 13, color: muted, marginTop: 4 }}>
                    {clienteModal.telefono && <span style={{ marginRight: 12 }}>📱 {clienteModal.telefono}</span>}
                    {clienteModal.email && <span>✉️ {clienteModal.email}</span>}
                  </div>
                </div>
                <button onClick={() => setClienteModal(null)} style={{ background: 'transparent', border: 'none', fontSize: 20, cursor: 'pointer', color: muted }}>✕</button>
              </div>
            </div>
            
            {/* Statistiche */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, padding: 16, borderBottom: '1px solid ' + bord }}>
              <div style={{ textAlign: 'center', padding: 12, background: bgInput, borderRadius: 10 }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#3b82f6' }}>{clienteModal.riparazioni.length}</div>
                <div style={{ fontSize: 11, color: muted }}>Riparazioni</div>
              </div>
              <div style={{ textAlign: 'center', padding: 12, background: bgInput, borderRadius: 10 }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#22c55e' }}>€{clienteModal.totaleSpeso.toFixed(0)}</div>
                <div style={{ fontSize: 11, color: muted }}>Totale speso</div>
              </div>
              <div style={{ textAlign: 'center', padding: 12, background: bgInput, borderRadius: 10 }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#f59e0b' }}>€{(clienteModal.totaleSpeso / clienteModal.riparazioni.length || 0).toFixed(0)}</div>
                <div style={{ fontSize: 11, color: muted }}>Media</div>
              </div>
            </div>
            
            {/* Info cliente */}
            {clienteModal.riparazioni.length > 0 && (
              <div style={{ padding: '12px 16px', borderBottom: '1px solid ' + bord, display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: muted }}>
                <span>📅 Prima visita: <strong>{new Date(Math.min(...clienteModal.riparazioni.map(r => new Date(r.dataIngresso)))).toLocaleDateString('it-IT')}</strong></span>
                <span>📅 Ultima visita: <strong>{new Date(Math.max(...clienteModal.riparazioni.map(r => new Date(r.dataIngresso)))).toLocaleDateString('it-IT')}</strong></span>
                {clienteModal.indirizzo && <span>📍 {clienteModal.indirizzo}</span>}
              </div>
            )}
            
            {/* Lista riparazioni */}
            <div style={{ padding: 16, maxHeight: 400, overflow: 'auto' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: muted, marginBottom: 12 }}>STORICO RIPARAZIONI</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {clienteModal.riparazioni.sort((a, b) => new Date(b.dataIngresso) - new Date(a.dataIngresso)).map(rip => {
                  const stato = STATI[rip.stato] || STATI.in_attesa;
                  return (
                    <div 
                      key={rip.id}
                      onClick={() => { setClienteModal(null); setDettaglioModal(rip); }}
                      style={{ 
                        padding: 12, 
                        background: bgInput, 
                        borderRadius: 10, 
                        borderLeft: `3px solid ${stato.color}`,
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>
                            <span style={{ color: '#3b82f6' }}>#{rip.id}</span>
                            <span style={{ marginLeft: 8 }}>{rip.dispositivo} {rip.marca}</span>
                          </div>
                          <div style={{ fontSize: 12, color: muted, marginTop: 2 }}>{rip.problema?.substring(0, 50)}{rip.problema?.length > 50 ? '...' : ''}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 11, padding: '2px 6px', borderRadius: 6, background: stato.color + "20", color: stato.color }}>
                            {stato.icon} {stato.label}
                          </div>
                          {(rip.importoFinale || rip.preventivo) > 0 && (
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#22c55e', marginTop: 4 }}>€{(rip.importoFinale || rip.preventivo).toFixed(2)}</div>
                          )}
                        </div>
                      </div>
                      <div style={{ fontSize: 11, color: muted, marginTop: 6 }}>
                        📅 {new Date(rip.dataIngresso).toLocaleDateString('it-IT')}
                        {rip.dataConsegna && ` → ${new Date(rip.dataConsegna).toLocaleDateString('it-IT')}`}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Azioni */}
            <div style={{ padding: 16, borderTop: '1px solid ' + bord, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', gap: 12 }}>
                <button 
                  onClick={() => {
                    const tel = (clienteModal.telefono || '').replace(/\D/g, '');
                    if (tel) window.open(`https://wa.me/39${tel}`, '_blank');
                  }}
                  style={{ flex: 1, padding: 12, background: 'rgba(37,211,102,0.2)', border: '1px solid rgba(37,211,102,0.3)', borderRadius: 10, color: '#25d366', fontWeight: 600, cursor: 'pointer' }}
                >
                  📱 WhatsApp
                </button>
                <button 
                  onClick={() => {
                    setClienteModal(null);
                    setSchedaModal({ 
                      isNuova: true, 
                      cliente: clienteModal.nome,
                      telefono: clienteModal.telefono,
                      email: clienteModal.email,
                      dispositivo: 'Notebook', 
                      stato: 'nuovo' 
                    });
                  }}
                  style={{ flex: 1, padding: 12, background: 'linear-gradient(135deg,#3b82f6,#2563eb)', border: 'none', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer' }}
                >
                  + Nuova Scheda
                </button>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button 
                  onClick={() => eliminaCliente(clienteModal.id)}
                  style={{ padding: '10px 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, color: '#ef4444', cursor: 'pointer' }}
                >
                  🗑️ Elimina
                </button>
                <button 
                  onClick={() => {
                    setClienteModal(null);
                    setClienteEditModal(clienteModal);
                  }}
                  style={{ flex: 1, padding: 12, background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: '1px solid ' + bord, borderRadius: 10, color: muted, cursor: 'pointer' }}
                >
                  ✏️ Modifica Dati
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal Modifica/Nuovo Cliente */}
      {clienteEditModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setClienteEditModal(null)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 450, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '20px', borderBottom: '1px solid ' + bord }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                {clienteEditModal.isNuovo ? '👤 Nuovo Cliente' : '✏️ Modifica Cliente'}
              </h3>
            </div>
            <ClienteForm 
              cliente={clienteEditModal}
              onSave={salvaCliente}
              onClose={() => setClienteEditModal(null)}
              dark={dark}
              bgInput={bgInput}
              bord={bord}
              muted={muted}
            />
          </div>
        </div>
      )}
      
      {/* Modal Nuova/Modifica Scheda */}
      {schedaModal && (
        <SchedaModal 
          scheda={schedaModal}
          onSave={salvaScheda}
          onSaveAndPrint={(scheda) => {
            salvaScheda(scheda);
            // Stampa dopo un breve ritardo per assicurarsi che la scheda sia salvata
            setTimeout(() => {
              const savedScheda = { ...scheda };
              if (scheda.isNuova) {
                const { id } = generaIdScheda();
                savedScheda.id = id;
                savedScheda.dataIngresso = new Date().toISOString();
              }
              stampaRicevuta(savedScheda, 'ingresso');
            }, 100);
          }}
          onClose={() => setSchedaModal(null)}
          dark={dark}
          bgCard={bgCard}
          bgInput={bgInput}
          bord={bord}
          muted={muted}
          tipiDispositivo={TIPI_DISPOSITIVO}
          clientiLab={data.clientiLab || []}
          contatore={data.laboratorioContatore || 0}
          anno={data.laboratorioAnno || new Date().getFullYear()}
          checklistItems={CHECKLIST_ITEMS}
          contiDisponibili={contiDisponibili}
        />
      )}
      
      {/* Modal Dettaglio Scheda */}
      {dettaglioModal && (
        <DettaglioSchedaModal
          scheda={dettaglioModal}
          stati={STATI}
          onCambiaStato={cambiaStato}
          onModifica={() => { setSchedaModal(dettaglioModal); setDettaglioModal(null); }}
          onWhatsApp={inviaWhatsApp}
          onStampa={stampaRicevuta}
          onStampaEtichetta={stampaEtichettaTermica}
          onElimina={eliminaScheda}
          onClose={() => setDettaglioModal(null)}
          dark={dark}
          bgCard={bgCard}
          bgInput={bgInput}
          bord={bord}
          muted={muted}
        />
      )}
      
      {/* Modal Incasso */}
      {incassoModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setIncassoModal(null)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 400, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '20px', borderBottom: '1px solid ' + bord, textAlign: 'center' }}>
              <span style={{ fontSize: 40 }}>💰</span>
              <h3 style={{ margin: '8px 0 0', fontSize: 18, fontWeight: 700 }}>Incassa Riparazione</h3>
              <p style={{ margin: '4px 0 0', color: muted, fontSize: 13 }}>#{incassoModal.numero} - {incassoModal.cliente}</p>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ textAlign: 'center', fontSize: 28, fontWeight: 700, color: '#22c55e', marginBottom: 4 }}>
                €{saldoDaIncassare(incassoModal).toFixed(2)}
              </div>
              {/* FIX: se c'è un acconto si mostra il dettaglio, così è chiaro che
                  la cifra grande è il SALDO residuo e non l'importo totale */}
              {accontoRegistrato(incassoModal) > 0 && (
                <div style={{ textAlign: 'center', fontSize: 12, color: muted, marginBottom: 16 }}>
                  Totale €{(incassoModal.importoFinale || incassoModal.preventivo || 0).toFixed(2)} − acconto già incassato €{accontoRegistrato(incassoModal).toFixed(2)}
                </div>
              )}
              <p style={{ fontSize: 13, color: muted, marginTop: 16, marginBottom: 16, textAlign: 'center' }}>Seleziona dove registrare l'incasso:</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 250, overflow: 'auto' }}>
                {contiDisponibili.map(conto => (
                  <button
                    key={conto.id}
                    onClick={() => {
                      // FIX: si conferma il saldo residuo, non l'importo pieno
                      const importo = saldoDaIncassare(incassoModal);
                      if (!window.confirm(`Confermi incasso di €${importo.toFixed(2)} su ${conto.nome}?`)) return;
                      confermaIncasso(incassoModal, conto.id);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '12px 16px',
                      background: dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                      border: '1px solid ' + bord,
                      borderRadius: 10,
                      cursor: 'pointer',
                      width: '100%',
                      textAlign: 'left'
                    }}
                  >
                    <span style={{ fontSize: 20 }}>{conto.icon}</span>
                    <span style={{ flex: 1, fontSize: 14, color: dark ? '#e2e8f0' : '#1e293b' }}>{conto.nome}</span>
                  </button>
                ))}
              </div>
              
              {/* Opzione Da Incassare */}
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid ' + bord }}>
                <button
                  onClick={async () => {
                    // FIX: si sposta il SALDO residuo, non l'importo pieno
                    const importo = saldoDaIncassare(incassoModal);
                    if (!window.confirm(`Confermi spostamento di €${importo.toFixed(2)} in "Da Incassare"?`)) return;

                    const oggi = new Date().toISOString();
                    
                    // Aggiorna scheda come consegnata ma non incassata
                    const schedaAggiornata = {
                      ...incassoModal,
                      stato: 'consegnato',
                      dataConsegna: oggi,
                      incassato: false,
                      daIncassare: true,
                      storicoStati: [...(incassoModal.storicoStati || []), { stato: 'consegnato', data: oggi }]
                    };
                    
                    let nuoveEntrate = [...data.entrate];
                    let nuoveCategorie = [...(data.categorieEntrate || [])];
                    const idEntrata = `lab_${incassoModal.id}`;
                    
                    // Debug: log per verificare la rimozione
                    const voceRiparazione = nuoveEntrate.find(e => e.id === idEntrata);
                    console.log('🔧 Da Incassare - Rimozione voce riparazione:', {
                      idEntrata,
                      voceRiparazione,
                      totaleEntraPrima: nuoveEntrate.length
                    });
                    
                    // Rimuovi TUTTE le voci associate a questa riparazione (sia lab_ che altre con schedaId)
                    const vociDaEliminare = nuoveEntrate.filter(e => e.id === idEntrata || e.schedaId === incassoModal.id).map(e => e.id);
                    nuoveEntrate = nuoveEntrate.filter(e => e.id !== idEntrata && e.schedaId !== incassoModal.id);

                    // FIX: elimina le voci anche da Supabase. syncTable fa solo upsert
                    // e non cancella nulla, quindi senza questa delete la voce
                    // "da incassare" tornava al primo sync e l'importo risultava doppio.
                    // L'errore viene intercettato: se la rete cade il salvataggio locale
                    // deve comunque andare avanti, avvisando l'utente.
                    if (userId && supabase && vociDaEliminare.length > 0) {
                      try {
                        for (const vid of vociDaEliminare) {
                          await supabase.from('entrate').delete().eq('id', vid).eq('user_id', userId);
                        }
                        console.log('   ✅ Eliminate da Supabase:', vociDaEliminare);
                      } catch (errDelete) {
                        console.error('❌ Errore eliminazione voci da Supabase:', errDelete);
                        alert('Attenzione: la consegna è stata registrata, ma la vecchia voce della riparazione non è stata rimossa dal server e potrebbe riapparire alla prossima sincronizzazione.');
                      }
                    }

                    console.log('🔧 Da Incassare - Dopo rimozione:', {
                      totaleEntraDopo: nuoveEntrate.length,
                      rimossi: data.entrate.length - nuoveEntrate.length
                    });
                    
                    // Cerca o crea la categoria "DA INCASSARE"
                    let catDaIncassare = nuoveCategorie.find(c => c.nome && c.nome.toUpperCase() === 'DA INCASSARE');
                    if (!catDaIncassare) {
                      catDaIncassare = {
                        id: 'cat_da_incassare',
                        nome: 'DA INCASSARE',
                        icon: '⏳'
                      };
                      nuoveCategorie.push(catDaIncassare);
                    }
                    
                    // Crea una nuova voce separata per questa riparazione nella categoria DA INCASSARE
                    nuoveEntrate.push({
                      id: 'da_incassare_lab_' + incassoModal.id + '_' + Date.now(),
                      nome: `#${incassoModal.numero} - ${incassoModal.cliente}`,
                      importo: importo,
                      icon: '🔧',
                      // FIX: senza schedaId la voce restava orfana se poi si eliminava la scheda
                      schedaId: incassoModal.id,
                      categoria: catDaIncassare.id,
                      note: `${incassoModal.dispositivo || 'Riparazione'}${incassoModal.marca ? ' ' + incassoModal.marca : ''}${incassoModal.diagnosi ? '\nDiagnosi: ' + incassoModal.diagnosi : ''}\n[${new Date().toLocaleDateString('it-IT')}]`
                    });
                    
                    // NON registra movimento - verrà registrato quando effettivamente incassato
                    
                    save({ 
                      ...data, 
                      laboratorio: laboratorio.map(s => s.id === incassoModal.id ? schedaAggiornata : s),
                      entrate: nuoveEntrate,
                      categorieEntrate: nuoveCategorie
                    });
                    
                    setIncassoModal(null);
                    setDettaglioModal(null);
                    
                    // Invia WhatsApp automaticamente
                    if (incassoModal.telefono && MESSAGGI_WHATSAPP['consegnato']) {
                      setTimeout(() => {
                        const messaggio = MESSAGGI_WHATSAPP['consegnato'].replace('{cliente}', incassoModal.cliente || 'Cliente');
                        const tel = (incassoModal.telefono || '').replace(/\D/g, '');
                        if (tel) {
                          window.open(`https://wa.me/39${tel}?text=${encodeURIComponent(messaggio)}`, '_blank');
                        }
                      }, 300);
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 16px',
                    background: 'rgba(245,158,11,0.15)',
                    border: '2px solid #f59e0b',
                    borderRadius: 10,
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: 'left'
                  }}
                >
                  <span style={{ fontSize: 20 }}>⏳</span>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#f59e0b', display: 'block' }}>Da Incassare</span>
                    <span style={{ fontSize: 11, color: muted }}>Sposta in categoria "DA INCASSARE"</span>
                  </div>
                </button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, padding: '16px 20px', borderTop: '1px solid ' + bord }}>
              <button onClick={() => setIncassoModal(null)} style={{ flex: 1, padding: 12, background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 10, color: muted, cursor: 'pointer' }}>Annulla</button>
              <button 
                onClick={async () => {
                  // Segna come consegnato senza incassare
                  const schedaAggiornata = {
                    ...incassoModal,
                    stato: 'consegnato',
                    dataConsegna: new Date().toISOString(),
                    incassato: false,
                    storicoStati: [...(incassoModal.storicoStati || []), { stato: 'consegnato', data: new Date().toISOString() }]
                  };
                  // Rimuovi voce temporanea dalle entrate
                  const idEntrata = `lab_${incassoModal.id}`;
                  console.log('🗑️ Rimozione voce entrata:', idEntrata);
                  const nuoveEntrate = data.entrate.filter(e => e.id !== idEntrata);
                  console.log('   Entrate prima:', data.entrate.length, 'dopo:', nuoveEntrate.length);
                  
                  // Elimina anche da Supabase
                  if (userId && supabase) {
                    await supabase.from('entrate').delete().eq('id', idEntrata).eq('user_id', userId);
                    console.log('   ✅ Eliminato da Supabase');
                  }
                  
                  save({ ...data, laboratorio: laboratorio.map(s => s.id === incassoModal.id ? schedaAggiornata : s), entrate: nuoveEntrate });
                  setIncassoModal(null);
                  setDettaglioModal(null);
                }}
                style={{ flex: 1, padding: 12, background: 'rgba(100,116,139,0.2)', border: '1px solid ' + bord, borderRadius: 10, color: muted, cursor: 'pointer' }}
              >
                Solo Consegna
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal Gestione Riparazione - Stati e WhatsApp */}
      {whatsappLabModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setWhatsappLabModal(null)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 500, padding: 24 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700 }}>🔧 Gestione Riparazione</h3>
            
            {/* Info scheda */}
            <div style={{ padding: 12, background: dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', borderRadius: 12, marginBottom: 20 }}>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{whatsappLabModal.cliente}</div>
              <div style={{ fontSize: 12, color: muted }}>
                📱 {whatsappLabModal.telefono} • 🔧 {whatsappLabModal.dispositivo} {whatsappLabModal.marca || ''}
              </div>
              <div style={{ fontSize: 12, color: muted, marginTop: 4 }}>
                📋 #{whatsappLabModal.id} • Stato attuale: <span style={{ color: STATI[whatsappLabModal.stato]?.color, fontWeight: 600 }}>{STATI[whatsappLabModal.stato]?.icon} {STATI[whatsappLabModal.stato]?.label}</span>
              </div>
            </div>
            
            {/* Lista stati con azioni */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {Object.entries(STATI).map(([key, val]) => {
                const isStatoCorrente = whatsappLabModal.stato === key;
                return (
                  <div 
                    key={key}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 10,
                      padding: '10px 12px', 
                      background: isStatoCorrente ? val.color + "20" : 'transparent',
                      border: `2px solid ${isStatoCorrente ? val.color : bord}`,
                      borderRadius: 10
                    }}
                  >
                    {/* Indicatore stato */}
                    <div style={{ 
                      width: 32, 
                      height: 32, 
                      borderRadius: '50%', 
                      background: isStatoCorrente ? val.color : 'transparent',
                      border: `2px solid ${val.color}`,
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      fontSize: 14,
                      flexShrink: 0
                    }}>
                      {isStatoCorrente ? '✓' : val.icon}
                    </div>
                    
                    {/* Label stato */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: isStatoCorrente ? val.color : (dark ? '#e2e8f0' : '#1e293b') }}>
                        {val.label}
                      </div>
                    </div>
                    
                    {/* Tasto cambia stato */}
                    {!isStatoCorrente && (
                      <button 
                        onClick={() => {
                          cambiaStato(whatsappLabModal.id, key);
                          setWhatsappLabModal({ ...whatsappLabModal, stato: key });
                        }}
                        style={{ 
                          padding: '6px 12px', 
                          background: `${val.color}15`, 
                          border: '1px solid ' + val.color + '50', 
                          borderRadius: 6, 
                          color: val.color, 
                          fontSize: 11, 
                          fontWeight: 600,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Imposta
                      </button>
                    )}
                    
                    {/* Tasto WhatsApp */}
                    <button 
                      onClick={() => inviaWhatsAppMessaggio(whatsappLabModal, key)}
                      style={{ 
                        padding: '6px 12px', 
                        background: 'rgba(37,211,102,0.15)', 
                        border: '1px solid rgba(37,211,102,0.4)', 
                        borderRadius: 6, 
                        color: '#25d366', 
                        fontSize: 11, 
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                      }}
                    >
                      💬 Invia
                    </button>
                  </div>
                );
              })}
            </div>
            
            <button 
              onClick={() => setWhatsappLabModal(null)} 
              style={{ width: '100%', marginTop: 16, padding: 14, background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 10, color: muted, fontSize: 14, cursor: 'pointer' }}
            >
              Chiudi
            </button>
          </div>
        </div>
      )}
      
      {/* Modal Modifica Messaggi WhatsApp */}
      {showMessaggiModal && (
        <MessaggiWhatsAppModal
          messaggi={MESSAGGI_WHATSAPP}
          messaggiDefault={MESSAGGI_DEFAULT}
          stati={STATI}
          onSave={(nuoviMessaggi) => {
            save({ ...data, messaggiLaboratorio: nuoviMessaggi });
            setShowMessaggiModal(false);
          }}
          onClose={() => setShowMessaggiModal(false)}
          dark={dark}
          bgCard={bgCard}
          bgInput={bgInput}
          bord={bord}
          muted={muted}
        />
      )}
    </section>
  );
}

// Modal per modificare messaggi WhatsApp
function MessaggiWhatsAppModal({ messaggi, messaggiDefault, stati, onSave, onClose, dark, bgCard, bgInput, bord, muted }) {
  const [form, setForm] = useState({ ...messaggiDefault, ...messaggi });
  
  const inputStyle = { width: '100%', padding: '10px 12px', background: bgInput, border: '1px solid ' + bord, borderRadius: 8, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 13, boxSizing: 'border-box', minHeight: 100, resize: 'vertical', fontFamily: 'inherit' };
  
  const resetMessaggio = (stato) => {
    setForm({ ...form, [stato]: messaggiDefault[stato] });
  };
  
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={onClose}>
      <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 600, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px', borderBottom: '1px solid ' + bord }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>📱 Messaggi WhatsApp</h3>
          <p style={{ margin: '8px 0 0', fontSize: 13, color: muted }}>Personalizza i messaggi inviati ai clienti. Usa <code style={{ background: bgInput, padding: '2px 6px', borderRadius: 4 }}>{'{cliente}'}</code> per inserire il nome.</p>
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 20 }}>
          {Object.entries(stati).map(([key, val]) => (
            <div key={key}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: val.color, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {val.icon} {val.label}
                </label>
                <button 
                  onClick={() => resetMessaggio(key)}
                  style={{ padding: '4px 8px', background: 'transparent', border: '1px solid ' + bord, borderRadius: 6, color: muted, fontSize: 11, cursor: 'pointer' }}
                >
                  ↺ Reset
                </button>
              </div>
              <textarea
                value={form[key] || ''}
                onChange={e => setForm({ ...form, [key]: e.target.value })}
                style={inputStyle}
                placeholder={`Messaggio per stato "${val.label}"...`}
              />
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 12, padding: '16px 20px', borderTop: '1px solid ' + bord }}>
          <button onClick={onClose} style={{ flex: 1, padding: 12, background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 10, color: muted, cursor: 'pointer' }}>Annulla</button>
          <button 
            onClick={() => {
              setForm({ ...messaggiDefault });
            }}
            style={{ padding: '12px 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, color: '#ef4444', cursor: 'pointer' }}
          >
            Reset Tutti
          </button>
          <button onClick={() => onSave(form)} style={{ flex: 1, padding: 12, background: 'linear-gradient(135deg,#22c55e,#16a34a)', border: 'none', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>💾 Salva</button>
        </div>
      </div>
    </div>
  );
}

// Form per modifica/nuovo cliente
function ClienteForm({ cliente, onSave, onClose, dark, bgInput, bord, muted }) {
  const [form, setForm] = useState({
    nome: cliente.nome || '',
    telefono: cliente.telefono || '',
    email: cliente.email || '',
    indirizzo: cliente.indirizzo || '',
    note: cliente.note || ''
  });
  
  const salva = () => {
    if (!form.nome || !form.telefono) {
      alert('Inserisci almeno nome e telefono!');
      return;
    }
    onSave({
      ...cliente,
      ...form
    });
  };
  
  const inputStyle = { width: '100%', padding: '10px 12px', background: bgInput, border: '1px solid ' + bord, borderRadius: 8, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14, boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: muted, marginBottom: 4, textTransform: 'uppercase' };
  
  return (
    <>
      <div style={{ padding: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle}>Nome *</label>
            <input type="text" value={form.nome} onChange={e => setForm({...form, nome: e.target.value})} style={inputStyle} placeholder="Mario Rossi" />
          </div>
          <div>
            <label style={labelStyle}>Telefono *</label>
            <input type="tel" value={form.telefono} onChange={e => setForm({...form, telefono: e.target.value})} style={inputStyle} placeholder="339-1234567" />
          </div>
          <div>
            <label style={labelStyle}>Email</label>
            <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} style={inputStyle} placeholder="email@esempio.it" />
          </div>
          <div>
            <label style={labelStyle}>Indirizzo</label>
            <input type="text" value={form.indirizzo} onChange={e => setForm({...form, indirizzo: e.target.value})} style={inputStyle} placeholder="Via Roma, 1 - Napoli" />
          </div>
          <div>
            <label style={labelStyle}>Note</label>
            <textarea value={form.note} onChange={e => setForm({...form, note: e.target.value})} style={{...inputStyle, minHeight: 80, resize: 'vertical'}} placeholder="Note sul cliente..." />
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, padding: '16px 20px', borderTop: '1px solid ' + bord }}>
        <button onClick={onClose} style={{ flex: 1, padding: 12, background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 10, color: muted, cursor: 'pointer' }}>Annulla</button>
        <button onClick={salva} style={{ flex: 1, padding: 12, background: 'linear-gradient(135deg,#3b82f6,#2563eb)', border: 'none', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>💾 Salva</button>
      </div>
    </>
  );
}

// Modal per nuova/modifica scheda
function SchedaModal({ scheda, onSave, onSaveAndPrint, onClose, dark, bgCard, bgInput, bord, muted, tipiDispositivo, clientiLab = [], contatore = 0, anno = new Date().getFullYear(), checklistItems = [], contiDisponibili = [] }) {
  const [form, setForm] = useState({
    cliente: scheda.cliente || '',
    telefono: scheda.telefono || '',
    email: scheda.email || '',
    dispositivo: scheda.dispositivo || 'Notebook',
    marca: scheda.marca || '',
    modello: scheda.modello || '',
    seriale: scheda.seriale || '',
    pinPassword: scheda.pinPassword || '',
    accessori: scheda.accessori || '',
    problema: scheda.problema || '',
    diagnosi: scheda.diagnosi || '',
    preventivo: scheda.preventivo || '',
    costoRiparazione: scheda.costoRiparazione || '',
    importoFinale: scheda.importoFinale || '',
    acconto: scheda.acconto || '',
    accontoConto: scheda.accontoConto || '',
    note: scheda.note || '',
    checklist: scheda.checklist || {},
    noteChecklist: scheda.noteChecklist || '',
    // Preserva stato
    stato: scheda.stato || 'nuovo',
    storicoStati: scheda.storicoStati || [],
    dataIngresso: scheda.dataIngresso,
    timerStart: scheda.timerStart,
    tempoLavorazione: scheda.tempoLavorazione,
    incassato: scheda.incassato,
    contoIncasso: scheda.contoIncasso,
    dataConsegna: scheda.dataConsegna,
    // Nuovi campi
    notePubbliche: scheda.notePubbliche || '',
    notePrivate: scheda.notePrivate || '',
    tempoStimato: scheda.tempoStimato || '',
    foto: scheda.foto || [],
    firme: scheda.firme || [],
    messaggiCliente: scheda.messaggiCliente || [],
    notificaWhatsapp: scheda.notificaWhatsapp !== false,
    urgente: scheda.urgente || false
  });
  
  const [showChecklist, setShowChecklist] = useState(scheda.isNuova || false);
  const [showFotoModal, setShowFotoModal] = useState(false);
  const [showFirmaModal, setShowFirmaModal] = useState(null); // null o {tipo: 'formattazione', descrizione: '...'}
  const [uploadingFoto, setUploadingFoto] = useState(false);
  
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchCliente, setSearchCliente] = useState('');
  
  // Filtra clienti per suggerimenti
  const clientiFiltrati = clientiLab.filter(c => {
    if (!searchCliente) return false;
    const q = searchCliente.toLowerCase();
    return c.nome?.toLowerCase().includes(q) || c.telefono?.includes(q);
  }).slice(0, 5);
  
  // Seleziona cliente da suggerimenti
  const selezionaCliente = (cliente) => {
    setForm(prev => ({
      ...prev,
      cliente: cliente.nome || '',
      telefono: cliente.telefono || '',
      email: cliente.email || ''
    }));
    setSearchCliente('');
    setShowSuggestions(false);
  };
  
  // FIX: validazione unica, condivisa da "Salva" e da "Salva e Stampa".
  // Prima il secondo pulsante ne aveva una copia incompleta.
  const validaForm = () => {
    if (!form.cliente || !form.telefono) {
      alert('Inserisci almeno nome cliente e telefono!');
      return false;
    }
    // Un acconto senza conto non finirebbe in nessuna cassa, e alla consegna
    // verrebbe comunque scalato dal saldo: quei soldi non risulterebbero da nessuna parte.
    if ((parseFloat(form.acconto) || 0) > 0 && !form.accontoConto) {
      alert('Hai inserito un acconto: seleziona anche il conto su cui registrarlo.');
      return false;
    }
    return true;
  };

  const salva = () => {
    if (!validaForm()) return;
    onSave({
      ...scheda,
      ...form,
      preventivo: parseFloat(form.preventivo) || 0,
      costoRiparazione: parseFloat(form.costoRiparazione) || 0,
      importoFinale: parseFloat(form.importoFinale) || 0
    });
  };
  
  // Genera ID progressivo per anteprima stampa
  const generaIdAnteprima = () => {
    if (scheda.id) return scheda.id; // Se già esistente
    const annoCorrente = new Date().getFullYear();
    const nuovoContatore = annoCorrente === anno ? contatore + 1 : 1;
    return `${annoCorrente}-${String(nuovoContatore).padStart(3, '0')}`;
  };
  
  // Stampa anteprima ricevuta (prima di salvare)
  const stampaAnteprima = () => {
    if (!form.cliente || !form.telefono) {
      alert('Inserisci almeno nome cliente e telefono per stampare!');
      return;
    }
    
    const idAnteprima = generaIdAnteprima();
    const dataOggi = new Date().toLocaleDateString('it-IT');
    const oraOggi = new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    
    // Calcola data consegna stimata
    const calcolaDataStimataAnteprima = () => {
      if (!form.tempoStimato) return null;
      if (form.tempoStimato.startsWith('data:')) {
        return new Date(form.tempoStimato.replace('data:', '')).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
      }
      const oggi = new Date();
      switch (form.tempoStimato) {
        case 'stesso_giorno': return 'Stesso giorno';
        case '1_giorno': 
          oggi.setDate(oggi.getDate() + 1);
          return oggi.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
        case '2_3_giorni':
          oggi.setDate(oggi.getDate() + 3);
          return 'Entro ' + oggi.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
        case '1_settimana':
          oggi.setDate(oggi.getDate() + 7);
          return 'Entro ' + oggi.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
        case '2_settimane':
          oggi.setDate(oggi.getDate() + 14);
          return 'Entro ' + oggi.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
        case 'da_valutare': return 'Da comunicare';
        default: return null;
      }
    };
    const dataStimataAnteprima = calcolaDataStimataAnteprima();
    
    // Checklist HTML per anteprima
    let checklistHtmlAnteprima = '';
    if (form.checklist && Object.keys(form.checklist).length > 0) {
      const checklistLabels = {
        'accensione': 'Accensione',
        'display': 'Display',
        'tastiera': 'Tastiera',
        'touchpad': 'Touchpad/Mouse',
        'audio': 'Audio',
        'wifi': 'WiFi',
        'batteria': 'Batteria',
        'caricatore': 'Caricatore',
        'graffi': 'Graffi/Danni',
        'backup': 'Backup'
      };
      const items = Object.entries(form.checklist)
        .filter(([k, v]) => v !== null)
        .map(([key, val]) => {
          const label = checklistLabels[key] || key;
          const icon = val === true ? (key === 'graffi' ? '⚠️' : '✓') : '✗';
          const color = val === true ? (key === 'graffi' ? '#f59e0b' : '#22c55e') : '#ef4444';
          return '<span style="display: inline-block; padding: 2px 6px; margin: 2px; border-radius: 4px; font-size: 9px; background: ' + color + '20; color: ' + color + '; border: 1px solid ' + color + '50;">' + icon + ' ' + label + '</span>';
        }).join('');
      
      if (items) {
        checklistHtmlAnteprima = '<div style="margin: 8px 0; padding: 8px; background: #f9f9f9; border: 1px solid #ddd; border-radius: 4px;"><div style="font-size: 8px; font-weight: bold; color: #666; text-transform: uppercase; margin-bottom: 4px;">📋 Checklist Ingresso</div><div>' + items + '</div>' + (form.noteChecklist ? '<div style="font-size: 9px; color: #666; margin-top: 6px; font-style: italic;">Note: ' + form.noteChecklist + '</div>' : '') + '</div>';
      }
    }
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Ricevuta Ingresso - ${idAnteprima}</title>
        <style>
          @page { size: A4; margin: 10mm; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; font-size: 11px; }
          .page { width: 100%; max-width: 190mm; margin: 0 auto; }
          .cut-line { border-top: 2px dashed #999; margin: 15px 0; position: relative; }
          .cut-line::before { content: '✂ - - - TAGLIARE QUI - - -'; position: absolute; top: -8px; left: 50%; transform: translateX(-50%); background: #fff; padding: 0 10px; font-size: 10px; color: #999; }
          .section { border: 1px solid #333; padding: 12px; margin-bottom: 5px; }
          .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 10px; }
          .header h1 { font-size: 16px; margin-bottom: 3px; }
          .header .info { font-size: 9px; color: #666; }
          .header .scheda-id { font-size: 18px; font-weight: bold; background: #f0f0f0; padding: 5px 15px; display: inline-block; margin-top: 8px; border: 1px solid #333; }
          .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin: 8px 0; }
          .field-label { font-size: 8px; color: #666; text-transform: uppercase; font-weight: bold; }
          .field-value { font-size: 11px; font-weight: 500; border-bottom: 1px dotted #ccc; min-height: 16px; padding: 2px 0; }
          .highlight-box { background: #f9f9f9; border: 1px solid #333; padding: 8px; margin: 8px 0; }
          .highlight-box .title { font-size: 9px; font-weight: bold; text-transform: uppercase; margin-bottom: 4px; }
          .footer { text-align: center; font-size: 9px; color: #666; margin-top: 10px; padding-top: 8px; border-top: 1px solid #ccc; }
          .lab-section { background: #f8f8f8; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="page">
          <!-- ETICHETTA LABORATORIO (in alto) -->
          <div class="section lab-section">
            <div class="header" style="background: #333; color: #fff; margin: -12px -12px 10px -12px; padding: 10px;">
              <h1 style="color: #fff; margin: 0;">🔧 ETICHETTA LABORATORIO</h1>
              <div class="scheda-id" style="background: #fff; color: #333; margin-top: 8px;">#${idAnteprima}</div>
            </div>
            <div class="grid">
              <div class="field"><div class="field-label">Cliente</div><div class="field-value" style="font-size: 13px; font-weight: bold;">${form.cliente || '-'}</div></div>
              <div class="field"><div class="field-label">Telefono</div><div class="field-value" style="font-size: 13px; font-weight: bold;">${form.telefono || '-'}</div></div>
              <div class="field"><div class="field-label">Data</div><div class="field-value">${dataOggi}</div></div>
              <div class="field"><div class="field-label">Dispositivo</div><div class="field-value">${form.dispositivo || ''} ${form.marca || ''}</div></div>
              <div class="field"><div class="field-label">Modello</div><div class="field-value">${form.modello || '-'}</div></div>
              <div class="field"><div class="field-label">S/N</div><div class="field-value">${form.seriale || '-'}</div></div>
              ${form.pinPassword ? '<div class="field" style="background: #fff3cd; padding: 4px; border: 1px solid #ffc107;"><div class="field-label">🔐 PIN/Password</div><div class="field-value" style="font-size: 14px; font-weight: bold;">' + form.pinPassword + '</div></div>' : ''}
              <div class="field"><div class="field-label">Accessori</div><div class="field-value">${form.accessori || '-'}</div></div>
            </div>
            <div class="highlight-box" style="background: #fff;"><div class="title">🔧 Problema</div><div style="font-size: 12px;">${form.problema || '-'}</div></div>
            ${checklistHtmlAnteprima}
            ${form.preventivo ? '<div style="text-align: center; font-size: 14px; font-weight: bold; padding: 8px; background: #e8f5e9; border: 1px solid #4caf50;">PREVENTIVO: €' + parseFloat(form.preventivo).toFixed(2) + '</div>' : ''}
            ${form.note ? '<div class="highlight-box" style="background: #fff3e0; border-color: #ff9800;"><div class="title">📝 Note interne</div><div style="font-size: 10px;">' + form.note + '</div></div>' : ''}
          </div>
          
          <div class="cut-line"></div>
          
          <!-- COPIA CLIENTE (in basso) -->
          <div class="section">
            <div class="header">
              <h1>🖥️ PC CARE NAPOLI</h1>
              <div class="info">Vico Acitillo, 148 - 80127 Napoli (NA) | Tel: 081.191.73.939</div>
              <div class="scheda-id">#${idAnteprima}</div>
              <div style="font-size: 10px; margin-top: 5px;">RICEVUTA DI INGRESSO - COPIA CLIENTE</div>
            </div>
            <div class="grid">
              <div class="field"><div class="field-label">Cliente</div><div class="field-value">${form.cliente || '-'}</div></div>
              <div class="field"><div class="field-label">Telefono</div><div class="field-value">${form.telefono || '-'}</div></div>
              <div class="field"><div class="field-label">Data Ingresso</div><div class="field-value">${dataOggi} - ${oraOggi}</div></div>
              <div class="field"><div class="field-label">Email</div><div class="field-value">${form.email || '-'}</div></div>
              ${dataStimataAnteprima ? '<div class="field" style="grid-column: span 2; background: #e0f2fe; padding: 6px; border: 1px solid #0284c7; border-radius: 4px;"><div class="field-label" style="color: #0369a1;">📅 Data Consegna Stimata</div><div class="field-value" style="font-weight: bold; color: #0284c7; font-size: 13px;">' + dataStimataAnteprima + '</div></div>' : ''}
            </div>
            <div class="highlight-box">
              <div class="title">📱 Dispositivo</div>
              <div><strong>${form.dispositivo || ''} ${form.marca || ''} ${form.modello || ''}</strong></div>
              ${form.seriale ? '<div style="font-size: 10px; color: #666;">S/N: ' + form.seriale + '</div>' : ''}
              ${form.accessori ? '<div style="font-size: 10px; margin-top: 4px;">📦 Accessori: ' + form.accessori + '</div>' : ''}
            </div>
            ${checklistHtmlAnteprima}
            <div class="highlight-box">
              <div class="title">🔧 Problema segnalato</div>
              <div>${form.problema || 'Non specificato'}</div>
            </div>
            ${form.preventivo ? '<div class="grid"><div class="field"><div class="field-label">Preventivo stimato</div><div class="field-value" style="font-size: 14px; font-weight: bold;">€' + parseFloat(form.preventivo).toFixed(2) + '</div></div></div>' : ''}
            
            <!-- QR Code per stato riparazione -->
            <div style="text-align: center; margin: 15px 0; padding: 12px; background: #f8f9fa; border: 1px solid #e2e8f0; border-radius: 8px;">
              <div style="font-size: 10px; font-weight: bold; text-transform: uppercase; margin-bottom: 8px;">📱 Controlla lo stato della riparazione</div>
              <img src="https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=https://gestpccare.it/stato" alt="QR Code" style="width: 80px; height: 80px;" />
              <div style="font-size: 9px; color: #666; margin-top: 6px;">Scansiona il QR o vai su <strong>gestpccare.it/stato</strong></div>
              <div style="font-size: 9px; color: #666;">Inserisci: Scheda <strong>#${idAnteprima}</strong> + tuo numero di telefono</div>
            </div>
            
            <div class="footer"><strong>Conservare questo documento per il ritiro del dispositivo.</strong></div>
          </div>
        </div>
        <script>window.onload = function() { window.print(); };</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };
  
  const inputStyle = { width: '100%', padding: '10px 12px', background: bgInput, border: '1px solid ' + bord, borderRadius: 8, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14, boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: muted, marginBottom: 4, textTransform: 'uppercase' };
  
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={onClose}>
      <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 500, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px', borderBottom: '1px solid ' + bord }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{scheda.isNuova ? '➕ Nuova Scheda Riparazione' : `✏️ Modifica Scheda #${scheda.id}`}</h3>
        </div>
        <div style={{ padding: 20 }}>
          {/* Dati Cliente */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#3b82f6', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>👤 DATI CLIENTE</span>
              {clientiLab.length > 0 && (
                <span style={{ fontSize: 10, color: muted, fontWeight: 400 }}>
                  {clientiLab.length} clienti in anagrafica
                </span>
              )}
            </div>
            
            {/* Ricerca cliente esistente */}
            {scheda.isNuova && clientiLab.length > 0 && (
              <div style={{ marginBottom: 12, position: 'relative' }}>
                <label style={labelStyle}>🔍 Cerca cliente esistente</label>
                <input 
                  type="text" 
                  value={searchCliente}
                  onChange={e => { setSearchCliente(e.target.value); setShowSuggestions(true); }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  style={{...inputStyle, background: dark ? 'rgba(59,130,246,0.1)' : 'rgba(59,130,246,0.05)', border: '1px solid rgba(59,130,246,0.3)'}} 
                  placeholder="Digita nome o telefono..." 
                />
                {showSuggestions && clientiFiltrati.length > 0 && (
                  <div style={{ 
                    position: 'absolute', 
                    top: '100%', 
                    left: 0, 
                    right: 0, 
                    background: dark ? '#1e293b' : '#fff', 
                    border: '1px solid ' + bord, 
                    borderRadius: 8, 
                    marginTop: 4, 
                    zIndex: 100,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                  }}>
                    {clientiFiltrati.map(c => (
                      <div 
                        key={c.id}
                        onMouseDown={(e) => { e.preventDefault(); selezionaCliente(c); }}
                        style={{ 
                          padding: '12px 14px', 
                          cursor: 'pointer', 
                          borderBottom: '1px solid ' + bord,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          background: 'transparent',
                          transition: 'background 0.15s'
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = dark ? 'rgba(59,130,246,0.2)' : 'rgba(59,130,246,0.1)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{c.nome}</div>
                          <div style={{ fontSize: 12, color: muted }}>{c.telefono} {c.email ? `• ${c.email}` : ''}</div>
                        </div>
                        <span style={{ fontSize: 16, color: '#3b82f6' }}>✓</span>
                      </div>
                    ))}
                  </div>
                )}
                {showSuggestions && searchCliente && clientiFiltrati.length === 0 && (
                  <div style={{ 
                    position: 'absolute', 
                    top: '100%', 
                    left: 0, 
                    right: 0, 
                    background: dark ? '#1e293b' : '#fff', 
                    border: '1px solid ' + bord, 
                    borderRadius: 8, 
                    marginTop: 4,
                    padding: '10px 12px',
                    fontSize: 12,
                    color: muted
                  }}>
                    👤 Nuovo cliente - compila i dati sotto
                  </div>
                )}
              </div>
            )}
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Nome Cliente *</label>
                <input type="text" value={form.cliente} onChange={e => setForm({...form, cliente: e.target.value})} style={inputStyle} placeholder="Mario Rossi" />
              </div>
              <div>
                <label style={labelStyle}>Telefono *</label>
                <input type="tel" value={form.telefono} onChange={e => setForm({...form, telefono: e.target.value})} style={inputStyle} placeholder="339-1234567" />
              </div>
              <div>
                <label style={labelStyle}>Email</label>
                <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} style={inputStyle} placeholder="email@esempio.it" />
              </div>
            </div>
          </div>
          
          {/* Dati Dispositivo */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#3b82f6', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>💻 DISPOSITIVO</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Tipo</label>
                <select value={form.dispositivo} onChange={e => setForm({...form, dispositivo: e.target.value})} style={inputStyle}>
                  {tipiDispositivo.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Marca</label>
                <input type="text" value={form.marca} onChange={e => setForm({...form, marca: e.target.value})} style={inputStyle} placeholder="HP, Dell, Lenovo..." />
              </div>
              <div>
                <label style={labelStyle}>Modello</label>
                <input type="text" value={form.modello} onChange={e => setForm({...form, modello: e.target.value})} style={inputStyle} placeholder="Pavilion 15" />
              </div>
              <div>
                <label style={labelStyle}>Seriale (S/N)</label>
                <input type="text" value={form.seriale} onChange={e => setForm({...form, seriale: e.target.value})} style={inputStyle} placeholder="ABC123..." />
              </div>
              <div>
                <label style={labelStyle}>🔐 PIN / Password</label>
                <input type="text" value={form.pinPassword} onChange={e => setForm({...form, pinPassword: e.target.value})} style={inputStyle} placeholder="PIN o password dispositivo" />
              </div>
              <div>
                <label style={labelStyle}>Accessori consegnati</label>
                <input type="text" value={form.accessori} onChange={e => setForm({...form, accessori: e.target.value})} style={inputStyle} placeholder="Caricatore, borsa, mouse..." />
              </div>
            </div>
          </div>
          
          {/* Problema */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#3b82f6', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>🔧 PROBLEMA / INTERVENTO</div>
            <label style={labelStyle}>Problema segnalato</label>
            <textarea value={form.problema} onChange={e => setForm({...form, problema: e.target.value})} style={{...inputStyle, minHeight: 80, resize: 'vertical'}} placeholder="Descrivere il problema..." />
            
            {!scheda.isNuova && (
              <>
                <label style={{...labelStyle, marginTop: 12}}>Diagnosi / Intervento</label>
                <textarea value={form.diagnosi} onChange={e => setForm({...form, diagnosi: e.target.value})} style={{...inputStyle, minHeight: 80, resize: 'vertical'}} placeholder="Note tecniche..." />
              </>
            )}
          </div>
          
          {/* Importi */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#3b82f6', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>💰 IMPORTI</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Preventivo (€)</label>
                <input type="number" step="0.01" value={form.preventivo} onChange={e => setForm({...form, preventivo: e.target.value})} style={inputStyle} placeholder="0.00" />
              </div>
              <div>
                <label style={labelStyle}>Costo Riparazione (€)</label>
                <input type="number" step="0.01" value={form.costoRiparazione} onChange={e => setForm({...form, costoRiparazione: e.target.value})} style={inputStyle} placeholder="0.00" />
              </div>
              <div>
                <label style={labelStyle}>Importo Finale Cliente (€)</label>
                <input type="number" step="0.01" value={form.importoFinale} onChange={e => setForm({...form, importoFinale: e.target.value})} style={inputStyle} placeholder="0.00" />
              </div>
              <div>
                <label style={labelStyle}>💰 Acconto (€)</label>
                <input type="number" step="0.01" value={form.acconto || ''} onChange={e => setForm({...form, acconto: e.target.value})} style={{...inputStyle, background: parseFloat(form.acconto) > 0 ? 'rgba(34,197,94,0.1)' : bgInput, borderColor: parseFloat(form.acconto) > 0 ? 'rgba(34,197,94,0.3)' : bord}} placeholder="0.00" />
              </div>
              {parseFloat(form.acconto) > 0 && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>💰 Dove registrare l'acconto</label>
                  <select value={form.accontoConto || ''} onChange={e => setForm({...form, accontoConto: e.target.value})} style={{...inputStyle, background: form.accontoConto ? 'rgba(34,197,94,0.1)' : bgInput, borderColor: form.accontoConto ? 'rgba(34,197,94,0.3)' : bord}}>
                    <option value="">-- Seleziona conto --</option>
                    {contiDisponibili.map(c => (
                      <option key={c.id} value={c.id}>{c.icon} {c.nome}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
          
          {/* Note */}
          <div>
            <label style={labelStyle}>Note interne (solo per voi)</label>
            <textarea value={form.note} onChange={e => setForm({...form, note: e.target.value})} style={{...inputStyle, minHeight: 60, resize: 'vertical'}} placeholder="Note visibili solo a te..." />
          </div>
          
          {/* Note pubbliche - visibili al cliente */}
          <div style={{ marginTop: 12 }}>
            <label style={labelStyle}>📢 Note per il cliente (visibili su /stato)</label>
            <textarea value={form.notePubbliche} onChange={e => setForm({...form, notePubbliche: e.target.value})} style={{...inputStyle, minHeight: 50, resize: 'vertical', background: 'rgba(34,197,94,0.1)', borderColor: 'rgba(34,197,94,0.3)'}} placeholder="Es: Abbiamo ordinato il pezzo, arriva domani..." />
          </div>
          
          {/* Tempo stimato */}
          <div style={{ marginTop: 12 }}>
            <label style={labelStyle}>⏱️ Tempo stimato riparazione</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <select 
                value={form.tempoStimato?.startsWith('data:') ? 'data_specifica' : form.tempoStimato} 
                onChange={e => {
                  if (e.target.value === 'data_specifica') {
                    // Imposta data di domani come default
                    const domani = new Date();
                    domani.setDate(domani.getDate() + 1);
                    setForm({...form, tempoStimato: 'data:' + domani.toISOString().split('T')[0]});
                  } else {
                    setForm({...form, tempoStimato: e.target.value});
                  }
                }} 
                style={{...inputStyle, flex: 1}}
              >
                <option value="">Non specificato</option>
                <option value="stesso_giorno">Stesso giorno</option>
                <option value="1_giorno">1 giorno</option>
                <option value="2_3_giorni">2-3 giorni</option>
                <option value="1_settimana">1 settimana</option>
                <option value="2_settimane">2 settimane</option>
                <option value="da_valutare">Da valutare dopo diagnosi</option>
                <option value="data_specifica">📅 Data specifica...</option>
              </select>
              {form.tempoStimato?.startsWith('data:') && (
                <input 
                  type="date" 
                  value={form.tempoStimato.replace('data:', '')}
                  onChange={e => setForm({...form, tempoStimato: 'data:' + e.target.value})}
                  style={{...inputStyle, width: 'auto'}}
                  min={new Date().toISOString().split('T')[0]}
                />
              )}
            </div>
          </div>
          
          {/* WhatsApp Notifiche */}
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
            <input 
              type="checkbox" 
              checked={form.notificaWhatsapp} 
              onChange={e => setForm({...form, notificaWhatsapp: e.target.checked})}
              style={{ width: 18, height: 18, accentColor: '#25d366' }}
            />
            <label style={{ fontSize: 13, fontWeight: 500 }}>📱 Invia notifiche WhatsApp al cambio stato</label>
          </div>
          
          {/* Urgente */}
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
            <input 
              type="checkbox" 
              checked={form.urgente} 
              onChange={e => setForm({...form, urgente: e.target.checked})}
              style={{ width: 18, height: 18, accentColor: '#ef4444' }}
            />
            <label style={{ fontSize: 13, fontWeight: 500, color: form.urgente ? '#ef4444' : undefined }}>🚨 URGENTE - Priorità alta</label>
          </div>
          
          {/* Foto dispositivo */}
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#8b5cf6', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <span>📷 FOTO DISPOSITIVO</span>
              <div style={{ display: 'flex', gap: 6 }}>
                {form.foto && form.foto.length > 0 && scheda.telefono && !scheda.isNuova && (
                  <button 
                    onClick={() => {
                      const linkStato = `${window.location.origin}/stato?id=${scheda.id}&tel=${scheda.telefono}`;
                      const msg = `📷 *PC Care Napoli*\n\nAbbiamo caricato ${form.foto.length} foto del tuo dispositivo.\n\n🔗 Visualizzale qui:\n${linkStato}`;
                      window.open(`https://wa.me/39${scheda.telefono.replace(/\D/g, '')}?text=${encodeURIComponent(msg)}`, '_blank');
                    }}
                    style={{ padding: '4px 10px', background: 'rgba(37,211,102,0.2)', border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer', color: '#25d366', fontWeight: 600 }}
                  >
                    📱 Notifica foto
                  </button>
                )}
                <button 
                  onClick={() => document.getElementById('foto-input-scheda').click()}
                  disabled={uploadingFoto}
                  style={{ padding: '4px 10px', background: 'rgba(139,92,246,0.2)', border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer', color: '#8b5cf6', fontWeight: 600 }}
                >
                  {uploadingFoto ? '⏳ Caricamento...' : '+ Aggiungi foto'}
                </button>
              </div>
              <input 
                id="foto-input-scheda"
                type="file"
                accept="image/*"
                multiple
                capture="environment"
                style={{ display: 'none' }}
                onChange={async (e) => {
                  const files = Array.from(e.target.files);
                  if (files.length === 0) return;
                  setUploadingFoto(true);
                  try {
                    const nuoveFoto = [];
                    for (const file of files) {
                      // Comprimi e converti in base64
                      const reader = new FileReader();
                      const base64 = await new Promise((resolve) => {
                        reader.onload = () => resolve(reader.result);
                        reader.readAsDataURL(file);
                      });
                      nuoveFoto.push({
                        id: Date.now() + Math.random().toString(36).substr(2,5),
                        data: base64,
                        nome: file.name,
                        dataCaricamento: new Date().toISOString()
                      });
                    }
                    setForm(prev => ({ ...prev, foto: [...(prev.foto || []), ...nuoveFoto] }));
                  } catch (err) {
                    console.error('Errore upload foto:', err);
                    alert('Errore nel caricamento delle foto');
                  }
                  setUploadingFoto(false);
                  e.target.value = '';
                }}
              />
            </div>
            {form.foto && form.foto.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                {form.foto.map((foto, idx) => (
                  <div key={foto.id || idx} style={{ position: 'relative', paddingTop: '100%', borderRadius: 8, overflow: 'hidden', background: bgInput }}>
                    <img 
                      src={foto.data || foto} 
                      alt={`Foto ${idx + 1}`}
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <button
                      onClick={() => setForm(prev => ({ ...prev, foto: prev.foto.filter((_, i) => i !== idx) }))}
                      style={{ position: 'absolute', top: 4, right: 4, width: 20, height: 20, background: 'rgba(239,68,68,0.9)', border: 'none', borderRadius: '50%', color: '#fff', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >✕</button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: 20, background: bgInput, borderRadius: 8, textAlign: 'center', color: muted, fontSize: 12 }}>
                Nessuna foto. Clicca "Aggiungi foto" per scattare o caricare.
              </div>
            )}
          </div>
          
          {/* Firme digitali / Autorizzazioni */}
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#f59e0b', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>✍️ FIRME / AUTORIZZAZIONI</span>
              <button 
                onClick={() => setShowFirmaModal({ tipo: '', descrizione: '' })}
                style={{ padding: '4px 10px', background: 'rgba(245,158,11,0.2)', border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer', color: '#f59e0b', fontWeight: 600 }}
              >
                + Richiedi firma
              </button>
            </div>
            {form.firme && form.firme.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {form.firme.map((firma, idx) => (
                  <div key={idx} style={{ padding: 10, background: firma.richiesta && !firma.firma ? 'rgba(245,158,11,0.1)' : bgInput, borderRadius: 8, border: firma.richiesta && !firma.firma ? '1px solid rgba(245,158,11,0.3)' : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 20 }}>{firma.richiesta && !firma.firma ? '⏳' : '✅'}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>{firma.tipo}</div>
                        <div style={{ fontSize: 9, color: firma.richiesta && !firma.firma ? '#f59e0b' : muted }}>
                          {firma.richiesta && !firma.firma ? '📱 In attesa firma cliente' : `Firmato: ${new Date(firma.dataFirma || firma.data).toLocaleString('it-IT')}`}
                        </div>
                      </div>
                      {firma.firma && (
                        <>
                          <button
                            onClick={() => {
                              // Mostra anteprima firma
                              const modal = document.createElement('div');
                              modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.8);display:flex;align-items:center;justify-content:center;z-index:9999;padding:20px;';
                              modal.onclick = () => modal.remove();
                              modal.innerHTML = `
                                <div style="background:#fff;border-radius:12px;padding:20px;max-width:500px;width:100%;max-height:90vh;overflow:auto;" onclick="event.stopPropagation()">
                                  <h3 style="margin:0 0 12px;font-size:16px;">${firma.tipo}</h3>
                                  <div style="font-size:11px;color:#666;max-height:200px;overflow:auto;padding:12px;background:#f5f5f5;border-radius:8px;margin-bottom:12px;white-space:pre-wrap;">${firma.descrizione || 'Nessuna descrizione'}</div>
                                  <div style="text-align:center;padding:12px;background:#f0f0f0;border-radius:8px;">
                                    <div style="font-size:10px;color:#666;margin-bottom:8px;">FIRMA:</div>
                                    <img src="${firma.firma}" style="max-width:100%;height:auto;border:1px solid #ddd;border-radius:4px;" />
                                  </div>
                                  <div style="font-size:10px;color:#666;margin-top:8px;text-align:center;">
                                    Data: ${new Date(firma.dataFirma || firma.data).toLocaleString('it-IT')}
                                  </div>
                                </div>
                              `;
                              document.body.appendChild(modal);
                            }}
                            style={{ padding: '4px 8px', background: 'rgba(59,130,246,0.2)', border: 'none', borderRadius: 4, color: '#3b82f6', fontSize: 12, cursor: 'pointer' }}
                            title="Visualizza firma"
                          >👁️</button>
                          <button
                            onClick={() => {
                              // Scarica PDF con modulo e firma
                              const printWindow = window.open('', '_blank');
                              printWindow.document.write(`
                                <!DOCTYPE html>
                                <html>
                                <head>
                                  <title>Autorizzazione - ${firma.tipo}</title>
                                  <style>
                                    * { margin: 0; padding: 0; box-sizing: border-box; }
                                    body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
                                    .header { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #333; }
                                    .header h1 { font-size: 18px; margin-bottom: 5px; }
                                    .header p { font-size: 12px; color: #666; }
                                    .scheda-info { background: #f5f5f5; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
                                    .scheda-info p { font-size: 12px; margin: 4px 0; }
                                    .titolo-modulo { font-size: 16px; font-weight: bold; text-align: center; margin: 20px 0; padding: 10px; background: #e0e0e0; }
                                    .testo-modulo { font-size: 11px; line-height: 1.6; white-space: pre-wrap; padding: 20px; border: 1px solid #ddd; border-radius: 8px; margin-bottom: 20px; }
                                    .firma-section { margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; }
                                    .firma-section h3 { font-size: 12px; margin-bottom: 10px; }
                                    .firma-img { text-align: center; padding: 20px; background: #fafafa; border: 1px solid #ddd; border-radius: 8px; }
                                    .firma-img img { max-width: 300px; }
                                    .data-firma { text-align: center; font-size: 11px; color: #666; margin-top: 10px; }
                                    .footer { margin-top: 40px; text-align: center; font-size: 10px; color: #999; }
                                    @media print { body { padding: 20px; } }
                                  </style>
                                </head>
                                <body>
                                  <div class="header">
                                    <h1>🖥️ PC CARE NAPOLI</h1>
                                    <p>Vico Acitillo, 148 - 80127 Napoli | Tel: 081.191.73.939</p>
                                  </div>
                                  
                                  <div class="scheda-info">
                                    <p><strong>Scheda:</strong> #${scheda.id || form.id || 'N/D'}</p>
                                    <p><strong>Cliente:</strong> ${form.cliente || 'N/D'}</p>
                                    <p><strong>Telefono:</strong> ${form.telefono || 'N/D'}</p>
                                    <p><strong>Dispositivo:</strong> ${form.marca || ''} ${form.modello || ''}</p>
                                    <p><strong>Data documento:</strong> ${new Date(firma.data).toLocaleDateString('it-IT')}</p>
                                  </div>
                                  
                                  <div class="titolo-modulo">${firma.tipo}</div>
                                  
                                  <div class="testo-modulo">${firma.descrizione || 'Nessuna descrizione disponibile'}</div>
                                  
                                  <div class="firma-section">
                                    <h3>FIRMA DEL CLIENTE:</h3>
                                    <div class="firma-img">
                                      <img src="${firma.firma}" alt="Firma cliente" />
                                    </div>
                                    <div class="data-firma">
                                      Firmato digitalmente il ${new Date(firma.dataFirma || firma.data).toLocaleString('it-IT')}
                                    </div>
                                  </div>
                                  
                                  <div class="footer">
                                    Documento generato automaticamente da PC Care Napoli - Gestionale v2.4.4
                                  </div>
                                  
                                  <script>window.onload = function() { window.print(); }</script>
                                </body>
                                </html>
                              `);
                              printWindow.document.close();
                            }}
                            style={{ padding: '4px 8px', background: 'rgba(34,197,94,0.2)', border: 'none', borderRadius: 4, color: '#22c55e', fontSize: 12, cursor: 'pointer' }}
                            title="Scarica/Stampa PDF"
                          >📄</button>
                        </>
                      )}
                      {firma.richiesta && !firma.firma && form.telefono && (
                        <button
                          onClick={() => {
                            const telefono = form.telefono?.replace(/\D/g, '');
                            const messaggio = `Gentile ${form.cliente},

è richiesta la tua firma per: *${firma.tipo}*

Scheda: #${scheda.id || 'N/D'}

👉 Firma qui:
https://gestpccare.it/stato

Inserisci scheda ${scheda.id || 'N/D'} e il tuo numero.

PC CARE NAPOLI`;
                            const telFormattato = telefono.startsWith('39') ? telefono : '39' + telefono;
                            window.open(`https://wa.me/${telFormattato}?text=${encodeURIComponent(messaggio)}`, '_blank');
                          }}
                          style={{ padding: '4px 8px', background: 'rgba(37,211,102,0.2)', border: 'none', borderRadius: 4, color: '#25d366', fontSize: 14, cursor: 'pointer' }}
                          title="Invia promemoria WhatsApp"
                        >📱</button>
                      )}
                      <button
                        onClick={() => setForm(prev => ({ ...prev, firme: prev.firme.filter((_, i) => i !== idx) }))}
                        style={{ padding: '4px 8px', background: 'rgba(239,68,68,0.1)', border: 'none', borderRadius: 4, color: '#ef4444', fontSize: 10, cursor: 'pointer' }}
                      >✕</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: 16, background: bgInput, borderRadius: 8, textAlign: 'center', color: muted, fontSize: 12 }}>
                Nessuna firma richiesta. Usa per autorizzazioni (formattazione, riparazione scheda madre, ecc.)
              </div>
            )}
          </div>
          
          {/* Checklist Ingresso */}
          {checklistItems.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div 
                onClick={() => setShowChecklist(!showChecklist)}
                style={{ 
                  fontSize: 12, 
                  fontWeight: 700, 
                  color: '#f59e0b', 
                  marginBottom: 12, 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 6,
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                📋 CHECKLIST INGRESSO {showChecklist ? '▼' : '▶'}
                <span style={{ fontSize: 10, fontWeight: 400, color: muted, marginLeft: 'auto' }}>
                  {Object.values(form.checklist).filter(Boolean).length}/{checklistItems.length} verificati
                </span>
              </div>
              
              {showChecklist && (
                <>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(2, 1fr)', 
                    gap: 8,
                    marginBottom: 12
                  }}>
                    {checklistItems.map(item => {
                      const checked = form.checklist[item.id];
                      const isWarning = item.id === 'graffi';
                      return (
                        <div
                          key={item.id}
                          onClick={() => setForm({
                            ...form, 
                            checklist: { ...form.checklist, [item.id]: checked === true ? false : checked === false ? null : true }
                          })}
                          style={{
                            padding: '10px 12px',
                            borderRadius: 8,
                            border: '1px solid ' + (checked === true ? (isWarning ? '#f59e0b' : '#22c55e') : checked === false ? '#ef4444' : bord),
                            background: checked === true 
                              ? (isWarning ? 'rgba(245,158,11,0.1)' : 'rgba(34,197,94,0.1)') 
                              : checked === false 
                                ? 'rgba(239,68,68,0.1)' 
                                : 'transparent',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            transition: 'all 0.15s'
                          }}
                        >
                          <span style={{ fontSize: 16 }}>
                            {checked === true ? (isWarning ? '⚠️' : '✅') : checked === false ? '❌' : '⬜'}
                          </span>
                          <span style={{ fontSize: 12, fontWeight: 500, color: dark ? '#e2e8f0' : '#1e293b' }}>
                            {item.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div>
                    <label style={labelStyle}>Note checklist</label>
                    <textarea 
                      value={form.noteChecklist} 
                      onChange={e => setForm({...form, noteChecklist: e.target.value})} 
                      style={{...inputStyle, minHeight: 50, resize: 'vertical'}} 
                      placeholder="Es: Graffio angolo sinistro, tasto F5 duro..." 
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, padding: '16px 20px', borderTop: '1px solid ' + bord, flexWrap: 'wrap' }}>
          <button onClick={onClose} style={{ padding: '12px 16px', background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 10, color: muted, cursor: 'pointer' }}>Annulla</button>
          <button onClick={stampaAnteprima} style={{ padding: '12px 16px', background: 'rgba(100,116,139,0.1)', border: '1px solid rgba(100,116,139,0.3)', borderRadius: 10, color: '#64748b', cursor: 'pointer' }} title="Solo anteprima">🖨️</button>
          <button onClick={salva} style={{ flex: 1, padding: 12, background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: '1px solid ' + bord, borderRadius: 10, color: dark ? '#e2e8f0' : '#1e293b', fontWeight: 500, cursor: 'pointer' }}>💾 Salva</button>
          {scheda.isNuova && onSaveAndPrint && (
            <button 
              onClick={() => {
                // FIX: questo pulsante duplicava la validazione di salva() e si era
                // perso il controllo sull'acconto. È proprio il pulsante usato
                // all'accettazione, cioè quando l'acconto viene inserito.
                if (!validaForm()) return;
                onSaveAndPrint({
                  ...scheda,
                  ...form,
                  preventivo: parseFloat(form.preventivo) || 0,
                  costoRiparazione: parseFloat(form.costoRiparazione) || 0,
                  importoFinale: parseFloat(form.importoFinale) || 0
                });
              }} 
              style={{ flex: 1, padding: 12, background: 'linear-gradient(135deg,#22c55e,#16a34a)', border: 'none', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer' }}
            >
              💾🖨️ Salva e Stampa
            </button>
          )}
        </div>
      </div>
      
      {/* Modal Firma Digitale */}
      {showFirmaModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 20 }} onClick={() => setShowFirmaModal(null)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 16, width: '100%', maxWidth: 400, padding: 20 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700 }}>✍️ Richiedi Firma / Autorizzazione</h3>
            
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Tipo autorizzazione</label>
              <select 
                value={showFirmaModal.tipo} 
                onChange={e => {
                  const tipo = e.target.value;
                  // Testi predefiniti per ogni modulo
                  const testiModuli = {
                    'Autorizzazione formattazione': `AUTORIZZAZIONE ALLA FORMATTAZIONE E CANCELLAZIONE DATI

Il/La sottoscritto/a ${form.cliente || '[CLIENTE]'}, proprietario/a del dispositivo ${form.marca || ''} ${form.modello || ''} (S/N: ${form.seriale || 'N/D'}), consegnato in data ${new Date().toLocaleDateString('it-IT')} con scheda n° ${scheda.id || '[ID]'}:

• AUTORIZZA PC Care Napoli ad effettuare la formattazione completa del dispositivo
• DICHIARA di essere consapevole che tale operazione comporterà la CANCELLAZIONE IRREVERSIBILE di tutti i dati presenti
• DICHIARA di aver provveduto autonomamente al backup dei propri dati oppure di rinunciare espressamente al loro recupero
• SOLLEVA PC Care Napoli da qualsiasi responsabilità per la perdita di dati, documenti, foto, video o qualsiasi altro contenuto digitale

PC Care Napoli non potrà in alcun modo essere ritenuta responsabile per danni diretti o indiretti derivanti dalla perdita dei dati.`,

                    'Autorizzazione riparazione scheda madre': `AUTORIZZAZIONE INTERVENTO SU SCHEDA MADRE

Il/La sottoscritto/a ${form.cliente || '[CLIENTE]'}, proprietario/a del dispositivo ${form.marca || ''} ${form.modello || ''}, consegnato con scheda n° ${scheda.id || '[ID]'}:

• AUTORIZZA PC Care Napoli ad effettuare interventi di riparazione sulla scheda madre/logica del dispositivo
• DICHIARA di essere consapevole che:
  - L'intervento su scheda madre è un'operazione complessa con esito non garantito
  - Esiste il rischio che il dispositivo non sia riparabile o subisca danni ulteriori
  - In caso di esito negativo, il dispositivo potrebbe risultare non più funzionante
  - Il costo dell'intervento è dovuto anche in caso di esito negativo della riparazione

• ACCETTA il preventivo di €${form.preventivo || '___'} per l'intervento
• SOLLEVA PC Care Napoli da responsabilità per eventuali danni derivanti dall'intervento`,

                    'Accettazione preventivo': `ACCETTAZIONE PREVENTIVO

Il/La sottoscritto/a ${form.cliente || '[CLIENTE]'}, proprietario/a del dispositivo ${form.marca || ''} ${form.modello || ''}, scheda n° ${scheda.id || '[ID]'}:

• ACCETTA il preventivo di €${form.preventivo || '___'} (IVA inclusa) per la riparazione del dispositivo
• AUTORIZZA PC Care Napoli a procedere con l'intervento di riparazione
• SI IMPEGNA al ritiro e al pagamento dell'importo pattuito entro 30 giorni dalla comunicazione di "pronto per il ritiro"
• PRENDE ATTO che decorso tale termine verranno applicati costi di giacenza di €1,00/giorno

Problema segnalato: ${form.problema || 'N/D'}
Intervento previsto: ${form.diagnosi || 'Da definire'}`,

                    'Esonero responsabilità dati': `ESONERO RESPONSABILITÀ PER I DATI

Il/La sottoscritto/a ${form.cliente || '[CLIENTE]'}, proprietario/a del dispositivo consegnato con scheda n° ${scheda.id || '[ID]'}:

• DICHIARA di essere stato/a informato/a che qualsiasi intervento di riparazione può comportare rischi per i dati memorizzati sul dispositivo
• DICHIARA di aver provveduto al backup completo di tutti i dati importanti PRIMA della consegna del dispositivo
• ESONERA PC Care Napoli da qualsiasi responsabilità per:
  - Perdita totale o parziale di dati
  - Danneggiamento di file o documenti
  - Impossibilità di recupero dati
  - Qualsiasi danno diretto o indiretto legato ai dati

PC Care Napoli consiglia SEMPRE di effettuare un backup completo prima di qualsiasi intervento.`,

                    'Autorizzazione sostituzione componenti': `AUTORIZZAZIONE SOSTITUZIONE COMPONENTI

Il/La sottoscritto/a ${form.cliente || '[CLIENTE]'}, proprietario/a del dispositivo ${form.marca || ''} ${form.modello || ''}, scheda n° ${scheda.id || '[ID]'}:

• AUTORIZZA PC Care Napoli a sostituire i componenti difettosi con:
  □ Ricambi originali (se disponibili)
  □ Ricambi compatibili di qualità equivalente
  □ Ricambi rigenerati/ricondizionati

• ACCETTA che la scelta del ricambio sia effettuata da PC Care Napoli in base a disponibilità e convenienza, salvo diversa indicazione scritta
• PRENDE ATTO che i componenti sostituiti rimarranno di proprietà di PC Care Napoli
• ACCETTA il preventivo di €${form.preventivo || '___'} per l'intervento comprensivo di ricambi e manodopera`,

                    'Rinuncia riparazione': `RINUNCIA ALLA RIPARAZIONE

Il/La sottoscritto/a ${form.cliente || '[CLIENTE]'}, proprietario/a del dispositivo ${form.marca || ''} ${form.modello || ''}, scheda n° ${scheda.id || '[ID]'}:

• RINUNCIA alla riparazione del dispositivo
• RICHIEDE la restituzione del dispositivo nello stato in cui si trova
• PRENDE ATTO che:
  - Il dispositivo verrà restituito non riparato
  - È dovuto il pagamento del diritto di diagnosi/chiamata di €${form.preventivo || '___'}
  - PC Care Napoli non è responsabile per eventuali danni pregressi

Motivo della rinuncia: ________________________________`,

                    'Ritiro dispositivo': `DICHIARAZIONE DI RITIRO DISPOSITIVO

Il/La sottoscritto/a ${form.cliente || '[CLIENTE]'}, in data odierna ${new Date().toLocaleDateString('it-IT')}:

• DICHIARA di ritirare il dispositivo ${form.marca || ''} ${form.modello || ''} (scheda n° ${scheda.id || '[ID]'})
• CONFERMA che il dispositivo è stato riconsegnato in condizioni conformi a quanto concordato
• CONFERMA di aver verificato il corretto funzionamento del dispositivo
• ACCETTA le condizioni di garanzia: 90 giorni sulla riparazione effettuata (esclusi danni accidentali, liquidi, software)

Importo pagato: €${form.importoFinale || form.preventivo || '___'}
Metodo di pagamento: □ Contanti □ POS □ Bonifico

Note al ritiro: ________________________________`,

                    'Altro': ''
                  };
                  
                  setShowFirmaModal({
                    ...showFirmaModal, 
                    tipo: tipo,
                    descrizione: testiModuli[tipo] || ''
                  });
                }}
                style={inputStyle}
              >
                <option value="">Seleziona modulo...</option>
                <option value="Autorizzazione formattazione">🗑️ Autorizzazione formattazione/cancellazione dati</option>
                <option value="Autorizzazione riparazione scheda madre">🔧 Autorizzazione riparazione scheda madre</option>
                <option value="Accettazione preventivo">💰 Accettazione preventivo</option>
                <option value="Esonero responsabilità dati">⚠️ Esonero responsabilità dati</option>
                <option value="Autorizzazione sostituzione componenti">🔄 Autorizzazione sostituzione componenti</option>
                <option value="Rinuncia riparazione">❌ Rinuncia alla riparazione</option>
                <option value="Ritiro dispositivo">📦 Ritiro dispositivo</option>
                <option value="Altro">📝 Altro (testo libero)</option>
              </select>
            </div>
            
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Testo del modulo {showFirmaModal.tipo && showFirmaModal.tipo !== 'Altro' && <span style={{ fontWeight: 400, color: '#22c55e' }}>(precompilato)</span>}</label>
              <textarea 
                value={showFirmaModal.descrizione}
                onChange={e => setShowFirmaModal({...showFirmaModal, descrizione: e.target.value})}
                style={{...inputStyle, minHeight: 150, resize: 'vertical', fontSize: 11, fontFamily: 'monospace', lineHeight: 1.4}}
                placeholder="Inserisci il testo del modulo..."
              />
            </div>
            
            {/* Opzione: richiedi firma al cliente */}
            <div style={{ marginBottom: 16, padding: 12, background: 'rgba(245,158,11,0.1)', borderRadius: 10, border: '1px solid rgba(245,158,11,0.3)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input 
                  type="checkbox"
                  checked={showFirmaModal.richiestaCliente || false}
                  onChange={e => setShowFirmaModal({...showFirmaModal, richiestaCliente: e.target.checked})}
                  style={{ width: 18, height: 18, accentColor: '#f59e0b' }}
                />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>📱 Richiedi firma al cliente</div>
                  <div style={{ fontSize: 11, color: muted }}>Il cliente potrà firmare dalla pagina /stato</div>
                </div>
              </label>
            </div>
            
            {!showFirmaModal.richiestaCliente && (
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Area firma (disegna qui)</label>
              <canvas 
                id="firma-canvas"
                width={350}
                height={150}
                style={{ width: '100%', height: 150, background: '#fff', border: '2px solid ' + bord, borderRadius: 8, touchAction: 'none' }}
                onMouseDown={(e) => {
                  const canvas = e.target;
                  const ctx = canvas.getContext('2d');
                  ctx.beginPath();
                  const rect = canvas.getBoundingClientRect();
                  ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
                  canvas.isDrawing = true;
                }}
                onMouseMove={(e) => {
                  const canvas = e.target;
                  if (!canvas.isDrawing) return;
                  const ctx = canvas.getContext('2d');
                  const rect = canvas.getBoundingClientRect();
                  ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
                  ctx.strokeStyle = '#000';
                  ctx.lineWidth = 2;
                  ctx.stroke();
                }}
                onMouseUp={(e) => { e.target.isDrawing = false; }}
                onMouseLeave={(e) => { e.target.isDrawing = false; }}
                onTouchStart={(e) => {
                  const canvas = e.target;
                  const ctx = canvas.getContext('2d');
                  ctx.beginPath();
                  const rect = canvas.getBoundingClientRect();
                  const touch = e.touches[0];
                  ctx.moveTo(touch.clientX - rect.left, touch.clientY - rect.top);
                  canvas.isDrawing = true;
                }}
                onTouchMove={(e) => {
                  e.preventDefault();
                  const canvas = e.target;
                  if (!canvas.isDrawing) return;
                  const ctx = canvas.getContext('2d');
                  const rect = canvas.getBoundingClientRect();
                  const touch = e.touches[0];
                  ctx.lineTo(touch.clientX - rect.left, touch.clientY - rect.top);
                  ctx.strokeStyle = '#000';
                  ctx.lineWidth = 2;
                  ctx.stroke();
                }}
                onTouchEnd={(e) => { e.target.isDrawing = false; }}
              />
              <button 
                onClick={() => {
                  const canvas = document.getElementById('firma-canvas');
                  const ctx = canvas.getContext('2d');
                  ctx.clearRect(0, 0, canvas.width, canvas.height);
                }}
                style={{ marginTop: 8, padding: '6px 12px', background: 'rgba(239,68,68,0.1)', border: 'none', borderRadius: 6, color: '#ef4444', fontSize: 12, cursor: 'pointer' }}
              >
                🗑️ Cancella firma
              </button>
            </div>
            )}
            
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowFirmaModal(null)} style={{ flex: 1, padding: 12, background: 'rgba(100,116,139,0.1)', border: 'none', borderRadius: 10, color: muted, cursor: 'pointer' }}>Annulla</button>
              <button 
                onClick={() => {
                  if (!showFirmaModal.tipo) {
                    alert('Seleziona il tipo di autorizzazione');
                    return;
                  }
                  
                  let nuovaFirma;
                  
                  if (showFirmaModal.richiestaCliente) {
                    // Richiesta firma - il cliente firmerà dalla pagina /stato
                    nuovaFirma = {
                      tipo: showFirmaModal.tipo,
                      descrizione: showFirmaModal.descrizione,
                      richiesta: true,
                      firma: null,
                      data: new Date().toISOString()
                    };
                    
                    // Chiedi se vuole inviare WhatsApp
                    const telefono = form.telefono?.replace(/\D/g, '');
                    if (telefono) {
                      const inviaWA = window.confirm('Vuoi inviare un messaggio WhatsApp al cliente per notificare la richiesta di firma?');
                      if (inviaWA) {
                        const messaggio = `Gentile ${form.cliente},

è richiesta la tua firma per: *${showFirmaModal.tipo}*

Scheda riparazione: #${scheda.id || 'N/D'}
Dispositivo: ${form.marca || ''} ${form.modello || ''}

👉 Clicca qui per leggere e firmare:
https://gestpccare.it/stato

Inserisci:
• Numero scheda: ${scheda.id || 'N/D'}
• Tuo numero di telefono

Per info: 081.191.73.939
PC CARE NAPOLI`;
                        
                        const telFormattato = telefono.startsWith('39') ? telefono : '39' + telefono;
                        window.open(`https://wa.me/${telFormattato}?text=${encodeURIComponent(messaggio)}`, '_blank');
                      }
                    }
                  } else {
                    // Firma immediata in negozio
                    const canvas = document.getElementById('firma-canvas');
                    const firmaBase64 = canvas.toDataURL('image/png');
                    nuovaFirma = {
                      tipo: showFirmaModal.tipo,
                      descrizione: showFirmaModal.descrizione,
                      richiesta: false,
                      firma: firmaBase64,
                      data: new Date().toISOString()
                    };
                  }
                  
                  setForm(prev => ({ ...prev, firme: [...(prev.firme || []), nuovaFirma] }));
                  setShowFirmaModal(null);
                }}
                style={{ flex: 1, padding: 12, background: 'linear-gradient(135deg,#22c55e,#16a34a)', border: 'none', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer' }}
              >
                {showFirmaModal.richiestaCliente ? '📱 Invia Richiesta' : '✓ Salva Firma'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Modal dettaglio scheda
function DettaglioSchedaModal({ scheda, stati, onCambiaStato, onModifica, onWhatsApp, onStampa, onStampaEtichetta, onElimina, onClose, dark, bgCard, bgInput, bord, muted }) {
  const stato = stati[scheda.stato] || stati.in_attesa;
  
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={onClose}>
      <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 500, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: '20px', borderBottom: '1px solid ' + bord, background: 'linear-gradient(135deg, ' + stato.color + '20, transparent)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 12, color: muted, marginBottom: 4 }}>SCHEDA</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#3b82f6' }}>#{scheda.id}</div>
            </div>
            <div style={{ padding: '6px 12px', borderRadius: 20, background: stato.color + "20", color: stato.color, fontWeight: 600, fontSize: 13 }}>
              {stato.icon} {stato.label}
            </div>
          </div>
        </div>
        
        {/* Contenuto */}
        <div style={{ padding: 20 }}>
          {/* Cliente */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: muted, marginBottom: 4 }}>CLIENTE</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{scheda.cliente}</div>
            <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: 13, color: muted }}>
              {scheda.telefono && <span>📱 {scheda.telefono}</span>}
              {scheda.email && <span>✉️ {scheda.email}</span>}
            </div>
          </div>
          
          {/* Dispositivo */}
          <div style={{ marginBottom: 16, padding: 12, background: bgInput, borderRadius: 10 }}>
            <div style={{ fontSize: 11, color: muted, marginBottom: 4 }}>DISPOSITIVO</div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>{scheda.dispositivo} {scheda.marca} {scheda.modello}</div>
            {scheda.seriale && <div style={{ fontSize: 12, color: muted, marginTop: 4 }}>S/N: {scheda.seriale}</div>}
            {scheda.pinPassword && <div style={{ fontSize: 12, color: muted, marginTop: 4 }}>🔐 PIN/Password: <strong>{scheda.pinPassword}</strong></div>}
            {scheda.accessori && <div style={{ fontSize: 12, color: muted, marginTop: 4 }}>📦 Accessori: {scheda.accessori}</div>}
          </div>
          
          {/* Problema / Diagnosi */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: muted, marginBottom: 4 }}>PROBLEMA SEGNALATO</div>
            <div style={{ fontSize: 14, padding: 12, background: bgInput, borderRadius: 10, borderLeft: '3px solid #f59e0b' }}>{scheda.problema || '-'}</div>
            
            {scheda.diagnosi && (
              <>
                <div style={{ fontSize: 11, color: muted, marginBottom: 4, marginTop: 12 }}>DIAGNOSI / INTERVENTO</div>
                <div style={{ fontSize: 14, padding: 12, background: bgInput, borderRadius: 10, borderLeft: '3px solid #22c55e' }}>{scheda.diagnosi}</div>
              </>
            )}
          </div>
          
          {/* Date e importi */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div style={{ padding: 12, background: bgInput, borderRadius: 10 }}>
              <div style={{ fontSize: 11, color: muted }}>DATA INGRESSO</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{new Date(scheda.dataIngresso).toLocaleDateString('it-IT')}</div>
            </div>
            {scheda.dataConsegna && (
              <div style={{ padding: 12, background: bgInput, borderRadius: 10 }}>
                <div style={{ fontSize: 11, color: muted }}>DATA CONSEGNA</div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{new Date(scheda.dataConsegna).toLocaleDateString('it-IT')}</div>
              </div>
            )}
            {scheda.preventivo > 0 && (
              <div style={{ padding: 12, background: bgInput, borderRadius: 10 }}>
                <div style={{ fontSize: 11, color: muted }}>PREVENTIVO</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#f59e0b' }}>€{scheda.preventivo.toFixed(2)}</div>
              </div>
            )}
            {scheda.costoRiparazione > 0 && (
              <div style={{ padding: 12, background: 'rgba(239,68,68,0.1)', borderRadius: 10, border: '1px solid rgba(239,68,68,0.3)' }}>
                <div style={{ fontSize: 11, color: '#ef4444' }}>💸 COSTO RIPARAZIONE</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#ef4444' }}>€{scheda.costoRiparazione.toFixed(2)}</div>
              </div>
            )}
            {scheda.importoFinale > 0 && (
              <div style={{ padding: 12, background: bgInput, borderRadius: 10 }}>
                <div style={{ fontSize: 11, color: muted }}>IMPORTO FINALE</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#22c55e' }}>€{scheda.importoFinale.toFixed(2)}</div>
              </div>
            )}
            {(scheda.importoFinale > 0 && scheda.costoRiparazione > 0) && (
              <div style={{ padding: 12, background: 'rgba(34,197,94,0.1)', borderRadius: 10, border: '1px solid rgba(34,197,94,0.3)' }}>
                <div style={{ fontSize: 11, color: '#22c55e' }}>📈 MARGINE</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#22c55e' }}>€{(scheda.importoFinale - scheda.costoRiparazione).toFixed(2)}</div>
              </div>
            )}
          </div>
          
          {/* Note */}
          {scheda.note && (
            <div style={{ marginBottom: 16, padding: 12, background: 'rgba(139,92,246,0.1)', borderRadius: 10 }}>
              <div style={{ fontSize: 11, color: '#8b5cf6', marginBottom: 4 }}>📝 NOTE INTERNE</div>
              <div style={{ fontSize: 13 }}>{scheda.note}</div>
            </div>
          )}
          
          {/* Tempo lavorazione */}
          {(scheda.tempoLavorazione > 0 || scheda.timerStart) && (
            <div style={{ marginBottom: 16, padding: 12, background: 'rgba(59,130,246,0.1)', borderRadius: 10, border: '1px solid rgba(59,130,246,0.3)' }}>
              <div style={{ fontSize: 11, color: '#3b82f6', marginBottom: 4 }}>⏱️ TEMPO LAVORAZIONE</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#3b82f6' }}>
                {(() => {
                  let totalMs = scheda.tempoLavorazione || 0;
                  if (scheda.timerStart) totalMs += Date.now() - scheda.timerStart;
                  const ore = Math.floor(totalMs / 3600000);
                  const minuti = Math.floor((totalMs % 3600000) / 60000);
                  return `${ore}h ${minuti}m`;
                })()}
                {scheda.timerStart && <span style={{ fontSize: 12, marginLeft: 8, color: '#22c55e' }}>● In corso</span>}
              </div>
            </div>
          )}
          
          {/* Checklist ingresso */}
          {scheda.checklist && Object.keys(scheda.checklist).length > 0 && (
            <div style={{ marginBottom: 16, padding: 12, background: 'rgba(245,158,11,0.1)', borderRadius: 10 }}>
              <div style={{ fontSize: 11, color: '#f59e0b', marginBottom: 8 }}>📋 CHECKLIST INGRESSO</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {Object.entries(scheda.checklist).map(([key, val]) => (
                  <span key={key} style={{ 
                    padding: '4px 8px', 
                    borderRadius: 4, 
                    fontSize: 11, 
                    background: val === true ? 'rgba(34,197,94,0.2)' : val === false ? 'rgba(239,68,68,0.2)' : 'rgba(100,116,139,0.2)',
                    color: val === true ? '#22c55e' : val === false ? '#ef4444' : '#64748b'
                  }}>
                    {val === true ? '✓' : val === false ? '✗' : '?'} {key}
                  </span>
                ))}
              </div>
              {scheda.noteChecklist && (
                <div style={{ marginTop: 8, fontSize: 12, color: muted }}>{scheda.noteChecklist}</div>
              )}
            </div>
          )}
          
          {/* Cambia stato */}
          {scheda.stato !== 'consegnato' && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: muted, marginBottom: 8 }}>CAMBIA STATO</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {Object.entries(stati).filter(([k]) => k !== scheda.stato).map(([key, val]) => (
                  <button
                    key={key}
                    onClick={() => onCambiaStato(scheda, key)}
                    style={{ 
                      padding: '8px 12px', 
                      background: `${val.color}15`, 
                      border: '1px solid ' + val.color + '50', 
                      borderRadius: 8, 
                      color: val.color, 
                      fontSize: 12, 
                      fontWeight: 500,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}
                  >
                    {val.icon} {val.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Azioni */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => onWhatsApp(scheda)} style={{ flex: 1, minWidth: 100, padding: '10px', background: 'rgba(37,211,102,0.2)', border: '1px solid rgba(37,211,102,0.3)', borderRadius: 8, color: '#25d366', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
              📱 WhatsApp
            </button>
            <button onClick={() => onStampaEtichetta(scheda)} style={{ flex: 1, minWidth: 100, padding: '10px', background: 'rgba(245,158,11,0.2)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, color: '#f59e0b', fontSize: 12, fontWeight: 500, cursor: 'pointer' }} title="Stampa etichetta termica 40x30mm">
              🏷️ Etichetta
            </button>
            <button onClick={() => onStampa(scheda, 'ingresso')} style={{ flex: 1, minWidth: 100, padding: '10px', background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: '1px solid ' + bord, borderRadius: 8, color: muted, fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
              🖨️ Ric. Ingresso
            </button>
            {scheda.stato === 'consegnato' && (
              <button onClick={() => onStampa(scheda, 'consegna')} style={{ flex: 1, minWidth: 100, padding: '10px', background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: '1px solid ' + bord, borderRadius: 8, color: muted, fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
                🖨️ Ric. Consegna
              </button>
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div style={{ display: 'flex', gap: 12, padding: '16px 20px', borderTop: '1px solid ' + bord }}>
          <button onClick={() => onElimina(scheda.id)} style={{ padding: '10px 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, color: '#ef4444', fontSize: 13, cursor: 'pointer' }}>🗑️</button>
          <button onClick={onModifica} style={{ flex: 1, padding: 12, background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: '1px solid ' + bord, borderRadius: 10, color: muted, cursor: 'pointer' }}>✏️ Modifica</button>
          <button onClick={onClose} style={{ flex: 1, padding: 12, background: 'linear-gradient(135deg,#3b82f6,#2563eb)', border: 'none', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Chiudi</button>
        </div>
      </div>
    </div>
  );
}

function FileUtiliTab({ data, save, del, dark, bgCard, bgInput, bord, muted, uploadFileToStorage, downloadFileFromStorage, deleteFileFromStorage }) {
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editNome, setEditNome] = useState('');
  const [downloading, setDownloading] = useState(null);

  const fileUtili = data.fileUtili || [];

  // Determina icona in base all'estensione
  const getFileIcon = (nome) => {
    const ext = nome?.split('.').pop()?.toLowerCase() || '';
    const icons = {
      pdf: '📕',
      doc: '📘', docx: '📘',
      xls: '📗', xlsx: '📗',
      ppt: '📙', pptx: '📙',
      jpg: '🖼️', jpeg: '🖼️', png: '🖼️', gif: '🖼️', webp: '🖼️',
      zip: '📦', rar: '📦', '7z': '📦',
      mp3: '🎵', wav: '🎵', m4a: '🎵',
      mp4: '🎬', mov: '🎬', avi: '🎬',
      txt: '📄',
      csv: '📊',
      json: '📋',
    };
    return icons[ext] || '📎';
  };

  // Colore in base all'estensione
  const getFileColor = (nome) => {
    const ext = nome?.split('.').pop()?.toLowerCase() || '';
    const colors = {
      pdf: '#ef4444',
      doc: '#3b82f6', docx: '#3b82f6',
      xls: '#22c55e', xlsx: '#22c55e',
      ppt: '#f59e0b', pptx: '#f59e0b',
      jpg: '#ec4899', jpeg: '#ec4899', png: '#ec4899', gif: '#ec4899', webp: '#ec4899',
      zip: '#8b5cf6', rar: '#8b5cf6', '7z': '#8b5cf6',
    };
    return colors[ext] || '#64748b';
  };

  // Formatta dimensione file
  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    
    const nuoviFile = [];
    for (const file of files) {
      const result = await uploadFileToStorage(file, 'file-utili');
      if (result) {
        nuoviFile.push(result);
      }
    }
    
    if (nuoviFile.length > 0) {
      save({ ...data, fileUtili: [...fileUtili, ...nuoviFile] });
      alert(`✅ ${nuoviFile.length} file caricati con successo!`);
    }
    setUploading(false);
  };

  const handleDownload = async (file) => {
    if (!file.path) {
      // File vecchio con solo link, apri il link
      if (file.url || file.link) {
        window.open(file.url || file.link, '_blank');
      }
      return;
    }
    
    setDownloading(file.id);
    await downloadFileFromStorage(file.path, file.nome);
    setDownloading(null);
  };

  const deleteFile = async (file) => {
    if (!window.confirm('Eliminare questo file?')) return;
    
    // Se ha un path, elimina anche da Supabase Storage
    if (file.path) {
      await deleteFileFromStorage(file.path);
    }
    
    // FIX: passa da del(), che cancella anche la riga dal database. Prima il blob
    // veniva rimosso dallo Storage ma la riga restava, e al sync ricompariva una
    // card il cui download dava errore.
    del('fileUtili', file.id);
  };

  const startEdit = (file) => {
    setEditingId(file.id);
    setEditNome(file.nome);
  };

  const saveEdit = (id) => {
    save({ ...data, fileUtili: fileUtili.map(f => f.id === id ? { ...f, nome: editNome } : f) });
    setEditingId(null);
  };

  return (
    <section style={{ background: bgCard, borderRadius: 16, border: '1px solid ' + bord, padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>📁 FILE UTILI</h2>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: muted }}>Carica e scarica file utili salvati su Supabase</p>
        </div>
        <span style={{ fontSize: 12, color: muted }}>{fileUtili.length} file</span>
      </div>

      {/* Area Upload */}
      <label style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        gap: 12, 
        padding: 32, 
        background: uploading ? 'rgba(59,130,246,0.1)' : (dark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'),
        border: `2px dashed ${uploading ? '#3b82f6' : bord}`,
        borderRadius: 16, 
        cursor: uploading ? 'wait' : 'pointer',
        marginBottom: 24,
        transition: 'all 0.2s'
      }}>
        <span style={{ fontSize: 40 }}>{uploading ? '⏳' : '📤'}</span>
        <span style={{ fontSize: 14, fontWeight: 500, color: uploading ? '#3b82f6' : muted }}>
          {uploading ? 'Caricamento in corso...' : 'Clicca o trascina i file qui'}
        </span>
        <span style={{ fontSize: 11, color: muted }}>Puoi selezionare più file alla volta (max 50MB per file)</span>
        <input 
          type="file" 
          multiple 
          style={{ display: 'none' }} 
          onChange={(e) => handleUpload(e.target.files)} 
          disabled={uploading}
        />
      </label>

      {/* Griglia File */}
      {fileUtili.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: muted }}>
          <span style={{ fontSize: 48, display: 'block', marginBottom: 12, opacity: 0.5 }}>📂</span>
          <p>Nessun file caricato</p>
          <p style={{ fontSize: 12 }}>Carica i file che usi più spesso per averli sempre a portata di mano</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
          {fileUtili.map(file => (
            <div 
              key={file.id} 
              style={{ 
                background: bgInput, 
                borderRadius: 12, 
                padding: 16, 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                position: 'relative',
                transition: 'transform 0.2s'
              }}
            >
              {/* Pulsanti azioni */}
              <div style={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 4 }}>
                <button 
                  onClick={() => startEdit(file)}
                  style={{ background: 'rgba(59,130,246,0.2)', border: 'none', borderRadius: 4, padding: '4px 6px', fontSize: 10, cursor: 'pointer' }}
                  title="Rinomina"
                >
                  ✏️
                </button>
                <button 
                  onClick={() => deleteFile(file)}
                  style={{ background: 'rgba(239,68,68,0.2)', border: 'none', borderRadius: 4, padding: '4px 6px', fontSize: 10, cursor: 'pointer', color: '#ef4444' }}
                  title="Elimina"
                >
                  ×
                </button>
              </div>
              
              {/* Icona cliccabile per download */}
              <div 
                onClick={() => handleDownload(file)}
                style={{ 
                  cursor: 'pointer', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  width: '100%' 
                }}
              >
                <div style={{ 
                  width: 64, 
                  height: 64, 
                  borderRadius: 16, 
                  background: getFileColor(file.nome) + '20',
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: 32,
                  marginBottom: 10
                }}>
                  {downloading === file.id ? '⏳' : getFileIcon(file.nome)}
                </div>
                
                {editingId === file.id ? (
                  <div style={{ width: '100%' }} onClick={e => e.stopPropagation()}>
                    <input 
                      type="text"
                      value={editNome}
                      onChange={e => setEditNome(e.target.value)}
                      style={{ 
                        width: '100%', 
                        padding: '6px 8px', 
                        background: dark ? 'rgba(0,0,0,0.3)' : '#fff',
                        border: '1px solid ' + bord,
                        borderRadius: 6,
                        fontSize: 11,
                        color: dark ? '#e2e8f0' : '#1e293b',
                        textAlign: 'center',
                        marginBottom: 6,
                        boxSizing: 'border-box'
                      }}
                      autoFocus
                      onKeyDown={e => e.key === 'Enter' && saveEdit(file.id)}
                    />
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                      <button onClick={() => setEditingId(null)} style={{ padding: '4px 8px', background: 'rgba(239,68,68,0.2)', border: 'none', borderRadius: 4, fontSize: 10, cursor: 'pointer' }}>✕</button>
                      <button onClick={() => saveEdit(file.id)} style={{ padding: '4px 8px', background: 'rgba(34,197,94,0.2)', border: 'none', borderRadius: 4, fontSize: 10, cursor: 'pointer' }}>✓</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <span style={{ 
                      fontSize: 11, 
                      fontWeight: 500, 
                      color: dark ? '#e2e8f0' : '#1e293b',
                      textAlign: 'center',
                      wordBreak: 'break-word',
                      lineHeight: 1.3
                    }}>
                      {file.nome.length > 25 ? file.nome.substring(0, 22) + '...' : file.nome}
                    </span>
                    {file.size && (
                      <span style={{ fontSize: 10, color: muted, marginTop: 4 }}>
                        {formatSize(file.size)}
                      </span>
                    )}
                    <span style={{ fontSize: 10, color: '#3b82f6', marginTop: 4 }}>
                      📥 Clicca per scaricare
                    </span>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ==================== CLIENTI TAB ====================
function ClientiTab({ data, save, del, dark, bgCard, bgInput, bord, muted }) {
  const [clienteModal, setClienteModal] = useState(null); // Per visualizzare dettagli
  const [clienteEditModal, setClienteEditModal] = useState(null); // Per creare/modificare
  const [searchTerm, setSearchTerm] = useState('');
  
  const clienti = data.clientiLab || [];
  const laboratorio = data.laboratorio || [];
  
  // Combina anagrafica clienti con statistiche dalle schede
  const clientiStats = useMemo(() => {
    // Crea mappa delle riparazioni per telefono
    const riparazioniPerTel = {};
    laboratorio.forEach(scheda => {
      const tel = scheda.telefono?.replace(/\D/g, '');
      if (tel) {
        if (!riparazioniPerTel[tel]) riparazioniPerTel[tel] = [];
        riparazioniPerTel[tel].push(scheda);
      }
    });
    
    // Arricchisci i clienti con le statistiche
    return clienti.map(c => {
      const tel = c.telefono?.replace(/\D/g, '');
      const riparazioni = tel ? (riparazioniPerTel[tel] || []) : [];
      const totaleSpeso = riparazioni.reduce((s, r) => s + (r.incassato ? (r.importoFinale || r.preventivo || 0) : 0), 0);
      const ultimaVisita = riparazioni.length > 0 
        ? new Date(Math.max(...riparazioni.map(r => new Date(r.dataIngresso || 0))))
        : null;
      return {
        ...c,
        riparazioni,
        totaleSpeso,
        ultimaVisita
      };
    });
  }, [laboratorio, clienti]);
  
  // Clienti filtrati per ricerca
  const clientiFiltrati = clientiStats.filter(c => 
    !searchTerm || 
    (c.nome || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
    (c.telefono || '').includes(searchTerm) ||
    (c.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.azienda || '').toLowerCase().includes(searchTerm.toLowerCase())
  ).sort((a, b) => (b.riparazioni?.length || 0) - (a.riparazioni?.length || 0));
  
  // Salva cliente
  const salvaCliente = (cliente) => {
    let nuoviClienti = [...clienti];
    
    if (cliente.id) {
      nuoviClienti = nuoviClienti.map(c => c.id === cliente.id ? { ...c, ...cliente } : c);
    } else {
      nuoviClienti.push({
        ...cliente,
        id: Date.now().toString(),
        dataCreazione: new Date().toISOString()
      });
    }
    
    save({ ...data, clientiLab: nuoviClienti });
    setClienteEditModal(null);
  };
  
  // Elimina cliente
  const eliminaCliente = (clienteId) => {
    if (window.confirm('Eliminare questo cliente?')) {
      // FIX: passa da del(), che cancella anche dal database
      del('clientiLab', clienteId);
      setClienteModal(null);
    }
  };
  
  const inputStyle = { width: '100%', padding: '12px 14px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14 };
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6, color: muted };
  
  return (
    <div style={{ paddingTop: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>👥 Clienti</h2>
        <button onClick={() => setClienteEditModal({ isNuovo: true })} style={{ padding: '12px 20px', background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', border: 'none', borderRadius: 12, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>+ Nuovo Cliente</button>
      </div>
      
      {/* Statistiche */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
        <div style={{ background: bgCard, borderRadius: 12, padding: 16, textAlign: 'center', border: '1px solid ' + bord }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#8b5cf6' }}>{clienti.length}</div>
          <div style={{ fontSize: 12, color: muted }}>Totale Clienti</div>
        </div>
        <div style={{ background: bgCard, borderRadius: 12, padding: 16, textAlign: 'center', border: '1px solid ' + bord }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#3b82f6' }}>{clientiStats.filter(c => c.riparazioni.length > 0).length}</div>
          <div style={{ fontSize: 12, color: muted }}>Con Riparazioni</div>
        </div>
        <div style={{ background: bgCard, borderRadius: 12, padding: 16, textAlign: 'center', border: '1px solid ' + bord }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#22c55e' }}>€{clientiStats.reduce((s, c) => s + (c.totaleSpeso || 0), 0).toFixed(0)}</div>
          <div style={{ fontSize: 12, color: muted }}>Totale Incassato</div>
        </div>
      </div>
      
      {/* Ricerca */}
      <input
        type="text"
        placeholder="🔍 Cerca per nome, telefono, email, azienda..."
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        style={{ ...inputStyle, marginBottom: 16 }}
      />
      
      {/* Lista clienti */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {clientiFiltrati.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: muted }}>
            <span style={{ fontSize: 48, display: 'block', marginBottom: 12, opacity: 0.5 }}>👥</span>
            <p>Nessun cliente trovato</p>
            <button onClick={() => setClienteEditModal({ isNuovo: true })} style={{ marginTop: 12, padding: '10px 20px', background: 'linear-gradient(135deg,#8b5cf6,#7c3aed)', border: 'none', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>+ Aggiungi Cliente</button>
          </div>
        ) : clientiFiltrati.map(cliente => (
          <div 
            key={cliente.id}
            onClick={() => setClienteModal(cliente)}
            style={{ 
              background: bgCard, 
              borderRadius: 16, 
              padding: 16, 
              border: '1px solid ' + bord,
              cursor: 'pointer',
              borderLeft: '4px solid ' + (cliente.riparazioni.length > 0 ? '#8b5cf6' : bord)
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{cliente.nome || 'Cliente'}</div>
                {cliente.azienda && <div style={{ fontSize: 13, color: '#8b5cf6', marginBottom: 4 }}>🏢 {cliente.azienda}</div>}
                <div style={{ fontSize: 13, color: muted, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {cliente.telefono && <span>📱 {cliente.telefono}</span>}
                  {cliente.email && <span>✉️ {cliente.email}</span>}
                </div>
                {cliente.indirizzo && <div style={{ fontSize: 12, color: muted, marginTop: 4 }}>📍 {cliente.indirizzo}</div>}
                {cliente.ultimaVisita && (
                  <div style={{ fontSize: 11, color: muted, marginTop: 6 }}>
                    Ultima visita: {cliente.ultimaVisita.toLocaleDateString('it-IT')}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 16 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: cliente.riparazioni.length > 0 ? '#3b82f6' : muted }}>{cliente.riparazioni.length}</div>
                  <div style={{ fontSize: 10, color: muted }}>Riparazioni</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: cliente.totaleSpeso > 0 ? '#22c55e' : muted }}>€{(cliente.totaleSpeso || 0).toFixed(0)}</div>
                  <div style={{ fontSize: 10, color: muted }}>Speso</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Modal Dettaglio Cliente */}
      {clienteModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setClienteModal(null)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 600, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '20px', borderBottom: '1px solid ' + bord, background: 'linear-gradient(135deg, rgba(139,92,246,0.2), transparent)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 12, color: muted, marginBottom: 4 }}>SCHEDA CLIENTE</div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{clienteModal.nome || 'Cliente'}</div>
                  {clienteModal.azienda && <div style={{ fontSize: 14, color: '#8b5cf6', marginTop: 4 }}>🏢 {clienteModal.azienda}</div>}
                  <div style={{ fontSize: 13, color: muted, marginTop: 8 }}>
                    {clienteModal.telefono && <span style={{ marginRight: 16 }}>📱 {clienteModal.telefono}</span>}
                    {clienteModal.email && <span>✉️ {clienteModal.email}</span>}
                  </div>
                  {clienteModal.indirizzo && <div style={{ fontSize: 13, color: muted, marginTop: 4 }}>📍 {clienteModal.indirizzo}</div>}
                </div>
                <button onClick={() => setClienteModal(null)} style={{ background: 'transparent', border: 'none', fontSize: 20, cursor: 'pointer', color: muted }}>✕</button>
              </div>
            </div>
            
            {/* Statistiche */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, padding: 16, borderBottom: '1px solid ' + bord }}>
              <div style={{ textAlign: 'center', padding: 12, background: bgInput, borderRadius: 10 }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#3b82f6' }}>{clienteModal.riparazioni?.length || 0}</div>
                <div style={{ fontSize: 11, color: muted }}>Riparazioni</div>
              </div>
              <div style={{ textAlign: 'center', padding: 12, background: bgInput, borderRadius: 10 }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#22c55e' }}>€{(clienteModal.totaleSpeso || 0).toFixed(0)}</div>
                <div style={{ fontSize: 11, color: muted }}>Totale speso</div>
              </div>
              <div style={{ textAlign: 'center', padding: 12, background: bgInput, borderRadius: 10 }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#f59e0b' }}>€{((clienteModal.totaleSpeso || 0) / (clienteModal.riparazioni?.length || 1)).toFixed(0)}</div>
                <div style={{ fontSize: 11, color: muted }}>Media</div>
              </div>
            </div>
            
            {/* Note cliente */}
            {clienteModal.note && (
              <div style={{ padding: 16, borderBottom: '1px solid ' + bord }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: muted, marginBottom: 8 }}>NOTE</div>
                <div style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{clienteModal.note}</div>
              </div>
            )}
            
            {/* Storico riparazioni */}
            <div style={{ padding: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: muted, marginBottom: 12 }}>STORICO RIPARAZIONI</div>
              {(clienteModal.riparazioni || []).length === 0 ? (
                <div style={{ textAlign: 'center', padding: 20, color: muted }}>Nessuna riparazione</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 300, overflow: 'auto' }}>
                  {(clienteModal.riparazioni || []).sort((a, b) => new Date(b.dataIngresso) - new Date(a.dataIngresso)).map(rip => (
                    <div key={rip.id} style={{ padding: 12, background: bgInput, borderRadius: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>#{rip.id} - {rip.dispositivo} {rip.marca}</div>
                          <div style={{ fontSize: 12, color: muted, marginTop: 4 }}>{rip.problema?.substring(0, 50)}{rip.problema?.length > 50 ? '...' : ''}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 11, color: muted }}>{new Date(rip.dataIngresso).toLocaleDateString('it-IT')}</div>
                          {(rip.importoFinale || rip.preventivo) > 0 && (
                            <div style={{ fontSize: 14, fontWeight: 700, color: rip.incassato ? '#22c55e' : '#f59e0b' }}>
                              €{(rip.importoFinale || rip.preventivo).toFixed(2)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Azioni */}
            <div style={{ display: 'flex', gap: 12, padding: 16, borderTop: '1px solid ' + bord }}>
              {clienteModal.telefono && (
                <button onClick={() => {
                  const tel = clienteModal.telefono.replace(/\D/g, '');
                  window.open(`https://wa.me/39${tel}`, '_blank');
                }} style={{ flex: 1, padding: 12, background: '#25d366', border: 'none', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
                  💬 WhatsApp
                </button>
              )}
              <button onClick={() => { setClienteEditModal(clienteModal); setClienteModal(null); }} style={{ flex: 1, padding: 12, background: bgInput, border: '1px solid ' + bord, borderRadius: 10, cursor: 'pointer', fontWeight: 600 }}>
                ✏️ Modifica
              </button>
              <button onClick={() => eliminaCliente(clienteModal.id)} style={{ padding: 12, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, color: '#ef4444', cursor: 'pointer' }}>
                🗑️
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal Crea/Modifica Cliente */}
      {clienteEditModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setClienteEditModal(null)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 500, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid ' + bord }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{clienteEditModal.isNuovo ? '👥 Nuovo Cliente' : '✏️ Modifica Cliente'}</h3>
            </div>
            <form onSubmit={e => { e.preventDefault(); salvaCliente(clienteEditModal); }} style={{ padding: 24 }}>
              <label style={labelStyle}>Nome / Cognome *</label>
              <input type="text" required value={clienteEditModal.nome || ''} onChange={e => setClienteEditModal({ ...clienteEditModal, nome: e.target.value })} style={{ ...inputStyle, marginBottom: 16 }} />
              
              <label style={labelStyle}>Azienda</label>
              <input type="text" value={clienteEditModal.azienda || ''} onChange={e => setClienteEditModal({ ...clienteEditModal, azienda: e.target.value })} style={{ ...inputStyle, marginBottom: 16 }} />
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={labelStyle}>Telefono *</label>
                  <input type="tel" required value={clienteEditModal.telefono || ''} onChange={e => setClienteEditModal({ ...clienteEditModal, telefono: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Email</label>
                  <input type="email" value={clienteEditModal.email || ''} onChange={e => setClienteEditModal({ ...clienteEditModal, email: e.target.value })} style={inputStyle} />
                </div>
              </div>
              
              <label style={labelStyle}>Indirizzo</label>
              <input type="text" value={clienteEditModal.indirizzo || ''} onChange={e => setClienteEditModal({ ...clienteEditModal, indirizzo: e.target.value })} style={{ ...inputStyle, marginBottom: 16 }} />
              
              <label style={labelStyle}>Codice Fiscale / P.IVA</label>
              <input type="text" value={clienteEditModal.codiceFiscale || ''} onChange={e => setClienteEditModal({ ...clienteEditModal, codiceFiscale: e.target.value })} style={{ ...inputStyle, marginBottom: 16 }} />
              
              <label style={labelStyle}>Note</label>
              <textarea value={clienteEditModal.note || ''} onChange={e => setClienteEditModal({ ...clienteEditModal, note: e.target.value })} style={{ ...inputStyle, minHeight: 80, resize: 'vertical', marginBottom: 20 }} />
              
              <div style={{ display: 'flex', gap: 12 }}>
                <button type="button" onClick={() => setClienteEditModal(null)} style={{ flex: 1, padding: 14, background: bgInput, border: '1px solid ' + bord, borderRadius: 12, cursor: 'pointer', color: muted }}>Annulla</button>
                <button type="submit" style={{ flex: 1, padding: 14, background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', border: 'none', borderRadius: 12, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>💾 Salva</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== ORDINI TAB ====================
function OrdiniTab({ data, save, dark, bgCard, bgInput, bord, muted, supabase, userId }) {
  const [ordineModal, setOrdineModal] = useState(null);
  const [filtro, setFiltro] = useState('tutti'); // tutti, in_attesa, arrivato, consegnato
  const [searchTerm, setSearchTerm] = useState('');
  const [showClientiDropdown, setShowClientiDropdown] = useState(false);
  const [clienteSearch, setClienteSearch] = useState('');
  const [showProdottiDropdown, setShowProdottiDropdown] = useState(false);
  const [prodottoSearch, setProdottoSearch] = useState('');
  
  const ordini = data.ordini || [];
  const clienti = data.clientiLab || [];
  const magazzino = data.magazzino || [];
  // Conti disponibili per registrare l'acconto (helper condiviso a livello di modulo)
  const contiDisponibili = getContiDisponibili(data);
  
  // Filtra clienti per ricerca
  const clientiFiltrati = clienti.filter(c => 
    !clienteSearch || 
    (c.nome || '').toLowerCase().includes(clienteSearch.toLowerCase()) ||
    (c.telefono || '').includes(clienteSearch)
  ).slice(0, 10);
  
  // Filtra prodotti magazzino per ricerca
  const prodottiFiltrati = magazzino.filter(p => 
    !prodottoSearch || 
    (p.nome || '').toLowerCase().includes(prodottoSearch.toLowerCase()) ||
    (p.codice || '').toLowerCase().includes(prodottoSearch.toLowerCase())
  ).slice(0, 10);
  
  const STATI_ORDINE = {
    in_attesa: { label: 'In Attesa', color: '#f59e0b', icon: '⏳' },
    arrivato: { label: 'Arrivato', color: '#3b82f6', icon: '📦' },
    consegnato: { label: 'Consegnato', color: '#22c55e', icon: '✅' },
    annullato: { label: 'Annullato', color: '#ef4444', icon: '❌' }
  };
  
  const ordiniVisibili = ordini
    .filter(o => filtro === 'tutti' || o.stato === filtro)
    .filter(o => !searchTerm || 
      (o.cliente || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (o.prodotto || '').toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => new Date(b.dataOrdine || 0) - new Date(a.dataOrdine || 0));
  
  // Seleziona cliente da anagrafica
  const selezionaCliente = (cliente) => {
    setOrdineModal({
      ...ordineModal,
      cliente: cliente.nome,
      telefono: cliente.telefono,
      clienteId: cliente.id
    });
    setShowClientiDropdown(false);
    setClienteSearch('');
  };
  
  // Seleziona prodotto da magazzino
  const selezionaProdotto = (prodotto) => {
    setOrdineModal({
      ...ordineModal,
      prodotto: prodotto.nome,
      prodottoId: prodotto.id,
      fornitore: prodotto.fornitore || ordineModal.fornitore,
      prezzo: prodotto.prezzoVendita || ordineModal.prezzo
    });
    setShowProdottiDropdown(false);
    setProdottoSearch('');
  };
  
  const salvaOrdine = async (ordine) => {
    let nuoveEntrate = [...(data.entrate || [])];
    let categorie = [...(data.categorieEntrate || [])];
    
    // Assicurati che la categoria "ordini" esista
    if (!categorie.find(c => c.id === 'ordini_clienti')) {
      categorie.push({
        id: 'ordini_clienti',
        nome: 'Ordini Clienti',
        icon: '📦',
        color: '#a855f7'
      });
    }
    
    if (ordine.id) {
      // Modifica ordine esistente
      console.log('📝 Modifica ordine:', ordine.id);
      
      // Trova ordine precedente per confrontare acconto
      const ordinePrecedente = ordini.find(o => o.id === ordine.id);
      const accontoPrecedente = ordinePrecedente?.acconto || 0;
      const accontoNuovo = ordine.acconto || 0;
      const contoPrecedente = ordinePrecedente?.accontoConto;
      const contoNuovo = ordine.accontoConto;
      
      // Gestione acconto: se cambia, aggiorna i conti
      if (accontoNuovo !== accontoPrecedente || contoNuovo !== contoPrecedente) {
        // Rimuovi dal conto precedente se c'era un acconto
        if (accontoPrecedente > 0 && contoPrecedente) {
          nuoveEntrate = nuoveEntrate.map(e => 
            e.id === contoPrecedente 
              ? { ...e, importo: (e.importo || 0) - accontoPrecedente }
              : e
          );
        }
        // Aggiungi al nuovo conto se c'è un acconto
        if (accontoNuovo > 0 && contoNuovo) {
          nuoveEntrate = nuoveEntrate.map(e => 
            e.id === contoNuovo 
              ? { ...e, importo: (e.importo || 0) + accontoNuovo }
              : e
          );
        }
      }
      
      // Aggiorna la voce entrata saldo (prezzo - acconto)
      const idEntrata = `ord_${ordine.id}`;
      const importoSaldo = (ordine.prezzo || 0) - (ordine.acconto || 0);
      
      nuoveEntrate = nuoveEntrate.map(e => 
        e.id === idEntrata 
          ? { ...e, nome: `📦 ${ordine.cliente} - ${ordine.prodotto}`, importo: importoSaldo, note: `Saldo ordine #${ordine.id}${accontoNuovo > 0 ? ` (acconto €${accontoNuovo.toFixed(2)} già incassato)` : ''}` }
          : e
      );
      
      save({ ...data, ordini: ordini.map(o => o.id === ordine.id ? { ...ordine, dataAcconto: ordine.acconto > 0 ? (ordine.dataAcconto || new Date().toISOString()) : null } : o), entrate: nuoveEntrate, categorieEntrate: categorie });
    } else {
      // Nuovo ordine
      const nuovoOrdine = { 
        ...ordine, 
        id: 'ord_' + Date.now().toString(),
        dataOrdine: new Date().toISOString(),
        dataAcconto: ordine.acconto > 0 ? new Date().toISOString() : null,
        stato: 'in_attesa'
      };
      console.log('➕ Nuovo ordine:', nuovoOrdine.id);
      
      // Se c'è un acconto e un conto selezionato, somma l'acconto al conto
      if (nuovoOrdine.acconto > 0 && nuovoOrdine.accontoConto) {
        const contoId = nuovoOrdine.accontoConto;
        nuoveEntrate = nuoveEntrate.map(e => 
          e.id === contoId 
            ? { ...e, importo: (e.importo || 0) + nuovoOrdine.acconto }
            : e
        );
      }
      
      // Crea voce entrata per il saldo (prezzo - acconto)
      const importoSaldo = (nuovoOrdine.prezzo || 0) - (nuovoOrdine.acconto || 0);
      if (importoSaldo > 0) {
        const idEntrata = `ord_${nuovoOrdine.id}`;
        nuoveEntrate.push({
          id: idEntrata,
          nome: `📦 ${nuovoOrdine.cliente} - ${nuovoOrdine.prodotto}`,
          importo: importoSaldo,
          note: `Saldo ordine #${nuovoOrdine.id}${nuovoOrdine.acconto > 0 ? ` (acconto €${nuovoOrdine.acconto.toFixed(2)} già incassato)` : ''}`,
          categoria: 'ordini_clienti',
          isOrdine: true,
          ordineId: nuovoOrdine.id
        });
      }
      
      save({ ...data, ordini: [...ordini, nuovoOrdine], entrate: nuoveEntrate, categorieEntrate: categorie });
    }
    setOrdineModal(null);
    setShowClientiDropdown(false);
    setShowProdottiDropdown(false);
  };
  
  const eliminaOrdine = async (id) => {
    if (window.confirm('Eliminare questo ordine?')) {
      console.log('🗑️ Eliminazione ordine:', id);
      
      // Rimuovi ordine e voci entrata (saldo + acconto)
      const nuoviOrdini = ordini.filter(o => o.id !== id);
      const idEntrata = `ord_${id}`;
      const idAcconto = `acc_${id}`;
      const nuoveEntrate = (data.entrate || []).filter(e => e.id !== idEntrata && e.id !== idAcconto);
      
      // Elimina da Supabase
      if (userId && supabase) {
        try {
          // Elimina ordine
          await supabase.from('ordini').delete().eq('id', id).eq('user_id', userId);
          // Elimina entrate (saldo e acconto)
          await supabase.from('entrate').delete().eq('id', idEntrata).eq('user_id', userId);
          await supabase.from('entrate').delete().eq('id', idAcconto).eq('user_id', userId);
          console.log('✅ Ordine e voci entrata eliminate da Supabase');
        } catch (e) {
          console.error('❌ Errore:', e);
        }
      }
      
      // Aggiorna lo stato locale
      save({ ...data, ordini: nuoviOrdini, entrate: nuoveEntrate });
    }
  };
  
  const cambiaStato = (id, nuovoStato) => {
    const ordine = ordini.find(o => o.id === id);
    if (!ordine) return;
    
    const ordineAggiornato = { 
      ...ordine, 
      stato: nuovoStato,
      dataArrivo: nuovoStato === 'arrivato' ? new Date().toISOString() : ordine.dataArrivo,
      dataConsegna: nuovoStato === 'consegnato' ? new Date().toISOString() : ordine.dataConsegna
    };
    
    let nuovoData = { ...data, ordini: ordini.map(o => o.id === id ? ordineAggiornato : o) };
    
    // Se l'ordine è collegato a un prodotto del magazzino
    // FIX: si carica solo se l'ordine NON era già in stato "arrivato". Prima non
    // c'era alcun controllo sullo stato precedente, quindi riportando un ordine
    // indietro e poi di nuovo ad "arrivato" la quantità veniva caricata due volte.
    if (ordine.prodottoId && nuovoStato === 'arrivato' && ordine.stato !== 'arrivato' && !ordine.caricatoInMagazzino) {
      // Carica automaticamente nel magazzino
      const prodotto = magazzino.find(p => p.id === ordine.prodottoId);
      if (prodotto) {
        ordineAggiornato.caricatoInMagazzino = true;
        const quantitaCarico = ordine.quantita || 1;
        const nuovaQuantita = (prodotto.quantita || 0) + quantitaCarico;
        
        // Aggiorna quantità prodotto
        nuovoData.magazzino = magazzino.map(p => 
          p.id === ordine.prodottoId ? { ...p, quantita: nuovaQuantita } : p
        );
        
        // Registra movimento
        const movimento = {
          id: 'mov_' + Date.now(),
          data: new Date().toISOString(),
          tipo: 'carico',
          prodottoId: ordine.prodottoId,
          prodottoNome: prodotto.nome,
          quantita: quantitaCarico,
          quantitaPrecedente: prodotto.quantita || 0,
          quantitaNuova: nuovaQuantita,
          note: `Ordine #${ordine.id} - ${ordine.cliente}`,
          ordineId: ordine.id
        };
        nuovoData.movimentiMagazzino = [...(data.movimentiMagazzino || []), movimento];
      }
    }
    
    // Se consegnato, rimuovi la voce dalle entrate (è stato incassato)
    if (nuovoStato === 'consegnato') {
      const idEntrata = `ord_${ordine.id}`;
      nuovoData.entrate = (nuovoData.entrate || data.entrate || []).filter(e => e.id !== idEntrata);

      // Elimina anche da Supabase
      if (userId && supabase) {
        supabase.from('entrate').delete().eq('id', idEntrata).eq('user_id', userId);
      }

      // FIX: alla consegna il pezzo esce dal magazzino. Prima la quantità veniva
      // solo caricata all'arrivo e non scaricata mai, quindi le giacenze a video
      // crescevano all'infinito e non corrispondevano più agli scaffali.
      // FIX: si scarica SOLO se il pezzo era stato effettivamente caricato.
      // Un ordine portato a "consegnato" senza essere mai passato da "arrivato"
      // (stato cambiato dal modale di modifica) altrimenti decrementava una
      // giacenza mai incrementata.
      if (ordine.prodottoId && ordine.caricatoInMagazzino && !ordine.scaricatoDaMagazzino) {
        const magazzinoCorrente = nuovoData.magazzino || magazzino;
        const prodotto = magazzinoCorrente.find(p => p.id === ordine.prodottoId);
        if (prodotto) {
          const quantitaScarico = ordine.quantita || 1;
          const nuovaQuantita = Math.max(0, (prodotto.quantita || 0) - quantitaScarico);
          ordineAggiornato.scaricatoDaMagazzino = true;

          nuovoData.magazzino = magazzinoCorrente.map(p =>
            p.id === ordine.prodottoId ? { ...p, quantita: nuovaQuantita } : p
          );

          nuovoData.movimentiMagazzino = [...(nuovoData.movimentiMagazzino || data.movimentiMagazzino || []), {
            id: 'mov_' + Date.now(),
            data: new Date().toISOString(),
            tipo: 'scarico',
            prodottoId: ordine.prodottoId,
            prodottoNome: prodotto.nome,
            // Si registra la quantità realmente scaricata, non quella richiesta
            quantita: (prodotto.quantita || 0) - nuovaQuantita,
            quantitaPrecedente: prodotto.quantita || 0,
            quantitaNuova: nuovaQuantita,
            note: `Consegna ordine #${ordine.id} - ${ordine.cliente}`,
            ordineId: ordine.id
          }];
        }
      }
    }
    
    save(nuovoData);
    
    // WhatsApp automatico
    if (ordine.telefono && (nuovoStato === 'arrivato' || nuovoStato === 'consegnato')) {
      const msg = nuovoStato === 'arrivato' 
        ? `Gentile ${ordine.cliente}, il suo ordine "${ordine.prodotto}" è arrivato! Può passare a ritirarlo. PC Care Napoli - 081.191.73.939`
        : `Gentile ${ordine.cliente}, grazie per aver ritirato il suo ordine. A presto! PC Care Napoli`;
      const tel = ordine.telefono.replace(/\D/g, '');
      window.open(`https://wa.me/39${tel}?text=${encodeURIComponent(msg)}`, '_blank');
    }
  };
  
  // Stampa ordine (copia negozio e cliente)
  const stampaOrdine = (ordine) => {
    const dataOrdine = new Date(ordine.dataOrdine).toLocaleDateString('it-IT');
    const stato = STATI_ORDINE[ordine.stato] || STATI_ORDINE.in_attesa;
    
    const generaCopiaHtml = (tipo) => `
      <div style="border: 2px solid #000; padding: 20px; margin-bottom: 20px; page-break-inside: avoid;">
        <div style="text-align: center; border-bottom: 2px solid #000; padding-bottom: 15px; margin-bottom: 15px;">
          <h1 style="margin: 0; font-size: 24px;">PC CARE NAPOLI</h1>
          <p style="margin: 5px 0; font-size: 12px;">Vico Acitillo, 148 - 80127 Napoli (NA)</p>
          <p style="margin: 5px 0; font-size: 12px;">Tel: 081 191 73 939 - info@pccarenapoli.it</p>
        </div>
        
        <div style="text-align: center; margin-bottom: 15px;">
          <h2 style="margin: 0; font-size: 18px; background: #f0f0f0; padding: 8px;">CONFERMA ORDINE - ${tipo}</h2>
        </div>
        
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
          <tr>
            <td style="padding: 8px; border: 1px solid #ccc; width: 30%; font-weight: bold;">N° Ordine:</td>
            <td style="padding: 8px; border: 1px solid #ccc;">#${ordine.id}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #ccc; font-weight: bold;">Data:</td>
            <td style="padding: 8px; border: 1px solid #ccc;">${dataOrdine}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #ccc; font-weight: bold;">Stato:</td>
            <td style="padding: 8px; border: 1px solid #ccc;">${stato.icon} ${stato.label}</td>
          </tr>
        </table>
        
        <h3 style="margin: 15px 0 10px; font-size: 14px; border-bottom: 1px solid #ccc; padding-bottom: 5px;">DATI CLIENTE</h3>
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
          <tr>
            <td style="padding: 8px; border: 1px solid #ccc; width: 30%; font-weight: bold;">Cliente:</td>
            <td style="padding: 8px; border: 1px solid #ccc;">${ordine.cliente || '-'}</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #ccc; font-weight: bold;">Telefono:</td>
            <td style="padding: 8px; border: 1px solid #ccc;">${ordine.telefono || '-'}</td>
          </tr>
        </table>
        
        <h3 style="margin: 15px 0 10px; font-size: 14px; border-bottom: 1px solid #ccc; padding-bottom: 5px;">DETTAGLI ORDINE</h3>
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
          <tr>
            <td style="padding: 8px; border: 1px solid #ccc; width: 30%; font-weight: bold;">Prodotto:</td>
            <td style="padding: 8px; border: 1px solid #ccc;">${ordine.prodotto || '-'}</td>
          </tr>
          ${ordine.quantita > 1 ? '<tr><td style="padding: 8px; border: 1px solid #ccc; font-weight: bold;">Quantità:</td><td style="padding: 8px; border: 1px solid #ccc;">' + ordine.quantita + '</td></tr>' : ''}
          ${(ordine.fornitore && tipo === 'COPIA NEGOZIO') ? '<tr><td style="padding: 8px; border: 1px solid #ccc; font-weight: bold;">Fornitore:</td><td style="padding: 8px; border: 1px solid #ccc;">' + ordine.fornitore + '</td></tr>' : ''}
          ${ordine.note ? '<tr><td style="padding: 8px; border: 1px solid #ccc; font-weight: bold;">Note:</td><td style="padding: 8px; border: 1px solid #ccc;">' + ordine.note + '</td></tr>' : ''}
        </table>
        
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px; background: #f9f9f9;">
          <tr>
            <td style="padding: 12px; border: 1px solid #ccc; font-weight: bold; font-size: 16px;">PREZZO:</td>
            <td style="padding: 12px; border: 1px solid #ccc; font-size: 20px; font-weight: bold; text-align: right;">€ ${(ordine.prezzo || 0).toFixed(2)}</td>
          </tr>
          ${ordine.acconto > 0 ? '<tr><td style="padding: 12px; border: 1px solid #ccc; font-weight: bold; color: #22c55e;">ACCONTO VERSATO:</td><td style="padding: 12px; border: 1px solid #ccc; font-size: 16px; font-weight: bold; text-align: right; color: #22c55e;">€ ' + ordine.acconto.toFixed(2) + '</td></tr><tr><td style="padding: 12px; border: 1px solid #ccc; font-weight: bold;">SALDO DA PAGARE:</td><td style="padding: 12px; border: 1px solid #ccc; font-size: 18px; font-weight: bold; text-align: right;">€ ' + ((ordine.prezzo || 0) - (ordine.acconto || 0)).toFixed(2) + '</td></tr>' : ''}
        </table>
        
        <div style="margin-top: 20px; padding-top: 15px; border-top: 1px dashed #ccc;">
          <p style="font-size: 11px; color: #666; margin: 5px 0;">• Il cliente verrà avvisato via WhatsApp all'arrivo della merce.</p>
          <p style="font-size: 11px; color: #666; margin: 5px 0;">• La merce non ritirata entro 30 giorni dall'avviso sarà considerata abbandonata.</p>
          <p style="font-size: 11px; color: #666; margin: 5px 0;">• L'acconto non è rimborsabile in caso di rinuncia all'ordine.</p>
        </div>
        
        <div style="display: flex; justify-content: space-between; margin-top: 30px; padding-top: 20px;">
          <div style="text-align: center; width: 45%;"><div style="border-top: 1px solid #000; padding-top: 5px; font-size: 12px;">Firma Cliente</div></div>
          <div style="text-align: center; width: 45%;"><div style="border-top: 1px solid #000; padding-top: 5px; font-size: 12px;">Firma Operatore</div></div>
        </div>
      </div>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write('<!DOCTYPE html><html><head><title>Ordine #' + ordine.id + '</title><style>@page { size: A4; margin: 10mm; } body { font-family: Arial, sans-serif; font-size: 14px; margin: 0; padding: 20px; } @media print { body { padding: 0; } }</style></head><body>' + generaCopiaHtml('COPIA NEGOZIO') + generaCopiaHtml('COPIA CLIENTE') + '<script>window.onload = function() { window.print(); }<\/script></body></html>');
    printWindow.document.close();
  };
  
  const inputStyle = { width: '100%', padding: '12px 14px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14 };
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6, color: muted };
  
  return (
    <div style={{ paddingTop: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>📦 Ordini</h2>
        <button onClick={() => setOrdineModal({})} style={{ padding: '12px 20px', background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', border: 'none', borderRadius: 12, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>+ Nuovo Ordine</button>
      </div>
      
      {/* Filtri */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="🔍 Cerca cliente o prodotto..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{ ...inputStyle, flex: 1, minWidth: 200 }}
        />
        {['tutti', 'in_attesa', 'arrivato', 'consegnato'].map(f => (
          <button
            key={f}
            onClick={() => setFiltro(f)}
            style={{
              padding: '10px 16px',
              background: filtro === f ? '#8b5cf6' : bgInput,
              border: '1px solid ' + (filtro === f ? '#8b5cf6' : bord),
              borderRadius: 10,
              color: filtro === f ? '#fff' : (dark ? '#e2e8f0' : '#1e293b'),
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: 13
            }}
          >
            {f === 'tutti' ? 'Tutti' : STATI_ORDINE[f]?.label}
          </button>
        ))}
      </div>
      
      {/* Statistiche */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, marginBottom: 20 }}>
        {Object.entries(STATI_ORDINE).map(([key, val]) => {
          const count = ordini.filter(o => o.stato === key).length;
          return (
            <div key={key} style={{ background: bgCard, borderRadius: 12, padding: 16, textAlign: 'center', border: '1px solid ' + bord }}>
              <div style={{ fontSize: 24 }}>{val.icon}</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: val.color }}>{count}</div>
              <div style={{ fontSize: 11, color: muted }}>{val.label}</div>
            </div>
          );
        })}
      </div>
      
      {/* Lista ordini */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {ordiniVisibili.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: muted }}>Nessun ordine trovato</div>
        ) : ordiniVisibili.map(ordine => {
          const stato = STATI_ORDINE[ordine.stato] || STATI_ORDINE.in_attesa;
          return (
            <div key={ordine.id} style={{ background: bgCard, borderRadius: 16, padding: 16, border: '1px solid ' + bord }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ padding: '4px 10px', background: stato.color + '20', color: stato.color, borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                      {stato.icon} {stato.label}
                    </span>
                    <span style={{ fontSize: 11, color: muted }}>{new Date(ordine.dataOrdine).toLocaleDateString('it-IT')}</span>
                  </div>
                  <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700 }}>{ordine.cliente}</h3>
                  <p style={{ margin: '0 0 4px', fontSize: 14, color: muted }}>📱 {ordine.telefono}</p>
                  <p style={{ margin: 0, fontSize: 14 }}><strong>Prodotto:</strong> {ordine.prodotto}</p>
                  {ordine.fornitore && <p style={{ margin: '4px 0 0', fontSize: 13, color: muted }}>Fornitore: {ordine.fornitore}</p>}
                  {ordine.note && <p style={{ margin: '4px 0 0', fontSize: 13, color: muted, fontStyle: 'italic' }}>{ordine.note}</p>}
                  {ordine.acconto > 0 && <p style={{ margin: '4px 0 0', fontSize: 13, color: '#22c55e' }}>Acconto: €{ordine.acconto.toFixed(2)}</p>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#22c55e', marginBottom: 8 }}>€{(ordine.prezzo || 0).toFixed(2)}</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {ordine.stato === 'in_attesa' && (
                      <button onClick={() => cambiaStato(ordine.id, 'arrivato')} style={{ padding: '6px 12px', background: '#3b82f6', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, cursor: 'pointer' }}>📦 Arrivato</button>
                    )}
                    {ordine.stato === 'arrivato' && (
                      <button onClick={() => cambiaStato(ordine.id, 'consegnato')} style={{ padding: '6px 12px', background: '#22c55e', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, cursor: 'pointer' }}>✅ Consegnato</button>
                    )}
                    <button onClick={() => stampaOrdine(ordine)} style={{ padding: '6px 12px', background: bgInput, border: '1px solid ' + bord, borderRadius: 8, fontSize: 12, cursor: 'pointer' }} title="Stampa ordine">🖨️</button>
                    <button onClick={() => setOrdineModal(ordine)} style={{ padding: '6px 12px', background: bgInput, border: '1px solid ' + bord, borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>✏️</button>
                    <button onClick={() => eliminaOrdine(ordine.id)} style={{ padding: '6px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#ef4444', fontSize: 12, cursor: 'pointer' }}>🗑️</button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Modal Ordine */}
      {ordineModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => { setOrdineModal(null); setShowClientiDropdown(false); }}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 500, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid ' + bord }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{ordineModal.id ? '✏️ Modifica Ordine' : '📦 Nuovo Ordine'}</h3>
            </div>
            <form onSubmit={e => { e.preventDefault(); salvaOrdine(ordineModal); }} style={{ padding: 24 }}>
              <label style={labelStyle}>Cliente *</label>
              <div style={{ position: 'relative', marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input 
                    type="text" 
                    required 
                    value={ordineModal.cliente || ''} 
                    onChange={e => setOrdineModal({ ...ordineModal, cliente: e.target.value })} 
                    style={{ ...inputStyle, flex: 1 }} 
                    placeholder="Nome cliente"
                  />
                  <button 
                    type="button"
                    onClick={() => setShowClientiDropdown(!showClientiDropdown)}
                    style={{ padding: '12px 16px', background: '#8b5cf6', border: 'none', borderRadius: 10, color: '#fff', cursor: 'pointer', fontWeight: 600 }}
                  >
                    👥
                  </button>
                </div>
                {showClientiDropdown && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: dark ? '#1e293b' : '#fff', border: '1px solid ' + bord, borderRadius: 10, marginTop: 4, maxHeight: 250, overflow: 'auto', zIndex: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
                    <input 
                      type="text"
                      placeholder="🔍 Cerca cliente..."
                      value={clienteSearch}
                      onChange={e => setClienteSearch(e.target.value)}
                      style={{ ...inputStyle, margin: 8, width: 'calc(100% - 16px)', borderRadius: 8 }}
                      autoFocus
                    />
                    {clientiFiltrati.length === 0 ? (
                      <p style={{ padding: 16, textAlign: 'center', color: muted, margin: 0 }}>Nessun cliente trovato</p>
                    ) : clientiFiltrati.map(c => (
                      <div 
                        key={c.id}
                        onClick={() => selezionaCliente(c)}
                        style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid ' + bord, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                      >
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{c.nome}</div>
                          <div style={{ fontSize: 12, color: muted }}>{c.telefono}</div>
                        </div>
                        {c.azienda && <span style={{ fontSize: 11, color: '#8b5cf6' }}>{c.azienda}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <label style={labelStyle}>Telefono *</label>
              <input type="tel" required value={ordineModal.telefono || ''} onChange={e => setOrdineModal({ ...ordineModal, telefono: e.target.value })} style={{ ...inputStyle, marginBottom: 16 }} />
              
              <label style={labelStyle}>Prodotto *</label>
              <div style={{ position: 'relative', marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input 
                    type="text" 
                    required 
                    value={ordineModal.prodotto || ''} 
                    onChange={e => setOrdineModal({ ...ordineModal, prodotto: e.target.value })} 
                    style={{ ...inputStyle, flex: 1 }} 
                    placeholder="Nome prodotto"
                  />
                  {magazzino.length > 0 && (
                    <button 
                      type="button"
                      onClick={() => { setShowProdottiDropdown(!showProdottiDropdown); setShowClientiDropdown(false); }}
                      style={{ padding: '12px 16px', background: '#14b8a6', border: 'none', borderRadius: 10, color: '#fff', cursor: 'pointer', fontWeight: 600 }}
                      title="Seleziona da magazzino"
                    >
                      🏪
                    </button>
                  )}
                </div>
                {showProdottiDropdown && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: dark ? '#1e293b' : '#fff', border: '1px solid ' + bord, borderRadius: 10, marginTop: 4, maxHeight: 250, overflow: 'auto', zIndex: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
                    <input 
                      type="text"
                      placeholder="🔍 Cerca prodotto..."
                      value={prodottoSearch}
                      onChange={e => setProdottoSearch(e.target.value)}
                      style={{ ...inputStyle, borderRadius: 0, border: 'none', borderBottom: '1px solid ' + bord }}
                      autoFocus
                    />
                    {prodottiFiltrati.length === 0 ? (
                      <div style={{ padding: 12, textAlign: 'center', color: muted }}>Nessun prodotto trovato</div>
                    ) : prodottiFiltrati.map(p => (
                      <div 
                        key={p.id}
                        onClick={() => selezionaProdotto(p)}
                        style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid ' + bord, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                      >
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{p.nome}</div>
                          <div style={{ fontSize: 12, color: muted }}>
                            {p.codice && <span>Cod: {p.codice} • </span>}
                            Disp: {p.quantita || 0}
                          </div>
                        </div>
                        {p.prezzoVendita > 0 && <span style={{ fontSize: 14, fontWeight: 600, color: '#22c55e' }}>€{p.prezzoVendita}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {ordineModal.prodottoId && (
                <div style={{ marginBottom: 16, padding: 10, background: 'rgba(20,184,166,0.1)', borderRadius: 8, fontSize: 12, color: '#14b8a6' }}>
                  🏪 Prodotto collegato al magazzino - verrà caricato automaticamente all'arrivo
                </div>
              )}
              
              <label style={labelStyle}>Fornitore</label>
              <input type="text" value={ordineModal.fornitore || ''} onChange={e => setOrdineModal({ ...ordineModal, fornitore: e.target.value })} style={{ ...inputStyle, marginBottom: 16 }} />
              
              <div style={{ display: 'grid', gridTemplateColumns: ordineModal.prodottoId ? '1fr 1fr 1fr' : '1fr 1fr', gap: 16, marginBottom: 16 }}>
                {ordineModal.prodottoId && (
                  <div>
                    <label style={labelStyle}>Quantità</label>
                    <input type="number" min="1" value={ordineModal.quantita || 1} onChange={e => setOrdineModal({ ...ordineModal, quantita: parseInt(e.target.value) || 1 })} style={inputStyle} />
                  </div>
                )}
                <div>
                  <label style={labelStyle}>Prezzo €</label>
                  <input type="number" step="0.01" value={ordineModal.prezzo || ''} onChange={e => setOrdineModal({ ...ordineModal, prezzo: parseFloat(e.target.value) || 0 })} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Acconto €</label>
                  <input type="number" step="0.01" value={ordineModal.acconto || ''} onChange={e => setOrdineModal({ ...ordineModal, acconto: parseFloat(e.target.value) || 0 })} style={inputStyle} />
                </div>
                {(ordineModal.acconto > 0) && (
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={labelStyle}>💰 Dove registrare l'acconto</label>
                    <select value={ordineModal.accontoConto || ''} onChange={e => setOrdineModal({ ...ordineModal, accontoConto: e.target.value })} style={{...inputStyle, background: ordineModal.accontoConto ? 'rgba(34,197,94,0.1)' : bgInput, borderColor: ordineModal.accontoConto ? 'rgba(34,197,94,0.3)' : bord}}>
                      <option value="">-- Seleziona conto --</option>
                      {contiDisponibili.map(c => (
                        <option key={c.id} value={c.id}>{c.icon} {c.nome}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              
              {ordineModal.id && (
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Stato</label>
                  <select value={ordineModal.stato || 'in_attesa'} onChange={e => setOrdineModal({ ...ordineModal, stato: e.target.value })} style={inputStyle}>
                    {Object.entries(STATI_ORDINE).map(([key, val]) => (
                      <option key={key} value={key}>{val.icon} {val.label}</option>
                    ))}
                  </select>
                </div>
              )}
              
              <label style={labelStyle}>Note</label>
              <textarea value={ordineModal.note || ''} onChange={e => setOrdineModal({ ...ordineModal, note: e.target.value })} style={{ ...inputStyle, minHeight: 80, resize: 'vertical', marginBottom: 20 }} />
              
              <div style={{ display: 'flex', gap: 12 }}>
                <button type="button" onClick={() => { setOrdineModal(null); setShowClientiDropdown(false); }} style={{ flex: 1, padding: 14, background: bgInput, border: '1px solid ' + bord, borderRadius: 12, cursor: 'pointer', color: muted }}>Annulla</button>
                <button type="submit" style={{ flex: 1, padding: 14, background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', border: 'none', borderRadius: 12, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>💾 Salva</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== MAGAZZINO TAB ====================
function MagazzinoTab({ data, save, dark, bgCard, bgInput, bord, muted }) {
  const [prodottoModal, setProdottoModal] = useState(null);
  const [movimentoModal, setMovimentoModal] = useState(null);
  const [filtro, setFiltro] = useState('tutti'); // tutti, disponibili, esauriti, in_arrivo
  const [searchTerm, setSearchTerm] = useState('');
  const [vista, setVista] = useState('prodotti'); // prodotti, movimenti
  const [categoriaFiltro, setCategoriaFiltro] = useState('tutte');
  
  const magazzino = data.magazzino || [];
  const movimentiMagazzino = data.movimentiMagazzino || [];
  const categorieMagazzino = data.categorieMagazzino || [
    { id: 'ricambi', nome: 'Ricambi PC', icon: '🔧', color: '#3b82f6' },
    { id: 'accessori', nome: 'Accessori', icon: '🎧', color: '#8b5cf6' },
    { id: 'consumabili', nome: 'Consumabili', icon: '📦', color: '#f59e0b' },
    { id: 'componenti', nome: 'Componenti', icon: '💾', color: '#10b981' },
    { id: 'altro', nome: 'Altro', icon: '📋', color: '#6b7280' }
  ];
  
  // Statistiche
  const totProdotti = magazzino.length;
  const totDisponibili = magazzino.filter(p => (p.quantita || 0) > 0).length;
  const totEsauriti = magazzino.filter(p => (p.quantita || 0) === 0).length;
  const totSottoScorta = magazzino.filter(p => (p.quantita || 0) > 0 && (p.quantita || 0) <= (p.scortaMinima || 0)).length;
  const valoreTotale = magazzino.reduce((s, p) => s + ((p.quantita || 0) * (p.prezzoAcquisto || 0)), 0);
  
  // Filtra prodotti
  const prodottiVisibili = magazzino
    .filter(p => {
      if (filtro === 'disponibili') return (p.quantita || 0) > 0;
      if (filtro === 'esauriti') return (p.quantita || 0) === 0;
      if (filtro === 'sotto_scorta') return (p.quantita || 0) > 0 && (p.quantita || 0) <= (p.scortaMinima || 0);
      return true;
    })
    .filter(p => categoriaFiltro === 'tutte' || p.categoria === categoriaFiltro)
    .filter(p => !searchTerm || 
      (p.nome || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.codice || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.fornitore || '').toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => (a.nome || '').localeCompare(b.nome || ''));
  
  // Salva prodotto
  const salvaProdotto = (prodotto) => {
    if (prodotto.id) {
      save({ ...data, magazzino: magazzino.map(p => p.id === prodotto.id ? prodotto : p) });
    } else {
      const nuovoProdotto = { 
        ...prodotto, 
        id: 'prod_' + Date.now(),
        dataCreazione: new Date().toISOString(),
        quantita: prodotto.quantita || 0
      };
      
      // Se c'è una quantità iniziale, registra come carico
      if (nuovoProdotto.quantita > 0) {
        const movimento = {
          id: 'mov_' + Date.now(),
          data: new Date().toISOString(),
          tipo: 'carico',
          prodottoId: nuovoProdotto.id,
          prodottoNome: nuovoProdotto.nome,
          quantita: nuovoProdotto.quantita,
          note: 'Carico iniziale'
        };
        save({ 
          ...data, 
          magazzino: [...magazzino, nuovoProdotto],
          movimentiMagazzino: [...movimentiMagazzino, movimento]
        });
      } else {
        save({ ...data, magazzino: [...magazzino, nuovoProdotto] });
      }
    }
    setProdottoModal(null);
  };
  
  // Elimina prodotto
  const eliminaProdotto = (id) => {
    if (window.confirm('Eliminare questo prodotto dal magazzino?')) {
      save({ ...data, magazzino: magazzino.filter(p => p.id !== id) });
    }
  };
  
  // Registra movimento (carico/scarico)
  const registraMovimento = (prodottoId, tipo, quantita, note = '', ordineId = null) => {
    const prodotto = magazzino.find(p => p.id === prodottoId);
    if (!prodotto) return;
    
    const nuovaQuantita = tipo === 'carico' 
      ? (prodotto.quantita || 0) + quantita 
      : Math.max(0, (prodotto.quantita || 0) - quantita);
    
    const movimento = {
      id: 'mov_' + Date.now(),
      data: new Date().toISOString(),
      tipo,
      prodottoId,
      prodottoNome: prodotto.nome,
      quantita,
      quantitaPrecedente: prodotto.quantita || 0,
      quantitaNuova: nuovaQuantita,
      note,
      ordineId
    };
    
    save({
      ...data,
      magazzino: magazzino.map(p => p.id === prodottoId ? { ...p, quantita: nuovaQuantita } : p),
      movimentiMagazzino: [...movimentiMagazzino, movimento]
    });
    
    setMovimentoModal(null);
  };
  
  const inputStyle = { width: '100%', padding: '12px 14px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14 };
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6, color: muted };
  
  return (
    <div style={{ paddingTop: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>🏪 Magazzino</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button 
            onClick={() => setVista(vista === 'prodotti' ? 'movimenti' : 'prodotti')}
            style={{ padding: '12px 16px', background: bgInput, border: '1px solid ' + bord, borderRadius: 12, color: dark ? '#e2e8f0' : '#1e293b', cursor: 'pointer' }}
          >
            {vista === 'prodotti' ? '📋 Movimenti' : '📦 Prodotti'}
          </button>
          <button onClick={() => setProdottoModal({})} style={{ padding: '12px 20px', background: 'linear-gradient(135deg, #14b8a6, #0d9488)', border: 'none', borderRadius: 12, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>+ Nuovo Prodotto</button>
        </div>
      </div>
      
      {/* Statistiche */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
        <div style={{ background: bgCard, borderRadius: 12, padding: 16, border: '1px solid ' + bord, textAlign: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#3b82f6' }}>{totProdotti}</div>
          <div style={{ fontSize: 11, color: muted }}>Prodotti totali</div>
        </div>
        <div style={{ background: bgCard, borderRadius: 12, padding: 16, border: '1px solid ' + bord, textAlign: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#22c55e' }}>{totDisponibili}</div>
          <div style={{ fontSize: 11, color: muted }}>Disponibili</div>
        </div>
        <div style={{ background: bgCard, borderRadius: 12, padding: 16, border: '1px solid ' + bord, textAlign: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#f59e0b' }}>{totSottoScorta}</div>
          <div style={{ fontSize: 11, color: muted }}>Sotto scorta</div>
        </div>
        <div style={{ background: bgCard, borderRadius: 12, padding: 16, border: '1px solid ' + bord, textAlign: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#ef4444' }}>{totEsauriti}</div>
          <div style={{ fontSize: 11, color: muted }}>Esauriti</div>
        </div>
        <div style={{ background: bgCard, borderRadius: 12, padding: 16, border: '1px solid ' + bord, textAlign: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#8b5cf6' }}>€{valoreTotale.toFixed(0)}</div>
          <div style={{ fontSize: 11, color: muted }}>Valore magazzino</div>
        </div>
      </div>
      
      {vista === 'prodotti' ? (
        <>
          {/* Filtri */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="🔍 Cerca prodotto, codice, fornitore..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ ...inputStyle, flex: 1, minWidth: 200 }}
            />
            <select
              value={categoriaFiltro}
              onChange={e => setCategoriaFiltro(e.target.value)}
              style={{ ...inputStyle, width: 'auto', minWidth: 150 }}
            >
              <option value="tutte">Tutte le categorie</option>
              {categorieMagazzino.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.icon} {cat.nome}</option>
              ))}
            </select>
          </div>
          
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {[
              { id: 'tutti', label: 'Tutti', count: totProdotti },
              { id: 'disponibili', label: 'Disponibili', count: totDisponibili, color: '#22c55e' },
              { id: 'sotto_scorta', label: 'Sotto scorta', count: totSottoScorta, color: '#f59e0b' },
              { id: 'esauriti', label: 'Esauriti', count: totEsauriti, color: '#ef4444' }
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFiltro(f.id)}
                style={{
                  padding: '8px 14px',
                  background: filtro === f.id ? (f.color || '#14b8a6') : bgInput,
                  border: '1px solid ' + (filtro === f.id ? (f.color || '#14b8a6') : bord),
                  borderRadius: 8,
                  color: filtro === f.id ? '#fff' : (dark ? '#e2e8f0' : '#1e293b'),
                  fontSize: 12,
                  cursor: 'pointer'
                }}
              >
                {f.label} ({f.count})
              </button>
            ))}
          </div>
          
          {/* Lista prodotti */}
          <div style={{ background: bgCard, borderRadius: 16, border: '1px solid ' + bord, overflow: 'hidden' }}>
            {prodottiVisibili.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: muted }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>📦</div>
                <div>Nessun prodotto trovato</div>
              </div>
            ) : (
              prodottiVisibili.map((prodotto, idx) => {
                const cat = categorieMagazzino.find(c => c.id === prodotto.categoria);
                const sottoScorta = (prodotto.quantita || 0) <= (prodotto.scortaMinima || 0) && (prodotto.quantita || 0) > 0;
                const esaurito = (prodotto.quantita || 0) === 0;
                
                return (
                  <div 
                    key={prodotto.id} 
                    style={{ 
                      padding: 16, 
                      borderBottom: idx < prodottiVisibili.length - 1 ? '1px solid ' + bord : 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 16,
                      background: esaurito ? 'rgba(239,68,68,0.05)' : sottoScorta ? 'rgba(245,158,11,0.05)' : 'transparent'
                    }}
                  >
                    {/* Icona categoria */}
                    <div style={{ 
                      width: 44, 
                      height: 44, 
                      borderRadius: 10, 
                      background: cat?.color + '20' || 'rgba(0,0,0,0.05)', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      fontSize: 20
                    }}>
                      {cat?.icon || '📦'}
                    </div>
                    
                    {/* Info prodotto */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{prodotto.nome}</div>
                      <div style={{ fontSize: 11, color: muted, display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 2 }}>
                        {prodotto.codice && <span>Cod: {prodotto.codice}</span>}
                        {prodotto.fornitore && <span>Forn: {prodotto.fornitore}</span>}
                        {prodotto.prezzoAcquisto > 0 && <span>Acq: €{prodotto.prezzoAcquisto}</span>}
                        {prodotto.prezzoVendita > 0 && <span>Vend: €{prodotto.prezzoVendita}</span>}
                      </div>
                    </div>
                    
                    {/* Quantità */}
                    <div style={{ textAlign: 'center', minWidth: 80 }}>
                      <div style={{ 
                        fontSize: 20, 
                        fontWeight: 700, 
                        color: esaurito ? '#ef4444' : sottoScorta ? '#f59e0b' : '#22c55e' 
                      }}>
                        {prodotto.quantita || 0}
                      </div>
                      <div style={{ fontSize: 10, color: muted }}>
                        {esaurito ? 'ESAURITO' : sottoScorta ? 'SOTTO SCORTA' : 'disponibili'}
                      </div>
                    </div>
                    
                    {/* Azioni */}
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button 
                        onClick={() => setMovimentoModal({ prodotto, tipo: 'carico' })}
                        style={{ padding: '8px 12px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 8, color: '#22c55e', cursor: 'pointer', fontWeight: 600 }}
                        title="Carico"
                      >
                        +
                      </button>
                      <button 
                        onClick={() => setMovimentoModal({ prodotto, tipo: 'scarico' })}
                        style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#ef4444', cursor: 'pointer', fontWeight: 600 }}
                        title="Scarico"
                        disabled={(prodotto.quantita || 0) === 0}
                      >
                        −
                      </button>
                      <button 
                        onClick={() => setProdottoModal(prodotto)}
                        style={{ padding: '8px 12px', background: bgInput, border: '1px solid ' + bord, borderRadius: 8, cursor: 'pointer' }}
                      >
                        ✏️
                      </button>
                      <button 
                        onClick={() => eliminaProdotto(prodotto.id)}
                        style={{ padding: '8px 12px', background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', opacity: 0.5 }}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      ) : (
        /* Vista Movimenti */
        <div style={{ background: bgCard, borderRadius: 16, border: '1px solid ' + bord, overflow: 'hidden' }}>
          <div style={{ padding: 16, borderBottom: '1px solid ' + bord, fontWeight: 600 }}>
            📋 Ultimi movimenti
          </div>
          {movimentiMagazzino.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: muted }}>Nessun movimento registrato</div>
          ) : (
            [...movimentiMagazzino].reverse().slice(0, 50).map((mov, idx) => (
              <div key={mov.id} style={{ padding: 12, borderBottom: '1px solid ' + bord, display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ 
                  width: 36, 
                  height: 36, 
                  borderRadius: 8, 
                  background: mov.tipo === 'carico' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: mov.tipo === 'carico' ? '#22c55e' : '#ef4444',
                  fontWeight: 700
                }}>
                  {mov.tipo === 'carico' ? '+' : '−'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>{mov.prodottoNome}</div>
                  <div style={{ fontSize: 11, color: muted }}>
                    {new Date(mov.data).toLocaleDateString('it-IT')} {new Date(mov.data).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                    {mov.note && <span> • {mov.note}</span>}
                  </div>
                </div>
                <div style={{ 
                  fontWeight: 700, 
                  fontSize: 16,
                  color: mov.tipo === 'carico' ? '#22c55e' : '#ef4444'
                }}>
                  {mov.tipo === 'carico' ? '+' : '-'}{mov.quantita}
                </div>
              </div>
            ))
          )}
        </div>
      )}
      
      {/* Modal Prodotto */}
      {prodottoModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setProdottoModal(null)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 500, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid ' + bord }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{prodottoModal.id ? '✏️ Modifica Prodotto' : '➕ Nuovo Prodotto'}</h3>
            </div>
            <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={labelStyle}>Nome prodotto *</label>
                <input
                  type="text"
                  value={prodottoModal.nome || ''}
                  onChange={e => setProdottoModal({ ...prodottoModal, nome: e.target.value })}
                  style={inputStyle}
                  placeholder="es. SSD Samsung 500GB"
                />
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Codice/SKU</label>
                  <input
                    type="text"
                    value={prodottoModal.codice || ''}
                    onChange={e => setProdottoModal({ ...prodottoModal, codice: e.target.value })}
                    style={inputStyle}
                    placeholder="es. SSD-SAM-500"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Categoria</label>
                  <select
                    value={prodottoModal.categoria || ''}
                    onChange={e => setProdottoModal({ ...prodottoModal, categoria: e.target.value })}
                    style={inputStyle}
                  >
                    <option value="">Seleziona...</option>
                    {categorieMagazzino.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.icon} {cat.nome}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label style={labelStyle}>Fornitore</label>
                <input
                  type="text"
                  value={prodottoModal.fornitore || ''}
                  onChange={e => setProdottoModal({ ...prodottoModal, fornitore: e.target.value })}
                  style={inputStyle}
                  placeholder="es. Amazon, Esprinet..."
                />
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Prezzo acquisto €</label>
                  <input
                    type="number"
                    step="0.01"
                    value={prodottoModal.prezzoAcquisto || ''}
                    onChange={e => setProdottoModal({ ...prodottoModal, prezzoAcquisto: parseFloat(e.target.value) || 0 })}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Prezzo vendita €</label>
                  <input
                    type="number"
                    step="0.01"
                    value={prodottoModal.prezzoVendita || ''}
                    onChange={e => setProdottoModal({ ...prodottoModal, prezzoVendita: parseFloat(e.target.value) || 0 })}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Scorta minima</label>
                  <input
                    type="number"
                    value={prodottoModal.scortaMinima || ''}
                    onChange={e => setProdottoModal({ ...prodottoModal, scortaMinima: parseInt(e.target.value) || 0 })}
                    style={inputStyle}
                    placeholder="0"
                  />
                </div>
              </div>
              
              {!prodottoModal.id && (
                <div>
                  <label style={labelStyle}>Quantità iniziale</label>
                  <input
                    type="number"
                    value={prodottoModal.quantita || ''}
                    onChange={e => setProdottoModal({ ...prodottoModal, quantita: parseInt(e.target.value) || 0 })}
                    style={inputStyle}
                    placeholder="0"
                  />
                </div>
              )}
              
              <div>
                <label style={labelStyle}>Note</label>
                <textarea
                  value={prodottoModal.note || ''}
                  onChange={e => setProdottoModal({ ...prodottoModal, note: e.target.value })}
                  style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
                  placeholder="Note aggiuntive..."
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, padding: '16px 24px', borderTop: '1px solid ' + bord }}>
              <button onClick={() => setProdottoModal(null)} style={{ flex: 1, padding: 14, background: bgInput, border: 'none', borderRadius: 10, color: muted, cursor: 'pointer' }}>Annulla</button>
              <button 
                onClick={() => salvaProdotto(prodottoModal)}
                disabled={!prodottoModal.nome}
                style={{ flex: 1, padding: 14, background: prodottoModal.nome ? 'linear-gradient(135deg, #14b8a6, #0d9488)' : '#ccc', border: 'none', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: prodottoModal.nome ? 'pointer' : 'not-allowed' }}
              >
                💾 Salva
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal Movimento (Carico/Scarico) */}
      {movimentoModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setMovimentoModal(null)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div style={{ 
              padding: '20px 24px', 
              borderBottom: '1px solid ' + bord,
              background: movimentoModal.tipo === 'carico' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)'
            }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: movimentoModal.tipo === 'carico' ? '#22c55e' : '#ef4444' }}>
                {movimentoModal.tipo === 'carico' ? '📥 Carico Merce' : '📤 Scarico Merce'}
              </h3>
              <p style={{ margin: '8px 0 0', fontSize: 14 }}>{movimentoModal.prodotto.nome}</p>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: muted }}>Quantità attuale: {movimentoModal.prodotto.quantita || 0}</p>
            </div>
            <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={labelStyle}>Quantità</label>
                <input
                  type="number"
                  min="1"
                  max={movimentoModal.tipo === 'scarico' ? movimentoModal.prodotto.quantita : 9999}
                  value={movimentoModal.quantita || ''}
                  onChange={e => setMovimentoModal({ ...movimentoModal, quantita: parseInt(e.target.value) || 0 })}
                  style={{ ...inputStyle, fontSize: 24, textAlign: 'center', fontWeight: 700 }}
                  autoFocus
                />
              </div>
              <div>
                <label style={labelStyle}>Note (opzionale)</label>
                <input
                  type="text"
                  value={movimentoModal.note || ''}
                  onChange={e => setMovimentoModal({ ...movimentoModal, note: e.target.value })}
                  style={inputStyle}
                  placeholder="es. Ordine #123, Vendita a cliente..."
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, padding: '16px 24px', borderTop: '1px solid ' + bord }}>
              <button onClick={() => setMovimentoModal(null)} style={{ flex: 1, padding: 14, background: bgInput, border: 'none', borderRadius: 10, color: muted, cursor: 'pointer' }}>Annulla</button>
              <button 
                onClick={() => registraMovimento(
                  movimentoModal.prodotto.id, 
                  movimentoModal.tipo, 
                  movimentoModal.quantita || 0,
                  movimentoModal.note || ''
                )}
                disabled={!movimentoModal.quantita || movimentoModal.quantita <= 0}
                style={{ 
                  flex: 1, 
                  padding: 14, 
                  background: movimentoModal.quantita > 0 
                    ? (movimentoModal.tipo === 'carico' ? '#22c55e' : '#ef4444')
                    : '#ccc', 
                  border: 'none', 
                  borderRadius: 10, 
                  color: '#fff', 
                  fontWeight: 600, 
                  cursor: movimentoModal.quantita > 0 ? 'pointer' : 'not-allowed' 
                }}
              >
                {movimentoModal.tipo === 'carico' ? '📥 Carica' : '📤 Scarica'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== PREVENTIVI TAB ====================
function PreventiviTab({ data, save, del, dark, bgCard, bgInput, bord, muted }) {
  const [preventivoModal, setPreventivoModal] = useState(null);
  const [filtro, setFiltro] = useState('tutti');
  const [searchTerm, setSearchTerm] = useState('');
  const [showClientiDropdown, setShowClientiDropdown] = useState(false);
  const [clienteSearch, setClienteSearch] = useState('');
  
  const preventivi = data.preventivi || [];
  const clienti = data.clientiLab || [];
  
  // Filtra clienti per ricerca
  const clientiFiltrati = clienti.filter(c => 
    !clienteSearch || 
    (c.nome || '').toLowerCase().includes(clienteSearch.toLowerCase()) ||
    (c.telefono || '').includes(clienteSearch)
  ).slice(0, 10);
  
  const STATI_PREVENTIVO = {
    in_attesa: { label: 'In Attesa', color: '#f59e0b', icon: '⏳' },
    accettato: { label: 'Accettato', color: '#22c55e', icon: '✅' },
    rifiutato: { label: 'Rifiutato', color: '#ef4444', icon: '❌' },
    scaduto: { label: 'Scaduto', color: '#6b7280', icon: '⌛' }
  };
  
  const preventiviVisibili = preventivi
    .filter(p => filtro === 'tutti' || p.stato === filtro)
    .filter(p => !searchTerm || 
      (p.cliente || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.oggetto || '').toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => new Date(b.data || 0) - new Date(a.data || 0));
  
  // Seleziona cliente da anagrafica
  const selezionaCliente = (cliente) => {
    setPreventivoModal({
      ...preventivoModal,
      cliente: cliente.nome,
      telefono: cliente.telefono,
      email: cliente.email || '',
      clienteId: cliente.id
    });
    setShowClientiDropdown(false);
    setClienteSearch('');
  };
  
  const salvaPreventivo = (preventivo) => {
    if (preventivo.id) {
      save({ ...data, preventivi: preventivi.map(p => p.id === preventivo.id ? preventivo : p) });
    } else {
      // Genera numero preventivo
      const anno = new Date().getFullYear();
      const numeriAnno = preventivi.filter(p => p.numero && p.numero.startsWith(`P${anno}`)).length;
      const nuovoNumero = `P${anno}-${String(numeriAnno + 1).padStart(3, '0')}`;
      
      const nuovoPreventivo = { 
        ...preventivo, 
        id: Date.now().toString(),
        numero: nuovoNumero,
        data: new Date().toISOString(),
        stato: 'in_attesa',
        voci: preventivo.voci || []
      };
      save({ ...data, preventivi: [...preventivi, nuovoPreventivo] });
    }
    setPreventivoModal(null);
  };
  
  const eliminaPreventivo = (id) => {
    if (window.confirm('Eliminare questo preventivo?')) {
      // FIX: passa da del(), che cancella anche dal database
      del('preventivi', id);
    }
  };
  
  const cambiaStato = (id, nuovoStato) => {
    save({ ...data, preventivi: preventivi.map(p => p.id === id ? { ...p, stato: nuovoStato } : p) });
  };
  
  const stampaPreventivo = (preventivo) => {
    const totale = (preventivo.voci || []).reduce((s, v) => s + (v.quantita || 1) * (v.prezzo || 0), 0);
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Preventivo ${preventivo.numero}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .header { display: flex; justify-content: space-between; margin-bottom: 30px; }
          .logo { font-size: 24px; font-weight: bold; }
          .numero { font-size: 18px; color: #666; }
          .cliente { background: #f5f5f5; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
          th { background: #f0f0f0; }
          .totale { text-align: right; font-size: 20px; font-weight: bold; margin-top: 20px; }
          .note { margin-top: 30px; padding: 15px; background: #fffbeb; border-radius: 8px; }
          .footer { margin-top: 40px; text-align: center; color: #666; font-size: 12px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">PC CARE NAPOLI</div>
          <div class="numero">Preventivo ${preventivo.numero}<br><small>${new Date(preventivo.data).toLocaleDateString('it-IT')}</small></div>
        </div>
        <div class="cliente">
          <strong>${preventivo.cliente}</strong><br>
          ${preventivo.telefono ? '📱 ' + preventivo.telefono + '<br>' : ''}
          ${preventivo.email ? '✉️ ' + preventivo.email : ''}
        </div>
        <h3>Oggetto: ${preventivo.oggetto || 'Preventivo'}</h3>
        <table>
          <thead><tr><th>Descrizione</th><th>Qtà</th><th>Prezzo</th><th>Totale</th></tr></thead>
          <tbody>
            ${(preventivo.voci || []).map(v => `
              <tr>
                <td>${v.descrizione}</td>
                <td>${v.quantita || 1}</td>
                <td>€${(v.prezzo || 0).toFixed(2)}</td>
                <td>€${((v.quantita || 1) * (v.prezzo || 0)).toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="totale">TOTALE: €${totale.toFixed(2)}</div>
        ${preventivo.note ? `<div class="note"><strong>Note:</strong><br>${preventivo.note}</div>` : ''}
        <div class="footer">
          Preventivo valido 30 giorni dalla data di emissione<br>
          PC Care Napoli - Via Example 123 - 081.191.73.939
        </div>
      </body>
      </html>
    `;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.print();
  };
  
  const inputStyle = { width: '100%', padding: '12px 14px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14 };
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6, color: muted };
  
  return (
    <div style={{ paddingTop: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>📝 Preventivi</h2>
        <button onClick={() => setPreventivoModal({ voci: [{ descrizione: '', quantita: 1, prezzo: 0 }] })} style={{ padding: '12px 20px', background: 'linear-gradient(135deg, #06b6d4, #0891b2)', border: 'none', borderRadius: 12, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>+ Nuovo Preventivo</button>
      </div>
      
      {/* Filtri */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="🔍 Cerca cliente o oggetto..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{ ...inputStyle, flex: 1, minWidth: 200 }}
        />
        {['tutti', 'in_attesa', 'accettato', 'rifiutato'].map(f => (
          <button
            key={f}
            onClick={() => setFiltro(f)}
            style={{
              padding: '10px 16px',
              background: filtro === f ? '#06b6d4' : bgInput,
              border: '1px solid ' + (filtro === f ? '#06b6d4' : bord),
              borderRadius: 10,
              color: filtro === f ? '#fff' : (dark ? '#e2e8f0' : '#1e293b'),
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: 13
            }}
          >
            {f === 'tutti' ? 'Tutti' : STATI_PREVENTIVO[f]?.label}
          </button>
        ))}
      </div>
      
      {/* Lista preventivi */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {preventiviVisibili.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: muted }}>Nessun preventivo trovato</div>
        ) : preventiviVisibili.map(preventivo => {
          const stato = STATI_PREVENTIVO[preventivo.stato] || STATI_PREVENTIVO.in_attesa;
          const totale = (preventivo.voci || []).reduce((s, v) => s + (v.quantita || 1) * (v.prezzo || 0), 0);
          return (
            <div key={preventivo.id} style={{ background: bgCard, borderRadius: 16, padding: 16, border: '1px solid ' + bord }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontWeight: 700, color: '#06b6d4' }}>{preventivo.numero}</span>
                    <span style={{ padding: '4px 10px', background: stato.color + '20', color: stato.color, borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                      {stato.icon} {stato.label}
                    </span>
                    <span style={{ fontSize: 11, color: muted }}>{new Date(preventivo.data).toLocaleDateString('it-IT')}</span>
                  </div>
                  <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700 }}>{preventivo.cliente}</h3>
                  {preventivo.telefono && <p style={{ margin: '0 0 4px', fontSize: 14, color: muted }}>📱 {preventivo.telefono}</p>}
                  <p style={{ margin: 0, fontSize: 14 }}><strong>Oggetto:</strong> {preventivo.oggetto || '-'}</p>
                  <p style={{ margin: '4px 0 0', fontSize: 13, color: muted }}>{(preventivo.voci || []).length} voci</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#22c55e', marginBottom: 8 }}>€{totale.toFixed(2)}</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {preventivo.stato === 'in_attesa' && (
                      <>
                        <button onClick={() => cambiaStato(preventivo.id, 'accettato')} style={{ padding: '6px 12px', background: '#22c55e', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, cursor: 'pointer' }}>✅</button>
                        <button onClick={() => cambiaStato(preventivo.id, 'rifiutato')} style={{ padding: '6px 12px', background: '#ef4444', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, cursor: 'pointer' }}>❌</button>
                      </>
                    )}
                    <button onClick={() => stampaPreventivo(preventivo)} style={{ padding: '6px 12px', background: bgInput, border: '1px solid ' + bord, borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>🖨️</button>
                    <button onClick={() => setPreventivoModal(preventivo)} style={{ padding: '6px 12px', background: bgInput, border: '1px solid ' + bord, borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>✏️</button>
                    <button onClick={() => eliminaPreventivo(preventivo.id)} style={{ padding: '6px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#ef4444', fontSize: 12, cursor: 'pointer' }}>🗑️</button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Modal Preventivo */}
      {preventivoModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => { setPreventivoModal(null); setShowClientiDropdown(false); }}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 600, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid ' + bord }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{preventivoModal.id ? '✏️ Modifica Preventivo' : '📝 Nuovo Preventivo'}</h3>
            </div>
            <form onSubmit={e => { e.preventDefault(); salvaPreventivo(preventivoModal); }} style={{ padding: 24 }}>
              <label style={labelStyle}>Cliente *</label>
              <div style={{ position: 'relative', marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input 
                    type="text" 
                    required 
                    value={preventivoModal.cliente || ''} 
                    onChange={e => setPreventivoModal({ ...preventivoModal, cliente: e.target.value })} 
                    style={{ ...inputStyle, flex: 1 }} 
                    placeholder="Nome cliente"
                  />
                  <button 
                    type="button"
                    onClick={() => setShowClientiDropdown(!showClientiDropdown)}
                    style={{ padding: '12px 16px', background: '#06b6d4', border: 'none', borderRadius: 10, color: '#fff', cursor: 'pointer', fontWeight: 600 }}
                  >
                    👥
                  </button>
                </div>
                {showClientiDropdown && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: dark ? '#1e293b' : '#fff', border: '1px solid ' + bord, borderRadius: 10, marginTop: 4, maxHeight: 250, overflow: 'auto', zIndex: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
                    <input 
                      type="text"
                      placeholder="🔍 Cerca cliente..."
                      value={clienteSearch}
                      onChange={e => setClienteSearch(e.target.value)}
                      style={{ ...inputStyle, margin: 8, width: 'calc(100% - 16px)', borderRadius: 8 }}
                      autoFocus
                    />
                    {clientiFiltrati.length === 0 ? (
                      <p style={{ padding: 16, textAlign: 'center', color: muted, margin: 0 }}>Nessun cliente trovato</p>
                    ) : clientiFiltrati.map(c => (
                      <div 
                        key={c.id}
                        onClick={() => selezionaCliente(c)}
                        style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid ' + bord, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                      >
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{c.nome}</div>
                          <div style={{ fontSize: 12, color: muted }}>{c.telefono} {c.email && `• ${c.email}`}</div>
                        </div>
                        {c.azienda && <span style={{ fontSize: 11, color: '#06b6d4' }}>{c.azienda}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={labelStyle}>Telefono</label>
                  <input type="tel" value={preventivoModal.telefono || ''} onChange={e => setPreventivoModal({ ...preventivoModal, telefono: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Email</label>
                  <input type="email" value={preventivoModal.email || ''} onChange={e => setPreventivoModal({ ...preventivoModal, email: e.target.value })} style={inputStyle} />
                </div>
              </div>
              
              <label style={labelStyle}>Oggetto</label>
              <input type="text" value={preventivoModal.oggetto || ''} onChange={e => setPreventivoModal({ ...preventivoModal, oggetto: e.target.value })} style={{ ...inputStyle, marginBottom: 16 }} />
              
              <label style={labelStyle}>Voci Preventivo</label>
              <div style={{ marginBottom: 16 }}>
                {(preventivoModal.voci || []).map((voce, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                    <input type="text" placeholder="Descrizione" value={voce.descrizione} onChange={e => {
                      const voci = [...(preventivoModal.voci || [])];
                      voci[idx] = { ...voci[idx], descrizione: e.target.value };
                      setPreventivoModal({ ...preventivoModal, voci });
                    }} style={{ ...inputStyle, flex: 3 }} />
                    <input type="number" placeholder="Qtà" value={voce.quantita || ''} onChange={e => {
                      const voci = [...(preventivoModal.voci || [])];
                      voci[idx] = { ...voci[idx], quantita: parseInt(e.target.value) || 1 };
                      setPreventivoModal({ ...preventivoModal, voci });
                    }} style={{ ...inputStyle, flex: 1, textAlign: 'center' }} />
                    <input type="number" step="0.01" placeholder="€" value={voce.prezzo || ''} onChange={e => {
                      const voci = [...(preventivoModal.voci || [])];
                      voci[idx] = { ...voci[idx], prezzo: parseFloat(e.target.value) || 0 };
                      setPreventivoModal({ ...preventivoModal, voci });
                    }} style={{ ...inputStyle, flex: 1 }} />
                    <button type="button" onClick={() => {
                      const voci = (preventivoModal.voci || []).filter((_, i) => i !== idx);
                      setPreventivoModal({ ...preventivoModal, voci });
                    }} style={{ padding: 10, background: 'rgba(239,68,68,0.1)', border: 'none', borderRadius: 8, color: '#ef4444', cursor: 'pointer' }}>🗑️</button>
                  </div>
                ))}
                <button type="button" onClick={() => {
                  const voci = [...(preventivoModal.voci || []), { descrizione: '', quantita: 1, prezzo: 0 }];
                  setPreventivoModal({ ...preventivoModal, voci });
                }} style={{ padding: '10px 16px', background: bgInput, border: '1px dashed ' + bord, borderRadius: 8, color: muted, cursor: 'pointer', width: '100%' }}>+ Aggiungi voce</button>
              </div>
              
              <div style={{ textAlign: 'right', fontSize: 18, fontWeight: 700, color: '#22c55e', marginBottom: 16 }}>
                Totale: €{(preventivoModal.voci || []).reduce((s, v) => s + (v.quantita || 1) * (v.prezzo || 0), 0).toFixed(2)}
              </div>
              
              <label style={labelStyle}>Note</label>
              <textarea value={preventivoModal.note || ''} onChange={e => setPreventivoModal({ ...preventivoModal, note: e.target.value })} style={{ ...inputStyle, minHeight: 80, resize: 'vertical', marginBottom: 20 }} />
              
              <div style={{ display: 'flex', gap: 12 }}>
                <button type="button" onClick={() => { setPreventivoModal(null); setShowClientiDropdown(false); }} style={{ flex: 1, padding: 14, background: bgInput, border: '1px solid ' + bord, borderRadius: 12, cursor: 'pointer', color: muted }}>Annulla</button>
                <button type="submit" style={{ flex: 1, padding: 14, background: 'linear-gradient(135deg, #06b6d4, #0891b2)', border: 'none', borderRadius: 12, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>💾 Salva</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== CASSA TAB ====================
function CassaTab({ data, save, del, dark, bgCard, bgInput, bord, muted }) {
  const [vendita, setVendita] = useState({ cliente: '', voci: [{ descrizione: '', quantita: 1, prezzo: 0 }], metodoPagamento: 'contanti' });
  const [storico, setStorico] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dataFiltro, setDataFiltro] = useState(new Date().toISOString().split('T')[0]);
  const [incassoModal, setIncassoModal] = useState(null); // Modal per scegliere dove incassare
  const [showOrdiniModal, setShowOrdiniModal] = useState(false); // Modal per selezionare ordine da ritirare
  const [showClienteSuggestions, setShowClienteSuggestions] = useState(false); // Suggerimenti cliente
  const [ordiniFilter, setOrdiniFilter] = useState('arrivato'); // Filtro ordini: 'arrivato' | 'in_attesa' | 'tutti'
  
  const vendite = data.vendite || [];
  const entrate = data.entrate || [];
  const ordini = data.ordini || [];
  
  // Ordini pronti per il ritiro (arrivati ma non ancora consegnati)
  const ordiniDaRitirare = ordini.filter(o => o.stato === 'arrivato');
  
  // Conti rapidi (stessi del resoconto)
  const CONTI_RAPIDI = [
    { nome: 'BANCA CARIPARMA PC CARE', icon: '🏦' },
    { nome: 'CASSA NEGOZIO', icon: '💵' },
    { nome: 'POSTEPAY', icon: '💳' },
    { nome: 'PAYPAL', icon: '🅿️' },
    { nome: 'REVOLUT', icon: '💜' },
    { nome: 'BUNQ', icon: '🟢' }
  ];
  
  const METODI_PAGAMENTO = [
    { id: 'contanti', label: 'Contanti', icon: '💵' },
    { id: 'carta', label: 'Carta', icon: '💳' },
    { id: 'bonifico', label: 'Bonifico', icon: '🏦' },
    { id: 'satispay', label: 'Satispay', icon: '📱' }
  ];
  
  const venditeOggi = vendite.filter(v => v.data && v.data.startsWith(dataFiltro));
  const totaleOggi = venditeOggi.reduce((s, v) => s + (v.totale || 0), 0);
  
  const venditeVisibili = (storico ? vendite : venditeOggi)
    .filter(v => !searchTerm || (v.cliente || '').toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => new Date(b.data || 0) - new Date(a.data || 0));
  
  const calcolaTotale = () => {
    return (vendita.voci || []).reduce((s, v) => s + (v.quantita || 1) * (v.prezzo || 0), 0);
  };
  
  // Seleziona ordine da ritirare - prepara i dati per l'incasso
  const selezionaOrdine = (ordine) => {
    const importoDaPagare = (ordine.prezzo || 0) - (ordine.acconto || 0);
    setIncassoModal({
      totale: importoDaPagare,
      cliente: ordine.cliente,
      voci: [{ descrizione: ordine.prodotto, quantita: 1, prezzo: importoDaPagare }],
      metodoPagamento: 'contanti',
      descrizione: `Ordine: ${ordine.prodotto}`,
      note: ordine.note,
      ordineId: ordine.id, // Riferimento all'ordine
      ordineCompleto: ordine // Salviamo tutto l'ordine per aggiornarlo
    });
    setShowOrdiniModal(false);
  };
  
  // Apre il modal per scegliere dove incassare
  const registraVendita = () => {
    const totale = calcolaTotale();
    if (totale <= 0) { alert('Inserisci almeno una voce con prezzo'); return; }
    
    // Prepara i dati della vendita e apre il modal
    const descrizioneVoci = (vendita.voci || []).filter(v => v.descrizione).map(v => v.descrizione).join(', ') || 'Vendita';
    setIncassoModal({
      totale,
      cliente: vendita.cliente || 'Cliente Generico',
      voci: vendita.voci.filter(v => v.descrizione && v.prezzo > 0),
      metodoPagamento: vendita.metodoPagamento,
      descrizione: descrizioneVoci,
      note: vendita.note
    });
  };
  
  // Conferma incasso su un conto specifico
  const confermaIncasso = (contoNome) => {
    if (!incassoModal) return;
    
    // Se è un ordine, aggiorna lo stato a "consegnato"
    let nuoviOrdini = [...ordini];
    if (incassoModal.ordineId) {
      nuoviOrdini = ordini.map(o => 
        o.id === incassoModal.ordineId 
          ? { ...o, stato: 'consegnato', dataConsegna: new Date().toISOString(), metodoPagamento: contoNome }
          : o
      );
      
      // Invia WhatsApp se c'è telefono
      const ordine = incassoModal.ordineCompleto;
      if (ordine && ordine.telefono) {
        const msg = `Gentile ${ordine.cliente}, grazie per aver ritirato il suo ordine "${ordine.prodotto}". A presto! PC Care Napoli`;
        const tel = ordine.telefono.replace(/\D/g, '');
        window.open(`https://wa.me/39${tel}?text=${encodeURIComponent(msg)}`, '_blank');
      }
    }
    
    const nuovaVendita = {
      id: Date.now().toString(),
      data: new Date().toISOString(),
      cliente: incassoModal.cliente,
      voci: incassoModal.voci,
      totale: incassoModal.totale,
      metodoPagamento: incassoModal.metodoPagamento,
      incassatoSu: contoNome,
      note: incassoModal.note
    };
    
    // Trova o crea l'entrata nel conto selezionato
    let nuoveEntrate = [...entrate];
    const contoEsistente = nuoveEntrate.find(e => e.nome === contoNome);
    
    if (contoEsistente) {
      // Aggiungi al conto esistente
      nuoveEntrate = nuoveEntrate.map(e => 
        e.nome === contoNome 
          ? { ...e, importo: (e.importo || 0) + incassoModal.totale }
          : e
      );
    } else {
      // Crea nuovo conto
      const iconaConto = CONTI_RAPIDI.find(c => c.nome === contoNome)?.icon || '💰';
      nuoveEntrate.push({
        id: 'cassa_' + Date.now(),
        nome: contoNome,
        importo: incassoModal.totale,
        icon: iconaConto,
        categoria: (data.categorieEntrate || [])[0]?.id || 'cat_default'
      });
    }
    
    // Registra movimento nello storico
    const descrizioneMovimento = incassoModal.ordineId 
      ? `Ordine consegnato - ${incassoModal.cliente} - ${incassoModal.descrizione}`
      : `Vendita Cassa - ${incassoModal.cliente}`;
    
    const movimento = {
      id: Date.now().toString(),
      data: new Date().toISOString(),
      tipo: 'entrata',
      descrizione: descrizioneMovimento,
      importo: incassoModal.totale,
      metodo: `Incassato su: ${contoNome}`
    };
    
    save({ 
      ...data, 
      vendite: [...vendite, nuovaVendita],
      entrate: nuoveEntrate,
      ordini: nuoviOrdini, // Salva ordini aggiornati (con stato consegnato)
      movimenti: [...(data.movimenti || []), movimento]
    });
    
    // Reset
    setVendita({ cliente: '', voci: [{ descrizione: '', quantita: 1, prezzo: 0 }], metodoPagamento: 'contanti' });
    setIncassoModal(null);
  };
  
  const eliminaVendita = (id) => {
    if (window.confirm('Eliminare questa vendita?')) {
      // FIX: passa da del(), che cancella anche dal database. Prima la vendita
      // tornava al riavvio e l'incasso di giornata risultava gonfiato.
      del('vendite', id);
    }
  };
  
  const stampaRicevuta = (v) => {
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Ricevuta</title>
        <style>
          body { font-family: monospace; padding: 20px; max-width: 300px; margin: 0 auto; }
          .header { text-align: center; border-bottom: 1px dashed #000; padding-bottom: 10px; margin-bottom: 10px; }
          .voce { display: flex; justify-content: space-between; margin: 5px 0; }
          .totale { border-top: 1px dashed #000; margin-top: 10px; padding-top: 10px; font-weight: bold; font-size: 16px; }
          .footer { text-align: center; margin-top: 20px; font-size: 10px; }
          @media print { body { padding: 5px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <strong>PC CARE NAPOLI</strong><br>
          <small>${new Date(v.data).toLocaleString('it-IT')}</small>
        </div>
        ${v.cliente !== 'Cliente Generico' ? `<div>Cliente: ${v.cliente}</div>` : ''}
        <div style="margin: 10px 0;">
          ${(v.voci || []).map(voce => `
            <div class="voce">
              <span>${voce.descrizione} x${voce.quantita || 1}</span>
              <span>€${((voce.quantita || 1) * (voce.prezzo || 0)).toFixed(2)}</span>
            </div>
          `).join('')}
        </div>
        <div class="totale">
          <div class="voce"><span>TOTALE</span><span>€${(v.totale || 0).toFixed(2)}</span></div>
          <div class="voce"><span>Pagamento</span><span>${METODI_PAGAMENTO.find(m => m.id === v.metodoPagamento)?.label || 'Contanti'}</span></div>
        </div>
        <div class="footer">
          Grazie e arrivederci!<br>
          081.191.73.939
        </div>
      </body>
      </html>
    `;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.print();
  };
  
  const inputStyle = { width: '100%', padding: '12px 14px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 14 };
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6, color: muted };
  
  return (
    <div style={{ paddingTop: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>🧾 Cassa</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setStorico(!storico)} style={{ padding: '10px 16px', background: storico ? '#10b981' : bgInput, border: '1px solid ' + (storico ? '#10b981' : bord), borderRadius: 10, color: storico ? '#fff' : (dark ? '#e2e8f0' : '#1e293b'), fontWeight: 600, cursor: 'pointer' }}>
            {storico ? '📋 Oggi' : '📜 Storico'}
          </button>
        </div>
      </div>
      
      {/* Riepilogo giornaliero */}
      <div style={{ background: 'linear-gradient(135deg, #10b981, #059669)', borderRadius: 16, padding: 20, marginBottom: 20, color: '#fff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 14, opacity: 0.9 }}>Incasso {storico ? 'Totale' : 'Oggi'}</div>
            <div style={{ fontSize: 32, fontWeight: 800 }}>€{(storico ? vendite.reduce((s, v) => s + (v.totale || 0), 0) : totaleOggi).toFixed(2)}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 14, opacity: 0.9 }}>{storico ? 'Totale vendite' : 'Vendite oggi'}</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{storico ? vendite.length : venditeOggi.length}</div>
          </div>
        </div>
        {!storico && (
          <input type="date" value={dataFiltro} onChange={e => setDataFiltro(e.target.value)} style={{ marginTop: 12, padding: 8, borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.2)', color: '#fff' }} />
        )}
      </div>
      
      {/* Form nuova vendita */}
      {!storico && (
        <div style={{ background: bgCard, borderRadius: 16, padding: 20, marginBottom: 20, border: '1px solid ' + bord }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>➕ Nuova Vendita</h3>
            <button 
              onClick={() => setShowOrdiniModal(true)}
              style={{ 
                padding: '8px 14px', 
                background: ordiniDaRitirare.length > 0 ? 'linear-gradient(135deg, #8b5cf6, #7c3aed)' : (dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'), 
                border: ordiniDaRitirare.length > 0 ? 'none' : '1px solid ' + bord, 
                borderRadius: 10, 
                color: ordiniDaRitirare.length > 0 ? '#fff' : muted, 
                fontWeight: 600, 
                cursor: 'pointer',
                fontSize: 13,
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              📦 Ordini {ordiniDaRitirare.length > 0 ? `da ritirare (${ordiniDaRitirare.length})` : '(vedi tutti)'}
            </button>
          </div>
          
          {/* Campo cliente con autocomplete */}
          <label style={labelStyle}>Cliente</label>
          <div style={{ position: 'relative', marginBottom: 16 }}>
            <input 
              type="text" 
              value={vendita.cliente} 
              onChange={e => {
                setVendita({ ...vendita, cliente: e.target.value });
                setShowClienteSuggestions(true);
              }}
              onFocus={() => setShowClienteSuggestions(true)}
              placeholder="Cerca o digita nome cliente..." 
              style={inputStyle} 
            />
            {showClienteSuggestions && vendita.cliente && vendita.cliente.length >= 2 && (
              <div style={{ 
                position: 'absolute', 
                top: '100%', 
                left: 0, 
                right: 0, 
                background: dark ? '#1e293b' : '#fff', 
                border: '1px solid ' + bord, 
                borderRadius: 10, 
                marginTop: 4, 
                maxHeight: 200, 
                overflow: 'auto', 
                zIndex: 100,
                boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
              }}>
                {(data.clientiLab || [])
                  .filter(c => c.nome?.toLowerCase().includes(vendita.cliente.toLowerCase()) || c.telefono?.includes(vendita.cliente))
                  .slice(0, 8)
                  .map(cliente => (
                    <div 
                      key={cliente.id}
                      onClick={() => {
                        setVendita({ ...vendita, cliente: cliente.nome });
                        setShowClienteSuggestions(false);
                      }}
                      style={{ 
                        padding: '10px 14px', 
                        cursor: 'pointer', 
                        borderBottom: '1px solid ' + bord,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{cliente.nome}</div>
                        {cliente.telefono && <div style={{ fontSize: 12, color: muted }}>{cliente.telefono}</div>}
                      </div>
                      <span style={{ fontSize: 12, color: muted }}>👤</span>
                    </div>
                  ))}
                {(data.clientiLab || []).filter(c => c.nome?.toLowerCase().includes(vendita.cliente.toLowerCase()) || c.telefono?.includes(vendita.cliente)).length === 0 && (
                  <div style={{ padding: '12px 14px', color: muted, fontSize: 13, textAlign: 'center' }}>
                    Nessun cliente trovato - verrà creato come nuovo
                  </div>
                )}
              </div>
            )}
          </div>
          
          <label style={labelStyle}>Articoli</label>
          <div style={{ marginBottom: 16 }}>
            {(vendita.voci || []).map((voce, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <input type="text" placeholder="Descrizione" value={voce.descrizione} onChange={e => {
                  const voci = [...(vendita.voci || [])];
                  voci[idx] = { ...voci[idx], descrizione: e.target.value };
                  setVendita({ ...vendita, voci });
                }} style={{ ...inputStyle, flex: 3 }} />
                <input type="number" placeholder="Qtà" value={voce.quantita || ''} onChange={e => {
                  const voci = [...(vendita.voci || [])];
                  voci[idx] = { ...voci[idx], quantita: parseInt(e.target.value) || 1 };
                  setVendita({ ...vendita, voci });
                }} style={{ ...inputStyle, flex: 1, textAlign: 'center' }} />
                <input type="number" step="0.01" placeholder="€" value={voce.prezzo || ''} onChange={e => {
                  const voci = [...(vendita.voci || [])];
                  voci[idx] = { ...voci[idx], prezzo: parseFloat(e.target.value) || 0 };
                  setVendita({ ...vendita, voci });
                }} style={{ ...inputStyle, flex: 1 }} />
                {vendita.voci.length > 1 && (
                  <button type="button" onClick={() => {
                    const voci = (vendita.voci || []).filter((_, i) => i !== idx);
                    setVendita({ ...vendita, voci });
                  }} style={{ padding: 10, background: 'rgba(239,68,68,0.1)', border: 'none', borderRadius: 8, color: '#ef4444', cursor: 'pointer' }}>🗑️</button>
                )}
              </div>
            ))}
            <button type="button" onClick={() => {
              const voci = [...(vendita.voci || []), { descrizione: '', quantita: 1, prezzo: 0 }];
              setVendita({ ...vendita, voci });
            }} style={{ padding: '10px 16px', background: bgInput, border: '1px dashed ' + bord, borderRadius: 8, color: muted, cursor: 'pointer', width: '100%' }}>+ Aggiungi articolo</button>
          </div>
          
          <label style={labelStyle}>Metodo di Pagamento</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {METODI_PAGAMENTO.map(m => (
              <button key={m.id} type="button" onClick={() => setVendita({ ...vendita, metodoPagamento: m.id })} style={{
                padding: '10px 16px',
                background: vendita.metodoPagamento === m.id ? '#10b981' : bgInput,
                border: '1px solid ' + (vendita.metodoPagamento === m.id ? '#10b981' : bord),
                borderRadius: 10,
                color: vendita.metodoPagamento === m.id ? '#fff' : (dark ? '#e2e8f0' : '#1e293b'),
                cursor: 'pointer',
                fontWeight: 600
              }}>
                {m.icon} {m.label}
              </button>
            ))}
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, gap: 12, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#22c55e' }}>Totale: €{calcolaTotale().toFixed(2)}</div>
            <button onClick={registraVendita} style={{ padding: '14px 28px', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none', borderRadius: 12, color: '#fff', fontWeight: 700, fontSize: 16, cursor: 'pointer' }}>
              💰 Registra Vendita
            </button>
          </div>
        </div>
      )}
      
      {/* Storico vendite */}
      {storico && (
        <input type="text" placeholder="🔍 Cerca cliente..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ ...inputStyle, marginBottom: 16 }} />
      )}
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {venditeVisibili.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: muted }}>Nessuna vendita {storico ? 'trovata' : 'oggi'}</div>
        ) : venditeVisibili.map(v => (
          <div key={v.id} style={{ background: bgCard, borderRadius: 12, padding: 16, border: '1px solid ' + bord }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 11, color: muted, marginBottom: 4 }}>{new Date(v.data).toLocaleString('it-IT')}</div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{v.cliente}</div>
                <div style={{ fontSize: 13, color: muted }}>
                  {(v.voci || []).map(voce => `${voce.descrizione} x${voce.quantita || 1}`).join(', ')}
                </div>
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  {METODI_PAGAMENTO.find(m => m.id === v.metodoPagamento)?.icon} {METODI_PAGAMENTO.find(m => m.id === v.metodoPagamento)?.label}
                  {v.incassatoSu && <span style={{ color: '#22c55e', marginLeft: 8 }}>→ {v.incassatoSu}</span>}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#22c55e', marginBottom: 8 }}>€{(v.totale || 0).toFixed(2)}</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => stampaRicevuta(v)} style={{ padding: '6px 10px', background: bgInput, border: '1px solid ' + bord, borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>🖨️</button>
                  <button onClick={() => eliminaVendita(v.id)} style={{ padding: '6px 10px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#ef4444', fontSize: 12, cursor: 'pointer' }}>🗑️</button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Modal Incasso - Scegli dove incassare */}
      {incassoModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setIncassoModal(null)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 400, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid ' + bord, background: 'linear-gradient(135deg, rgba(16,185,129,0.2), transparent)' }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>💰 Incassa Vendita</h3>
              <p style={{ margin: '4px 0 0', color: muted, fontSize: 13 }}>{incassoModal.cliente} - {incassoModal.descrizione}</p>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#22c55e', marginTop: 8 }}>
                €{incassoModal.totale.toFixed(2)}
              </div>
            </div>
            
            <div style={{ padding: 20 }}>
              <p style={{ fontSize: 13, color: muted, marginBottom: 16 }}>Seleziona il conto su cui incassare:</p>
              
              {/* Conti rapidi */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                {CONTI_RAPIDI.map(conto => (
                  <button
                    key={conto.nome}
                    onClick={() => {
                      if (!window.confirm(`Confermi incasso di €${incassoModal.totale.toFixed(2)} su ${conto.nome}?`)) return;
                      confermaIncasso(conto.nome);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '14px 16px',
                      background: dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                      border: '1px solid ' + bord,
                      borderRadius: 12,
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.15s'
                    }}
                  >
                    <span style={{ fontSize: 24 }}>{conto.icon}</span>
                    <span style={{ flex: 1, fontWeight: 600, color: dark ? '#e2e8f0' : '#1e293b' }}>{conto.nome}</span>
                    <span style={{ color: '#22c55e', fontWeight: 600 }}>+€{incassoModal.totale.toFixed(2)}</span>
                  </button>
                ))}
              </div>
              
              {/* Altri conti dalle entrate */}
              {entrate.filter(e => !CONTI_RAPIDI.some(c => c.nome === e.nome)).length > 0 && (
                <>
                  <p style={{ fontSize: 12, color: muted, marginBottom: 8 }}>Altri conti:</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 200, overflow: 'auto' }}>
                    {entrate
                      .filter(e => !CONTI_RAPIDI.some(c => c.nome === e.nome))
                      .map(entrata => (
                        <button
                          key={entrata.id}
                          onClick={() => {
                            if (!window.confirm(`Confermi incasso di €${incassoModal.totale.toFixed(2)} su ${entrata.nome}?`)) return;
                            confermaIncasso(entrata.nome);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            padding: '12px 14px',
                            background: dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.01)',
                            border: '1px solid ' + bord,
                            borderRadius: 10,
                            cursor: 'pointer',
                            textAlign: 'left'
                          }}
                        >
                          <span style={{ fontSize: 18 }}>{entrata.icon || '💰'}</span>
                          <span style={{ flex: 1, fontSize: 14, color: dark ? '#e2e8f0' : '#1e293b' }}>{entrata.nome}</span>
                          <span style={{ color: '#22c55e', fontSize: 13 }}>+€{incassoModal.totale.toFixed(2)}</span>
                        </button>
                      ))}
                  </div>
                </>
              )}
              
              {/* Pulsante Da Incassare */}
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid ' + bord }}>
                <button
                  onClick={() => {
                    if (!window.confirm(`Confermi spostamento di €${incassoModal.totale.toFixed(2)} in "Da Incassare"?`)) return;
                    
                    // Sposta in "Da Incassare" senza incassare subito
                    let nuoveEntrate = [...entrate];
                    let nuoveCategorie = [...(data.categorieEntrate || [])];
                    
                    let catDaIncassare = nuoveCategorie.find(c => c.nome && c.nome.toUpperCase() === 'DA INCASSARE');
                    if (!catDaIncassare) {
                      catDaIncassare = { id: 'cat_da_incassare', nome: 'DA INCASSARE', icon: '⏳' };
                      nuoveCategorie.push(catDaIncassare);
                    }
                    
                    nuoveEntrate.push({
                      id: 'da_incassare_cassa_' + Date.now(),
                      nome: incassoModal.cliente,
                      importo: incassoModal.totale,
                      icon: '🧾',
                      categoria: catDaIncassare.id,
                      note: `${incassoModal.descrizione}\n[${new Date().toLocaleDateString('it-IT')}]`
                    });
                    
                    const nuovaVendita = {
                      id: Date.now().toString(),
                      data: new Date().toISOString(),
                      cliente: incassoModal.cliente,
                      voci: incassoModal.voci,
                      totale: incassoModal.totale,
                      metodoPagamento: 'da_incassare',
                      note: incassoModal.note
                    };
                    
                    save({ 
                      ...data, 
                      vendite: [...vendite, nuovaVendita],
                      entrate: nuoveEntrate,
                      categorieEntrate: nuoveCategorie
                    });
                    
                    setVendita({ cliente: '', voci: [{ descrizione: '', quantita: 1, prezzo: 0 }], metodoPagamento: 'contanti' });
                    setIncassoModal(null);
                  }}
                  style={{ 
                    width: '100%',
                    padding: '14px 20px', 
                    background: 'rgba(245,158,11,0.15)', 
                    border: '2px solid #f59e0b', 
                    borderRadius: 12, 
                    color: '#f59e0b', 
                    fontWeight: 700, 
                    fontSize: 14, 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8
                  }}
                >
                  <span>⏳</span> Da Incassare (dopo)
                </button>
              </div>
            </div>
            
            <div style={{ padding: '16px 20px', borderTop: '1px solid ' + bord }}>
              <button onClick={() => setIncassoModal(null)} style={{ width: '100%', padding: 12, background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 10, color: muted, cursor: 'pointer' }}>Annulla</button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal Ordini da Ritirare */}
      {showOrdiniModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setShowOrdiniModal(false)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 550, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid ' + bord, background: 'linear-gradient(135deg, rgba(139,92,246,0.2), transparent)' }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>📦 Gestione Ordini</h3>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: muted }}>
                {ordiniDaRitirare.length} da ritirare • {ordini.filter(o => o.stato === 'in_attesa').length} in attesa • {ordini.filter(o => o.stato === 'consegnato').length} consegnati
              </p>
            </div>
            
            {/* Tab filtri */}
            <div style={{ display: 'flex', borderBottom: '1px solid ' + bord, padding: '0 16px' }}>
              {[
                { id: 'arrivato', label: '📥 Da ritirare', count: ordiniDaRitirare.length },
                { id: 'in_attesa', label: '⏳ In attesa', count: ordini.filter(o => o.stato === 'in_attesa').length },
                { id: 'tutti', label: '📋 Tutti', count: ordini.length }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setOrdiniFilter(tab.id)}
                  style={{
                    padding: '12px 16px',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: ordiniFilter === tab.id ? '2px solid #8b5cf6' : '2px solid transparent',
                    color: ordiniFilter === tab.id ? '#8b5cf6' : muted,
                    fontWeight: ordiniFilter === tab.id ? 700 : 500,
                    cursor: 'pointer',
                    fontSize: 13
                  }}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </div>
            
            <div style={{ padding: 16, flex: 1, overflow: 'auto' }}>
              {(() => {
                const ordiniFiltrati = ordiniFilter === 'tutti' 
                  ? ordini.filter(o => o.stato !== 'consegnato')
                  : ordini.filter(o => o.stato === ordiniFilter);
                
                if (ordiniFiltrati.length === 0) {
                  return <p style={{ textAlign: 'center', color: muted, padding: 20 }}>Nessun ordine in questa categoria</p>;
                }
                
                return ordiniFiltrati.map(ordine => {
                  const importoDaPagare = (ordine.prezzo || 0) - (ordine.acconto || 0);
                  const isArrivato = ordine.stato === 'arrivato';
                  return (
                    <div 
                      key={ordine.id}
                      onClick={() => isArrivato && selezionaOrdine(ordine)}
                      style={{ 
                        padding: 16, 
                        background: bgInput, 
                        borderRadius: 12, 
                        marginBottom: 12, 
                        cursor: isArrivato ? 'pointer' : 'default',
                        border: isArrivato ? '2px solid #8b5cf6' : '1px solid ' + bord,
                        opacity: isArrivato ? 1 : 0.8
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <span style={{ 
                              padding: '2px 8px', 
                              borderRadius: 6, 
                              fontSize: 10, 
                              fontWeight: 700,
                              background: ordine.stato === 'arrivato' ? '#22c55e' : ordine.stato === 'in_attesa' ? '#f59e0b' : '#64748b',
                              color: '#fff'
                            }}>
                              {ordine.stato === 'arrivato' ? '✓ ARRIVATO' : ordine.stato === 'in_attesa' ? '⏳ IN ATTESA' : ordine.stato?.toUpperCase()}
                            </span>
                          </div>
                          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{ordine.cliente}</div>
                          <div style={{ fontSize: 14, color: '#8b5cf6', marginBottom: 4 }}>{ordine.prodotto}</div>
                          {ordine.telefono && <div style={{ fontSize: 12, color: muted }}>📱 {ordine.telefono}</div>}
                          {ordine.acconto > 0 && (
                            <div style={{ fontSize: 12, color: '#22c55e', marginTop: 4 }}>
                              ✓ Acconto versato: €{ordine.acconto.toFixed(2)}
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 11, color: muted }}>{isArrivato ? 'Da pagare' : 'Totale'}</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: isArrivato ? '#22c55e' : (dark ? '#e2e8f0' : '#1e293b') }}>
                            €{isArrivato ? importoDaPagare.toFixed(2) : (ordine.prezzo || 0).toFixed(2)}
                          </div>
                          {isArrivato && (
                            <div style={{ fontSize: 11, color: '#8b5cf6', marginTop: 6, fontWeight: 600 }}>
                              👆 Clicca per incassare
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
            
            <div style={{ padding: 16, borderTop: '1px solid ' + bord }}>
              <button onClick={() => setShowOrdiniModal(false)} style={{ width: '100%', padding: 12, background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 10, color: muted, cursor: 'pointer', fontWeight: 600 }}>Chiudi</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ConfigTab({ data, save, delCat, moveCat, setCatModal, exportExcel, downloadBackup, restoreBackup, theme, setTheme, dark, bgCard, bgInput, bord, muted, loadData, text, currentUser, getUsers }) {
  
  // Stati per gestione utenti
  const [showUserModal, setShowUserModal] = useState(null); // null | 'new' | 'edit' | 'password'
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ username: '', password: '', confirmPassword: '', isAdmin: false, tabs: [] });
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  
  const TAB_INFO = {
    home: { icon: '🏠', label: 'Home' },
    resoconto: { icon: '📋', label: 'Resoconto' },
    laboratorio: { icon: '🔧', label: 'Laboratorio' },
    clienti: { icon: '👥', label: 'Clienti' },
    ordini: { icon: '📦', label: 'Ordini' },
    preventivi: { icon: '📝', label: 'Preventivi' },
    cassa: { icon: '🧾', label: 'Cassa' },
    weekend: { icon: '🗓️', label: 'Weekend' },
    abbonamenti: { icon: '📅', label: 'Abbonamenti' },
    contratti: { icon: '📑', label: 'Contratti' },
    ricorrenze: { icon: '🔄', label: 'Ricorrenze' },
    storico: { icon: '📈', label: 'Storico' },
    promemoria: { icon: '🔔', label: 'Promemoria' },
    fileUtili: { icon: '📁', label: 'File Utili' },
    config: { icon: '⚙️', label: 'Impostazioni' }
  };
  
  const ALL_TABS = ['home', 'resoconto', 'laboratorio', 'clienti', 'ordini', 'magazzino', 'preventivi', 'cassa', 'weekend', 'abbonamenti', 'contratti', 'ricorrenze', 'storico', 'promemoria', 'fileUtili', 'config'];

  const DEFAULT_TABS = ['home', 'resoconto', 'laboratorio', 'clienti', 'ordini', 'magazzino', 'preventivi', 'cassa', 'weekend', 'abbonamenti', 'contratti', 'ricorrenze', 'storico', 'promemoria', 'fileUtili', 'config'];
  
  // Ottieni utenti
  const users = data?.utenti || getUsers();
  const currentUserData = users.find(u => u.username === currentUser);
  const isAdmin = currentUserData?.isAdmin || currentUser === 'Oreste';
  
  // Aggiungi tab mancanti al tabOrder salvato
  let tabOrder = data.tabOrder || DEFAULT_TABS;
  DEFAULT_TABS.forEach(t => {
    if (!tabOrder.includes(t)) {
      if (t === 'home') {
        tabOrder = [t, ...tabOrder];
      } else if (t === 'config') {
        tabOrder = [...tabOrder, t];
      } else {
        // Inserisci prima di config
        const configIdx = tabOrder.indexOf('config');
        if (configIdx >= 0) {
          tabOrder = [...tabOrder.slice(0, configIdx), t, ...tabOrder.slice(configIdx)];
        } else {
          tabOrder = [...tabOrder, t];
        }
      }
    }
  });

  const moveTab = (tabId, dir) => {
    const arr = [...tabOrder];
    const idx = arr.indexOf(tabId);
    if (dir === 'up' && idx > 0) {
      [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
    } else if (dir === 'down' && idx < arr.length - 1) {
      [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
    }
    save({ ...data, tabOrder: arr });
  };
  
  // Funzioni gestione utenti
  const saveUsers = (newUsers) => {
    localStorage.setItem('resoconto-users', JSON.stringify(newUsers));
    save({ ...data, utenti: newUsers });
  };
  
  const openNewUser = () => {
    setUserForm({ username: '', password: '', confirmPassword: '', isAdmin: false, tabs: [...ALL_TABS] });
    setShowUserModal('new');
  };
  
  const openEditUser = (user) => {
    setEditingUser(user);
    setUserForm({ 
      username: user.username, 
      password: '', 
      confirmPassword: '', 
      isAdmin: user.isAdmin || false, 
      tabs: user.tabs?.length > 0 ? user.tabs : [...ALL_TABS] 
    });
    setShowUserModal('edit');
  };
  
  const openChangePassword = () => {
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setShowUserModal('password');
  };
  
  const handleSaveUser = () => {
    if (!userForm.username.trim()) {
      alert('Inserisci un username');
      return;
    }
    
    if (showUserModal === 'new') {
      if (!userForm.password) {
        alert('Inserisci una password');
        return;
      }
      if (userForm.password !== userForm.confirmPassword) {
        alert('Le password non coincidono');
        return;
      }
      if (users.find(u => u.username.toLowerCase() === userForm.username.toLowerCase())) {
        alert('Username già esistente');
        return;
      }
      
      const newUser = {
        username: userForm.username.trim(),
        password: userForm.password,
        isAdmin: userForm.isAdmin,
        tabs: userForm.tabs
      };
      saveUsers([...users, newUser]);
      alert(`Utente "${newUser.username}" creato con successo!`);
    } else if (showUserModal === 'edit') {
      const updatedUsers = users.map(u => {
        if (u.username === editingUser.username) {
          return {
            ...u,
            username: userForm.username.trim(),
            password: userForm.password || u.password,
            isAdmin: userForm.isAdmin,
            tabs: userForm.tabs
          };
        }
        return u;
      });
      saveUsers(updatedUsers);
      alert('Utente aggiornato!');
    }
    
    setShowUserModal(null);
    setEditingUser(null);
  };
  
  const handleChangePassword = () => {
    const currentUserObj = users.find(u => u.username === currentUser);
    
    if (currentUserObj.password !== passwordForm.currentPassword) {
      alert('Password attuale non corretta');
      return;
    }
    if (!passwordForm.newPassword) {
      alert('Inserisci la nuova password');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('Le nuove password non coincidono');
      return;
    }
    
    const updatedUsers = users.map(u => 
      u.username === currentUser ? { ...u, password: passwordForm.newPassword } : u
    );
    saveUsers(updatedUsers);
    alert('Password cambiata con successo!');
    setShowUserModal(null);
  };
  
  const handleDeleteUser = (username) => {
    if (username === 'Oreste') {
      alert('Non puoi eliminare l\'utente admin principale');
      return;
    }
    if (!window.confirm(`Eliminare l'utente "${username}"?`)) return;
    
    const updatedUsers = users.filter(u => u.username !== username);
    saveUsers(updatedUsers);
  };
  
  const toggleUserTab = (tabId) => {
    const newTabs = userForm.tabs.includes(tabId)
      ? userForm.tabs.filter(t => t !== tabId)
      : [...userForm.tabs, tabId];
    setUserForm({ ...userForm, tabs: newTabs });
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24, textAlign: 'center' }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>⚙️ Impostazioni</h1>
        <p style={{ margin: '8px 0 0', fontSize: 14, color: muted }}>Gestisci le configurazioni dell'applicazione</p>
      </div>

      {/* SEZIONE 1: TEMA E BACKUP */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 12, fontWeight: 700, color: muted, textTransform: 'uppercase', marginBottom: 12, paddingLeft: 4 }}>🎨 Aspetto e Backup</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          
          {/* Tema */}
          <div style={{ background: bgCard, borderRadius: 16, border: '1px solid ' + bord, padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Tema Applicazione</div>
                <div style={{ fontSize: 12, color: muted }}>Attualmente: {theme === 'dark' ? 'Scuro' : 'Chiaro'}</div>
              </div>
              <button 
                onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')} 
                style={{ 
                  width: 56, height: 56, borderRadius: 14, 
                  background: theme === 'dark' ? 'linear-gradient(135deg, #1e293b, #334155)' : 'linear-gradient(135deg, #fef3c7, #fde68a)',
                  border: `2px solid ${theme === 'dark' ? '#475569' : '#fbbf24'}`,
                  fontSize: 28, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
              >{theme === 'dark' ? '🌙' : '☀️'}</button>
            </div>
          </div>

          {/* Backup Locale */}
          <div style={{ background: bgCard, borderRadius: 16, border: '1px solid ' + bord, padding: 20 }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>💾 Backup Locale</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={downloadBackup} style={{ flex: 1, padding: '12px 16px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>⬇️ Scarica</button>
              <label style={{ flex: 1, padding: '12px 16px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 500, textAlign: 'center' }}>⬆️ Ripristina<input type="file" accept=".json" onChange={restoreBackup} style={{ display: 'none' }} /></label>
            </div>
          </div>

          {/* Export */}
          <div style={{ background: bgCard, borderRadius: 16, border: '1px solid ' + bord, padding: 20 }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>📊 Esportazione</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => exportExcel(data)} style={{ flex: 1, padding: '12px 16px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 500, color: '#22c55e' }}>📄 Excel</button>
              <button onClick={() => { if (window.confirm('Caricare i dati dal backup locale?')) loadData(true); }} style={{ flex: 1, padding: '12px 16px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 500, color: '#f59e0b' }}>🔄 Locale</button>
            </div>
          </div>
        </div>
      </div>

      {/* SEZIONE 2: CATEGORIE */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 12, fontWeight: 700, color: muted, textTransform: 'uppercase', marginBottom: 12, paddingLeft: 4 }}>📂 Categorie</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
          {/* Categorie Entrate */}
          <div style={{ background: bgCard, borderRadius: 16, border: '1px solid ' + bord, overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid ' + bord, background: 'rgba(34,197,94,0.05)' }}><h4 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#22c55e' }}>↓ Entrate</h4></div>
            <div style={{ padding: 12, maxHeight: 280, overflow: 'auto' }}>
              {(data.categorieEntrate || []).map((cat, i) => (
                <div key={cat.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, background: bgInput, marginBottom: 6 }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <button onClick={() => moveCat('entrate', cat.id, 'up')} style={{ background: 'transparent', border: 'none', color: muted, fontSize: 9, cursor: 'pointer', opacity: i === 0 ? 0.3 : 1 }}>▲</button>
                    <button onClick={() => moveCat('entrate', cat.id, 'down')} style={{ background: 'transparent', border: 'none', color: muted, fontSize: 9, cursor: 'pointer', opacity: i === (data.categorieEntrate || []).length - 1 ? 0.3 : 1 }}>▼</button>
                  </div>
                  <div style={{ width: 28, height: 28, borderRadius: 6, background: cat.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>{cat.icon}</div>
                  <div style={{ flex: 1 }}><div style={{ fontSize: 12, fontWeight: 600 }}>{cat.nome}</div><div style={{ fontSize: 10, color: muted }}>{data.entrate.filter(e => e.categoria === cat.id).length} voci</div></div>
                  <button onClick={() => setCatModal({ type: 'entrate', category: cat, isNew: false })} style={{ background: 'transparent', border: 'none', fontSize: 11, cursor: 'pointer', opacity: 0.5 }}>✏️</button>
                  <button onClick={() => delCat('entrate', cat.id)} style={{ background: 'transparent', border: 'none', fontSize: 13, color: '#ef4444', cursor: 'pointer', opacity: 0.5 }}>×</button>
                </div>
              ))}
              <button onClick={() => setCatModal({ type: 'entrate', category: { nome: '', icon: '📥', color: '#22c55e' }, isNew: true })} style={{ width: '100%', padding: 8, background: 'transparent', border: `1px dashed ${bord}`, borderRadius: 6, color: muted, fontSize: 11, cursor: 'pointer' }}>+ Nuova</button>
            </div>
          </div>
          {/* Categorie Uscite */}
          <div style={{ background: bgCard, borderRadius: 16, border: '1px solid ' + bord, overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid ' + bord, background: 'rgba(239,68,68,0.05)' }}><h4 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#ef4444' }}>↑ Uscite</h4></div>
            <div style={{ padding: 12, maxHeight: 280, overflow: 'auto' }}>
              {(data.categorieUscite || []).map((cat, i) => (
                <div key={cat.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, background: bgInput, marginBottom: 6 }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <button onClick={() => moveCat('uscite', cat.id, 'up')} style={{ background: 'transparent', border: 'none', color: muted, fontSize: 9, cursor: 'pointer', opacity: i === 0 ? 0.3 : 1 }}>▲</button>
                    <button onClick={() => moveCat('uscite', cat.id, 'down')} style={{ background: 'transparent', border: 'none', color: muted, fontSize: 9, cursor: 'pointer', opacity: i === (data.categorieUscite || []).length - 1 ? 0.3 : 1 }}>▼</button>
                  </div>
                  <div style={{ width: 28, height: 28, borderRadius: 6, background: cat.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>{cat.icon}</div>
                  <div style={{ flex: 1 }}><div style={{ fontSize: 12, fontWeight: 600 }}>{cat.nome}</div><div style={{ fontSize: 10, color: muted }}>{data.uscite.filter(e => e.categoria === cat.id).length} voci</div></div>
                  <button onClick={() => setCatModal({ type: 'uscite', category: cat, isNew: false })} style={{ background: 'transparent', border: 'none', fontSize: 11, cursor: 'pointer', opacity: 0.5 }}>✏️</button>
                  <button onClick={() => delCat('uscite', cat.id)} style={{ background: 'transparent', border: 'none', fontSize: 13, color: '#ef4444', cursor: 'pointer', opacity: 0.5 }}>×</button>
                </div>
              ))}
              <button onClick={() => setCatModal({ type: 'uscite', category: { nome: '', icon: '📤', color: '#ef4444' }, isNew: true })} style={{ width: '100%', padding: 8, background: 'transparent', border: `1px dashed ${bord}`, borderRadius: 6, color: muted, fontSize: 11, cursor: 'pointer' }}>+ Nuova</button>
            </div>
          </div>
        </div>
      </div>

      {/* SEZIONE 4: NAVIGAZIONE */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 12, fontWeight: 700, color: muted, textTransform: 'uppercase', marginBottom: 12, paddingLeft: 4 }}>📑 Ordine Tab</h3>
        <div style={{ background: bgCard, borderRadius: 16, border: '1px solid ' + bord, padding: 16 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {tabOrder.map((tabId, i) => {
              const t = TAB_INFO[tabId]; if (!t) return null;
              return (
                <div key={tabId} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 8, background: bgInput, border: '1px solid ' + bord }}>
                  <span style={{ fontSize: 10, color: muted, fontWeight: 600 }}>#{i + 1}</span>
                  <span style={{ fontSize: 14 }}>{t.icon}</span>
                  <span style={{ fontSize: 12, fontWeight: 500 }}>{t.label}</span>
                  <div style={{ display: 'flex', gap: 2, marginLeft: 4 }}>
                    <button onClick={() => moveTab(tabId, 'up')} disabled={i === 0} style={{ background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 3, color: muted, fontSize: 9, padding: '2px 5px', cursor: 'pointer', opacity: i === 0 ? 0.3 : 1 }}>◀</button>
                    <button onClick={() => moveTab(tabId, 'down')} disabled={i === tabOrder.length - 1} style={{ background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 3, color: muted, fontSize: 9, padding: '2px 5px', cursor: 'pointer', opacity: i === tabOrder.length - 1 ? 0.3 : 1 }}>▶</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* SEZIONE 5: NOTIFICHE */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 12, fontWeight: 700, color: muted, textTransform: 'uppercase', marginBottom: 12, paddingLeft: 4 }}>🔔 Notifiche</h3>
        <div style={{ background: bgCard, borderRadius: 16, border: '1px solid ' + bord, padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div><div style={{ fontSize: 14, fontWeight: 500 }}>Alert Abbonamenti in Scadenza</div><div style={{ fontSize: 11, color: muted }}>Mostra avviso all'apertura</div></div>
            <button onClick={() => save({ ...data, alertAbbDisattivato: !data.alertAbbDisattivato })} style={{ width: 50, height: 28, borderRadius: 14, border: 'none', background: data.alertAbbDisattivato ? (dark ? 'rgba(255,255,255,0.1)' : '#e2e8f0') : '#22c55e', cursor: 'pointer', position: 'relative' }}>
              <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: data.alertAbbDisattivato ? 3 : 25, transition: 'left 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
            </button>
          </div>
          {localStorage.getItem('alertAbbPosticipato') && (
            <div style={{ marginTop: 12, padding: 10, background: 'rgba(245,158,11,0.1)', borderRadius: 8, fontSize: 11, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#f59e0b' }}>⏰ Alert posticipato fino al {new Date(localStorage.getItem('alertAbbPosticipato')).toLocaleDateString('it-IT')}</span>
              <button onClick={() => { localStorage.removeItem('alertAbbPosticipato'); window.location.reload(); }} style={{ padding: '4px 8px', background: 'transparent', border: '1px solid #f59e0b', borderRadius: 6, color: '#f59e0b', fontSize: 10, cursor: 'pointer' }}>Annulla</button>
            </div>
          )}
        </div>
      </div>

      {/* SEZIONE 6: MESSAGGI WHATSAPP */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 12, fontWeight: 700, color: muted, textTransform: 'uppercase', marginBottom: 12, paddingLeft: 4 }}>💬 Messaggi WhatsApp</h3>
        <div style={{ background: bgCard, borderRadius: 16, border: '1px solid ' + bord, padding: 20 }}>
          <p style={{ margin: '0 0 16px', fontSize: 11, color: muted }}>Variabili: {'{CLIENTE}'} {'{RIFERIMENTO}'} {'{GIORNO}'} {'{COSTO}'} {'{FREQUENZA}'} {'{MESI_SCADENZA}'}</p>
          <div style={{ display: 'grid', gap: 12 }}>
            <div><label style={{ fontSize: 12, fontWeight: 600, color: '#eab308', marginBottom: 6, display: 'block' }}>🟡 Primo Avviso</label><textarea value={data.messaggiWA?.primo || `Gentile {CLIENTE}, le ricordiamo che il suo abbonamento "{RIFERIMENTO}" scade il giorno {GIORNO}. Importo: €{COSTO}. PC Care`} onChange={e => save({ ...data, messaggiWA: { ...data.messaggiWA, primo: e.target.value } })} style={{ width: '100%', height: 60, padding: 10, background: bgInput, border: '1px solid ' + bord, borderRadius: 8, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 12, resize: 'vertical', boxSizing: 'border-box' }} /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600, color: '#f97316', marginBottom: 6, display: 'block' }}>🟠 Secondo Avviso</label><textarea value={data.messaggiWA?.secondo || `Gentile {CLIENTE}, SECONDO AVVISO: abbonamento "{RIFERIMENTO}" in scadenza il {GIORNO}. Importo €{COSTO}. PC Care`} onChange={e => save({ ...data, messaggiWA: { ...data.messaggiWA, secondo: e.target.value } })} style={{ width: '100%', height: 60, padding: 10, background: bgInput, border: '1px solid ' + bord, borderRadius: 8, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 12, resize: 'vertical', boxSizing: 'border-box' }} /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600, color: '#22c55e', marginBottom: 6, display: 'block' }}>🟢 Conferma Pagamento</label><textarea value={data.messaggiWA?.conferma || `Gentile {CLIENTE}, confermiamo il pagamento per "{RIFERIMENTO}". Grazie! PC Care`} onChange={e => save({ ...data, messaggiWA: { ...data.messaggiWA, conferma: e.target.value } })} style={{ width: '100%', height: 60, padding: 10, background: bgInput, border: '1px solid ' + bord, borderRadius: 8, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 12, resize: 'vertical', boxSizing: 'border-box' }} /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600, color: '#3b82f6', marginBottom: 6, display: 'block' }}>🔵 Invio Dati Pagamento</label><textarea value={data.messaggiWA?.datiPagamento || `Gentile {CLIENTE}, di seguito i dati per il pagamento dell'abbonamento "{RIFERIMENTO}" (€{COSTO}):\n\n💳 IBAN: IT00X0000000000000000000000\n🏦 Intestato a: PC Care Napoli\n📝 Causale: {RIFERIMENTO} - {CLIENTE}\n\nCordiali saluti, PC Care`} onChange={e => save({ ...data, messaggiWA: { ...data.messaggiWA, datiPagamento: e.target.value } })} style={{ width: '100%', height: 100, padding: 10, background: bgInput, border: '1px solid ' + bord, borderRadius: 8, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 12, resize: 'vertical', boxSizing: 'border-box' }} /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600, color: '#8b5cf6', marginBottom: 6, display: 'block' }}>🟣 Info Abbonamento</label><textarea value={data.messaggiWA?.infoAbbonamento || `*INFO ABBONAMENTO*\n\nCliente: *{CLIENTE}*\nServizio: {RIFERIMENTO}\nTipo: {FREQUENZA}\nScadenza: ogni {GIORNO} di {MESI_SCADENZA}\nCosto: €{COSTO}\n\nPC Care Napoli - 081.191.73.939`} onChange={e => save({ ...data, messaggiWA: { ...data.messaggiWA, infoAbbonamento: e.target.value } })} style={{ width: '100%', height: 100, padding: 10, background: bgInput, border: '1px solid ' + bord, borderRadius: 8, color: dark ? '#e2e8f0' : '#1e293b', fontSize: 12, resize: 'vertical', boxSizing: 'border-box' }} /></div>
          </div>
        </div>
      </div>

      {/* SEZIONE 7: GESTIONE UTENTI */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 12, fontWeight: 700, color: muted, textTransform: 'uppercase', marginBottom: 12, paddingLeft: 4 }}>👥 Gestione Utenti</h3>
        <div style={{ background: bgCard, borderRadius: 16, border: '1px solid ' + bord, overflow: 'hidden' }}>
          
          {/* Cambio password - sempre visibile */}
          <div style={{ padding: 20, borderBottom: '1px solid ' + bord }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>🔐 La tua Password</div>
                <div style={{ fontSize: 12, color: muted }}>Connesso come: <strong>{currentUser}</strong></div>
              </div>
              <button 
                onClick={openChangePassword}
                style={{ padding: '10px 20px', background: 'linear-gradient(135deg,#3b82f6,#2563eb)', border: 'none', borderRadius: 10, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                ✏️ Cambia Password
              </button>
            </div>
          </div>
          
          {/* Gestione utenti - solo admin */}
          {isAdmin && (
            <div style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>👥 Utenti Registrati</div>
                  <div style={{ fontSize: 12, color: muted }}>{users.length} utenti nel sistema</div>
                </div>
                <button 
                  onClick={openNewUser}
                  style={{ padding: '10px 20px', background: 'linear-gradient(135deg,#22c55e,#16a34a)', border: 'none', borderRadius: 10, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  + Nuovo Utente
                </button>
              </div>
              
              {/* Lista utenti */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {users.map(user => (
                  <div 
                    key={user.username}
                    style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      padding: '12px 16px', 
                      background: bgInput, 
                      borderRadius: 10,
                      border: user.username === currentUser ? '2px solid #3b82f6' : '1px solid ' + bord
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ 
                        width: 40, 
                        height: 40, 
                        borderRadius: '50%', 
                        background: user.isAdmin ? 'linear-gradient(135deg,#f59e0b,#d97706)' : 'linear-gradient(135deg,#3b82f6,#2563eb)', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: 16
                      }}>
                        {user.username.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>
                          {user.username}
                          {user.isAdmin && <span style={{ marginLeft: 8, fontSize: 10, padding: '2px 6px', background: 'rgba(245,158,11,0.2)', color: '#f59e0b', borderRadius: 4 }}>ADMIN</span>}
                          {user.username === currentUser && <span style={{ marginLeft: 8, fontSize: 10, padding: '2px 6px', background: 'rgba(59,130,246,0.2)', color: '#3b82f6', borderRadius: 4 }}>TU</span>}
                        </div>
                        <div style={{ fontSize: 11, color: muted }}>
                          {user.tabs?.length > 0 && user.tabs.length < ALL_TABS.length 
                            ? `${user.tabs.length} tab abilitati` 
                            : 'Tutti i tab'}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button 
                        onClick={() => openEditUser(user)}
                        style={{ padding: '6px 12px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, color: '#3b82f6', fontSize: 12, cursor: 'pointer' }}
                      >
                        ✏️ Modifica
                      </button>
                      {user.username !== 'Oreste' && (
                        <button 
                          onClick={() => handleDeleteUser(user.username)}
                          style={{ padding: '6px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#ef4444', fontSize: 12, cursor: 'pointer' }}
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Modal Gestione Utente */}
      {showUserModal && showUserModal !== 'password' && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setShowUserModal(null)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 500, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: 20, borderBottom: '1px solid ' + bord }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                {showUserModal === 'new' ? '➕ Nuovo Utente' : `✏️ Modifica ${editingUser?.username}`}
              </h3>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: muted, marginBottom: 6 }}>Username</label>
                <input 
                  type="text" 
                  value={userForm.username}
                  onChange={e => setUserForm({ ...userForm, username: e.target.value })}
                  disabled={editingUser?.username === 'Oreste'}
                  style={{ width: '100%', padding: '12px 14px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: text, fontSize: 14 }}
                />
              </div>
              
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: muted, marginBottom: 6 }}>
                  {showUserModal === 'new' ? 'Password' : 'Nuova Password (lascia vuoto per non cambiare)'}
                </label>
                <input 
                  type="password" 
                  value={userForm.password}
                  onChange={e => setUserForm({ ...userForm, password: e.target.value })}
                  style={{ width: '100%', padding: '12px 14px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: text, fontSize: 14 }}
                />
              </div>
              
              {(showUserModal === 'new' || userForm.password) && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: muted, marginBottom: 6 }}>Conferma Password</label>
                  <input 
                    type="password" 
                    value={userForm.confirmPassword}
                    onChange={e => setUserForm({ ...userForm, confirmPassword: e.target.value })}
                    style={{ width: '100%', padding: '12px 14px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: text, fontSize: 14 }}
                  />
                </div>
              )}
              
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                  <input 
                    type="checkbox"
                    checked={userForm.isAdmin}
                    onChange={e => setUserForm({ ...userForm, isAdmin: e.target.checked })}
                    disabled={editingUser?.username === 'Oreste'}
                    style={{ width: 18, height: 18 }}
                  />
                  <span style={{ fontSize: 14 }}>👑 Amministratore (può gestire utenti)</span>
                </label>
              </div>
              
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: muted, marginBottom: 10 }}>📱 Tab Visibili</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                  {ALL_TABS.filter(t => t !== 'config').map(tabId => {
                    const tab = TAB_INFO[tabId];
                    const isSelected = userForm.tabs.includes(tabId);
                    return (
                      <div 
                        key={tabId}
                        onClick={() => toggleUserTab(tabId)}
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 8, 
                          padding: '10px 12px', 
                          background: isSelected ? 'rgba(34,197,94,0.1)' : bgInput,
                          border: isSelected ? '2px solid #22c55e' : '1px solid ' + bord,
                          borderRadius: 8,
                          cursor: 'pointer'
                        }}
                      >
                        <span style={{ fontSize: 16 }}>{tab?.icon}</span>
                        <span style={{ fontSize: 13, fontWeight: isSelected ? 600 : 400 }}>{tab?.label}</span>
                        {isSelected && <span style={{ marginLeft: 'auto', color: '#22c55e' }}>✓</span>}
                      </div>
                    );
                  })}
                </div>
                <div style={{ fontSize: 11, color: muted, marginTop: 8 }}>
                  ⚙️ Impostazioni è sempre visibile per tutti
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, padding: 20, borderTop: '1px solid ' + bord }}>
              <button onClick={() => setShowUserModal(null)} style={{ flex: 1, padding: 14, background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 10, color: muted, fontSize: 14, cursor: 'pointer' }}>Annulla</button>
              <button onClick={handleSaveUser} style={{ flex: 1, padding: 14, background: 'linear-gradient(135deg,#3b82f6,#2563eb)', border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>💾 Salva</button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal Cambio Password */}
      {showUserModal === 'password' && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setShowUserModal(null)}>
          <div style={{ background: dark ? '#1e293b' : '#fff', borderRadius: 20, width: '100%', maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: 20, borderBottom: '1px solid ' + bord }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>🔐 Cambia Password</h3>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: muted, marginBottom: 6 }}>Password Attuale</label>
                <input 
                  type="password" 
                  value={passwordForm.currentPassword}
                  onChange={e => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  style={{ width: '100%', padding: '12px 14px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: text, fontSize: 14 }}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: muted, marginBottom: 6 }}>Nuova Password</label>
                <input 
                  type="password" 
                  value={passwordForm.newPassword}
                  onChange={e => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  style={{ width: '100%', padding: '12px 14px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: text, fontSize: 14 }}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: muted, marginBottom: 6 }}>Conferma Nuova Password</label>
                <input 
                  type="password" 
                  value={passwordForm.confirmPassword}
                  onChange={e => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  style={{ width: '100%', padding: '12px 14px', background: bgInput, border: '1px solid ' + bord, borderRadius: 10, color: text, fontSize: 14 }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, padding: 20, borderTop: '1px solid ' + bord }}>
              <button onClick={() => setShowUserModal(null)} style={{ flex: 1, padding: 14, background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 10, color: muted, fontSize: 14, cursor: 'pointer' }}>Annulla</button>
              <button onClick={handleChangePassword} style={{ flex: 1, padding: 14, background: 'linear-gradient(135deg,#3b82f6,#2563eb)', border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>🔐 Cambia</button>
            </div>
          </div>
        </div>
      )}

      {/* SEZIONE 8: ZONA PERICOLOSA */}
      <div>
        <h3 style={{ fontSize: 12, fontWeight: 700, color: '#ef4444', textTransform: 'uppercase', marginBottom: 12, paddingLeft: 4 }}>⚠️ Zona Pericolosa</h3>
        <div style={{ background: 'rgba(239,68,68,0.05)', borderRadius: 16, border: '1px solid rgba(239,68,68,0.2)', padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div><div style={{ fontSize: 14, fontWeight: 600, color: '#ef4444' }}>Elimina tutti gli abbonamenti</div><div style={{ fontSize: 11, color: muted }}>{(data.abbonamenti || []).length} abbonamenti - azione irreversibile</div></div>
            <button onClick={() => { const n = (data.abbonamenti || []).length; if (!n) { alert('Nessun abbonamento.'); return; } if (!window.confirm(`Eliminare ${n} abbonamenti?`)) return; if (window.prompt('Scrivi ELIMINA:') !== 'ELIMINA') { alert('Annullato.'); return; } save({ ...data, abbonamenti: [] }); alert('✅ Eliminati!'); }} style={{ padding: '10px 20px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#ef4444', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>🗑️ Elimina Tutti</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditModal({ modal, setModal, upd, dark, inputStyle, labelStyle }) {
  const [item, setItem] = useState(modal.item);
  const save = () => {
    if (modal.section === 'contratti') {
      ['cliente', 'descrizione', 'scadenza', 'costo', 'linkDocumento', 'email', 'telefono'].forEach(f => upd('contratti', item.id, f, item[f]));
    } else {
      upd(modal.section, item.id, 'nome', item.nome);
      upd(modal.section, item.id, 'note', item.note);
      if (item.categoria) upd(modal.section, item.id, 'categoria', item.categoria);
    }
    setModal(null);
  };
  return (
    <Modal title="Modifica" onClose={() => setModal(null)} onSave={save} dark={dark}>
      {modal.section === 'contratti' ? (
        <>
          <label style={labelStyle}>Cliente</label>
          <input type="text" value={item.cliente || ''} onChange={e => setItem({ ...item, cliente: e.target.value })} style={inputStyle} />
          <label style={labelStyle}>Email Cliente</label>
          <input type="email" value={item.email || ''} onChange={e => setItem({ ...item, email: e.target.value })} style={inputStyle} placeholder="cliente@email.com" />
          <label style={labelStyle}>Telefono Cliente (per WhatsApp)</label>
          <input type="tel" value={item.telefono || ''} onChange={e => setItem({ ...item, telefono: e.target.value })} style={inputStyle} placeholder="333 1234567" />
          <label style={labelStyle}>Descrizione</label>
          <input type="text" value={item.descrizione || ''} onChange={e => setItem({ ...item, descrizione: e.target.value })} style={inputStyle} />
          <label style={labelStyle}>Scadenza</label>
          <input type="date" value={item.scadenza || ''} onChange={e => setItem({ ...item, scadenza: e.target.value })} style={inputStyle} />
          <label style={labelStyle}>Costo</label>
          <input type="number" value={item.costo || ''} onChange={e => setItem({ ...item, costo: parseFloat(e.target.value) || 0 })} style={inputStyle} />
          <label style={labelStyle}>Link Documento</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="url" value={item.linkDocumento || ''} onChange={e => setItem({ ...item, linkDocumento: e.target.value })} style={{ ...inputStyle, flex: 1 }} placeholder="https://..." />
          </div>
          {item.linkDocumento && (
            <a href={item.linkDocumento} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: '#3b82f6', marginTop: 4, display: 'block' }}>📄 Apri documento</a>
          )}
        </>
      ) : (
        <>
          <label style={labelStyle}>Nome</label>
          <input type="text" value={item.nome || ''} onChange={e => setItem({ ...item, nome: e.target.value })} style={inputStyle} />
          {modal.categories && (
            <>
              <label style={labelStyle}>Categoria</label>
              <select value={item.categoria || ''} onChange={e => setItem({ ...item, categoria: e.target.value })} style={inputStyle}>
                {modal.categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.nome}</option>)}
              </select>
            </>
          )}
          <label style={labelStyle}>Note</label>
          <input type="text" value={item.note || ''} onChange={e => setItem({ ...item, note: e.target.value })} style={inputStyle} />
        </>
      )}
    </Modal>
  );
}

function AddModalComp({ modal, setModal, add, data, dark, inputStyle, labelStyle }) {
  const [form, setForm] = useState(
    modal.type === 'nota' ? { titolo: '', contenuto: '', allegato: '' }
    : modal.type === 'contratto' ? { cliente: '', email: '', telefono: '', descrizione: '', scadenza: '', costo: '', linkDocumento: '' }
    : modal.type === 'ricorrenza' ? { nome: '', tipo: 'uscita', importo: '', giorno: 1 }
    : modal.type === 'categorized' ? { nome: '', importo: '', note: '', categoria: modal.categories?.[0]?.id || '' }
    : { nome: '', importo: '', note: '' }
  );
  const voci = modal.type === 'ricorrenza' ? (form.tipo === 'entrata' ? data.entrate : data.uscite) : [];
  const save = () => {
    if (modal.type === 'nota') add(modal.section, { titolo: form.titolo, contenuto: form.contenuto, allegato: form.allegato || null });
    else if (modal.type === 'contratto') add(modal.section, { ...form, costo: parseFloat(form.costo) || 0 });
    else if (modal.type === 'ricorrenza') add(modal.section, { ...form, importo: parseFloat(form.importo) || 0, giorno: parseInt(form.giorno) || 1 });
    else if (modal.type === 'categorized') add(modal.section, { nome: form.nome, importo: parseFloat(form.importo) || 0, note: form.note, categoria: form.categoria });
    else add(modal.section, { nome: form.nome, importo: parseFloat(form.importo) || 0, note: form.note });
  };

  return (
    <Modal title={modal.type === 'nota' ? 'Nuova Nota' : modal.type === 'contratto' ? 'Nuovo Contratto' : modal.type === 'ricorrenza' ? 'Nuova Ricorrenza' : 'Nuova Voce'} onClose={() => setModal(null)} onSave={save} dark={dark}>
      {modal.type === 'nota' ? (
        <>
          <label style={labelStyle}>Titolo</label>
          <input type="text" value={form.titolo} onChange={e => setForm({ ...form, titolo: e.target.value })} style={inputStyle} />
          <label style={labelStyle}>Contenuto</label>
          <textarea value={form.contenuto} onChange={e => setForm({ ...form, contenuto: e.target.value })} style={{ ...inputStyle, minHeight: 120 }} />
          
          {/* Upload Allegato */}
          <label style={labelStyle}>Allegato</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {form.allegato ? (
              <>
                <a href={form.allegato} target="_blank" rel="noopener noreferrer" style={{ flex: 1, padding: '12px 14px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 10, color: '#22c55e', fontSize: 13, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 }}>
                  📎 Allegato caricato
                </a>
                <button 
                  type="button"
                  onClick={() => setForm({ ...form, allegato: '' })}
                  style={{ padding: '12px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, color: '#ef4444', fontSize: 14, cursor: 'pointer' }}
                >
                  ✕
                </button>
              </>
            ) : (
              <input type="url" value={form.allegato || ''} onChange={e => setForm({ ...form, allegato: e.target.value })} style={{ ...inputStyle, flex: 1 }} placeholder="Link allegato (https://...)" />
            )}
          </div>
        </>
      ) : modal.type === 'contratto' ? (
        <>
          <label style={labelStyle}>Cliente</label>
          <input type="text" value={form.cliente} onChange={e => setForm({ ...form, cliente: e.target.value })} style={inputStyle} />
          <label style={labelStyle}>Email Cliente</label>
          <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={inputStyle} placeholder="cliente@email.com" />
          <label style={labelStyle}>Telefono Cliente (per WhatsApp)</label>
          <input type="tel" value={form.telefono} onChange={e => setForm({ ...form, telefono: e.target.value })} style={inputStyle} placeholder="333 1234567" />
          <label style={labelStyle}>Descrizione</label>
          <input type="text" value={form.descrizione} onChange={e => setForm({ ...form, descrizione: e.target.value })} style={inputStyle} />
          <label style={labelStyle}>Scadenza</label>
          <input type="date" value={form.scadenza} onChange={e => setForm({ ...form, scadenza: e.target.value })} style={inputStyle} />
          <label style={labelStyle}>Costo (€)</label>
          <input type="number" value={form.costo} onChange={e => setForm({ ...form, costo: e.target.value })} style={inputStyle} />
          <label style={labelStyle}>Link Documento</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="url" value={form.linkDocumento} onChange={e => setForm({ ...form, linkDocumento: e.target.value })} style={{ ...inputStyle, flex: 1 }} placeholder="https://..." />
          </div>
        </>
      ) : modal.type === 'ricorrenza' ? (
        <>
          <label style={labelStyle}>Tipo</label>
          <select value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value, nome: '' })} style={inputStyle}>
            <option value="entrata">↓ Entrata</option>
            <option value="uscita">↑ Uscita</option>
          </select>
          <label style={labelStyle}>Voce</label>
          <select value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} style={inputStyle}>
            <option value="">Seleziona...</option>
            {voci.map(v => <option key={v.id} value={v.nome}>{v.nome}</option>)}
          </select>
          <label style={labelStyle}>Importo (€)</label>
          <input type="number" value={form.importo} onChange={e => setForm({ ...form, importo: e.target.value })} style={inputStyle} />
          <label style={labelStyle}>Giorno del mese (1-31)</label>
          <input type="number" min="1" max="31" value={form.giorno} onChange={e => setForm({ ...form, giorno: e.target.value })} style={inputStyle} />
        </>
      ) : modal.type === 'categorized' ? (
        <>
          <label style={labelStyle}>Categoria</label>
          <select value={form.categoria} onChange={e => setForm({ ...form, categoria: e.target.value })} style={inputStyle}>
            {modal.categories?.map(c => <option key={c.id} value={c.id}>{c.icon} {c.nome}</option>)}
          </select>
          <label style={labelStyle}>Nome</label>
          <input type="text" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} style={inputStyle} />
          <label style={labelStyle}>Importo (€)</label>
          <input type="number" value={form.importo} onChange={e => setForm({ ...form, importo: e.target.value })} style={inputStyle} />
          <label style={labelStyle}>Note</label>
          <input type="text" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} style={inputStyle} />
        </>
      ) : (
        <>
          <label style={labelStyle}>Nome</label>
          <input type="text" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} style={inputStyle} />
          <label style={labelStyle}>Importo (€)</label>
          <input type="number" value={form.importo} onChange={e => setForm({ ...form, importo: e.target.value })} style={inputStyle} />
          <label style={labelStyle}>Note</label>
          <input type="text" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} style={inputStyle} />
        </>
      )}
    </Modal>
  );
}

function CatModalComp({ modal, setModal, addCat, data, dark, inputStyle, labelStyle }) {
  const [form, setForm] = useState({ nome: modal.category?.nome || '', icon: modal.category?.icon || '📥', color: modal.category?.color || '#3b82f6' });
  const save = () => {
    if (modal.isNew) addCat(modal.type, form);
    else {
      const k = modal.type === 'entrate' ? 'categorieEntrate' : 'categorieUscite';
      // Update existing - handled in parent
    }
    setModal(null);
  };
  return (
    <Modal title={modal.isNew ? 'Nuova Categoria' : 'Modifica Categoria'} onClose={() => setModal(null)} onSave={save} dark={dark}>
      <label style={labelStyle}>Nome</label>
      <input type="text" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} style={inputStyle} />
      <label style={labelStyle}>Icona</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
        {ICONE.map(icon => (
          <button key={icon} onClick={() => setForm({ ...form, icon })} style={{ width: 40, height: 40, border: form.icon === icon ? '2px solid #3b82f6' : '1px solid ' + (dark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'), borderRadius: 8, background: form.icon === icon ? 'rgba(59,130,246,0.2)' : (dark ? 'rgba(15,23,42,0.6)' : '#f1f5f9'), fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</button>
        ))}
      </div>
      <label style={labelStyle}>Colore</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
        {COLORI.map(color => (
          <button key={color} onClick={() => setForm({ ...form, color })} style={{ width: 32, height: 32, border: form.color === color ? '2px solid #fff' : '2px solid transparent', borderRadius: 8, background: color, cursor: 'pointer', boxShadow: form.color === color ? '0 0 0 2px rgba(59,130,246,0.5)' : 'none' }} />
        ))}
      </div>
    </Modal>
  );
}

function PrintPreview({ data, totals, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 2000, overflow: 'auto', padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 12, padding: 16 }}>
        <button onClick={() => window.print()} style={{ padding: '12px 24px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>🖨️ Stampa</button>
        <button onClick={onClose} style={{ padding: '12px 24px', background: '#374151', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>✕ Chiudi</button>
      </div>
      <div id="print-content" style={{ maxWidth: 800, margin: '0 auto', background: '#fff', padding: 40, borderRadius: 8, color: '#1a1a2e' }}>
        <h1 style={{ textAlign: 'center' }}>📊 GESTIONE PC CARE - {new Date().toLocaleDateString('it-IT')}</h1>
        <div style={{ display: 'flex', justifyContent: 'space-around', padding: 20, background: '#f3f4f6', borderRadius: 8, marginBottom: 20 }}>
          <div style={{ textAlign: 'center' }}><small>ENTRATE</small><div style={{ fontSize: 20, fontWeight: 700, color: '#16a34a' }}>{fmtCur(totals.e)}</div></div>
          <div style={{ textAlign: 'center' }}><small>USCITE</small><div style={{ fontSize: 20, fontWeight: 700, color: '#dc2626' }}>{fmtCur(totals.u)}</div></div>
          <div style={{ textAlign: 'center' }}><small>SALDO</small><div style={{ fontSize: 20, fontWeight: 700, color: totals.s >= 0 ? '#16a34a' : '#dc2626' }}>{fmtCur(totals.s)}</div></div>
        </div>
        <h3 style={{ color: '#16a34a' }}>ENTRATE</h3>
        <table style={{ width: '100%', marginBottom: 20 }}><tbody>
          {data.entrate.filter(i => i.importo > 0).map(i => <tr key={i.id} style={{ borderBottom: '1px solid #e5e7eb' }}><td style={{ padding: 8 }}>{i.nome}</td><td style={{ padding: 8, textAlign: 'right', color: '#16a34a' }}>{fmtCur(i.importo)}</td></tr>)}
        </tbody></table>
        <h3 style={{ color: '#dc2626' }}>USCITE</h3>
        <table style={{ width: '100%' }}><tbody>
          {data.uscite.filter(i => i.importo > 0).map(i => <tr key={i.id} style={{ borderBottom: '1px solid #e5e7eb' }}><td style={{ padding: 8 }}>{i.nome}</td><td style={{ padding: 8, textAlign: 'right', color: '#dc2626' }}>{fmtCur(i.importo)}</td></tr>)}
        </tbody></table>
      </div>
      <style>{`@media print{body *{visibility:hidden}#print-content,#print-content *{visibility:visible}#print-content{position:absolute;left:0;top:0;width:100%}}`}</style>
    </div>
  );
}


